// Trivial change: trigger redeploy on Vercel
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import QRCode from "qrcode";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_51TDDeoE4AeaMGNwkifjoHseev2BlnFdVLx4q5419FeuK5hTz4P27psYSw4CFAYbgqaROESgjaYWNNQoZOY9G2J6300KrUT7ExY');

const API_BASE = process.env.REACT_APP_API_URL || '';
const apiFetch = (path, options) => fetch(`${API_BASE}${path}`, options);

const BIZ = { name: "Festus Okunlola", tradingAs: "Replai", address: "Fritz-Litzlfelderstraße 21, 85614 Kirchseeon, Germany", phone: "+4917665714223", email: "hello@replai.work", web: "https://replai.work" };

const PLANS = [
  { id: "starter", name: "Starter", price: 99, reviews: 30, platforms: "Google Business Profile", response: "24 hours", reports: "Monthly" },
  { id: "growth", name: "Growth", price: 199, reviews: 100, platforms: "Google + Facebook + Trustpilot", response: "4 hours", reports: "Weekly" },
  { id: "enterprise", name: "Enterprise", price: 399, reviews: "Unlimited", platforms: "All platforms + 10 locations", response: "2 hours", reports: "Weekly + custom" },
];

const PLAN_PLATFORM_MAP = {
  starter: ["Google Business Profile"],
  growth: ["Google Business Profile", "Facebook", "Trustpilot"],
  enterprise: ["Google Business Profile", "Facebook", "Trustpilot", "Yelp", "TripAdvisor"],
};

const PLAN_LOCATION_CAPS = { starter: 1, growth: 3, enterprise: 10 };
const getLocationCap = (planId) => PLAN_LOCATION_CAPS[planId] || 1;

const getPlatformsForPlan = (planId) => PLAN_PLATFORM_MAP[planId] || ["Google Business Profile"];
const STAFF_TITLES_LADDER = [
  "Associate",
  "Specialist",
  "Supervisor",
  "Manager",
  "Senior Manager",
  "Country Manager",
  "Regional Director",
  "Head of Operations",
];
const STAFF_SCOPE_OPTIONS = ["city", "country", "global"];
const DEFAULT_STAFF_TITLE = "Associate";
const ONBOARDING_VERSION = "v2";
const LANGUAGE_OPTIONS = [
  "English",
  "German",
  "French",
  "Spanish",
  "Italian",
  "Portuguese",
  "Dutch",
];
const UI_LANGUAGE_OPTIONS = [
  { code: "en", label: "English" },
  { code: "de", label: "German" },
  { code: "fr", label: "French" },
  { code: "es", label: "Spanish" },
  { code: "it", label: "Italian" },
  { code: "pt", label: "Portuguese" },
  { code: "nl", label: "Dutch" },
  { code: "ar", label: "Arabic" },
  { code: "tr", label: "Turkish" },
  { code: "zh-CN", label: "Chinese" },
];
const normalizeUiLanguageCode = (value) => {
  if (!value) return "en";
  const lower = String(value).toLowerCase();
  if (lower.startsWith("zh")) return "zh-CN";
  const base = lower.split("-")[0];
  return UI_LANGUAGE_OPTIONS.some((item) => item.code === base) ? base : "en";
};
const formatSignedDelta = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0.00";
  const fixed = n.toFixed(2);
  if (n > 0) return `+${fixed}`;
  return fixed;
};
const extractMailboxEmailAddress = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const bracketMatch = text.match(/<([^>]+)>/);
  if (bracketMatch?.[1]) return bracketMatch[1].trim();
  const plainMatch = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  return plainMatch?.[0] || "";
};
const normalizeReplySubject = (subject) => {
  const cleaned = String(subject || "").trim() || "No subject";
  return /^re:/i.test(cleaned) ? cleaned : `Re: ${cleaned}`;
};
const normalizeMailboxThreadSubject = (subject) => (
  String(subject || "No subject")
    .replace(/^(\s*(re|fw|fwd):\s*)+/i, "")
    .trim()
    .toLowerCase() || "no subject"
);
const extractMailboxReferenceTokens = (value) => {
  const text = String(value || "").trim();
  if (!text) return [];
  const bracketMatches = text.match(/<[^>]+>/g);
  if (bracketMatches?.length) return Array.from(new Set(bracketMatches.map((item) => item.trim())));
  return Array.from(new Set(text.split(/\s+/).map((item) => item.trim()).filter(Boolean)));
};
const getMailboxTimestamp = (value) => {
  const timestamp = Date.parse(String(value || ""));
  return Number.isFinite(timestamp) ? timestamp : 0;
};
const buildMailboxFallbackThreadKey = (message, mailboxAddress = "") => {
  const mailboxEmail = extractMailboxEmailAddress(mailboxAddress).toLowerCase();
  const participants = [
    extractMailboxEmailAddress(message?.from || ""),
    ...String(message?.to || "").split(",").map((entry) => extractMailboxEmailAddress(entry)),
  ]
    .map((entry) => entry.toLowerCase())
    .filter(Boolean);
  const counterparts = Array.from(new Set(participants.filter((entry) => entry !== mailboxEmail)));
  const participantKey = (counterparts.length ? counterparts : participants).sort().join("|") || "unknown";
  return `fallback:${normalizeMailboxThreadSubject(message?.subject)}:${participantKey}`;
};
const getStaffThreadUpdatedAt = (thread) => {
  const threadTs = Date.parse(String(thread?.updatedAt || thread?.createdAt || ""));
  const replyTs = Math.max(0, ...((thread?.replies || []).map((reply) => Date.parse(String(reply?.createdAt || "")) || 0)));
  return Math.max(Number.isFinite(threadTs) ? threadTs : 0, replyTs);
};
const mergeStaffProblemThreads = (localThreads, remoteThreads) => {
  const local = Array.isArray(localThreads) ? localThreads : [];
  const remote = Array.isArray(remoteThreads) ? remoteThreads : [];
  if (!remote.length) return local;

  const mergedMap = new Map(local.map((thread) => [String(thread?.id || ""), thread]));
  for (const remoteThread of remote) {
    const id = String(remoteThread?.id || "");
    if (!id) continue;
    const localThread = mergedMap.get(id);
    if (!localThread || getStaffThreadUpdatedAt(remoteThread) >= getStaffThreadUpdatedAt(localThread)) {
      mergedMap.set(id, remoteThread);
    }
  }

  const merged = Array.from(mergedMap.values())
    .filter((thread) => thread && thread.id)
    .sort((a, b) => getStaffThreadUpdatedAt(b) - getStaffThreadUpdatedAt(a));

  if (merged.length === local.length) {
    let unchanged = true;
    for (let i = 0; i < merged.length; i += 1) {
      if (String(merged[i]?.id || "") !== String(local[i]?.id || "")) {
        unchanged = false;
        break;
      }
      if (getStaffThreadUpdatedAt(merged[i]) !== getStaffThreadUpdatedAt(local[i])) {
        unchanged = false;
        break;
      }
    }
    if (unchanged) return local;
  }

  return merged;
};
const isImageAttachment = (file) => String(file?.type || "").toLowerCase().startsWith("image/");
const isAudioAttachment = (file) => String(file?.type || "").toLowerCase().startsWith("audio/") || Boolean(file?.voiceNote);
const getStaffAttachmentDataUrl = (file) => {
  const rawType = String(file?.type || "").trim();
  const contentType = rawType || "application/octet-stream";
  if (String(file?.content || "")) return `data:${contentType};base64,${file.content}`;
  if (String(file?.dataUrl || "").startsWith("data:")) return file.dataUrl;
  return "";
};
const createEmptyMailboxCompose = () => ({ to: "", subject: "", body: "", inReplyTo: "", references: "", attachments: [] });
const readMailboxAttachmentFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve({
    name: file.name,
    type: file.type || "application/octet-stream",
    size: Number(file.size || 0),
    content: String(reader.result || "").split(",").pop() || "",
  });
  reader.onerror = () => reject(new Error(`Unable to read ${file.name}`));
  reader.readAsDataURL(file);
});
const buildMailboxAttachmentDataUrl = (attachment) => {
  const content = String(attachment?.content || "").trim();
  if (!content) return "";
  const type = String(attachment?.type || "application/octet-stream").trim() || "application/octet-stream";
  return `data:${type};base64,${content}`;
};
const MAILBOX_PREVIEW_PAGE_SIZE = 20;
const getClientOnboardingRating = (client) => Number(client?.ratingAtOnboarding ?? client?.avgRating ?? 0);
const getClientRatingGrowth = (client) => Number((Number(client?.avgRating || 0) - getClientOnboardingRating(client)).toFixed(2));
const I18N_DICTIONARY = {
  en: {
    tabs: {
      queue: "Review Queue",
      workboard: "Staff Workboard",
      staff: "Staff Directory",
      clients: "Client Management",
      selfserve: "Self-Serve Operations",
      outreach: "Outreach & Marketing",
      contracts: "Contracts",
      analytics: "Analytics",
      settings: "Settings",
    },
    selfServe: {
      title: "Self-Serve Operations",
      accounts: "Self-Serve Accounts",
      fullDashboard: "Open Full Dashboard",
      closeDashboard: "Close Client Dashboard",
      backToOps: "Back to Self-Serve Ops",
      page: "Page",
      of: "of",
    },
    staff: {
      privileges: "Privileges",
      selectPreset: "Quick Presets",
      reviewGroup: "Review Workflow",
      clientGroup: "Client Management",
      supportGroup: "Support",
      reportingGroup: "Reporting & Contracts",
      accessGroup: "Settings & Access",
    },
  },
  de: {
    tabs: {
      queue: "Bewertungswarteschlange",
      workboard: "Mitarbeiter-Board",
      staff: "Mitarbeiterverzeichnis",
      clients: "Kundenverwaltung",
      selfserve: "Self-Serve Betrieb",
      outreach: "Outreach & Marketing",
      contracts: "Vertraege",
      analytics: "Analysen",
      settings: "Einstellungen",
    },
    selfServe: {
      title: "Self-Serve Betrieb",
      accounts: "Self-Serve Konten",
      fullDashboard: "Vollstaendiges Dashboard",
      closeDashboard: "Kunden-Dashboard schliessen",
      backToOps: "Zurueck zu Self-Serve Ops",
      page: "Seite",
      of: "von",
    },
    staff: {
      privileges: "Berechtigungen",
      selectPreset: "Schnellvorlagen",
      reviewGroup: "Review-Workflow",
      clientGroup: "Kundenverwaltung",
      supportGroup: "Support",
      reportingGroup: "Reporting & Vertraege",
      accessGroup: "Einstellungen & Zugriff",
    },
    phrases: {
      "Reviews": "Bewertungen",
      "Workboard": "Arbeitsboard",
      "Staff": "Mitarbeiter",
      "Clients": "Kunden",
      "Self-Serve Ops": "Self-Serve Ops",
      "Outreach": "Outreach",
      "Settings": "Einstellungen",
      "Notifications": "Benachrichtigungen",
      "All Clients": "Alle Kunden",
      "All Status": "Alle Status",
      "Pending": "Ausstehend",
      "Approved": "Freigegeben",
      "Published": "Veroeffentlicht",
      "Rejected": "Abgelehnt",
      "+ Add Client": "+ Kunde hinzufuegen",
      "+ Add Prospect": "+ Lead hinzufuegen",
      "Create Staff": "Mitarbeiter erstellen",
      "Filter Staff": "Mitarbeiter filtern",
      "Name": "Name",
      "Username": "Benutzername",
      "Password": "Passwort",
      "Email": "E-Mail",
      "Phone": "Telefon",
      "City": "Stadt",
      "Country": "Land",
      "Title": "Titel",
      "Scope": "Bereich",
      "Scope Value": "Bereichswert",
      "Preferred Language": "Bevorzugte Sprache",
      "Create Staff Account": "Mitarbeiterkonto erstellen",
      "Edit": "Bearbeiten",
      "Delete": "Loeschen",
      "Save": "Speichern",
      "Cancel": "Abbrechen",
      "Search Clients": "Kunden suchen",
      "Self-Serve Accounts": "Self-Serve Konten",
      "Showing": "Zeige",
      "Prev": "Zurueck",
      "Next": "Weiter",
      "All": "Alle",
      "Active": "Aktiv",
      "Suspended": "Gesperrt",
      "Open Full Dashboard": "Vollstaendiges Dashboard",
      "Close Client Dashboard": "Kunden-Dashboard schliessen",
      "Back to Self-Serve Ops": "Zurueck zu Self-Serve Ops",
      "Overview": "Uebersicht",
      "Billing": "Abrechnung",
      "Support": "Support",
      "Actions": "Aktionen",
      "Status": "Status",
      "Plan": "Plan",
      "Rating": "Bewertung",
      "Growth": "Wachstum",
      "Self-Serve Clients": "Self-Serve Kunden",
      "Open Tickets": "Offene Tickets",
      "Avg Rating": "Durchschn. Bewertung",
      "Avg Growth": "Durchschn. Wachstum",
      "Self-Serve MRR": "Self-Serve MRR",
      "Reset Owner Password": "Owner-Passwort zuruecksetzen",
      "Suspend": "Sperren",
      "Reactivate": "Reaktivieren",
      "Generate Report": "Report erstellen",
      "Delete Account": "Konto loeschen",
      "Disconnect Google": "Google trennen",
      "Connect Google": "Google verbinden",
      "Trustpilot": "Trustpilot",
      "Connect Trustpilot": "Trustpilot verbinden",
      "Disconnect Trustpilot": "Trustpilot trennen",
      "Sync Trustpilot": "Trustpilot synchronisieren",
      "Connected and ready to sync reviews.": "Verbunden und bereit zur Synchronisierung von Bewertungen.",
      "Connect your Trustpilot account to start review sync.": "Verbinden Sie Ihr Trustpilot-Konto, um die Synchronisierung zu starten.",
      "🔌 Disconnect Trustpilot": "🔌 Trustpilot trennen",
      "🔗 Connect Trustpilot": "🔗 Trustpilot verbinden",
      "🔄 Sync Trustpilot": "🔄 Trustpilot synchronisieren",
      "Trustpilot OAuth started for {{client}}": "Trustpilot OAuth fuer {{client}} gestartet",
      "Synced {{count}} Trustpilot review(s) for {{client}}": "{{count}} Trustpilot-Bewertung(en) fuer {{client}} synchronisiert",
      "Disconnect Trustpilot for {{client}}?": "Trustpilot fuer {{client}} trennen?",
      "Disconnected Trustpilot for {{client}}": "Trustpilot fuer {{client}} getrennt",
      "Trustpilot integration is now wired to backend endpoints. Configure": "Die Trustpilot-Integration ist jetzt mit den Backend-Endpunkten verbunden. Konfigurieren Sie",
      "to use the real OAuth + review sync flow.": "um den echten OAuth- und Bewertungs-Sync zu verwenden.",
      "Trustpilot bridge configured": "Trustpilot-Bridge konfiguriert",
      "Trustpilot bridge not configured yet": "Trustpilot-Bridge noch nicht konfiguriert",
      "Sync Reviews": "Bewertungen synchronisieren",
      "Package": "Paket",
      "Price": "Preis",
      "Payment": "Zahlung",
      "Tickets": "Tickets",
      "Detail:": "Detail:",
      "Enabled": "Aktiviert",
      "Disabled": "Deaktiviert",
      "Resolve": "Loesen",
      "Close": "Schliessen",
      "Reply": "Antworten",
      "Mark In Progress": "Als in Arbeit markieren",
      "No self-serve clients match your filters.": "Keine passenden Self-Serve Kunden gefunden.",
      "No support tickets for this client.": "Keine Support-Tickets fuer diesen Kunden.",
      "No reviews for this client yet.": "Noch keine Bewertungen fuer diesen Kunden.",
      "Logout": "Abmelden",
      "Preparing secure login...": "Sichere Anmeldung wird vorbereitet...",
      "Choose your workspace": "Ihren Arbeitsbereich waehlen",
      "Client self-serve portal": "Kunden-Self-Serve-Portal",
      "Private agency access link is invalid or expired.": "Privater Agentur-Zugangslink ist ungueltig oder abgelaufen.",
      "Agency Dashboard": "Agentur-Dashboard",
      "Client Self-Serve": "Kunden Self-Serve",
      "Loading accounts...": "Konten werden geladen...",
      "Log In to Agency Dashboard": "Am Agentur-Dashboard anmelden",
      "Forgot password?": "Passwort vergessen?",
      "Username or Email": "Benutzername oder E-Mail",
      "Request New Password via Email": "Neues Passwort per E-Mail anfordern",
      "If the account exists, a reset email request has been sent.": "Falls das Konto existiert, wurde eine Zuruecksetzungs-E-Mail gesendet.",
      "Back to login": "Zurueck zur Anmeldung",
      "Log In": "Anmelden",
      "Self-Serve": "Self-Serve",
      "Client Username": "Kunden-Benutzername",
      "Log In to Client Dashboard": "Am Kunden-Dashboard anmelden",
      "Business name": "Firmenname",
      "Contact person": "Kontaktperson",
      "Street address + number": "Strasse + Hausnummer",
      "Postcode": "Postleitzahl",
      "Confirm password": "Passwort bestaetigen",
      "Payment method": "Zahlungsmethode",
      "Credit Card Payment": "Kreditkartenzahlung",
      "Self-Serve Terms & Conditions": "Self-Serve Geschaeftsbedingungen",
      "I accept the self-serve terms and authorize Replai to provision my workspace.": "Ich akzeptiere die Self-Serve-Bedingungen und autorisiere Replai, meinen Arbeitsbereich einzurichten.",
      "Opening secure checkout...": "Sichere Kaufabwicklung wird geoeffnet...",
      "Create Self-Serve Account": "Self-Serve-Konto erstellen",
      "Request Managed Account": "Managed-Konto anfordern",
      "Submitting...": "Wird gesendet...",
      "Preferred package": "Bevorzugtes Paket",
      "Preferred payment method": "Bevorzugte Zahlungsmethode",
      "Authenticator code (6 digits)": "Authenticator-Code (6 Ziffern)",
      "Backup code (e.g. ABCD-EFGH)": "Backup-Code (z.B. ABCD-EFGH)",
      "Verify & Continue": "Verifizieren & Fortfahren",
      "Recovery Backup Codes": "Wiederherstellungs-Backup-Codes",
      "I have saved these codes": "Ich habe diese Codes gespeichert",
      "Set a new password to continue": "Neues Passwort festlegen, um fortzufahren",
      "For security, owner accounts must change the initial password on first login.": "Aus Sicherheitsgruenden muessen Owner-Konten das Erstpasswort bei der ersten Anmeldung aendern.",
      "Current password": "Aktuelles Passwort",
      "New password": "Neues Passwort",
      "Confirm new password": "Neues Passwort bestaetigen",
      "Update password": "Passwort aktualisieren",
      "Security Setup Required": "Sicherheitseinrichtung erforderlich",
      "Every account must enable all recommended protection layers before continuing.": "Jedes Konto muss alle empfohlenen Schutzebenen aktivieren, bevor es fortfahren kann.",
      "Activate All Recommended Layers": "Alle empfohlenen Ebenen aktivieren",
      "Activate": "Aktivieren",
      "Authenticator MFA": "Authenticator-MFA",
      "Set Up Your Authenticator App": "Authenticator-App einrichten",
      "Verify & Activate MFA": "MFA verifizieren & aktivieren",
      "Or enter this key manually in your app:": "Oder geben Sie diesen Schluessel manuell in Ihrer App ein:",
      "Owner": "Eigentuemer",
      "Member": "Mitglied",
      "Client Dashboard": "Kunden-Dashboard",
      "Back to Homepage": "Zurueck zur Startseite",
      "Language": "Sprache",
      "Managed": "Managed",
      "Free trial is": "Die kostenlose Testphase betraegt",
      "days": "Tage",
      "Billing country is verified server-side": "Abrechnungsland wird serverseitig verifiziert",
      "Country field edits do not reduce pricing.": "Aenderungen am Laenderfeld fuehren nicht zu Preissenkungen.",
      "Tell us about your business and we'll reach out within 24 hours to set up your managed account, including trial access and billing.": "Teilen Sie uns Ihre Geschaeftsdaten mit und wir melden uns innerhalb von 24 Stunden, um Ihr Managed-Konto einzurichten, einschliesslich Testzugang und Abrechnung.",
      "Progress:": "Fortschritt:",
      "enabled": "aktiviert",
      "Download an authenticator app on your smartphone:": "Laden Sie eine Authenticator-App auf Ihr Smartphone herunter:",
      "Google Authenticator, Microsoft Authenticator, Authy, or similar.": "Google Authenticator, Microsoft Authenticator, Authy oder aehnliche.",
      "Scan a QR code": "QR-Code scannen",
      "Use Google Authenticator, Microsoft Authenticator, Authy, or any TOTP app to scan this QR code. A new 6-digit code will be generated every 30 seconds.": "Verwenden Sie Google Authenticator, Microsoft Authenticator, Authy oder eine andere TOTP-App, um diesen QR-Code zu scannen. Alle 30 Sekunden wird ein neuer 6-stelliger Code generiert.",
      "Enter the": "Geben Sie den",
      "6-digit code": "6-stelligen Code",
      "from your app to confirm setup": "aus Ihrer App ein, um die Einrichtung zu bestaetigen",
    },
    messages: {
      "Support response saved": "Support-Antwort gespeichert",
      "Billing settings updated": "Abrechnungseinstellungen aktualisiert",
      "Self-serve account deleted": "Self-Serve Konto geloescht",
      "Owner password reset. Temporary password displayed.": "Owner-Passwort zurueckgesetzt. Temporaeres Passwort angezeigt.",
      "No owner account found": "Kein Owner-Konto gefunden",
      "Enter a subject and message": "Betreff und Nachricht eingeben",
      "No permission": "Keine Berechtigung",
      "Staff account created": "Mitarbeiterkonto erstellt",
      "Staff profile updated": "Mitarbeiterprofil aktualisiert",
      "Staff account deleted": "Mitarbeiterkonto geloescht",
      "Password updated successfully": "Passwort erfolgreich aktualisiert",
      "Your password has been changed": "Ihr Passwort wurde geaendert",
      "Current password is incorrect": "Aktuelles Passwort ist falsch",
      "Password confirmation does not match": "Passwortbestaetigung stimmt nicht ueberein",
      "Review moved back to pending": "Bewertung wurde auf ausstehend gesetzt",
      "Response approved": "Antwort freigegeben",
      "Response published": "Antwort veroeffentlicht",
      "Draft updated": "Entwurf aktualisiert",
      "Member user added": "Mitglied hinzugefuegt",
      "Member removed": "Mitglied entfernt",
      "Profile completed": "Profil abgeschlossen",
      "Onboarding reopened": "Onboarding erneut gestartet",
      "Updated": "Aktualisiert",
      "Approved": "Freigegeben",
      "Rejected": "Abgelehnt",
      "Published to Google ✓": "Bei Google veroeffentlicht ✓",
      "Moved back to pending": "Auf ausstehend zurueckgesetzt",
      "Response deleted": "Antwort geloescht",
      "Prospect added": "Lead hinzugefuegt",
      "Client added": "Kunde hinzugefuegt",
      "Google sync failed": "Google-Sync fehlgeschlagen",
      "Trustpilot connection confirmed ✅": "Trustpilot-Verbindung bestaetigt ✅",
      "Trustpilot auth window opened. Complete consent, then click Sync Reviews.": "Trustpilot-Autorisierungsfenster geoeffnet. Bitte Zustimmung abschliessen und dann auf Bewertungen synchronisieren klicken.",
      "Trustpilot connect endpoint is not configured": "Trustpilot-Connect-Endpunkt ist nicht konfiguriert",
      "Failed to start Trustpilot connection": "Trustpilot-Verbindung konnte nicht gestartet werden",
      "Trustpilot review sync failed": "Trustpilot-Bewertungssync fehlgeschlagen",
      "Trustpilot sync failed": "Trustpilot-Sync fehlgeschlagen",
      "Failed to send reminder email": "Erinnerungs-E-Mail konnte nicht gesendet werden",
      "Failed to build reminder preview": "Erinnerungsvorschau konnte nicht erstellt werden",
    },
  },
  fr: {
    tabs: {
      queue: "File des avis",
      workboard: "Tableau equipe",
      staff: "Annuaire equipe",
      clients: "Gestion clients",
      selfserve: "Operations self-serve",
      outreach: "Prospection & marketing",
      contracts: "Contrats",
      analytics: "Analytique",
      settings: "Parametres",
    },
    selfServe: {
      title: "Operations self-serve",
      accounts: "Comptes self-serve",
      fullDashboard: "Ouvrir le tableau complet",
      closeDashboard: "Fermer le tableau client",
      backToOps: "Retour aux operations self-serve",
      page: "Page",
      of: "sur",
    },
    staff: {
      privileges: "Privileges",
      selectPreset: "Profils rapides",
      reviewGroup: "Workflow des avis",
      clientGroup: "Gestion clients",
      supportGroup: "Support",
      reportingGroup: "Rapports & contrats",
      accessGroup: "Parametres & acces",
    },
    phrases: {
      "Reviews": "Avis",
      "Workboard": "Tableau",
      "Staff": "Equipe",
      "Clients": "Clients",
      "Self-Serve Ops": "Ops self-serve",
      "Outreach": "Prospection",
      "Settings": "Parametres",
      "Notifications": "Notifications",
      "All Clients": "Tous les clients",
      "All Status": "Tous les statuts",
      "Pending": "En attente",
      "Approved": "Approuve",
      "Published": "Publie",
      "Rejected": "Rejete",
      "+ Add Client": "+ Ajouter un client",
      "+ Add Prospect": "+ Ajouter un prospect",
      "Create Staff": "Creer un membre",
      "Filter Staff": "Filtrer l'equipe",
      "Name": "Nom",
      "Username": "Nom d'utilisateur",
      "Password": "Mot de passe",
      "Email": "E-mail",
      "Phone": "Telephone",
      "City": "Ville",
      "Country": "Pays",
      "Title": "Titre",
      "Scope": "Perimetre",
      "Scope Value": "Valeur du perimetre",
      "Preferred Language": "Langue preferee",
      "Create Staff Account": "Creer un compte equipe",
      "Edit": "Modifier",
      "Delete": "Supprimer",
      "Save": "Enregistrer",
      "Cancel": "Annuler",
      "Search Clients": "Rechercher des clients",
      "Self-Serve Accounts": "Comptes self-serve",
      "Showing": "Affichage",
      "Prev": "Precedent",
      "Next": "Suivant",
      "All": "Tous",
      "Active": "Actif",
      "Suspended": "Suspendu",
      "Open Full Dashboard": "Ouvrir le tableau complet",
      "Close Client Dashboard": "Fermer le tableau client",
      "Back to Self-Serve Ops": "Retour aux ops self-serve",
      "Overview": "Vue d'ensemble",
      "Billing": "Facturation",
      "Support": "Support",
      "Actions": "Actions",
      "Status": "Statut",
      "Plan": "Plan",
      "Rating": "Note",
      "Growth": "Croissance",
      "Self-Serve Clients": "Clients self-serve",
      "Open Tickets": "Tickets ouverts",
      "Avg Rating": "Note moyenne",
      "Avg Growth": "Croissance moyenne",
      "Self-Serve MRR": "MRR self-serve",
      "Reset Owner Password": "Reinitialiser le mot de passe owner",
      "Suspend": "Suspendre",
      "Reactivate": "Reactiver",
      "Generate Report": "Generer un rapport",
      "Delete Account": "Supprimer le compte",
      "Disconnect Google": "Deconnecter Google",
      "Connect Google": "Connecter Google",
      "Trustpilot": "Trustpilot",
      "Connect Trustpilot": "Connecter Trustpilot",
      "Disconnect Trustpilot": "Deconnecter Trustpilot",
      "Sync Trustpilot": "Synchroniser Trustpilot",
      "Connected and ready to sync reviews.": "Connecte et pret a synchroniser les avis.",
      "Connect your Trustpilot account to start review sync.": "Connectez votre compte Trustpilot pour commencer la synchronisation des avis.",
      "🔌 Disconnect Trustpilot": "🔌 Deconnecter Trustpilot",
      "🔗 Connect Trustpilot": "🔗 Connecter Trustpilot",
      "🔄 Sync Trustpilot": "🔄 Synchroniser Trustpilot",
      "Trustpilot OAuth started for {{client}}": "OAuth Trustpilot demarre pour {{client}}",
      "Synced {{count}} Trustpilot review(s) for {{client}}": "{{count}} avis Trustpilot synchronise(s) pour {{client}}",
      "Disconnect Trustpilot for {{client}}?": "Deconnecter Trustpilot pour {{client}} ?",
      "Disconnected Trustpilot for {{client}}": "Trustpilot deconnecte pour {{client}}",
      "Trustpilot integration is now wired to backend endpoints. Configure": "L'integration Trustpilot est maintenant connectee aux endpoints backend. Configurez",
      "to use the real OAuth + review sync flow.": "pour utiliser le vrai flux OAuth + synchronisation des avis.",
      "Trustpilot bridge configured": "Bridge Trustpilot configure",
      "Trustpilot bridge not configured yet": "Bridge Trustpilot pas encore configure",
      "Sync Reviews": "Synchroniser les avis",
      "Package": "Forfait",
      "Price": "Prix",
      "Payment": "Paiement",
      "Tickets": "Tickets",
      "Detail:": "Detail :",
      "Enabled": "Active",
      "Disabled": "Desactive",
      "Resolve": "Resoudre",
      "Close": "Fermer",
      "Reply": "Repondre",
      "Mark In Progress": "Marquer en cours",
      "No self-serve clients match your filters.": "Aucun client self-serve ne correspond a vos filtres.",
      "No support tickets for this client.": "Aucun ticket support pour ce client.",
      "No reviews for this client yet.": "Aucun avis pour ce client pour le moment.",
      "Logout": "Se deconnecter",
      "Preparing secure login...": "Preparation de la connexion securisee...",
      "Choose your workspace": "Choisissez votre espace de travail",
      "Client self-serve portal": "Portail client self-serve",
      "Private agency access link is invalid or expired.": "Le lien d'acces prive a l'agence est invalide ou expire.",
      "Agency Dashboard": "Tableau de bord agence",
      "Client Self-Serve": "Client Self-Serve",
      "Loading accounts...": "Chargement des comptes...",
      "Log In to Agency Dashboard": "Se connecter au tableau de bord agence",
      "Forgot password?": "Mot de passe oublie ?",
      "Username or Email": "Nom d'utilisateur ou e-mail",
      "Request New Password via Email": "Demander un nouveau mot de passe par e-mail",
      "If the account exists, a reset email request has been sent.": "Si le compte existe, un e-mail de reinitialisation a ete envoye.",
      "Back to login": "Retour a la connexion",
      "Log In": "Se connecter",
      "Self-Serve": "Self-Serve",
      "Client Username": "Nom d'utilisateur client",
      "Log In to Client Dashboard": "Se connecter au tableau de bord client",
      "Business name": "Nom de l'entreprise",
      "Contact person": "Personne de contact",
      "Street address + number": "Adresse + numero",
      "Postcode": "Code postal",
      "Confirm password": "Confirmer le mot de passe",
      "Payment method": "Mode de paiement",
      "Credit Card Payment": "Paiement par carte de credit",
      "Self-Serve Terms & Conditions": "Conditions generales Self-Serve",
      "I accept the self-serve terms and authorize Replai to provision my workspace.": "J'accepte les conditions self-serve et autorise Replai a provisionner mon espace de travail.",
      "Opening secure checkout...": "Ouverture du paiement securise...",
      "Create Self-Serve Account": "Creer un compte Self-Serve",
      "Request Managed Account": "Demander un compte Managed",
      "Submitting...": "Envoi en cours...",
      "Preferred package": "Forfait prefere",
      "Preferred payment method": "Mode de paiement prefere",
      "Authenticator code (6 digits)": "Code authenticateur (6 chiffres)",
      "Backup code (e.g. ABCD-EFGH)": "Code de secours (ex: ABCD-EFGH)",
      "Verify & Continue": "Verifier et continuer",
      "Recovery Backup Codes": "Codes de secours de recuperation",
      "I have saved these codes": "J'ai sauvegarde ces codes",
      "Set a new password to continue": "Definissez un nouveau mot de passe pour continuer",
      "For security, owner accounts must change the initial password on first login.": "Pour des raisons de securite, les comptes owner doivent changer le mot de passe initial a la premiere connexion.",
      "Current password": "Mot de passe actuel",
      "New password": "Nouveau mot de passe",
      "Confirm new password": "Confirmer le nouveau mot de passe",
      "Update password": "Mettre a jour le mot de passe",
      "Security Setup Required": "Configuration de securite requise",
      "Every account must enable all recommended protection layers before continuing.": "Chaque compte doit activer toutes les couches de protection recommandees avant de continuer.",
      "Activate All Recommended Layers": "Activer toutes les couches recommandees",
      "Activate": "Activer",
      "Authenticator MFA": "MFA Authenticateur",
      "Set Up Your Authenticator App": "Configurer votre application d'authentification",
      "Verify & Activate MFA": "Verifier et activer le MFA",
      "Or enter this key manually in your app:": "Ou entrez cette cle manuellement dans votre application :",
      "Owner": "Proprietaire",
      "Member": "Membre",
      "Client Dashboard": "Tableau de bord client",
      "Back to Homepage": "Retour a l'accueil",
      "Language": "Langue",
      "Managed": "Managed",
      "Free trial is": "La periode d'essai gratuite est de",
      "days": "jours",
      "Billing country is verified server-side": "Le pays de facturation est verifie cote serveur",
      "Country field edits do not reduce pricing.": "Les modifications du champ pays ne reduisent pas les tarifs.",
      "Tell us about your business and we'll reach out within 24 hours to set up your managed account, including trial access and billing.": "Parlez-nous de votre entreprise et nous vous contacterons sous 24 heures pour configurer votre compte managed, y compris l'acces d'essai et la facturation.",
      "Progress:": "Progression :",
      "enabled": "active(s)",
      "Download an authenticator app on your smartphone:": "Telechargez une application d'authentification sur votre smartphone :",
      "Google Authenticator, Microsoft Authenticator, Authy, or similar.": "Google Authenticator, Microsoft Authenticator, Authy ou similaire.",
      "Scan a QR code": "Scanner un QR code",
      "Use Google Authenticator, Microsoft Authenticator, Authy, or any TOTP app to scan this QR code. A new 6-digit code will be generated every 30 seconds.": "Utilisez Google Authenticator, Microsoft Authenticator, Authy ou toute application TOTP pour scanner ce QR code. Un nouveau code a 6 chiffres sera genere toutes les 30 secondes.",
      "Enter the": "Entrez le",
      "6-digit code": "code a 6 chiffres",
      "from your app to confirm setup": "depuis votre application pour confirmer la configuration",
    },
    messages: {
      "Support response saved": "Reponse support enregistree",
      "Billing settings updated": "Parametres de facturation mis a jour",
      "Self-serve account deleted": "Compte self-serve supprime",
      "Owner password reset. Temporary password displayed.": "Mot de passe owner reinitialise. Mot de passe temporaire affiche.",
      "No owner account found": "Aucun compte owner trouve",
      "Enter a subject and message": "Saisissez un sujet et un message",
      "No permission": "Aucune permission",
      "Staff account created": "Compte equipe cree",
      "Staff profile updated": "Profil equipe mis a jour",
      "Staff account deleted": "Compte equipe supprime",
      "Password updated successfully": "Mot de passe mis a jour avec succes",
      "Your password has been changed": "Votre mot de passe a ete modifie",
      "Current password is incorrect": "Le mot de passe actuel est incorrect",
      "Password confirmation does not match": "La confirmation du mot de passe ne correspond pas",
      "Review moved back to pending": "Avis remis en attente",
      "Response approved": "Reponse approuvee",
      "Response published": "Reponse publiee",
      "Draft updated": "Brouillon mis a jour",
      "Member user added": "Membre ajoute",
      "Member removed": "Membre supprime",
      "Profile completed": "Profil complete",
      "Onboarding reopened": "Onboarding relance",
      "Updated": "Mis a jour",
      "Approved": "Approuve",
      "Rejected": "Rejete",
      "Published to Google ✓": "Publie sur Google ✓",
      "Moved back to pending": "Remis en attente",
      "Response deleted": "Reponse supprimee",
      "Prospect added": "Prospect ajoute",
      "Client added": "Client ajoute",
      "Google sync failed": "Echec de synchronisation Google",
      "Trustpilot connection confirmed ✅": "Connexion Trustpilot confirmee ✅",
      "Trustpilot auth window opened. Complete consent, then click Sync Reviews.": "Fenetre d'autorisation Trustpilot ouverte. Terminez le consentement puis cliquez sur Synchroniser les avis.",
      "Trustpilot connect endpoint is not configured": "L'endpoint de connexion Trustpilot n'est pas configure",
      "Failed to start Trustpilot connection": "Echec du demarrage de la connexion Trustpilot",
      "Trustpilot review sync failed": "Echec de synchronisation des avis Trustpilot",
      "Trustpilot sync failed": "Echec de synchronisation Trustpilot",
      "Failed to send reminder email": "Echec de l'envoi de l'e-mail de rappel",
      "Failed to build reminder preview": "Echec de generation de l'apercu du rappel",
    },
  },
};
const resolveI18nKey = (obj, path) => String(path || "").split(".").reduce((acc, part) => (acc && typeof acc === "object" ? acc[part] : undefined), obj);
const PERMISSION_GROUPS = [
  { key: "staff.reviewGroup", perms: ["review.respond", "review.edit", "review.publish", "review.escalate"] },
  { key: "staff.clientGroup", perms: ["client.view", "client.manage", "selfserve.clients.view", "selfserve.clients.manage"] },
  { key: "staff.supportGroup", perms: ["support.ticket.respond", "support.ticket.manage", "mailbox.access", "staff.chat"] },
  { key: "staff.reportingGroup", perms: ["selfserve.reports.generate", "contract.view", "analytics.view"] },
  { key: "staff.accessGroup", perms: ["settings.view", "staff.password.reset", "staff.manage", "credentials.console", "security.layers.reset"] },
];
const PERMISSION_PRESETS = [
  { id: "support", label: "Support Agent", perms: ["review.respond", "review.escalate", "support.ticket.respond", "support.ticket.manage", "client.view", "selfserve.clients.view", "mailbox.access", "staff.chat"] },
  { id: "senior-support", label: "Senior Support", perms: ["review.respond", "review.edit", "review.escalate", "support.ticket.respond", "support.ticket.manage", "client.view", "selfserve.clients.view", "mailbox.access", "staff.chat", "settings.view", "staff.password.reset"] },
  { id: "success", label: "Client Success", perms: ["review.respond", "review.edit", "review.publish", "client.view", "client.manage", "selfserve.clients.view", "support.ticket.respond", "contract.view", "staff.chat"] },
  { id: "analyst", label: "Analyst", perms: ["client.view", "selfserve.clients.view", "selfserve.reports.generate", "analytics.view"] },
  { id: "senior-ops", label: "Senior Ops", perms: ["review.respond", "review.edit", "review.publish", "review.escalate", "client.view", "client.manage", "selfserve.clients.view", "selfserve.clients.manage", "selfserve.reports.generate", "support.ticket.respond", "support.ticket.manage", "contract.view", "analytics.view", "settings.view", "staff.password.reset", "security.layers.reset", "mailbox.access", "staff.chat", "credentials.console"] },
  { id: "ops", label: "Ops Manager", perms: ["review.respond", "review.edit", "review.publish", "review.escalate", "client.view", "client.manage", "selfserve.clients.view", "selfserve.clients.manage", "selfserve.reports.generate", "support.ticket.respond", "support.ticket.manage", "contract.view", "analytics.view", "settings.view", "staff.password.reset", "security.layers.reset", "mailbox.access", "staff.chat"] },
];
const TIMEZONE_OPTIONS = [
  "Europe/Berlin",
  "Europe/London",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
];
const NOTIFICATION_PREFERENCES = [
  { id: "dashboard", label: "Dashboard only" },
  { id: "dashboard-email", label: "Dashboard + email" },
  { id: "priority-only", label: "Priority only" },
];

// ── Sound notification system ────────────────────────────────────────────────
// All tones are synthesised via Web Audio API — no audio files needed.
const SOUND_TONES = [
  { id: "chime",    label: "Chime"     },
  { id: "ping",     label: "Ping"      },
  { id: "bell",     label: "Bell"      },
  { id: "pop",      label: "Pop"       },
  { id: "ding",     label: "Ding"      },
  { id: "chord",    label: "Chord"     },
  { id: "blip",     label: "Blip"      },
  { id: "soft",     label: "Soft"      },
];

const DEFAULT_SOUND_SETTINGS = {
  review:    { enabled: true,  tone: "chime"  },
  chat:      { enabled: true,  tone: "ping"   },
  mailbox:   { enabled: true,  tone: "bell"   },
};

function playNotificationSound(toneId) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const master = ctx.createGain();
    master.connect(ctx.destination);

    const play = (freq, type, startAt, dur, gainVal = 0.18) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.connect(g);
      g.connect(master);
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime + startAt);
      g.gain.setValueAtTime(gainVal, ctx.currentTime + startAt);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + startAt + dur);
      osc.start(ctx.currentTime + startAt);
      osc.stop(ctx.currentTime + startAt + dur + 0.05);
    };

    switch (toneId) {
      case "chime":
        play(1046.5, "sine", 0,    0.4);
        play(1318.5, "sine", 0.12, 0.35);
        play(1568,   "sine", 0.24, 0.4);
        break;
      case "ping":
        play(1800, "sine", 0, 0.3, 0.22);
        play(1800, "sine", 0, 0.01, 0.22);
        break;
      case "bell":
        play(880,  "sine",     0,    0.6, 0.2);
        play(1760, "triangle", 0,    0.3, 0.08);
        play(880,  "sine",     0.35, 0.4, 0.1);
        break;
      case "pop":
        play(400, "sine", 0,    0.05, 0.25);
        play(800, "sine", 0.05, 0.1,  0.15);
        break;
      case "ding":
        play(1174.66, "sine", 0,    0.5, 0.2);
        play(1174.66, "sine", 0,    0.02, 0.25);
        break;
      case "chord":
        play(523.25, "sine", 0, 0.5, 0.12);
        play(659.25, "sine", 0, 0.5, 0.12);
        play(783.99, "sine", 0, 0.5, 0.12);
        break;
      case "blip":
        play(600, "square", 0,    0.08, 0.1);
        play(900, "square", 0.09, 0.08, 0.1);
        break;
      case "soft":
        play(528, "sine", 0, 0.7, 0.12);
        play(660, "sine", 0.1, 0.6, 0.08);
        break;
      default:
        play(1046.5, "sine", 0, 0.35);
    }
    setTimeout(() => ctx.close(), 2000);
  } catch (_) {}
}

const normalizeSoundSettings = (raw) => ({
  review:  { enabled: raw?.review?.enabled  !== false, tone: raw?.review?.tone  || DEFAULT_SOUND_SETTINGS.review.tone  },
  chat:    { enabled: raw?.chat?.enabled    !== false, tone: raw?.chat?.tone    || DEFAULT_SOUND_SETTINGS.chat.tone    },
  mailbox: { enabled: raw?.mailbox?.enabled !== false, tone: raw?.mailbox?.tone || DEFAULT_SOUND_SETTINGS.mailbox.tone },
});

const SECURITY_LAYER_DEFINITIONS = [
  { key: "mfaApp", label: "Authenticator MFA", detail: "Require a time-based one-time code during sign-in." },
  { key: "backupCodes", label: "Recovery Backup Codes", detail: "Generate one-time emergency recovery codes." },
  { key: "loginAlerts", label: "Login Alerts", detail: "Get immediate alerts for successful and failed logins." },
  { key: "suspiciousLoginLock", label: "Suspicious Login Lock", detail: "Block unknown-device sign-ins until verified." },
];

const buildDefaultSecurityLayers = () => ({
  mfaApp: false,
  backupCodes: false,
  loginAlerts: false,
  suspiciousLoginLock: false,
});

const normalizeSecurityLayers = (layers) => ({
  ...buildDefaultSecurityLayers(),
  ...(layers && typeof layers === "object" ? layers : {}),
});

const hasAllRecommendedSecurityLayers = (user) => {
  const layers = normalizeSecurityLayers(user?.securityLayers);
  return SECURITY_LAYER_DEFINITIONS.every((item) => !!layers[item.key]);
};

const recommendedSecurityProgress = (user) => {
  const layers = normalizeSecurityLayers(user?.securityLayers);
  const enabled = SECURITY_LAYER_DEFINITIONS.filter((item) => !!layers[item.key]).length;
  const total = SECURITY_LAYER_DEFINITIONS.length;
  const pct = total ? Math.round((enabled / total) * 100) : 0;
  return { enabled, total, pct };
};

const formatAddress = (client) => {
  if (client.addressLine1 || client.city || client.country) {
    return [client.addressLine1, [client.postcode, client.city].filter(Boolean).join(" "), client.country].filter(Boolean).join(", ");
  }
  return client.location || "—";
};

const formatLongDate = (rawDate) => {
  if (!rawDate) return "—";
  const d = new Date(rawDate);
  if (Number.isNaN(d.getTime())) return rawDate;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const getPrivacyLawByCountry = (country) => {
  const c = String(country || "").trim().toLowerCase();
  if (!c) return "the applicable data privacy laws in the Client's jurisdiction";
  const euCountries = [
    "austria", "belgium", "bulgaria", "croatia", "cyprus", "czech republic", "czechia", "denmark", "estonia", "finland", "france", "germany", "greece", "hungary", "ireland", "italy", "latvia", "lithuania", "luxembourg", "malta", "netherlands", "poland", "portugal", "romania", "slovakia", "slovenia", "spain", "sweden",
  ];
  if (euCountries.includes(c)) return "GDPR (EU General Data Protection Regulation)";
  if (c === "united kingdom" || c === "uk" || c === "great britain") return "UK GDPR and the Data Protection Act 2018";
  if (c === "united states" || c === "usa" || c === "us") return "applicable U.S. state privacy laws (including CCPA/CPRA where applicable)";
  if (c === "canada") return "PIPEDA (Personal Information Protection and Electronic Documents Act)";
  if (c === "australia") return "Privacy Act 1988 (Cth) and the Australian Privacy Principles";
  if (c === "brazil") return "LGPD (Lei Geral de Proteção de Dados)";
  if (c === "south africa") return "POPIA (Protection of Personal Information Act)";
  if (c === "nigeria") return "Nigeria Data Protection Act (NDPA)";
  if (c === "singapore") return "PDPA (Personal Data Protection Act)";
  if (c === "india") return "Digital Personal Data Protection Act (DPDP Act)";
  return `the applicable data privacy laws of ${country}`;
};

const getMonitoringFrequencyByPlan = (planId) => {
  if (planId === "starter") return "Once daily";
  if (planId === "growth") return "3× daily";
  if (planId === "enterprise") return "Continuous monitoring";
  return "As agreed";
};

const parseCountryFromLocale = (locale) => {
  if (!locale) return "";
  const normalized = String(locale).replace(/_/g, "-");
  const parts = normalized.split("-").filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const token = String(parts[i] || "").toUpperCase();
    if (/^[A-Z]{2}$/.test(token)) return token;
  }
  return "";
};

const ALL_PERMISSIONS = [
  "review.respond",
  "review.edit",
  "review.publish",
  "review.escalate",
  "client.view",
  "client.manage",
  "selfserve.clients.view",
  "selfserve.clients.manage",
  "selfserve.reports.generate",
  "support.ticket.respond",
  "support.ticket.manage",
  "mailbox.access",
  "staff.chat",
  "contract.view",
  "analytics.view",
  "settings.view",
  "staff.password.reset",
  "staff.manage",
  "credentials.console",
  "security.layers.reset",
  "registrations.view",
  "cms.edit",
];

const DEFAULT_ADMIN = {
  id: "admin-1",
  role: "admin",
  name: "Festus Okunlola",
  username: "admin",
  passwordHash: "43c1b827e1f8a495a152a45c6cc7bc9d0e63e1602a8360d51b0c658d3a6f1477",
  passwordSalt: "replai_admin_salt_v1",
  email: "hello@replai.work",
  phone: BIZ.phone,
  city: "Kirchseeon",
  country: "Germany",
  title: "Head of Operations",
  scopeLevel: "global",
  scopeValue: "All Regions",
  preferredLanguage: "English",
  avatar: "",
  permissions: ALL_PERMISSIONS,
  profileCompleted: true,
  onboardingSeenVersion: null,
  mustChangePassword: true,
  securityLayers: buildDefaultSecurityLayers(),
  soundSettings: { ...DEFAULT_SOUND_SETTINGS },
};

const TESTIMONIALS = [
  { client: "Bella's Pizzeria", contact: "Marco B.", text: "Since Replai started handling our reviews, our Google rating went from 4.1 to 4.4 in just 3 weeks. Every review gets a professional response within hours. Worth every cent.", rating: 5, plan: "Growth" },
  { client: "Glow Skin Studio", contact: "Jenny L.", text: "I used to spend 2 hours a week writing review responses. Now I spend zero. Replai handles everything perfectly — our clients love the thoughtful replies.", rating: 5, plan: "Starter" },
  { client: "QuickFix Auto", contact: "Dave P.", text: "We had a tough 1-star review that could've hurt us badly. Replai crafted a response that turned the reviewer into a repeat customer. Game-changing service.", rating: 5, plan: "Starter" },
];

const OUTREACH_TEMPLATES = [
  { id: "free-sample", name: "Free Sample Hook", subject: "I wrote a response to your latest Google review (free)", body: `Hi {contact},

I was browsing {business} on Google and noticed your latest review from a customer doesn't have an owner response yet. I took the liberty of drafting one for you — feel free to use it as-is:

{sample_response}

I run Replai — a done-for-you review response service. I handle ALL your Google review responses, professionally and on-brand, published within 24 hours. Plans start at €99/month.

Want me to handle all your reviews this month? First week is free so you can see the quality.

See how it works: https://replai.work

Best,
${BIZ.name}
Replai | ${BIZ.email}` },
  { id: "intro-pricing", name: "Introduction + Pricing", subject: "Never miss a Google review again — from €99/month", body: `Hi {contact},

Quick question: who responds to {business}'s Google reviews?

If the answer is "nobody" or "me, when I remember" — I can help.

I run Replai, a done-for-you review response service. Here's what I do:

→ Monitor your Google reviews daily
→ Write professional, on-brand responses to every review
→ Publish responses within 24 hours
→ Handle negative reviews with empathetic, solution-oriented replies

Plans:
• Starter: €99/mo — up to 30 reviews, 1 location
• Growth: €199/mo — up to 100 reviews, multi-platform
• Enterprise: €399/mo — unlimited, 10 locations

Every plan includes a 7-day free trial. No commitment.

Interested? Reply to this email or book a quick call.

${BIZ.name}
Replai | ${BIZ.web}` },
  { id: "testimonial", name: "Social Proof + Testimonial", subject: "How {business_type} are getting 4.5+ stars on Google", body: `Hi {contact},

Businesses that respond to every Google review see an average 0.5-star rating increase within 6 months. But most business owners don't have the time.

That's why I built Replai — I respond to all your reviews for you. Here's what one of my clients said:

"{testimonial}"
— {testimonial_author}, {testimonial_business}

I'd love to help {business} get the same results. I'll even write a free sample response to one of your recent reviews so you can see the quality.

Interested?

${BIZ.name}
Replai | ${BIZ.web}` },
  { id: "follow-up", name: "Follow-Up (3-Day)", subject: "Re: Your Google reviews — free help", body: `Hi {contact},

Just following up on my message from a few days ago. I noticed {business} has gotten 2 more reviews since then that also don't have responses.

Want me to draft responses to those as well? Happy to do it free — no obligation.

If review management isn't a priority right now, no worries at all. Just thought I'd offer since it's what I do all day!

Best,
${BIZ.name}
Replai | ${BIZ.email}` },
  { id: "agency", name: "Agency Partnership", subject: "White-label review management for your clients", body: `Hi {contact},

I help marketing agencies offer review response management to their clients — without the operational overhead.

Here's how it works:
→ You sign up clients under your brand
→ I handle all review monitoring, response writing, and publishing
→ You set your own pricing and keep the margin
→ I work behind the scenes, fully white-label

Currently managing reviews for {client_count}+ businesses across Google, Facebook, and Trustpilot.

Would love to explore a partnership. Open to a quick call this week?

${BIZ.name}
Replai | ${BIZ.web}` },
];

const CLIENT_MSG_TEMPLATES = [
  { id: "payment-reminder", name: "💰 Payment Reminder", subject: "Invoice reminder — Replai service for {business}", body: `Hi {contact},

This is a friendly reminder that your Replai service invoice for this month (€{amount}) is due. If you've already sent payment, please disregard this message.

You can send payment via bank transfer to:
{bank_details}

Payment reference:
{payment_reference}

If you have any questions about the invoice, just reply to this email.

Thank you for being a Replai client!

Best,
${BIZ.name}` },
  { id: "monthly-report", name: "📊 Monthly Report", subject: "Your monthly review report — {business}", body: `Hi {contact},

Here's your Replai review management report for {month}:

📊 MONTHLY SUMMARY
• Total reviews received: {total_reviews}
• Reviews responded to: {responded}
• Average response time: {avg_time}
• Positive reviews: {positive}
• Negative reviews: {negative}
• Current Google rating: {rating}

🔑 HIGHLIGHTS
• {highlight_1}
• {highlight_2}

📈 RECOMMENDATION
{recommendation}

Let me know if you have any questions or want to discuss anything from this report.

Best,
${BIZ.name}
Replai | ${BIZ.web}` },
  { id: "review-escalation", name: "🚨 Review Escalation", subject: "Urgent: New negative review needs your attention — {business}", body: `Hi {contact},

A new review just came in for {business} that I wanted to flag for you before responding:

Review by: {reviewer}
Rating: {stars} star(s)
Content: "{review_text}"

This review mentions a specific issue that may require action on your end before I publish a response. I've drafted a response below, but wanted your approval first:

Drafted response:
"{draft_response}"

Please reply with:
✅ "Approve" — to publish as-is
✏️ Your edits — if you want changes
❌ "Hold" — if you want to handle this internally first

Best,
${BIZ.name}` },
  { id: "service-update", name: "ℹ️ Service Update", subject: "Quick update on your review management — {business}", body: `Hi {contact},

Just a quick update on your Replai service:

{update_message}

No action needed on your end — just keeping you in the loop. Let me know if you have any questions.

Best,
${BIZ.name}
Replai` },
  { id: "upsell", name: "⬆️ Plan Upgrade Suggestion", subject: "Your reviews are growing — time to upgrade?", body: `Hi {contact},

Great news — {business} has been getting more reviews lately! Over the past month, you received {total_reviews} reviews, which is approaching the limit of your current {current_plan} plan ({plan_limit} reviews/month).

To make sure every review gets a timely response, I'd recommend upgrading to the {suggested_plan} plan:

{suggested_plan} — €{suggested_price}/month
• {suggested_reviews} reviews/month
• {suggested_platforms}
• {suggested_response} response time

Want to upgrade? Just reply "yes" and I'll update your plan immediately. Your next invoice will reflect the new rate.

Best,
${BIZ.name}` },
];

const initClients = [
  { id: 1, name: "Bella's Pizzeria", location: "Brooklyn, NY", addressLine1: "42 Flatbush Ave", postcode: "11217", city: "Brooklyn", country: "USA", phone: "+1 718 555 0144", avatar: "🍕", email: "marco@bellaspizzeria.com", contact: "Marco Bellini", plan: "growth", platformsManaged: getPlatformsForPlan("growth"), status: "active", contractStatus: "signed", connected: true, trustpilotConnected: true, googleLocations: [], reviewCount: 47, avgRating: 4.4, lastPayment: "2026-03-01", nextPayment: "2026-04-01", paymentStatus: "paid", joinDate: "2026-01-15", monthlyReviews: [12, 15, 18], responded: 45 },
  { id: 2, name: "Glow Skin Studio", location: "Austin, TX", addressLine1: "1808 South Lamar Blvd", postcode: "78704", city: "Austin", country: "USA", phone: "+1 512 555 0192", avatar: "✨", email: "jenny@glowskinstudio.com", contact: "Jenny Lee", plan: "starter", platformsManaged: getPlatformsForPlan("starter"), status: "active", contractStatus: "signed", connected: true, trustpilotConnected: false, googleLocations: [], reviewCount: 89, avgRating: 4.6, lastPayment: "2026-03-01", nextPayment: "2026-04-01", paymentStatus: "paid", joinDate: "2026-02-01", monthlyReviews: [22, 28, 25], responded: 75 },
  { id: 3, name: "QuickFix Auto", location: "Denver, CO", addressLine1: "701 Colfax Ave", postcode: "80204", city: "Denver", country: "USA", phone: "+1 303 555 0105", avatar: "🔧", email: "dave@quickfixauto.com", contact: "Dave Patterson", plan: "starter", platformsManaged: getPlatformsForPlan("starter"), status: "active", contractStatus: "sent", connected: true, trustpilotConnected: false, googleLocations: [], reviewCount: 34, avgRating: 3.9, lastPayment: "2026-02-15", nextPayment: "2026-03-15", paymentStatus: "overdue", joinDate: "2026-02-15", monthlyReviews: [8, 11, 10], responded: 29 },
  { id: 4, name: "CloudNine Yoga", location: "Portland, OR", addressLine1: "98 Hawthorne Blvd", postcode: "97214", city: "Portland", country: "USA", phone: "+1 971 555 0164", avatar: "🧘", email: "sarah@cloudnineyoga.com", contact: "Sarah Mitchell", plan: null, platformsManaged: ["Google Business Profile"], status: "prospect", contractStatus: "none", connected: false, trustpilotConnected: false, googleLocations: [], reviewCount: 0, avgRating: 0, lastPayment: null, nextPayment: null, paymentStatus: null, joinDate: null, monthlyReviews: [], responded: 0 },
];

const initProspects = [
  { id: 101, name: "Tony's Deli", contact: "Tony Rossi", email: "tony@tonysdeli.com", location: "Austin, TX", type: "Restaurant", unrespondedReviews: 8, rating: 4.0, lastContact: null, status: "new", notes: "" },
  { id: 102, name: "Bright Smile Dental", contact: "Dr. Karen White", email: "info@brightsmile.com", location: "Denver, CO", type: "Dental", unrespondedReviews: 12, rating: 3.7, lastContact: null, status: "new", notes: "" },
  { id: 103, name: "Peak Fitness Gym", contact: "Ryan Cole", email: "ryan@peakfitness.com", location: "Nashville, TN", type: "Gym", unrespondedReviews: 5, rating: 4.3, lastContact: "2026-03-14", status: "contacted", notes: "Sent free sample, waiting for reply" },
  { id: 104, name: "LuxeNails Studio", contact: "Maria Santos", email: "maria@luxenails.com", location: "Scottsdale, AZ", type: "Salon", unrespondedReviews: 15, rating: 3.5, lastContact: "2026-03-12", status: "replied", notes: "Interested in Starter plan, wants to see pricing" },
];

const initReviews = [
  { id: 1, clientId: 1, author: "Sarah M.", rating: 5, text: "Best pizza in Brooklyn! The margherita is perfection.", date: "2026-03-16", platform: "Google", status: "pending", aiResponse: "Thank you so much, Sarah! We're thrilled you loved our margherita. Can't wait to see you again!" },
  { id: 2, clientId: 1, author: "Mike R.", rating: 2, text: "Waited 45 minutes for delivery. Pizza was cold.", date: "2026-03-15", platform: "Google", status: "pending", aiResponse: "Hi Mike, we sincerely apologize for the wait and cold pizza. We'd love to make this right. Please reach out to us directly." },
  { id: 3, clientId: 2, author: "Jenny L.", rating: 5, text: "Amazing facial treatment! My skin has never looked better.", date: "2026-03-16", platform: "Google", status: "pending", aiResponse: "Jenny, thank you! We're so glad your skin is glowing. We'd love to have you back for a follow-up!" },
  { id: 4, clientId: 3, author: "Dave P.", rating: 1, text: "Terrible service. Quoted $200, charged $450.", date: "2026-03-16", platform: "Google", status: "pending", aiResponse: "Dave, we're very sorry. Pricing transparency is critical to us. Please contact our manager directly." },
  { id: 9011, clientId: 901, author: "Mina K.", rating: 5, text: "Reception was warm and the dentist explained every step.", date: "2026-03-18", platform: "Google", status: "published", aiResponse: "Thank you, Mina! We appreciate your kind words and are happy your visit was clear and comfortable." },
  { id: 9012, clientId: 901, author: "Sebastian R.", rating: 4, text: "Great hygiene treatment, waiting time a bit long.", date: "2026-03-17", platform: "Google", status: "approved", aiResponse: "Thank you for your feedback, Sebastian. We're glad you had a good treatment and we're improving waiting times." },
  { id: 9021, clientId: 902, author: "Nnamdi E.", rating: 5, text: "Fast oil change and honest pricing. Highly recommended.", date: "2026-03-16", platform: "Google", status: "published", aiResponse: "Thank you, Nnamdi! We appreciate your trust and look forward to seeing you again." },
  { id: 9022, clientId: 902, author: "Adeola A.", rating: 3, text: "Work was okay but pickup took longer than expected.", date: "2026-03-15", platform: "Google", status: "pending", aiResponse: "Hi Adeola, thanks for your feedback. We are reviewing our pickup process to reduce delays." },
];

const DEFAULT_CLIENT_PORTAL_USERS = [
  { id: "client-user-1", clientId: 1, name: "Marco Bellini", username: "bella", passwordHash: "d0a00421138a3c15d8717731c55183eec46c5320436f85112406643b804b98b3", passwordSalt: "replai_client_salt_bella_v1", role: "owner", mustChangePassword: true },
  { id: "client-user-1b", clientId: 1, name: "Bella Team", username: "bellastaff", passwordHash: "cf3b94438c044824bc71e6ad789a03f9d2924998961c9c00f79cf31ada568054", passwordSalt: "replai_client_salt_bellastaff_v1", role: "member" },
  { id: "client-user-2", clientId: 2, name: "Jenny Lee", username: "glow", passwordHash: "593d52553d470b7def38497c8058fe7093bd06789d3e5f151f265c52d3235f16", passwordSalt: "replai_client_salt_glow_v1", role: "owner", mustChangePassword: true },
  { id: "client-user-3", clientId: 3, name: "Dave Patterson", username: "quickfix", passwordHash: "f93589094ffb5febcab47d46fea2965ca668adbb9f69c3c887523dba0cee0622", passwordSalt: "replai_client_salt_quickfix_v1", role: "owner", mustChangePassword: true },
];

const CLIENT_PAYMENT_METHODS = [
  { id: "paypal", label: "PayPal" },
  { id: "stripe", label: "Stripe" },
  { id: "bank-transfer", label: "Bank Transfer" },
  { id: "credit-card", label: "Credit Card" },
];
const AUTO_BILLING_METHODS = ["stripe", "credit-card", "paypal"];

const DEFAULT_BANK_TRANSFER_CONFIGS = [
  {
    id: "bt-de",
    countryCode: "DE",
    countryName: "Germany",
    accountHolder: "Replai",
    bankName: "",
    ibanOrAccount: "",
    swiftBic: "",
    routingCode: "",
    currency: "EUR",
    referencePrefix: "RPL",
    enabled: true,
  },
];

const CLIENT_FREE_TRIAL_DAYS = 7;

const DEFAULT_CONTRACT_ENVELOPES = [
  {
    id: "env-quickfix-20260320",
    clientId: 3,
    provider: "DocuSign",
    envelopeId: "DS-20260320-1001",
    status: "sent",
    sentAt: "2026-03-20T09:30:00.000Z",
    effectiveDate: "2026-03-20",
    signerName: "Dave Patterson",
    signerEmail: "dave@quickfixauto.com",
    signedAt: null,
    signedBy: null,
  },
  {
    id: "env-nordic-20260320",
    clientId: 901,
    provider: "DocuSign",
    envelopeId: "DS-20260320-1002",
    status: "sent",
    sentAt: "2026-03-20T10:00:00.000Z",
    effectiveDate: "2026-03-20",
    signerName: "Dr. Lena Hoffmann",
    signerEmail: "ops@nordicdental.de",
    signedAt: null,
    signedBy: null,
  },
];

const DEFAULT_SELF_SERVE_CLIENTS = [
  {
    id: 901,
    name: "Nordic Dental Group",
    location: "Berlin, DE",
    addressLine1: "Landsberger Allee 221",
    postcode: "13055",
    city: "Berlin",
    country: "Germany",
    phone: "+49 30 5551 9011",
    avatar: "🦷",
    email: "ops@nordicdental.de",
    contact: "Dr. Lena Hoffmann",
    plan: "growth",
    platformsManaged: getPlatformsForPlan("growth"),
    status: "active",
    contractStatus: "sent",
    connected: true,
    trustpilotConnected: true,
    reviewCount: 136,
    avgRating: 4.7,
    lastPayment: "2026-03-02",
    nextPayment: "2026-04-02",
    paymentStatus: "trialing",
    joinDate: "2026-03-01",
    monthlyReviews: [30, 34, 38],
    responded: 126,
    serviceMode: "self-serve",
    paymentMethod: "stripe",
    paymentDetailMasked: "•••• 4242",
    autoChargeAfterTrial: true,
    trialStartDate: "2026-03-01",
    trialEndDate: "2026-03-08",
  },
  {
    id: 902,
    name: "Prime Auto Care",
    location: "Lagos, NG",
    addressLine1: "17 Admiralty Way",
    postcode: "101233",
    city: "Lagos",
    country: "Nigeria",
    phone: "+234 803 555 0190",
    avatar: "🚗",
    email: "support@primeautocare.ng",
    contact: "Ifeanyi Okoro",
    plan: "starter",
    platformsManaged: getPlatformsForPlan("starter"),
    status: "active",
    contractStatus: "signed",
    connected: true,
    trustpilotConnected: false,
    googleLocations: [],
    reviewCount: 78,
    avgRating: 4.3,
    lastPayment: "2026-03-05",
    nextPayment: "2026-04-05",
    paymentStatus: "paid",
    joinDate: "2026-02-10",
    monthlyReviews: [18, 21, 24],
    responded: 69,
    serviceMode: "self-serve",
    paymentMethod: "paypal",
    paymentDetailMasked: "billing@primeautocare.ng",
    autoChargeAfterTrial: false,
    trialStartDate: "2026-02-10",
    trialEndDate: "2026-02-17",
  },
];

const DEFAULT_SELF_SERVE_USERS = [
  {
    id: "client-user-901-owner",
    clientId: 901,
    name: "Dr. Lena Hoffmann",
    username: "nordic_owner",
    passwordHash: "951e57f0341f4f4e4dcffbe6ce8d53ca8e2418a07ecf9ddf7a8b96a8fd5cef64",
    passwordSalt: "replai_client_salt_nordic_owner_v1",
    role: "owner",
    mustChangePassword: true,
  },
  {
    id: "client-user-902-owner",
    clientId: 902,
    name: "Ifeanyi Okoro",
    username: "prime_owner",
    passwordHash: "83dd9f0786cbf8debb20b0f488b2ef229e5fc7f2cb2faeb7eb531be7b811586f",
    passwordSalt: "replai_client_salt_prime_owner_v1",
    role: "owner",
    mustChangePassword: true,
  },
];

const DEFAULT_SELF_SERVE_TICKETS = [
  {
    id: "support-9001",
    ticketNumber: "RPL-20260320-9001",
    clientId: 901,
    clientName: "Nordic Dental Group",
    requesterId: "client-user-901-owner",
    requesterName: "Dr. Lena Hoffmann",
    requesterRole: "owner",
    category: "platform-connection",
    priority: "high",
    subject: "Google location 2 disconnected",
    message: "Our second clinic location got disconnected overnight. Please reconnect and re-sync reviews.",
    status: "in-progress",
    createdAt: "2026-03-19T09:22:00.000Z",
    updatedAt: "2026-03-20T08:10:00.000Z",
    adminResponse: "Re-auth has been triggered. We are waiting for Google callback from your owner account.",
    attachments: [{ name: "error-screenshot.png", size: 384120, type: "image/png" }],
    acknowledgementEmail: {
      sent: true,
      to: "ops@nordicdental.de",
      sentAt: "2026-03-19T09:22:09.000Z",
      provider: "simulated",
    },
  },
  {
    id: "support-9002",
    ticketNumber: "RPL-20260320-9002",
    clientId: 902,
    clientName: "Prime Auto Care",
    requesterId: "client-user-902-owner",
    requesterName: "Ifeanyi Okoro",
    requesterRole: "owner",
    category: "billing",
    priority: "medium",
    subject: "Need VAT invoice copy",
    message: "Please send VAT-compliant invoice copy for March so our accountant can close books.",
    status: "open",
    createdAt: "2026-03-20T07:44:00.000Z",
    updatedAt: "2026-03-20T07:44:00.000Z",
    adminResponse: "",
    attachments: [],
    acknowledgementEmail: {
      sent: true,
      to: "support@primeautocare.ng",
      sentAt: "2026-03-20T07:44:06.000Z",
      provider: "simulated",
    },
  },
];

const getPaymentDetailMeta = (methodId) => {
  if (methodId === "paypal") return { label: "PayPal email", placeholder: "billing@business.com" };
  if (methodId === "stripe") return { label: "Card ending (last 4 digits)", placeholder: "4242" };
  if (methodId === "credit-card") return { label: "Card ending (last 4 digits)", placeholder: "1234" };
  if (methodId === "bank-transfer") return { label: "Bank account / IBAN (last 6)", placeholder: "...789012" };
  return { label: "Payment detail", placeholder: "Enter payment detail" };
};

const maskPaymentDetail = (value = "") => {
  const clean = String(value || "").trim();
  if (!clean) return "";
  if (clean.includes("@")) {
    const [name, domain] = clean.split("@");
    const visible = (name || "").slice(0, 2);
    return `${visible}${"•".repeat(Math.max((name || "").length - 2, 2))}@${domain || ""}`;
  }
  const suffix = clean.slice(-4);
  return `•••• ${suffix}`;
};

const normalizeCountryKey = (value = "") => String(value || "").trim().toUpperCase().replace(/\s+/g, " ");

const buildBankTransferInstructionText = (config, reference = "") => {
  if (!config) return "";
  return [
    config.accountHolder || "",
    config.bankName ? `Bank: ${config.bankName}` : "",
    config.ibanOrAccount ? `Account/IBAN: ${config.ibanOrAccount}` : "",
    config.swiftBic ? `SWIFT/BIC: ${config.swiftBic}` : "",
    config.routingCode ? `Routing code: ${config.routingCode}` : "",
    config.currency ? `Currency: ${config.currency}` : "",
    reference ? `Payment reference: ${reference}` : "",
  ].filter(Boolean).join("\n");
};

const addDaysISO = (dateInput, days) => {
  const d = new Date(dateInput);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};

// eslint-disable-next-line no-unused-vars
const CLIENT_SELF_SERVE_TERMS = [
  "You are responsible for the final review response published from your workspace.",
  "Replai provides AI-assisted drafts, analytics, and platform connection tools, but does not guarantee rating improvements or review removals.",
  "Subscriptions renew monthly unless cancelled before the next billing cycle.",
  "By connecting Google or other review platforms, you confirm you are authorized to manage those business profiles.",
  "Payment methods may be charged automatically where supported by the selected processor.",
  "Your team must keep login credentials secure and notify Replai immediately about unauthorized access.",
];

const CLIENT_TERMS_LAST_UPDATED = "20 March 2026";

const CLIENT_SELF_SERVE_TERMS_TEXT = `REPLAI.WORK — TERMS OF SERVICE
Last Updated: ${CLIENT_TERMS_LAST_UPDATED}

These Terms of Service (“Terms”) govern your access to and use of the Replai.work platform (“Replai”, “we”, “our”, “the Service”). By creating an account, connecting a review platform, or using any part of the Service, you agree to be bound by these Terms.
If you do not agree, you must not use Replai.

1. Definitions
• “Client”, “User”, “You” — the individual or business using Replai.
• “Self‑Service Account” — an account where the user manages their own reviews using Replai’s tools.
• “AI‑Assisted Drafts” — suggested responses generated by Replai’s AI systems.
• “Connected Platforms” — third‑party services such as Google Business Profile, Facebook, Yelp, Trustpilot, etc.
• “Subscription” — the paid plan selected during registration.

2. Eligibility
To use Replai, you must:
• Be at least 18 years old
• Have the legal authority to manage the business profiles you connect
• Provide accurate registration information
• Agree to these Terms
You are responsible for ensuring your use of Replai complies with local laws.

3. Description of the Service
Replai provides tools for:
• Monitoring online reviews
• Generating AI‑assisted response drafts
• Publishing responses to connected platforms
• Viewing analytics and performance insights
• Managing review workflows
Replai does not guarantee rating improvements, review removals, SEO ranking changes, customer satisfaction outcomes, or platform moderation decisions.
Replai is not affiliated with Google, Facebook, Yelp, Trustpilot, or any other review platform.

4. User Responsibilities
You agree to review and approve all responses before publishing, use the Service lawfully, maintain account security, ensure you are authorized to connect business profiles, and provide accurate business/contact/payment information.

5. AI‑Assisted Drafts
AI drafts may contain errors. You must review and edit drafts before publishing and ensure compliance with platform policies. AI output is provided “as is”.

6. Subscription, Billing & Payments
Subscriptions renew automatically each month unless cancelled before the next billing cycle. By subscribing, you authorize Replai or its payment processor to store your payment method, charge recurring subscription fees, and charge applicable taxes.
Failed payments may suspend/limit access until payment is completed.
No refunds for partial periods, unused time, or downgrades during a billing cycle.

7. Acceptable Use Policy
You may not use Replai to publish illegal/harmful content, impersonate others, manipulate reviews, violate platform terms, reverse engineer/disrupt the Service, or mislead customers.
Replai may suspend/terminate violating accounts.

8. Third‑Party Platforms
Replai does not control external platforms. Availability, API limits, outages, and restrictions may affect functionality.

9. Data Protection & Privacy
Replai processes data solely to provide the Service, complies with GDPR/CCPA and applicable laws, does not sell your data, and does not use private review content for AI model training.

10. Intellectual Property
Replai retains ownership of the platform/software/AI systems. You retain ownership of your business data, reviews, and final published responses.

11. Service Availability
Replai targets high uptime but does not guarantee uninterrupted operation.

12. Termination
You may cancel anytime; cancellation is effective at the end of the current billing period.
Replai may suspend/terminate for failed payments, misuse, fraud, abuse, or Terms violations.

13. Limitation of Liability
To the maximum extent permitted by law, Replai is not liable for indirect/incidental/consequential damages. Total liability is limited to one month of subscription fees.

14. Disclaimer of Warranties
Replai provides the Service "as is," and you are responsible for your use of the platform and the content you publish.

15. Governing Law
These Terms are governed by the laws of Germany. Jurisdiction: Munich, Germany.

16. Changes to These Terms
Replai may update these Terms and will notify users of material changes. Continued use constitutes acceptance.

17. Contact Information
Replai (Festus Okunlola)
Fritz‑Litzlfelderstraße 21
85614 Kirchseeon, Germany
Email: hello@replai.work`;

const SUPPORT_CATEGORY_OPTIONS = ["billing", "plan-change", "account-cancellation", "account-access", "platform-connection", "review-issue", "technical", "other"];
const SUPPORT_PRIORITY_OPTIONS = ["low", "medium", "high", "urgent"];
const SUPPORT_STATUS_FILTER_OPTIONS = [
  { id: "active", label: "Active" },
  { id: "new", label: "New" },
  { id: "responded", label: "Responded" },
  { id: "closed", label: "Closed" },
  { id: "all", label: "All" },
];

const isSupportTicketArchived = (ticket) => ["resolved", "closed"].includes(String(ticket?.status || "").toLowerCase());

const getSupportTicketStatusBucket = (ticket) => {
  const status = String(ticket?.status || "open").toLowerCase();
  if (status === "open") return "new";
  if (status === "in-progress") return "responded";
  if (status === "resolved" || status === "closed") return "closed";
  return "active";
};

const matchesSupportTicketFilter = (ticket, filterId = "active") => {
  if (filterId === "all") return true;
  if (filterId === "active") return !isSupportTicketArchived(ticket);
  return getSupportTicketStatusBucket(ticket) === filterId;
};

const buildProfileDraft = (account = {}) => ({
  name: String(account?.name || ""),
  firstName: String(account?.firstName || ""),
  lastName: String(account?.lastName || ""),
  gender: String(account?.gender || ""),
  username: String(account?.username || ""),
  email: String(account?.email || ""),
  phone: String(account?.phone || ""),
  city: String(account?.city || ""),
  country: String(account?.country || ""),
  preferredLanguage: String(account?.preferredLanguage || "English"),
  timezone: String(account?.timezone || "Europe/Berlin"),
  bio: String(account?.bio || ""),
  avatar: String(account?.avatar || ""),
  notificationPreference: String(account?.notificationPreference || "dashboard"),
  soundSettings: normalizeSoundSettings(account?.soundSettings),
});

const buildBusinessInfoDraft = (value = BIZ) => ({
  name: String(value?.name || ""),
  tradingAs: String(value?.tradingAs || ""),
  address: String(value?.address || ""),
  phone: String(value?.phone || ""),
  email: String(value?.email || ""),
  web: String(value?.web || ""),
});

const Badge = ({ type, label }) => {
  const m = { active: "#065F46|#D1FAE5", signed: "#065F46|#D1FAE5", paid: "#065F46|#D1FAE5", trialing: "#1E40AF|#DBEAFE", paused: "#92400E|#FEF3C7", prospect: "#1E40AF|#DBEAFE", sent: "#92400E|#FEF3C7", contacted: "#92400E|#FEF3C7", overdue: "#991B1B|#FEE2E2", none: "#6B7280|#1E1E2A", new: "#6B7280|#1E1E2A", replied: "#065F46|#D1FAE5", pending: "#92400E|#FEF3C7", approved: "#065F46|#D1FAE5", published: "#1E40AF|#DBEAFE", rejected: "#991B1B|#FEE2E2" };
  const [c, bg] = (m[type] || m.none).split("|");
  return <span style={{ background: bg, color: c, padding: "3px 10px", borderRadius: 99, fontSize: 11, fontWeight: 600, letterSpacing: "0.03em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{label || type}</span>;
};

const Stars = ({ r }) => <div style={{ display: "flex", gap: 1 }}>{[1,2,3,4,5].map(s => <span key={s} style={{ color: s <= r ? "#F59E0B" : "#374151", fontSize: 13 }}>★</span>)}</div>;

const fmtCurrency = (n = 0) => `€${Number(n || 0).toLocaleString()}`;

const TinyTrend = ({ data = [], color = "#6366F1", fill = "rgba(99,102,241,0.16)", height = 84 }) => {
  const safe = (data || []).map((v) => Number(v || 0));
  if (safe.length < 2) {
    return <div style={{ height, borderRadius: 8, background: "#0A0A0F", border: "1px solid #1E1E2A" }} />;
  }
  const min = Math.min(...safe);
  const max = Math.max(...safe);
  const range = Math.max(max - min, 1);
  const width = 100;
  const pts = safe.map((v, i) => {
    const x = (i / (safe.length - 1)) * width;
    const y = 100 - (((v - min) / range) * 86 + 7);
    return `${x},${y}`;
  }).join(" ");
  const area = `0,100 ${pts} 100,100`;
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: "100%", height, display: "block", background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8 }}>
      <polyline points={area} fill={fill} stroke="none" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const TinyBars = ({ data = [], color = "#10B981", maxValue = null }) => {
  const safe = (data || []).map((v) => Number(v || 0));
  const max = maxValue || Math.max(...safe, 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 84, padding: "8px 10px", background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8 }}>
      {safe.map((v, i) => (
        <div key={`bar-${i}`} style={{ flex: 1, height: `${Math.max((v / max) * 100, 6)}%`, borderRadius: 6, background: color, opacity: 0.9 }} title={String(v)} />
      ))}
    </div>
  );
};

const ProgressDonut = ({ value = 0, color = "#10B981", size = 78, label = "" }) => {
  const pct = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: `conic-gradient(${color} ${pct}%, #1E1E2A ${pct}% 100%)`, display: "grid", placeItems: "center" }}>
      <div style={{ width: size - 14, height: size - 14, borderRadius: "50%", background: "#0A0A0F", display: "grid", placeItems: "center", textAlign: "center" }}>
        <div style={{ fontSize: 13, color: "#fff", fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{Math.round(pct)}%</div>
        {label ? <div style={{ fontSize: 9, color: "#6B7280", textTransform: "uppercase" }}>{label}</div> : null}
      </div>
    </div>
  );
};

const inp = { width: "100%", background: "#0A0A0F", border: "1px solid #2D2D3A", borderRadius: 8, color: "#E5E5E5", padding: "10px 14px", fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" };
const lbl = { fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6, display: "block", fontWeight: 600 };

const PermissionMatrix = ({ selected = [], onChange, t, idPrefix }) => {
  const toggle = (permission, checked) => {
    const next = checked
      ? [...new Set([...selected, permission])]
      : selected.filter((item) => item !== permission);
    onChange(next);
  };

  const applyPreset = (presetPerms) => {
    onChange([...new Set(presetPerms.filter((perm) => perm !== "staff.manage"))]);
  };

  return (
    <div>
      <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 8 }}>{t("staff.selectPreset", "Quick Presets")}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        {PERMISSION_PRESETS.map((preset) => (
          <button key={`${idPrefix}-preset-${preset.id}`} onClick={() => applyPreset(preset.perms)} type="button" style={{ background: "#0A0A0F", color: "#C7D2FE", border: "1px solid #2D2D3A", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>{preset.label}</button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(220px,1fr))", gap: 10 }}>
        {PERMISSION_GROUPS.map((group) => (
          <div key={`${idPrefix}-group-${group.key}`} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 10, color: "#9CA3AF", textTransform: "uppercase", marginBottom: 8 }}>{t(group.key, group.key)}</div>
            <div style={{ display: "grid", gap: 6 }}>
              {group.perms.map((perm) => (
                <label key={`${idPrefix}-${perm}`} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "#D1D5DB" }}>
                  <input
                    type="checkbox"
                    checked={selected.includes(perm)}
                    onChange={(e) => toggle(perm, e.target.checked)}
                  />
                  {perm}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const detectReviewLanguage = (text = "") => {
  const t = String(text || "").toLowerCase();
  if (/[äöüß]/.test(t) || /\b(danke|bitte|nicht|sehr|schlecht|gut)\b/.test(t)) return "German";
  if (/[àâçéèêëîïôûùüÿœ]/.test(t) || /\b(merci|bonjour|service|très|pas)\b/.test(t)) return "French";
  if (/[ñ¿¡]/.test(t) || /\b(gracias|hola|muy|servicio|comida|malo)\b/.test(t)) return "Spanish";
  if (/[àèéìíîòóù]/.test(t) || /\b(grazie|servizio|molto|ottimo|pessimo)\b/.test(t)) return "Italian";
  if (/[ãõç]/.test(t) || /\b(obrigado|obrigada|atendimento|muito|péssimo)\b/.test(t)) return "Portuguese";
  if (/\b(bedankt|goede|slechte|service|erg)\b/.test(t)) return "Dutch";
  return "English";
};

const languageFallbackTemplates = {
  English: {
    positive: "Thank you for your kind review. We truly appreciate your support and look forward to welcoming you again soon.",
    neutral: "Thank you for your feedback. We appreciate your comments and will use them to keep improving our service.",
    negative: `We're sorry your experience did not meet expectations. Please contact us at ${BIZ.email} so we can make this right.`,
  },
  German: {
    positive: "Vielen Dank für Ihre tolle Bewertung. Wir freuen uns sehr über Ihr Feedback und hoffen, Sie bald wieder begrüßen zu dürfen.",
    neutral: "Vielen Dank für Ihr Feedback. Wir nehmen Ihre Hinweise ernst und arbeiten kontinuierlich an Verbesserungen.",
    negative: `Es tut uns leid, dass Ihre Erfahrung nicht zufriedenstellend war. Bitte kontaktieren Sie uns unter ${BIZ.email}, damit wir eine Lösung finden können.`,
  },
  French: {
    positive: "Merci beaucoup pour votre excellent avis. Votre retour nous fait très plaisir et nous espérons vous revoir bientôt.",
    neutral: "Merci pour votre retour. Nous prenons vos commentaires en compte afin d'améliorer continuellement notre service.",
    negative: `Nous sommes désolés que votre expérience n'ait pas été satisfaisante. Merci de nous contacter à ${BIZ.email} afin que nous puissions vous aider.`,
  },
  Spanish: {
    positive: "Muchas gracias por su excelente reseña. Valoramos mucho su apoyo y esperamos verle de nuevo pronto.",
    neutral: "Gracias por su comentario. Tomamos en cuenta su opinión para seguir mejorando nuestro servicio.",
    negative: `Lamentamos que su experiencia no haya sido satisfactoria. Por favor, contáctenos en ${BIZ.email} para poder ayudarle.`,
  },
  Italian: {
    positive: "Grazie mille per la sua ottima recensione. Apprezziamo molto il suo supporto e speriamo di rivederla presto.",
    neutral: "Grazie per il suo feedback. I suoi commenti ci aiutano a migliorare continuamente il servizio.",
    negative: `Ci dispiace che la sua esperienza non sia stata all'altezza. Ci contatti a ${BIZ.email} così potremo aiutarla.`,
  },
  Portuguese: {
    positive: "Muito obrigado pela excelente avaliação. Agradecemos o seu apoio e esperamos recebê-lo novamente em breve.",
    neutral: "Obrigado pelo seu feedback. Consideramos seus comentários para melhorar continuamente nosso serviço.",
    negative: `Lamentamos que sua experiência não tenha sido satisfatória. Entre em contato conosco em ${BIZ.email} para que possamos ajudar.`,
  },
  Dutch: {
    positive: "Hartelijk dank voor uw mooie beoordeling. We waarderen uw steun en hopen u snel weer te mogen verwelkomen.",
    neutral: "Bedankt voor uw feedback. We nemen uw opmerkingen mee om onze service verder te verbeteren.",
    negative: `Het spijt ons dat uw ervaring niet aan de verwachtingen voldeed. Neem contact op via ${BIZ.email}, zodat we dit kunnen oplossen.`,
  },
};

const REVIEW_RESPONSE_TONES = [
  { id: "friendly-casual", label: "Friendly & Casual" },
  { id: "professional-formal", label: "Professional & Formal" },
  { id: "luxury-premium", label: "Luxury & Premium" },
];

const toneFallbackClosers = {
  "friendly-casual": [
    "Thanks again for stopping by.",
    "Hope to see you again soon.",
  ],
  "professional-formal": [
    "We appreciate your continued trust in our service.",
    "Thank you for taking the time to share your feedback.",
  ],
  "luxury-premium": [
    "We look forward to welcoming you again for another exceptional visit.",
    "It would be our pleasure to host you again soon.",
  ],
};

const bytesToHex = (bytes) => Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
const generateSalt = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return bytesToHex(bytes);
};
const hashPassword = async (password, salt) => {
  const data = new TextEncoder().encode(`${password}:${salt}`);
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
  return bytesToHex(new Uint8Array(hashBuffer));
};
const verifyPassword = async (password, salt, hash) => {
  if (!password || !salt || !hash) return false;
  const computed = await hashPassword(password, salt);
  return computed === hash;
};

const hexToBytes = (hex) => {
  const clean = String(hex || "").replace(/[^0-9a-f]/gi, "").toLowerCase();
  if (!clean || clean.length % 2 !== 0) return new Uint8Array([]);
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < clean.length; i += 2) {
    out[i / 2] = Number.parseInt(clean.slice(i, i + 2), 16);
  }
  return out;
};

const generateSecretHex = (byteLength = 20) => {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return bytesToHex(bytes);
};

const normalizeOtpCode = (code) => String(code || "").replace(/\D/g, "").slice(0, 6);

const getOrCreateDeviceId = () => {
  const key = "replai_device_id";
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const created = `dev-${generateSecretHex(10)}`;
  localStorage.setItem(key, created);
  return created;
};

const getCurrentDeviceLabel = () => {
  const platform = String((typeof navigator !== "undefined" && navigator.platform) || "Unknown Device");
  const language = String((typeof navigator !== "undefined" && navigator.language) || "");
  return `${platform}${language ? ` • ${language}` : ""}`;
};

const buildDefaultAccountSecurityState = () => ({
  mfaSecret: "",
  backupCodeHashes: [],
  backupCodesGeneratedAt: null,
  trustedDevices: [],
  loginAlerts: [],
});

const normalizeAccountSecurityState = (state) => {
  const base = buildDefaultAccountSecurityState();
  const next = {
    ...base,
    ...(state && typeof state === "object" ? state : {}),
  };
  next.backupCodeHashes = Array.isArray(next.backupCodeHashes) ? next.backupCodeHashes.filter(Boolean) : [];
  next.loginAlerts = Array.isArray(next.loginAlerts) ? next.loginAlerts.filter(Boolean).slice(0, 50) : [];
  next.trustedDevices = Array.isArray(next.trustedDevices)
    ? next.trustedDevices
      .filter((item) => item && item.id)
      .map((item) => ({ id: String(item.id), label: String(item.label || "Unknown Device"), trustedAt: item.trustedAt || new Date().toISOString() }))
    : [];
  return next;
};

const normalizeSecurityAccount = (account) => {
  const rawAccount = account || {};
  const isBuiltInAdmin = String(rawAccount.id || "") === DEFAULT_ADMIN.id;
  const nextRole = isBuiltInAdmin ? "admin" : rawAccount.role;
  const nextPermissions = nextRole === "admin"
    ? Array.from(new Set([...(DEFAULT_ADMIN.permissions || []), ...((rawAccount.permissions || []))]))
    : (Array.isArray(rawAccount.permissions) ? rawAccount.permissions : []);

  return {
    ...(isBuiltInAdmin ? DEFAULT_ADMIN : {}),
    ...rawAccount,
    role: nextRole,
    title: rawAccount.title || (nextRole === "admin" ? DEFAULT_ADMIN.title : rawAccount.title),
    scopeLevel: rawAccount.scopeLevel || (nextRole === "admin" ? DEFAULT_ADMIN.scopeLevel : rawAccount.scopeLevel),
    scopeValue: rawAccount.scopeValue || (nextRole === "admin" ? DEFAULT_ADMIN.scopeValue : rawAccount.scopeValue),
    permissions: nextPermissions,
    securityLayers: normalizeSecurityLayers(rawAccount?.securityLayers),
    securityState: normalizeAccountSecurityState(rawAccount?.securityState),
  };
};

const hashBackupCode = async (code) => hashPassword(String(code || "").trim().toUpperCase(), "replai_backup_code_v1");

const generateBackupCodes = (count = 8) => {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: count }, () => {
    const bytes = new Uint8Array(8);
    window.crypto.getRandomValues(bytes);
    const chars = Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
    return `${chars.slice(0, 4)}-${chars.slice(4)}`;
  });
};

const computeTotpCode = async (secretHex, timestampMs = Date.now(), stepSec = 30, digits = 6) => {
  const keyBytes = hexToBytes(secretHex);
  if (!keyBytes.length) return "";
  const counter = Math.floor(timestampMs / 1000 / stepSec);
  const buffer = new ArrayBuffer(8);
  const view = new DataView(buffer);
  view.setUint32(0, Math.floor(counter / 0x100000000));
  view.setUint32(4, counter % 0x100000000);
  const key = await window.crypto.subtle.importKey("raw", keyBytes, { name: "HMAC", hash: "SHA-1" }, false, ["sign"]);
  const digest = new Uint8Array(await window.crypto.subtle.sign("HMAC", key, buffer));
  const offset = digest[digest.length - 1] & 0x0f;
  const code = ((digest[offset] & 0x7f) << 24)
    | ((digest[offset + 1] & 0xff) << 16)
    | ((digest[offset + 2] & 0xff) << 8)
    | (digest[offset + 3] & 0xff);
  return String(code % (10 ** digits)).padStart(digits, "0");
};

const verifyTotpCode = async (secretHex, otpCode) => {
  const code = normalizeOtpCode(otpCode);
  if (!code || !secretHex) return false;
  const now = Date.now();
  const windows = [0, -30000, 30000];
  for (const diff of windows) {
    const expected = await computeTotpCode(secretHex, now + diff);
    if (expected === code) return true;
  }
  return false;
};

// Base32 encode (RFC 4648) — required by authenticator apps for TOTP URI
const hexToBase32 = (hex) => {
  const bytes = hexToBytes(hex);
  if (!bytes.length) return "";
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
  let bits = 0;
  let value = 0;
  let output = "";
  for (let i = 0; i < bytes.length; i++) {
    value = (value << 8) | bytes[i];
    bits += 8;
    while (bits >= 5) {
      output += alphabet[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) output += alphabet[(value << (5 - bits)) & 31];
  return output;
};

// Format base32 in groups of 4 for manual entry display
const formatBase32ForDisplay = (b32) =>
  String(b32 || "").replace(/(.{4})/g, "$1 ").trim();

// Build an otpauth:// URI for authenticator app import
const buildTotpUri = (secretHex, accountName, issuer = "Replai") => {
  const secret = hexToBase32(secretHex);
  const label = encodeURIComponent(`${issuer}:${accountName}`);
  const enc = encodeURIComponent(issuer);
  return `otpauth://totp/${label}?secret=${secret}&issuer=${enc}&algorithm=SHA1&digits=6&period=30`;
};

// Generate a QR code data-URL for the given text (async, uses qrcode library)
const generateQrDataUrl = (text) =>
  QRCode.toDataURL(text, { errorCorrectionLevel: "M", margin: 1, width: 220 });

const downloadBlob = (content, fileName, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// Stripe Payment Form Component
const StripePaymentForm = ({ planId, country, email, businessName, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [initialized, setInitialized] = useState(false);

  // Create Payment Intent
  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        const res = await apiFetch('/api/billing/create-payment-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            planId,
            country,
            email,
            billingName: businessName,
            browserLocale: typeof navigator !== 'undefined' ? (navigator.language || '') : '',
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (data.success) {
          setClientSecret(data.clientSecret);
          setInitialized(true);
        } else {
          onError(data.message || 'Failed to create payment intent');
        }
      } catch (error) {
        onError(error.message);
      }
    };
    if (planId && country && email) {
      createPaymentIntent();
    }
  }, [planId, country, email, businessName, onError]);

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { name: businessName, email },
        },
      });

      if (error) {
        onError(error.message);
        setLoading(false);
        return;
      }

      // Confirm payment and create subscription
      const confirmRes = await apiFetch('/api/billing/confirm-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentIntentId: paymentIntent.id,
          clientData: { planId, country, email, contact: businessName, businessName },
        }),
      });

      const confirmData = await confirmRes.json().catch(() => ({}));
      if (confirmData.success) {
        onSuccess(confirmData);
      } else {
        onError(confirmData.message || 'Failed to confirm payment');
      }
    } catch (error) {
      onError(error.message);
    }
    setLoading(false);
  };

  if (!initialized) {
    return <div style={{ padding: 10, color: '#999' }}>Loading payment form...</div>;
  }

  return (
    <form onSubmit={handlePayment} style={{ marginTop: 10 }}>
      <div style={{
        padding: 15,
        border: '1px solid #ddd',
        borderRadius: 5,
        marginBottom: 15,
        background: '#fafafa',
      }}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '14px',
                color: '#424770',
                '::placeholder': { color: '#aab7c4' },
              },
              invalid: { color: '#fa755a' },
            },
          }}
        />
      </div>
      <button
        type="submit"
        disabled={!stripe || loading || !initialized}
        style={{
          width: '100%',
          padding: '10px 20px',
          background: loading ? '#ccc' : '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: 5,
          cursor: loading ? 'not-allowed' : 'pointer',
          fontSize: 14,
        }}
      >
        {loading ? 'Processing...' : 'Complete Payment'}
      </button>
    </form>
  );
};

export default function App() {
  const portalParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const requestedAgencyAccess = portalParams.get("workspace") === "agency";
  const agencySecret = useMemo(() => {
    const fromUrl = portalParams.get("secret") || "";
    if (fromUrl) {
      // Persist the secret for this browser session so staff can log in
      // even if they navigate to a URL without the ?secret= param.
      sessionStorage.setItem("replai_agency_secret", fromUrl);
      return fromUrl;
    }
    return sessionStorage.getItem("replai_agency_secret") || "";
  }, [portalParams]);
  const [agencyAccessEnabled, setAgencyAccessEnabled] = useState(false);
  const [agencyAccessChecked, setAgencyAccessChecked] = useState(!requestedAgencyAccess);
  const defaultLoginMode = requestedAgencyAccess ? "staff" : "client";
  const [staffMembers, setStaffMembers] = useState(() => {
    const raw = localStorage.getItem("replai_staff_members");
    if (!raw) return [normalizeSecurityAccount(DEFAULT_ADMIN)];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) {
        const normalizedStaff = parsed.map((s) => normalizeSecurityAccount(s));
        const hasAdmin = normalizedStaff.some((s) => s.role === "admin");
        return hasAdmin ? normalizedStaff : [normalizeSecurityAccount(DEFAULT_ADMIN), ...normalizedStaff];
      }
    } catch (_) {
      // ignore parse errors
    }
    return [normalizeSecurityAccount(DEFAULT_ADMIN)];
  });
  const [currentUser, setCurrentUser] = useState(() => {
    const raw = sessionStorage.getItem("replai_current_user");
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return normalizeSecurityAccount(parsed);
    } catch (_) { return null; }
  });
  const [clientPortalUsers, setClientPortalUsers] = useState(() => {
    const raw = localStorage.getItem("replai_client_portal_users");
    if (!raw) return [...DEFAULT_CLIENT_PORTAL_USERS, ...DEFAULT_SELF_SERVE_USERS];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) {
        const normalized = parsed.map((u) => {
          const role = u.role === "member" ? "member" : "owner";
          return normalizeSecurityAccount({ ...u, role, mustChangePassword: u.mustChangePassword ?? (role === "owner") });
        });
        const existingIds = new Set(normalized.map((u) => String(u.id)));
        const seededUsers = DEFAULT_SELF_SERVE_USERS.filter((u) => !existingIds.has(String(u.id))).map((u) => normalizeSecurityAccount(u));
        return [...normalized, ...seededUsers];
      }
      return [...DEFAULT_CLIENT_PORTAL_USERS, ...DEFAULT_SELF_SERVE_USERS];
    } catch (_) {
      return [...DEFAULT_CLIENT_PORTAL_USERS, ...DEFAULT_SELF_SERVE_USERS];
    }
  });
  const [currentClientPortalUser, setCurrentClientPortalUser] = useState(() => {
    const raw = sessionStorage.getItem("replai_current_client_portal_user");
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return normalizeSecurityAccount(parsed);
    } catch (_) { return null; }
  });
  const [loginMode, setLoginMode] = useState(defaultLoginMode);
  const regParam = portalParams.get("register");
  const [clientAuthScreen, setClientAuthScreen] = useState(regParam === "self-serve" ? "register" : regParam === "managed" ? "managed-register" : "login");
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [showStaffLoginPassword, setShowStaffLoginPassword] = useState(false);
  const [showStaffMustChangePw, setShowStaffMustChangePw] = useState({ current: false, next: false, confirm: false });
  const [clientLoginForm, setClientLoginForm] = useState({ username: "", password: "" });
  const [clientLoginError, setClientLoginError] = useState("");
  const [showClientLoginPassword, setShowClientLoginPassword] = useState(false);
  const [showClientMustChangePw, setShowClientMustChangePw] = useState({ current: false, next: false, confirm: false });
  const [chatDeleteConfirmModal, setChatDeleteConfirmModal] = useState({ open: false, type: "", threadId: null, msgId: null });
  const [authChallenge, setAuthChallenge] = useState(null);
  const [authChallengeForm, setAuthChallengeForm] = useState({ otpCode: "", backupCode: "" });
  const [revealedBackupCodes, setRevealedBackupCodes] = useState(null);
  // MFA enrollement modal — shown when user clicks "Activate" on Authenticator MFA
  const [mfaSetupModal, setMfaSetupModal] = useState(null);
  // { scope: "staff"|"client", pendingSecretHex, qrDataUrl, base32Secret, accountName, error }
  const [mfaSetupCode, setMfaSetupCode] = useState("");
  const [clientRegisterError, setClientRegisterError] = useState("");
  const [clientRegisterProcessing, setClientRegisterProcessing] = useState(false);
  const [clientRegisterForm, setClientRegisterForm] = useState({ businessName: "", contact: "", email: "", phone: "", addressLine1: "", postcode: "", country: "", city: "", username: "", password: "", confirmPassword: "", plan: "starter", paymentMethod: "paypal", paymentDetail: "", acceptTerms: false, stripeCustomerId: null, stripeSubscriptionId: null, paymentProcessed: false });
  const [managedRegisterForm, setManagedRegisterForm] = useState({ businessName: "", contact: "", email: "", phone: "", addressLine1: "", postcode: "", country: "", city: "", preferredPlan: "starter", paymentMethod: "bank-transfer", paymentDetail: "" });
  const [managedRegisterError, setManagedRegisterError] = useState("");
  const [managedRegisterProcessing, setManagedRegisterProcessing] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [mustChangeForm, setMustChangeForm] = useState({ currentPassword: "", nextPassword: "", confirmPassword: "" });
  const [clientMustChangeForm, setClientMustChangeForm] = useState({ currentPassword: "", nextPassword: "", confirmPassword: "" });
  const [clientSettingsPasswordForm, setClientSettingsPasswordForm] = useState({ currentPassword: "", nextPassword: "", confirmPassword: "" });
  const [settingsPasswordForm, setSettingsPasswordForm] = useState({ currentPassword: "", nextPassword: "", confirmPassword: "" });
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotPasswordForm, setForgotPasswordForm] = useState({ usernameOrEmail: "" });
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [staffProfileDraft, setStaffProfileDraft] = useState(() => buildProfileDraft());
  const [clientProfileDraft, setClientProfileDraft] = useState(() => buildProfileDraft());
  const [businessInfoDraft, setBusinessInfoDraft] = useState(() => {
    const raw = localStorage.getItem("replai_business_info");
    if (!raw) return buildBusinessInfoDraft();
    try {
      return buildBusinessInfoDraft(JSON.parse(raw));
    } catch (_) {
      return buildBusinessInfoDraft();
    }
  });
  const deviceId = useMemo(() => getOrCreateDeviceId(), []);
  const deviceLabel = useMemo(() => getCurrentDeviceLabel(), []);
  const [uiLanguage, setUiLanguage] = useState(() => {
    const saved = localStorage.getItem("replai_ui_language");
    if (saved) return normalizeUiLanguageCode(saved);
    return normalizeUiLanguageCode(typeof navigator !== "undefined" ? navigator.language : "en");
  });
  const [staffForm, setStaffForm] = useState({ firstName: "", lastName: "", gender: "", username: "", password: "", email: "", phone: "", city: "", country: "", title: DEFAULT_STAFF_TITLE, scopeLevel: "country", scopeValue: "", preferredLanguage: "English", avatar: "", role: "staff", permissions: ["review.respond", "review.escalate"] });
  const [editingStaffId, setEditingStaffId] = useState(null);
  const [staffEditForm, setStaffEditForm] = useState({ firstName: "", lastName: "", gender: "", name: "", email: "", phone: "", city: "", country: "", title: DEFAULT_STAFF_TITLE, scopeLevel: "country", scopeValue: "", preferredLanguage: "English", role: "staff", permissions: [], resetPassword: "", newUsername: "" });
  const [staffProblemThreads, setStaffProblemThreads] = useState(() => {
    const raw = localStorage.getItem("replai_staff_problem_threads");
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  });
  const [staffProblemForm, setStaffProblemForm] = useState({ subject: "", message: "", mode: "area", recipientId: "", clientId: "" });
  const [staffProblemAttachments, setStaffProblemAttachments] = useState([]);
  const [staffReplyDrafts, setStaffReplyDrafts] = useState({});
  const [staffReplyAttachments, setStaffReplyAttachments] = useState({});
  const [staffRecipientSearch, setStaffRecipientSearch] = useState("");
  const [staffDirectorySearch, setStaffDirectorySearch] = useState("");
  const [activeStaffThreadId, setActiveStaffThreadId] = useState("");
  const [staffComposeOpen, setStaffComposeOpen] = useState(false);
  const [staffDeleteUndo, setStaffDeleteUndo] = useState(null);
  const staffDeleteUndoTimerRef = useRef(null);
  const [voiceRecordingTarget, setVoiceRecordingTarget] = useState(null);
  const mediaRecorderRef = useRef(null);
  const mediaRecorderStreamRef = useRef(null);
  const mediaRecorderChunksRef = useRef([]);
  const [credentialsFilter, setCredentialsFilter] = useState({ query: "", type: "all" });
  const [credInlineEdit, setCredInlineEdit] = useState(null);
  const [staffFilter, setStaffFilter] = useState({ city: "all", country: "all", query: "" });
  const [clientSearch, setClientSearch] = useState("");
  const [clientStatusFilter, setClientStatusFilter] = useState("all");
  const [clientSort, setClientSort] = useState("name-asc");
  const [clientPage, setClientPage] = useState(1);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientReviewFilterStatus, setClientReviewFilterStatus] = useState("all");
  const [clientReviewFilterPlatform, setClientReviewFilterPlatform] = useState("all");
  const [clientReviewSearch, setClientReviewSearch] = useState("");
  const [highlightedReviewId, setHighlightedReviewId] = useState(null);
  const [clientPortalFilterStatus, setClientPortalFilterStatus] = useState("all");
  const [clientPortalFilterPlatform, setClientPortalFilterPlatform] = useState("all");
  const [clientPortalSearch, setClientPortalSearch] = useState("");
  const [clientPortalEditReviewId, setClientPortalEditReviewId] = useState(null);
  const [clientPortalEditText, setClientPortalEditText] = useState("");
  const [clientPortalTab, setClientPortalTab] = useState("overview");
  const [clientPortalMemberForm, setClientPortalMemberForm] = useState({ name: "", username: "", password: "" });
  const [clientBillingChangeForm, setClientBillingChangeForm] = useState({ newMethod: "bank-transfer", paymentDetail: "", reason: "" });
  const [supportRequests, setSupportRequests] = useState(() => {
    const raw = localStorage.getItem("replai_support_requests");
    if (!raw) return DEFAULT_SELF_SERVE_TICKETS;
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return DEFAULT_SELF_SERVE_TICKETS;
      const existingIds = new Set(parsed.map((ticket) => String(ticket.id)));
      const seededTickets = DEFAULT_SELF_SERVE_TICKETS.filter((ticket) => !existingIds.has(String(ticket.id)));
      return [...parsed, ...seededTickets];
    } catch (_) {
      return DEFAULT_SELF_SERVE_TICKETS;
    }
  });
  const [managedAccountRequests, setManagedAccountRequests] = useState([]);
  const [managedRequestFilter, setManagedRequestFilter] = useState("pending");
  const [contractEnvelopes, setContractEnvelopes] = useState(() => {
    const raw = localStorage.getItem("replai_contract_envelopes");
    if (!raw) return DEFAULT_CONTRACT_ENVELOPES;
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return DEFAULT_CONTRACT_ENVELOPES;
      const existingIds = new Set(parsed.map((item) => String(item.id)));
      const seeded = DEFAULT_CONTRACT_ENVELOPES.filter((item) => !existingIds.has(String(item.id)));
      return [...parsed, ...seeded];
    } catch (_) {
      return DEFAULT_CONTRACT_ENVELOPES;
    }
  });
  const [bankTransferConfigs, setBankTransferConfigs] = useState(() => {
    const raw = localStorage.getItem("replai_bank_transfer_configs");
    if (!raw) return DEFAULT_BANK_TRANSFER_CONFIGS;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length ? parsed : DEFAULT_BANK_TRANSFER_CONFIGS;
    } catch (_) {
      return DEFAULT_BANK_TRANSFER_CONFIGS;
    }
  });
  const [bankTransferConfigForm, setBankTransferConfigForm] = useState({
    countryCode: "",
    countryName: "",
    accountHolder: "",
    bankName: "",
    ibanOrAccount: "",
    swiftBic: "",
    routingCode: "",
    currency: "EUR",
    referencePrefix: "RPL",
    enabled: true,
  });
  const [editingBankTransferConfigId, setEditingBankTransferConfigId] = useState(null);
  const [bankTransferEditForm, setBankTransferEditForm] = useState({
    countryCode: "",
    countryName: "",
    accountHolder: "",
    bankName: "",
    ibanOrAccount: "",
    swiftBic: "",
    routingCode: "",
    currency: "EUR",
    referencePrefix: "RPL",
    enabled: true,
  });
  const [contractESignForm, setContractESignForm] = useState({ fullName: "", title: "", accept: false });
  const [clientSupportForm, setClientSupportForm] = useState({ category: "technical", priority: "medium", subject: "", message: "" });
  const [clientSupportAttachments, setClientSupportAttachments] = useState([]);
  const [adminSupportReplyDrafts, setAdminSupportReplyDrafts] = useState({});
  const [clientSupportReplyDrafts, setClientSupportReplyDrafts] = useState({});
  const [clientSupportStatusFilter, setClientSupportStatusFilter] = useState("active");
  const [selectedClientSupportTicketId, setSelectedClientSupportTicketId] = useState(null);
  const [adminSupportStatusFilter, setAdminSupportStatusFilter] = useState("active");
  const [reviewNotifications, setReviewNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [securityEventStatusFilter, setSecurityEventStatusFilter] = useState("all");
  const [securityEventScopeFilter, setSecurityEventScopeFilter] = useState("all");
  const [securityEventOwnerFilter, setSecurityEventOwnerFilter] = useState("mine");
  const [securityEventSearch, setSecurityEventSearch] = useState("");
  const [backendActivityAudit, setBackendActivityAudit] = useState([]);
  const [backendActivitySearch, setBackendActivitySearch] = useState("");
  const [backendActivityLoading, setBackendActivityLoading] = useState(false);
  const [activityActorFilter, setActivityActorFilter] = useState("");
  const [activityActionFilter, setActivityActionFilter] = useState("all");
  const [activityDateFrom, setActivityDateFrom] = useState("");
  const [activityDateTo, setActivityDateTo] = useState("");
  const [activityPage, setActivityPage] = useState(1);
  
  // Pricing state for regional pricing
  const [userCountry, setUserCountry] = useState(() => {
    const savedCountry = localStorage.getItem("replai_user_country");
    if (savedCountry) return savedCountry;
    const localeCountry = parseCountryFromLocale(typeof navigator !== "undefined" ? navigator.language : "");
    return localeCountry || "DE";
  });
  const [localizedPricing, setLocalizedPricing] = useState(null);
  const [billingConfig, setBillingConfig] = useState({ detectedCountry: "", availablePaymentMethods: [] });
  // eslint-disable-next-line no-unused-vars
  const [pricingLoading, setPricingLoading] = useState(false);
  const [clientPlanChangeForm, setClientPlanChangeForm] = useState({ plan: "starter", reason: "" });
  const [dashboardMailbox, setDashboardMailbox] = useState({ messages: [], configured: false, loading: false, error: "", mailbox: "", hasLoaded: false, cached: false });
  const [mailboxCompose, setMailboxCompose] = useState(() => createEmptyMailboxCompose());
  const [mailboxSending, setMailboxSending] = useState(false);
  const [mailboxSendStatus, setMailboxSendStatus] = useState(null); // { type: "success"|"error"|"queued", msg: string }
  const [mailboxSearch, setMailboxSearch] = useState("");
  const [mailboxThreadFilter, setMailboxThreadFilter] = useState("all");
  const [mailboxSenderFilter, setMailboxSenderFilter] = useState("all");
  const [mailboxDateFilter, setMailboxDateFilter] = useState("all");
  const [expandedMailboxMessageId, setExpandedMailboxMessageId] = useState("");
  const [mailboxFolder, setMailboxFolder] = useState("inbox");
  const [mailboxSentMessages, setMailboxSentMessages] = useState([]);
  const [mailboxActionLoadingId, setMailboxActionLoadingId] = useState("");
  const [mailboxComposeOpen, setMailboxComposeOpen] = useState(false);
  const [mailboxSelectedKeys, setMailboxSelectedKeys] = useState([]);
  const [mailboxPreviewMessageKey, setMailboxPreviewMessageKey] = useState("");
  const [mailboxThreadPage, setMailboxThreadPage] = useState(1);
  const [mailboxPreviewPage, setMailboxPreviewPage] = useState(1);
  const [mailboxDraftSavedAt, setMailboxDraftSavedAt] = useState("");
  const [mailboxDraftStatus, setMailboxDraftStatus] = useState("idle");
  const mailboxAutoReadThreadIds = useRef(new Set());
  const mailboxDraftHydratedRef = useRef(false);
  const mailboxDraftLoadRef = useRef("");

  const [tab, setTab] = useState("queue");
  const [clients, setClients] = useState(() => {
    const raw = localStorage.getItem("replai_clients");
    if (!raw) return [...initClients, ...DEFAULT_SELF_SERVE_CLIENTS];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || !parsed.length) return [...initClients, ...DEFAULT_SELF_SERVE_CLIENTS];
      const existingIds = new Set(parsed.map((c) => Number(c.id)));
      const seededSelfServe = DEFAULT_SELF_SERVE_CLIENTS.filter((c) => !existingIds.has(Number(c.id)));
      return [...parsed, ...seededSelfServe];
    } catch (_) {
      return [...initClients, ...DEFAULT_SELF_SERVE_CLIENTS];
    }
  });
  const [registrationNotifications, setRegistrationNotifications] = useState(() => {
    try { return JSON.parse(localStorage.getItem("replai_registration_notifs") || "[]"); } catch { return []; }
  });
  const [registrationsSeenIds, setRegistrationsSeenIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("replai_registrations_seen") || "[]"); } catch { return []; }
  });
    const [selectedSelfServeClientId, setSelectedSelfServeClientId] = useState(null);
    const [selfServeSearch, setSelfServeSearch] = useState("");
    const [selfServeStatusFilter, setSelfServeStatusFilter] = useState("all");
    const [selfServePage, setSelfServePage] = useState(1);
    const [selfServeClientDashboardId, setSelfServeClientDashboardId] = useState(null);
    const [selfServeClientDashboardTab, setSelfServeClientDashboardTab] = useState("overview");
  const [prospects, setProspects] = useState(() => {
    const raw = localStorage.getItem("replai_prospects");
    if (!raw) return initProspects;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length ? parsed : initProspects;
    } catch (_) {
      return initProspects;
    }
  });
  const [reviews, setReviews] = useState(() => {
    const raw = localStorage.getItem("replai_reviews");
    if (!raw) return initReviews;
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || !parsed.length) return initReviews;
      const existingIds = new Set(parsed.map((r) => Number(r.id)));
      const seededReviews = initReviews.filter((r) => !existingIds.has(Number(r.id)));
      return [...parsed, ...seededReviews];
    } catch (_) {
      return initReviews;
    }
  });
  const [selClient, setSelClient] = useState("all");
  const [filterSt, setFilterSt] = useState("all");
  const [reviewResponseToneById, setReviewResponseToneById] = useState({});
  const [editRev, setEditRev] = useState(null);
  const [editTxt, setEditTxt] = useState("");
  const [notif, setNotif] = useState(null);

  // Modals
  const [modal, setModal] = useState(null); // outreach | clientMsg | newProspect | newClient | contract | report
  const [modalData, setModalData] = useState({});
  const [reportFrom, setReportFrom] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0]);
  const [reportTo, setReportTo] = useState(new Date().toISOString().split("T")[0]);
  const [reportIntro, setReportIntro] = useState("Hi {contact},\n\nPlease find your detailed Replai performance report below. This covers your review activity, response performance, and account metrics for the selected period.");
  const [analyticsReportScope, setAnalyticsReportScope] = useState("all");
  const [analyticsReportFrom, setAnalyticsReportFrom] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0]);
  const [analyticsReportTo, setAnalyticsReportTo] = useState(new Date().toISOString().split("T")[0]);
  const [analyticsReportIntro, setAnalyticsReportIntro] = useState("Hi,\n\nPlease find the analytics report for the selected period.");
  const [analyticsReportRecipient, setAnalyticsReportRecipient] = useState("");
  const [contractFilter, setContractFilter] = useState("all");
  const [contractEffectiveDate, setContractEffectiveDate] = useState(new Date().toISOString().split("T")[0]);
  const [operatorSignature, setOperatorSignature] = useState("");
  const [googleBridgeConfig, setGoogleBridgeConfig] = useState({ enabled: false, authUrl: "", bridgeUrlConfigured: false });
  const [googleBusyClientId, setGoogleBusyClientId] = useState(null);
  const [trustpilotBridgeConfig, setTrustpilotBridgeConfig] = useState({ enabled: false, authUrl: "", bridgeUrlConfigured: false });
  const [trustpilotBusyClientId, setTrustpilotBusyClientId] = useState(null);
  const [facebookBusyClientId, setFacebookBusyClientId] = useState(null);
  const [tempPasswordModal, setTempPasswordModal] = useState(null);
  const [cancelSubModal, setCancelSubModal] = useState({ open: false, reason: "", processing: false });
  const [deleteAccountModal, setDeleteAccountModal] = useState({ open: false, confirmText: "", password: "", error: "", processing: false });
  const [brandVoiceDraft, setBrandVoiceDraft] = useState({ description: "", tone: "", keywords: "", language: "" });
  const [landingContent, setLandingContent] = useState(null);
  const [landingContentLoading, setLandingContentLoading] = useState(false);
  const [landingContentSaving, setLandingContentSaving] = useState(false);
  const [landingContentDraft, setLandingContentDraft] = useState(null);

  const loadAgencyBranding = () => {
    try {
      const raw = localStorage.getItem("replai_agency_branding");
      return raw ? JSON.parse(raw) : { enabled: false, brandName: "", logoEmoji: "🏢", primaryColor: "#10B981", supportEmail: "", footerText: "" };
    } catch { return { enabled: false, brandName: "", logoEmoji: "🏢", primaryColor: "#10B981", supportEmail: "", footerText: "" }; }
  };
  const [agencyBranding, setAgencyBranding] = useState(loadAgencyBranding);
  const saveAgencyBranding = (update) => {
    const next = { ...agencyBranding, ...update };
    setAgencyBranding(next);
    try { localStorage.setItem("replai_agency_branding", JSON.stringify(next)); } catch {}
  };
  const brandName = agencyBranding.enabled && agencyBranding.brandName.trim() ? agencyBranding.brandName.trim() : "Replai";
  const brandLogo = agencyBranding.enabled && agencyBranding.logoEmoji ? agencyBranding.logoEmoji : null;
  const brandColor = agencyBranding.enabled && agencyBranding.primaryColor ? agencyBranding.primaryColor : "#10B981";

  const t = useCallback((key, fallback) => {
    const lang = normalizeUiLanguageCode(uiLanguage);
    const dict = I18N_DICTIONARY[lang] || I18N_DICTIONARY.en;
    return resolveI18nKey(dict, key) || fallback || key;
  }, [uiLanguage]);
  const tp = useCallback((value) => {
    const source = String(value || "");
    const lang = normalizeUiLanguageCode(uiLanguage);
    if (lang === "en") return source;
    const dict = I18N_DICTIONARY[lang] || I18N_DICTIONARY.en;
    return resolveI18nKey(dict, `phrases.${source}`) || source;
  }, [uiLanguage]);
  const tm = useCallback((value) => {
    const source = String(value || "");
    const lang = normalizeUiLanguageCode(uiLanguage);
    if (lang === "en") return source;
    const dict = I18N_DICTIONARY[lang] || I18N_DICTIONARY.en;
    return resolveI18nKey(dict, `messages.${source}`) || resolveI18nKey(dict, `phrases.${source}`) || source;
  }, [uiLanguage]);

  // Outreach state
  const [outreachTemplate, setOutreachTemplate] = useState(OUTREACH_TEMPLATES[0]);
  const [outreachRecipient, setOutreachRecipient] = useState(null);
  const [outreachBody, setOutreachBody] = useState("");
  const [outreachSubject, setOutreachSubject] = useState("");
  const [outreachAttachment, setOutreachAttachment] = useState(null);
  const [prospectSearch, setProspectSearch] = useState("");
  const [prospectStatusFilter, setProspectStatusFilter] = useState("all");
  const [prospectSort, setProspectSort] = useState("last-contact-desc");
  const [prospectPage, setProspectPage] = useState(1);

  // Client message state
  const [clientMsgTemplate, setClientMsgTemplate] = useState(CLIENT_MSG_TEMPLATES[0]);
  const [clientMsgRecipient, setClientMsgRecipient] = useState(null);
  const [clientMsgBody, setClientMsgBody] = useState("");
  const [clientMsgSubject, setClientMsgSubject] = useState("");
  const [clientMsgAttachment, setClientMsgAttachment] = useState(null);
  const [clientMsgPreview, setClientMsgPreview] = useState(null);

  const notify = useCallback((msg, type = "success") => {
    setNotif({ msg: tm(msg), type });
    setTimeout(() => setNotif(null), 3000);
  }, [tm]);

  const getAuditActor = useCallback(() => {
    if (currentUser) {
      return {
        id: currentUser.id,
        name: currentUser.name,
        username: currentUser.username,
        role: currentUser.role,
        scope: currentUser.scopeLevel || "staff",
      };
    }
    if (currentClientPortalUser) {
      return {
        id: currentClientPortalUser.id,
        name: currentClientPortalUser.name,
        username: currentClientPortalUser.username,
        role: currentClientPortalUser.role || "owner",
        scope: "client-portal",
      };
    }
    return {
      id: "system",
      name: "System",
      username: "system",
      role: "system",
      scope: "system",
    };
  }, [currentUser, currentClientPortalUser]);

  const logBackendActivity = useCallback(async (action, detail, meta = {}) => {
    try {
      await apiFetch("/api/activity/audit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action,
          detail,
          actor: getAuditActor(),
          meta: {
            tab,
            ...meta,
          },
        }),
      });
    } catch (_) {
      // best-effort audit trail only
    }
  }, [getAuditActor, tab]);

  const loadBackendActivityAudit = useCallback(async () => {
    setBackendActivityLoading(true);
    try {
      const res = await apiFetch("/api/activity/audit?limit=400", { cache: "no-store" });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success) {
        setBackendActivityAudit(Array.isArray(data.audit) ? data.audit : []);
      }
    } catch (_) {
      // keep existing local cache
    } finally {
      setBackendActivityLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const originalConfirm = window.confirm;
    window.confirm = (message) => originalConfirm(tm(String(message || "")));
    return () => {
      window.confirm = originalConfirm;
    };
  }, [tm]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const root = document.getElementById("replai-dashboard-root");
    if (!root) return undefined;

    const applyPhraseTranslations = () => {
      if (normalizeUiLanguageCode(uiLanguage) === "en") return;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        const original = String(node.nodeValue || "");
        const trimmed = original.trim();
        if (!trimmed) return;
        const translated = tp(trimmed);
        if (translated !== trimmed) {
          node.nodeValue = original.replace(trimmed, translated);
        }
      });

      root.querySelectorAll("input[placeholder], textarea[placeholder]").forEach((el) => {
        const p = el.getAttribute("placeholder");
        if (!p) return;
        const translated = tp(p);
        if (translated !== p) el.setAttribute("placeholder", translated);
      });
    };

    applyPhraseTranslations();
    const observer = new MutationObserver(() => applyPhraseTranslations());
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [uiLanguage, tp, tab, clientPortalTab, selfServeClientDashboardId, selfServeClientDashboardTab, modal]);

  // Load pricing for user's country
  const loadPricing = useCallback(async (countryCode = userCountry) => {
    setPricingLoading(true);
    try {
      const res = await apiFetch(`/api/pricing/all?country=${encodeURIComponent(countryCode)}`);
      const data = await res.json().catch(() => ({}));
      if (data?.success && data?.pricing) {
        setLocalizedPricing(data.pricing);
        localStorage.setItem("replai_user_country", countryCode);
        setUserCountry(countryCode);
      }
    } catch (err) {
      console.error('Failed to load pricing:', err);
    } finally {
      setPricingLoading(false);
    }
  }, [userCountry]);

  const detectPricingCountry = useCallback(async () => {
    try {
      const browserLocale = typeof navigator !== "undefined" ? (navigator.language || "") : "";
      const res = await apiFetch("/api/pricing/detect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ browserLocale }),
      });
      const data = await res.json().catch(() => ({}));
      const detected = String(data?.detectedCountry || "").toUpperCase().trim();
      if (/^[A-Z]{2}$/.test(detected)) {
        setUserCountry(detected);
      }
    } catch (_) {
      // keep fallback country
    }
  }, []);

  const loadBillingConfig = useCallback(async () => {
    try {
      const res = await apiFetch("/api/billing/config", { cache: "no-store" });
      const data = await res.json().catch(() => ({}));
      if (!data?.success) return;
      const detected = String(data?.detectedCountry || "").toUpperCase().trim();
      const available = Array.isArray(data?.availablePaymentMethods) ? data.availablePaymentMethods : [];
      setBillingConfig({ detectedCountry: detected, availablePaymentMethods: available });
      if (/^[A-Z]{2}$/.test(detected)) {
        setUserCountry(detected);
      }
    } catch (_) {
      // keep defaults
    }
  }, []);

  useEffect(() => {
    loadBillingConfig();
  }, [loadBillingConfig]);

  useEffect(() => {
    const saved = localStorage.getItem("replai_operator_signature");
    if (saved) setOperatorSignature(saved);
  }, []);

  useEffect(() => {
    if (operatorSignature) localStorage.setItem("replai_operator_signature", operatorSignature);
    else localStorage.removeItem("replai_operator_signature");
  }, [operatorSignature]);

  // --- Staff backend sync ---
  const pendingStaffSave = useRef(null);
  const staffSyncReady = useRef(false);
  const [staffSyncLoading, setStaffSyncLoading] = useState(false);

  // Load staff from backend as soon as we have a secret — runs in parallel with the
  // agency-access check so accounts are ready before the login button is pressed.
  useEffect(() => {
    if (!agencySecret) return;
    let alive = true;
    setStaffSyncLoading(true);
    apiFetch("/api/staff/accounts", { headers: { "X-Agency-Secret": agencySecret } })
      .then((r) => r.json().catch(() => ({})))
      .then((data) => {
        if (!alive) return;
        // Mark ready BEFORE calling setStaffMembers so the debounced save
        // effect fires correctly for any password change that happened while
        // the fetch was in-flight (race condition fix).
        staffSyncReady.current = true;
        setStaffSyncLoading(false);
        if (data?.success && Array.isArray(data.staff) && data.staff.length) {
          setStaffMembers((prev) => {
            const backendMap = new Map(data.staff.map((s) => [String(s.id), s]));
            const localMap = new Map(prev.map((s) => [String(s.id), s]));
            const merged = [...backendMap.values()];
            for (const [id, member] of localMap.entries()) {
              if (!backendMap.has(id)) merged.push(member);
            }
            return merged;
          });
        } else {
          // Server returned empty — push local data up immediately so other
          // devices can log in (handles server restarts / Railway redeploys).
          setStaffMembers((prev) => {
            if (prev.length > 0) {
              apiFetch("/api/staff/accounts", {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
                body: JSON.stringify({ staff: prev }),
              }).catch(() => {});
            }
            return prev;
          });
        }
      })
      .catch(() => {
        if (!alive) return;
        staffSyncReady.current = true;
        setStaffSyncLoading(false);
      });
    return () => { alive = false; };
  }, [agencySecret]);

  useEffect(() => {
    if (!agencySecret || !staffSyncReady.current) return;
    clearTimeout(pendingStaffSave.current);
    pendingStaffSave.current = setTimeout(() => {
      apiFetch("/api/staff/accounts", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
        body: JSON.stringify({ staff: staffMembers }),
      }).catch(() => {});
    }, 1500);
  }, [staffMembers, agencySecret]);

  // --- Server-side persistence (Chunk 2) ---
  const serverLoadDone = useRef(false);
  const [serverSnapshotReady, setServerSnapshotReady] = useState(false);
  const pendingServerSave = useRef(null);

  // On mount: load operational data from server, overwrite localStorage + state
  useEffect(() => {
    let alive = true;
    apiFetch("/api/data/snapshot")
      .then((r) => r.json().catch(() => ({})))
      .then((data) => {
        if (!alive || !data?.success) {
          serverLoadDone.current = true;
          setServerSnapshotReady(true);
          return;
        }
        if (Array.isArray(data.clients) && data.clients.length) setClients(data.clients);
        if (Array.isArray(data.reviews) && data.reviews.length) setReviews(data.reviews);
        if (Array.isArray(data.prospects) && data.prospects.length) setProspects(data.prospects);
        if (Array.isArray(data.supportRequests) && data.supportRequests.length) setSupportRequests(data.supportRequests);
        if (Array.isArray(data.contractEnvelopes) && data.contractEnvelopes.length) setContractEnvelopes(data.contractEnvelopes);
        if (Array.isArray(data.bankTransferConfigs) && data.bankTransferConfigs.length) setBankTransferConfigs(data.bankTransferConfigs);
        if (Array.isArray(data.staffProblemThreads)) {
          setStaffProblemThreads((prev) => mergeStaffProblemThreads(prev, data.staffProblemThreads));
        }
        if (Array.isArray(data.clientPortalUsers) && data.clientPortalUsers.length) setClientPortalUsers(data.clientPortalUsers);
        setTimeout(() => {
          if (!alive) return;
          serverLoadDone.current = true;
          setServerSnapshotReady(true);
        }, 300);
      })
      .catch(() => {
        if (!alive) return;
        serverLoadDone.current = true;
        setServerSnapshotReady(true);
      });
    return () => { alive = false; };
  }, []);

  // Debounced save to server whenever operational data changes (2-second debounce)
  useEffect(() => {
    if (!serverSnapshotReady) return;
    clearTimeout(pendingServerSave.current);
    pendingServerSave.current = setTimeout(() => {
      const actor = currentUser
        ? {
          id: currentUser.id,
          name: currentUser.name,
          username: currentUser.username,
          role: currentUser.role,
          scope: currentUser.scopeLevel || "staff",
        }
        : currentClientPortalUser
          ? {
            id: currentClientPortalUser.id,
            name: currentClientPortalUser.name,
            username: currentClientPortalUser.username,
            role: currentClientPortalUser.role || "owner",
            scope: "client-portal",
          }
          : { id: "system", name: "System", username: "system", role: "system", scope: "system" };
      apiFetch("/api/data/snapshot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clients,
          reviews,
          prospects,
          supportRequests,
          contractEnvelopes,
          bankTransferConfigs,
          staffProblemThreads,
          clientPortalUsers,
          audit: {
            action: "dashboard.snapshot.sync",
            source: "dashboard-autosave",
            actor,
          },
        }),
      }).catch(() => {});
    }, 2000);
  }, [clients, reviews, prospects, supportRequests, contractEnvelopes, bankTransferConfigs, staffProblemThreads, clientPortalUsers, serverSnapshotReady, currentUser, currentClientPortalUser]);
  // -----------------------------------------

  useEffect(() => {
    localStorage.setItem("replai_staff_members", JSON.stringify(staffMembers));
  }, [staffMembers]);

  useEffect(() => {
    localStorage.setItem("replai_clients", JSON.stringify(clients));
  }, [clients]);

  useEffect(() => {
    localStorage.setItem("replai_registration_notifs", JSON.stringify(registrationNotifications));
  }, [registrationNotifications]);


  useEffect(() => {
    localStorage.setItem("replai_registrations_seen", JSON.stringify(registrationsSeenIds));
  }, [registrationsSeenIds]);

  useEffect(() => {
    setClients((prev) => {
      let changed = false;
      const next = prev.map((client) => {
        if (client.ratingAtOnboarding === undefined || client.ratingAtOnboarding === null) {
          changed = true;
          return { ...client, ratingAtOnboarding: Number(client.avgRating || 0) };
        }
        return client;
      });
      return changed ? next : prev;
    });
  }, []);

  useEffect(() => {
    localStorage.setItem("replai_prospects", JSON.stringify(prospects));
  }, [prospects]);

  useEffect(() => {
    localStorage.setItem("replai_bank_transfer_configs", JSON.stringify(bankTransferConfigs));
  }, [bankTransferConfigs]);

  useEffect(() => {
    localStorage.setItem("replai_reviews", JSON.stringify(reviews));
  }, [reviews]);

  // ── Negative review alert emails ─────────────────────────────────────────
  const alertedReviewIds = useRef(new Set());
  useEffect(() => {
    if (!agencySecret) return;
    const negativeReviews = reviews.filter((r) => Number(r.rating) <= 2 && r.status === "pending");
    negativeReviews.forEach((review) => {
      if (alertedReviewIds.current.has(review.id)) return;
      alertedReviewIds.current.add(review.id);
      const client = clients.find((c) => c.id === review.clientId);
      if (!client?.email) return;
      apiFetch("/api/notify/negative-review", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
        body: JSON.stringify({
          clientName: client.name,
          clientEmail: client.email,
          reviewAuthor: review.author,
          reviewRating: review.rating,
          reviewText: review.text,
          reviewPlatform: review.platform,
          reviewDate: review.date,
          dashboardUrl: window.location.origin,
        }),
      }).catch((err) => { console.error("[negative-review-alert] email dispatch failed for review", review.id, err); });
    });
  }, [reviews, clients, agencySecret]);

  // ── Sound notification triggers ───────────────────────────────────────────
  const prevPendingReviewCount = useRef(null);
  const prevChatCount = useRef(null);
  const prevMailboxCount = useRef(null);

  // New pending review sound
  useEffect(() => {
    if (!currentUser) return;
    const ss = normalizeSoundSettings(currentUser.soundSettings);
    if (!ss.review.enabled) { prevPendingReviewCount.current = null; return; }
    const count = reviews.filter((r) => r.status === "pending").length;
    if (prevPendingReviewCount.current !== null && count > prevPendingReviewCount.current) {
      playNotificationSound(ss.review.tone);
    }
    prevPendingReviewCount.current = count;
  }, [reviews, currentUser]);

  // New staff chat message sound — access check inlined to avoid dependency on
  // canAccessProblemThread which is defined later in the component body.
  useEffect(() => {
    if (!currentUser) return;
    const ss = normalizeSoundSettings(currentUser.soundSettings);
    if (!ss.chat.enabled) { prevChatCount.current = null; return; }
    const userId = currentUser.id;
    const isAdmin = currentUser.role === "admin";
    const count = staffProblemThreads.reduce((acc, t) => {
      const canAccess = isAdmin || t.senderId === userId || (t.recipientIds || []).includes(userId);
      if (!canAccess) return acc;
      const replies = Array.isArray(t.replies) ? t.replies : [];
      return acc + replies.filter((r) => r.senderId !== userId).length;
    }, 0);
    if (prevChatCount.current !== null && count > prevChatCount.current) {
      playNotificationSound(ss.chat.tone);
    }
    prevChatCount.current = count;
  }, [staffProblemThreads, currentUser]);

  // New mailbox message sound
  useEffect(() => {
    if (!currentUser) return;
    const ss = normalizeSoundSettings(currentUser.soundSettings);
    if (!ss.mailbox.enabled) { prevMailboxCount.current = null; return; }
    const count = dashboardMailbox.messages.filter((m) => !m?.read).length;
    if (prevMailboxCount.current !== null && count > prevMailboxCount.current) {
      playNotificationSound(ss.mailbox.tone);
    }
    prevMailboxCount.current = count;
  }, [dashboardMailbox.messages, currentUser]);

  useEffect(() => {
    localStorage.setItem("replai_support_requests", JSON.stringify(supportRequests));
  }, [supportRequests]);

  useEffect(() => {
    localStorage.setItem("replai_staff_problem_threads", JSON.stringify(staffProblemThreads));
  }, [staffProblemThreads]);

  const currentUserId = currentUser?.id || null;

  // Poll shared snapshot for staff chat + reviews so all sessions stay in sync
  // and sound notifications fire without requiring a page reload or tab switch.
  useEffect(() => {
    if (!currentUserId) return;
    let active = true;
    const syncSnapshot = () => {
      apiFetch("/api/data/snapshot")
        .then((res) => res.json().catch(() => ({})))
        .then((data) => {
          if (!active || !data?.success) return;
          if (Array.isArray(data.staffProblemThreads)) {
            setStaffProblemThreads((prev) => mergeStaffProblemThreads(prev, data.staffProblemThreads));
          }
          if (Array.isArray(data.reviews) && data.reviews.length) {
            setReviews((prev) => {
              // Merge: keep local edits, add any new reviews from server
              const localMap = new Map(prev.map((r) => [r.id, r]));
              const serverMap = new Map(data.reviews.map((r) => [r.id, r]));
              const merged = [...serverMap.values()];
              for (const [id, r] of localMap.entries()) {
                if (!serverMap.has(id)) merged.push(r);
              }
              return merged;
            });
          }
        })
        .catch(() => {});
    };
    syncSnapshot();
    const intervalId = setInterval(syncSnapshot, 6000);
    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [currentUserId]);


  useEffect(() => () => {
    if (staffDeleteUndoTimerRef.current) {
      clearTimeout(staffDeleteUndoTimerRef.current);
      staffDeleteUndoTimerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
    if (mediaRecorderStreamRef.current) {
      mediaRecorderStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaRecorderStreamRef.current = null;
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("replai_contract_envelopes", JSON.stringify(contractEnvelopes));
  }, [contractEnvelopes]);

  useEffect(() => {
    const envelopeMap = new Map(contractEnvelopes.map((item) => [item.clientId, item]));
    setClients((prev) => {
      let changed = false;
      const next = prev.map((client) => {
        const envelope = envelopeMap.get(client.id);
        if (!envelope) return client;
        const targetStatus = envelope.status === "signed" ? "signed" : envelope.status === "sent" ? "sent" : client.contractStatus;
        if (targetStatus !== client.contractStatus) {
          changed = true;
          return { ...client, contractStatus: targetStatus };
        }
        return client;
      });
      return changed ? next : prev;
    });
  }, [contractEnvelopes]);

  useEffect(() => {
    const pending = contractEnvelopes.filter((item) => item.status === "sent" && item.envelopeId);
    if (!pending.length) return;
    let stopped = false;

    const syncStatus = async () => {
      try {
        const results = await Promise.all(
          pending.map((item) =>
            apiFetch(`/api/contracts/status?envelopeId=${encodeURIComponent(item.envelopeId)}`)
              .then((res) => res.json().catch(() => ({})))
              .then((data) => ({ envelopeId: item.envelopeId, data }))
              .catch(() => ({ envelopeId: item.envelopeId, data: null }))
          )
        );
        if (stopped) return;
        setContractEnvelopes((prev) => {
          let changed = false;
          const next = prev.map((entry) => {
            const hit = results.find((r) => r.envelopeId === entry.envelopeId);
            const status = hit?.data?.status;
            if (status === "signed" && entry.status !== "signed") {
              changed = true;
              return {
                ...entry,
                status: "signed",
                signedAt: hit.data.signedAt || entry.signedAt || new Date().toISOString(),
                signedBy: hit.data.signedBy || entry.signedBy || "Client",
                signerTitle: hit.data.signerTitle || entry.signerTitle || "Owner",
              };
            }
            return entry;
          });
          return changed ? next : prev;
        });
      } catch (_) {
        // ignore transient errors
      }
    };

    syncStatus();
    const timer = setInterval(syncStatus, 30000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [contractEnvelopes]);

  useEffect(() => {
    localStorage.setItem("replai_client_portal_users", JSON.stringify(clientPortalUsers));
  }, [clientPortalUsers]);

  useEffect(() => {
    if (currentUser) sessionStorage.setItem("replai_current_user", JSON.stringify(currentUser));
    else sessionStorage.removeItem("replai_current_user");
  }, [currentUser]);

  useEffect(() => {
    if (currentClientPortalUser) sessionStorage.setItem("replai_current_client_portal_user", JSON.stringify(currentClientPortalUser));
    else sessionStorage.removeItem("replai_current_client_portal_user");
  }, [currentClientPortalUser]);

  useEffect(() => {
    setStaffProfileDraft(buildProfileDraft(currentUser || {}));
  }, [currentUser]);

  useEffect(() => {
    setClientProfileDraft(buildProfileDraft(currentClientPortalUser || {}));
  }, [currentClientPortalUser]);


  useEffect(() => {
    localStorage.setItem("replai_business_info", JSON.stringify(businessInfoDraft));
  }, [businessInfoDraft]);

  useEffect(() => {
    localStorage.setItem("replai_ui_language", uiLanguage);
  }, [uiLanguage]);

  useEffect(() => {
    const linkedClient = currentClientPortalUser ? clients.find((c) => c.id === currentClientPortalUser.clientId) : null;
    const currentMethod = String(linkedClient?.paymentMethod || "bank-transfer").toLowerCase();
    setClientBillingChangeForm((prev) => ({
      ...prev,
      newMethod: currentMethod,
      paymentDetail: "",
      reason: "",
    }));
  }, [clients, currentClientPortalUser]);

  useEffect(() => {
    const now = Date.now();
    setClients((prev) => {
      let changed = false;
      const next = prev.map((client) => {
        const pending = client?.pendingPlanChange;
        if (!pending?.newPlan || !pending?.effectiveDate) return client;
        const effectiveAt = new Date(pending.effectiveDate).getTime();
        if (!Number.isFinite(effectiveAt) || effectiveAt > now) return client;
        changed = true;
        return {
          ...client,
          plan: pending.newPlan,
          platformsManaged: getPlatformsForPlan(pending.newPlan),
          pendingPlanChange: null,
          updatedAt: new Date().toISOString(),
        };
      });
      return changed ? next : prev;
    });
  }, [clients]);

  useEffect(() => {
    if (!requestedAgencyAccess) return;
    let active = true;
    const validateAgencySecret = async () => {
      try {
        const secret = portalParams.get("secret") || "";
        const res = await apiFetch("/api/agency-access", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ secret }),
        });
        const data = await res.json().catch(() => ({}));
        if (!active) return;
        const enabled = !!data?.success && !!(data?.allowed ?? data?.valid);
        setAgencyAccessEnabled(enabled);
      } catch (err) {
        console.error("[agency-access] fetch error:", err);
        if (!active) return;
        setAgencyAccessEnabled(false);
      } finally {
        if (active) setAgencyAccessChecked(true);
      }
    };
    validateAgencySecret();
    return () => { active = false; };
  }, [requestedAgencyAccess, portalParams]);

  useEffect(() => {
    let alive = true;
    const migratePasswords = async () => {
      try {
        const next = await Promise.all(staffMembers.map(async (member) => {
          if (member.passwordHash && member.passwordSalt) {
            return {
              ...member,
              title: member.title || (member.role === "admin" ? "Head of Operations" : DEFAULT_STAFF_TITLE),
              scopeLevel: member.scopeLevel || (member.role === "admin" ? "global" : "country"),
              scopeValue: member.scopeValue || (member.role === "admin" ? "All Regions" : member.country || member.city || "Unassigned"),
              preferredLanguage: member.preferredLanguage || "English",
              timezone: member.timezone || "Europe/Berlin",
              notificationPreference: member.notificationPreference || "dashboard",
              bio: member.bio || "",
              onboardingSeenVersion: member.onboardingSeenVersion || null,
            };
          }
          if (member.password) {
            const salt = generateSalt();
            const passwordHash = await hashPassword(member.password, salt);
            const { password, ...rest } = member;
            return {
              ...rest,
              passwordHash,
              passwordSalt: salt,
              mustChangePassword: member.mustChangePassword ?? true,
              title: rest.title || (rest.role === "admin" ? "Head of Operations" : DEFAULT_STAFF_TITLE),
              scopeLevel: rest.scopeLevel || (rest.role === "admin" ? "global" : "country"),
              scopeValue: rest.scopeValue || (rest.role === "admin" ? "All Regions" : rest.country || rest.city || "Unassigned"),
              preferredLanguage: rest.preferredLanguage || "English",
              timezone: rest.timezone || "Europe/Berlin",
              notificationPreference: rest.notificationPreference || "dashboard",
              bio: rest.bio || "",
              onboardingSeenVersion: rest.onboardingSeenVersion || null,
            };
          }
          if (member.role === "admin") {
            return {
              ...member,
              passwordHash: member.passwordHash || DEFAULT_ADMIN.passwordHash,
              passwordSalt: member.passwordSalt || DEFAULT_ADMIN.passwordSalt,
              mustChangePassword: member.mustChangePassword ?? true,
              title: member.title || "Head of Operations",
              scopeLevel: member.scopeLevel || "global",
              scopeValue: member.scopeValue || "All Regions",
              preferredLanguage: member.preferredLanguage || "English",
              timezone: member.timezone || "Europe/Berlin",
              notificationPreference: member.notificationPreference || "dashboard",
              bio: member.bio || "",
              onboardingSeenVersion: member.onboardingSeenVersion || null,
            };
          }
          return {
            ...member,
            title: member.title || DEFAULT_STAFF_TITLE,
            scopeLevel: member.scopeLevel || "country",
            scopeValue: member.scopeValue || member.country || member.city || "Unassigned",
            preferredLanguage: member.preferredLanguage || "English",
            timezone: member.timezone || "Europe/Berlin",
            notificationPreference: member.notificationPreference || "dashboard",
            bio: member.bio || "",
            onboardingSeenVersion: member.onboardingSeenVersion || null,
          };
        }));
        const normalizedNext = next.map((member) => normalizeSecurityAccount(member));
        if (!alive) return;
        if (JSON.stringify(normalizedNext) !== JSON.stringify(staffMembers)) setStaffMembers(normalizedNext);
      } catch (_) {
        // ignore migration failures
      } finally {
        if (alive) setAuthReady(true);
      }
    };
    migratePasswords();
    return () => { alive = false; };
  }, [staffMembers]);

  useEffect(() => {
    let alive = true;
    const migrateClientPortalPasswords = async () => {
      try {
        const next = await Promise.all(clientPortalUsers.map(async (user) => {
          const role = user.role === "member" ? "member" : "owner";
          const normalized = normalizeSecurityAccount({ ...user, role, mustChangePassword: user.mustChangePassword ?? (role === "owner") });
          if (normalized.passwordHash && normalized.passwordSalt) return normalized;
          if (user.password) {
            const salt = generateSalt();
            const passwordHash = await hashPassword(user.password, salt);
            const { password, ...rest } = user;
            const restRole = rest.role === "member" ? "member" : "owner";
            return normalizeSecurityAccount({ ...rest, role: restRole, mustChangePassword: rest.mustChangePassword ?? (restRole === "owner"), passwordHash, passwordSalt: salt });
          }
          return normalized;
        }));
        if (!alive) return;
        if (JSON.stringify(next) !== JSON.stringify(clientPortalUsers)) setClientPortalUsers(next);
      } catch (_) {
        // ignore migration failures
      }
    };
    migrateClientPortalPasswords();
    return () => { alive = false; };
  }, [clientPortalUsers]);

  useEffect(() => {
    if (!currentUser) return;
    const synced = staffMembers.find((s) => s.id === currentUser.id);
    if (synced && JSON.stringify(synced) !== JSON.stringify(currentUser)) setCurrentUser(synced);
  }, [staffMembers, currentUser]);

  useEffect(() => {
    if (!currentClientPortalUser) return;
    const synced = clientPortalUsers.find((u) => u.id === currentClientPortalUser.id);
    if (synced && JSON.stringify(synced) !== JSON.stringify(currentClientPortalUser)) setCurrentClientPortalUser(synced);
  }, [clientPortalUsers, currentClientPortalUser]);

  useEffect(() => {
    setClients((prev) => {
      const next = prev.map((client) => ({
        ...(client.autoChargeAfterTrial && client.paymentStatus === "trialing" && client.trialEndDate && new Date(client.trialEndDate) <= new Date()
          ? {
            ...client,
            serviceMode: client.serviceMode || "agency",
            paymentMethod: client.paymentMethod || "bank-transfer",
            autoChargeAfterTrial: true,
            trialStartDate: client.trialStartDate || client.joinDate || null,
            trialEndDate: client.trialEndDate || (client.joinDate ? addDaysISO(client.joinDate, CLIENT_FREE_TRIAL_DAYS) : null),
            paymentDetailMasked: client.paymentDetailMasked || "",
            paymentStatus: "paid",
            lastPayment: client.trialEndDate,
            nextPayment: addDaysISO(client.trialEndDate, 30),
          }
          : {
            ...client,
            serviceMode: client.serviceMode || "agency",
            paymentMethod: client.paymentMethod || "bank-transfer",
            autoChargeAfterTrial: client.serviceMode === "self-serve" ? (client.autoChargeAfterTrial ?? true) : (client.autoChargeAfterTrial ?? false),
            trialStartDate: client.trialStartDate || (client.serviceMode === "self-serve" ? (client.joinDate || null) : null),
            trialEndDate: client.trialEndDate || (client.serviceMode === "self-serve" && client.joinDate ? addDaysISO(client.joinDate, CLIENT_FREE_TRIAL_DAYS) : null),
            paymentDetailMasked: client.paymentDetailMasked || "",
          }),
      }));
      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
  }, []);

  useEffect(() => {
    const loadInboundLeads = async () => {
      try {
        const res = await apiFetch("/leads.json", { cache: "no-store" });
        if (!res.ok) return;
        const leads = await res.json().catch(() => []);
        if (!Array.isArray(leads)) return;
        setProspects((prev) => {
          const existingEmails = new Set(prev.map((p) => (p.email || "").toLowerCase()));
          const mapped = leads
            .filter((l) => l.email && !existingEmails.has(String(l.email).toLowerCase()))
            .map((l) => ({
              id: Number(l.id) || Date.now() + Math.floor(Math.random() * 1000),
              name: l.business || l.name || "Inbound Lead",
              contact: l.name || "Website Contact",
              email: l.email,
              location: l.location || "Unknown",
              type: "Inbound Lead",
              unrespondedReviews: 0,
              rating: 0,
              lastContact: null,
              status: "new",
              notes: `Captured from landing form (${l.inquiry || "General Inquiry"})`,
            }));
          return [...prev, ...mapped];
        });
      } catch (_) {
        // ignore if leads.json is not available
      }
    };
    loadInboundLeads();
  }, []);

  useEffect(() => {
    const loadGoogleConfig = async () => {
      try {
        const res = await apiFetch("/api/google/config", { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (!data || !data.success) return;
        setGoogleBridgeConfig({
          enabled: !!data.enabled,
          authUrl: data.authUrl || "",
          bridgeUrlConfigured: !!data.bridgeUrlConfigured,
        });
      } catch (_) {
        // keep default config
      }
    };
    const loadTrustpilotConfig = async () => {
      try {
        const res = await apiFetch("/api/trustpilot/config", { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (!data || !data.success) return;
        setTrustpilotBridgeConfig({
          enabled: !!data.enabled,
          authUrl: data.authUrl || "",
          bridgeUrlConfigured: !!data.bridgeUrlConfigured,
        });
      } catch (_) {
        // keep default config
      }
    };
    loadGoogleConfig();
    loadTrustpilotConfig();
  }, []);

  const loadManagedAccountRequests = useCallback(async () => {
    try {
      const res = await apiFetch("/api/managed-account-request", { cache: "no-store" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) return;
      setManagedAccountRequests(Array.isArray(data.requests) ? data.requests : []);
    } catch (_) {
      // keep previous state if request listing is unavailable
    }
  }, []);

  useEffect(() => {
    loadManagedAccountRequests();
  }, [loadManagedAccountRequests]);

  useEffect(() => {
    if (tab !== "selfserve") return;
    loadManagedAccountRequests();
  }, [tab, loadManagedAccountRequests]);

  useEffect(() => {
    // Load pricing on mount
    loadPricing(userCountry);
  }, [loadPricing, userCountry]);

  useEffect(() => {
    detectPricingCountry();
  }, [detectPricingCountry]);

  useEffect(() => {
    if (!userCountry) return;
    if (clientRegisterForm.country) return;
    setClientRegisterForm((prev) => ({ ...prev, country: userCountry }));
  }, [userCountry, clientRegisterForm.country]);

  useEffect(() => {
    const activeSelfServe = clients.filter((c) => c.serviceMode === "self-serve");
    const q = selfServeSearch.trim().toLowerCase();
    const filtered = activeSelfServe.filter((c) => {
      const statusOk = selfServeStatusFilter === "all" || String(c.status || "").toLowerCase() === selfServeStatusFilter;
      if (!statusOk) return false;
      if (!q) return true;
      const searchOk = [c.name, c.contact, c.email, c.city, c.country, c.location, c.plan].some((v) => String(v || "").toLowerCase().includes(q));
      return searchOk;
    });

    if (!filtered.length) {
      if (selectedSelfServeClientId !== null) setSelectedSelfServeClientId(null);
      return;
    }
    const exists = filtered.some((c) => c.id === selectedSelfServeClientId);
    if (!exists) setSelectedSelfServeClientId(filtered[0].id);
    if (selfServePage > Math.max(1, Math.ceil(filtered.length / 6))) setSelfServePage(1);
  }, [clients, selfServeSearch, selfServeStatusFilter, selectedSelfServeClientId, selfServePage]);

  useEffect(() => {
    setReviewNotifications((prev) => {
      const prevMap = new Map(prev.map((n) => [n.reviewId, n]));
      const pending = reviews.filter((r) => r.status === "pending");
      return pending.map((r) => {
        const existing = prevMap.get(r.id);
        if (existing) return existing;
        const client = clients.find((c) => c.id === r.clientId);
        return {
          id: `notif-${r.id}`,
          reviewId: r.id,
          clientId: r.clientId,
          message: `New review for ${client?.name || "Client"} by ${r.author}`,
          createdAt: new Date().toISOString(),
          read: false,
        };
      });
    });
  }, [reviews, clients]);

  const getReviewTone = (reviewId) => String(reviewResponseToneById[reviewId] || "friendly-casual");
  const setReviewTone = (reviewId, toneId) => {
    setReviewResponseToneById((prev) => ({ ...prev, [reviewId]: toneId }));
  };

  const regenerateResponse = async (reviewId) => {
    const review = reviews.find(r => r.id === reviewId);
    if (!review) return;
    const selectedTone = getReviewTone(reviewId);
    const detectedLanguage = detectReviewLanguage(review.text);
    const currentDraft = String(review.aiResponse || "").trim();
    try {
      const client = clients.find((c) => c.id === review.clientId);
      const res = await apiFetch("/api/ai-response", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessName: client?.name || "a local business",
          rating: review.rating,
          reviewText: review.text,
          responseLanguage: detectedLanguage,
          staffPreferredLanguage: currentUser?.preferredLanguage || "English",
          responseTone: selectedTone,
          currentResponse: currentDraft,
        }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        const next = String(data?.response || "").trim();
        if (next && next !== currentDraft) {
          setReviews((p) => p.map((r) => (r.id === reviewId ? { ...r, aiResponse: next, reviewLanguage: detectedLanguage } : r)));
          notify(`AI response regenerated in ${detectedLanguage} (${REVIEW_RESPONSE_TONES.find((t) => t.id === selectedTone)?.label || "selected tone"}) ✨`);
          return;
        }
      }
    } catch (_) {
      // fallback below
    }

    const bundle = languageFallbackTemplates[detectedLanguage] || languageFallbackTemplates[currentUser?.preferredLanguage] || languageFallbackTemplates.English;
    const base = review.rating >= 4 ? bundle.positive : review.rating >= 3 ? bundle.neutral : bundle.negative;
    const closers = toneFallbackClosers[selectedTone] || toneFallbackClosers["friendly-casual"];
    const pool = [
      base,
      `${base} ${closers[0]}`,
      `${base} ${closers[1]}`,
    ];
    let next = pool.find((candidate) => candidate !== currentDraft) || `${base} ${closers[0]}`;
    setReviews(p => p.map(r => r.id === reviewId ? { ...r, aiResponse: next, reviewLanguage: detectedLanguage } : r));
    notify(`Used fallback template in ${detectedLanguage} (${REVIEW_RESPONSE_TONES.find((t) => t.id === selectedTone)?.label || "selected tone"})`);
  };

  const normalizeScopeText = (value) => String(value || "").trim().toLowerCase();

  const canAccessClientByScope = (user, client) => {
    if (!user || !client) return false;
    const scopeLevel = String(user.scopeLevel || (user.role === "admin" ? "global" : "country")).toLowerCase();
    if (scopeLevel === "global") return true;

    const clientCountry = normalizeScopeText(client.country);
    const clientCity = normalizeScopeText(client.city);
    const scopeCountry = normalizeScopeText(scopeLevel === "country" ? (user.scopeValue || user.country) : user.country);
    const scopeCity = normalizeScopeText(scopeLevel === "city" ? (user.scopeValue || user.city) : user.city);

    if (scopeLevel === "country") {
      return !!scopeCountry && clientCountry === scopeCountry;
    }

    if (scopeLevel === "city") {
      return !!scopeCity && clientCity === scopeCity && (!scopeCountry || clientCountry === scopeCountry);
    }

    return user.role === "admin";
  };

  const visibleAgencyClients = currentUser ? clients.filter((client) => canAccessClientByScope(currentUser, client)) : clients;
  const visibleAgencyClientIds = new Set(visibleAgencyClients.map((client) => client.id));
  const visibleAgencyReviews = currentUser ? reviews.filter((review) => visibleAgencyClientIds.has(review.clientId)) : reviews;
  const visibleAgencySupportRequests = currentUser ? supportRequests.filter((ticket) => visibleAgencyClientIds.has(ticket.clientId)) : supportRequests;
  const mailboxAccessAllowed = !!currentUser
    && (currentUser.role === "admin"
      || (currentUser.permissions || []).includes("mailbox.access")
      || (currentUser.permissions || []).includes("support.ticket.manage")
      || (currentUser.permissions || []).includes("settings.view"));
  const filteredReviews = visibleAgencyReviews.filter(r => (selClient === "all" || r.clientId === +selClient) && (filterSt === "all" || r.status === filterSt));
  const pendingCount = visibleAgencyReviews.filter(r => r.status === "pending").length;
  const activeClients = visibleAgencyClients.filter(c => c.status === "active");
  const selfServeClients = visibleAgencyClients.filter((c) => c.serviceMode === "self-serve");
  const filteredSelfServeClients = selfServeClients.filter((c) => {
    const statusOk = selfServeStatusFilter === "all" || String(c.status || "").toLowerCase() === selfServeStatusFilter;
    if (!statusOk) return false;
    const q = selfServeSearch.trim().toLowerCase();
    if (!q) return true;
    return [c.name, c.contact, c.email, c.city, c.country, c.location, c.plan].some((v) => String(v || "").toLowerCase().includes(q));
  });
  const sortedSelfServeClients = [...filteredSelfServeClients].sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  const SELF_SERVE_PAGE_SIZE = 6;
  const totalSelfServePages = Math.max(1, Math.ceil(sortedSelfServeClients.length / SELF_SERVE_PAGE_SIZE));
  const currentSelfServePage = Math.min(selfServePage, totalSelfServePages);
  const pagedSelfServeClients = sortedSelfServeClients.slice((currentSelfServePage - 1) * SELF_SERVE_PAGE_SIZE, currentSelfServePage * SELF_SERVE_PAGE_SIZE);
  const selectedSelfServeClient = selfServeClients.find((c) => c.id === selectedSelfServeClientId) || filteredSelfServeClients[0] || null;
  const focusedSelfServeClient = selfServeClients.find((c) => c.id === selfServeClientDashboardId) || null;
  const focusedSelfServeTickets = focusedSelfServeClient ? visibleAgencySupportRequests.filter((ticket) => ticket.clientId === focusedSelfServeClient.id) : [];
  const focusedSelfServeReviews = focusedSelfServeClient ? visibleAgencyReviews.filter((r) => r.clientId === focusedSelfServeClient.id) : [];
  const selectedSelfServeTickets = selectedSelfServeClient ? visibleAgencySupportRequests.filter((ticket) => ticket.clientId === selectedSelfServeClient.id) : [];
  const selectedSelfServeReviews = selectedSelfServeClient ? visibleAgencyReviews.filter((r) => r.clientId === selectedSelfServeClient.id) : [];
  const selfServeAverageRating = selfServeClients.length ? Number((selfServeClients.reduce((sum, c) => sum + Number(c.avgRating || 0), 0) / selfServeClients.length).toFixed(2)) : 0;
  const selfServeTicketSlaRisk = visibleAgencySupportRequests.filter((ticket) => {
    const created = new Date(ticket.createdAt).getTime();
    return Number.isFinite(created) && (Date.now() - created) > (48 * 60 * 60 * 1000) && ticket.status !== "resolved" && ticket.status !== "closed";
  }).length;
  const filteredClientDirectory = visibleAgencyClients.filter((c) => {
    const statusOk = clientStatusFilter === "all" || String(c.status || "").toLowerCase() === clientStatusFilter;
    if (!statusOk) return false;
    const q = clientSearch.trim().toLowerCase();
    if (!q) return true;
    return [c.name, c.contact, c.email, c.city, c.country, c.location, c.plan].some((v) => String(v || "").toLowerCase().includes(q));
  });
  const sortedClientDirectory = [...filteredClientDirectory].sort((a, b) => {
    if (clientSort === "name-desc") return String(b.name || "").localeCompare(String(a.name || ""));
    if (clientSort === "reviews-desc") return Number(b.reviewCount || 0) - Number(a.reviewCount || 0);
    if (clientSort === "reviews-asc") return Number(a.reviewCount || 0) - Number(b.reviewCount || 0);
    if (clientSort === "newest") return new Date(b.joinDate || "").getTime() - new Date(a.joinDate || "").getTime();
    if (clientSort === "oldest") return new Date(a.joinDate || "").getTime() - new Date(b.joinDate || "").getTime();
    return String(a.name || "").localeCompare(String(b.name || ""));
  });
  const CLIENTS_PAGE_SIZE = 8;
  const totalClientPages = Math.max(1, Math.ceil(sortedClientDirectory.length / CLIENTS_PAGE_SIZE));
  const currentClientPage = Math.min(clientPage, totalClientPages);
  const pagedClientDirectory = sortedClientDirectory.slice((currentClientPage - 1) * CLIENTS_PAGE_SIZE, currentClientPage * CLIENTS_PAGE_SIZE);
  const selectedClient = visibleAgencyClients.find((c) => c.id === selectedClientId) || null;
  const selectedClientReviews = selectedClient ? visibleAgencyReviews.filter((r) => r.clientId === selectedClient.id) : [];
  const clientPortalClient = currentClientPortalUser ? clients.find((c) => c.id === currentClientPortalUser.clientId) || null : null;
  const clientPortalLinkedUsers = currentClientPortalUser ? clientPortalUsers.filter((u) => u.clientId === currentClientPortalUser.clientId) : [];
  const isClientPortalOwner = (currentClientPortalUser?.role || "owner") === "owner";
  const clientPortalPackage = PLANS.find((p) => p.id === clientPortalClient?.plan) || null;
  const clientPortalPaymentMethodLabel = CLIENT_PAYMENT_METHODS.find((m) => m.id === (clientPortalClient?.paymentMethod || "bank-transfer"))?.label || "Bank Transfer";
  const availableRegisterPaymentMethods = (billingConfig.availablePaymentMethods?.length
    ? CLIENT_PAYMENT_METHODS.filter((m) => billingConfig.availablePaymentMethods.includes(m.id))
    : CLIENT_PAYMENT_METHODS.filter((m) => m.id !== "bank-transfer"));
  const registerPaymentMeta = getPaymentDetailMeta(clientRegisterForm.paymentMethod);
  const clientTrialActive = !!(clientPortalClient?.trialEndDate && new Date(clientPortalClient.trialEndDate) >= new Date());
  const clientPortalReviews = clientPortalClient ? reviews.filter((r) => r.clientId === clientPortalClient.id) : [];
  const clientSupportRequests = clientPortalClient ? supportRequests.filter((ticket) => ticket.clientId === clientPortalClient.id) : [];
  const filteredClientSupportRequests = clientSupportRequests.filter((ticket) => matchesSupportTicketFilter(ticket, clientSupportStatusFilter));
  const selectedClientSupportTicket = filteredClientSupportRequests.find((ticket) => ticket.id === selectedClientSupportTicketId)
    || filteredClientSupportRequests[0]
    || null;
  const clientPendingPlanChange = clientPortalClient?.pendingPlanChange || null;
  const openSupportRequests = visibleAgencySupportRequests.filter((ticket) => ticket.status !== "resolved" && ticket.status !== "closed");
  const filteredAdminSupportRequests = visibleAgencySupportRequests.filter((ticket) => matchesSupportTicketFilter(ticket, adminSupportStatusFilter));
  const selfServeAverageGrowth = selfServeClients.length
    ? Number((selfServeClients.reduce((sum, client) => sum + getClientRatingGrowth(client), 0) / selfServeClients.length).toFixed(2))
    : 0;

  useEffect(() => {
    if (!filteredClientSupportRequests.length) {
      if (selectedClientSupportTicketId !== null) setSelectedClientSupportTicketId(null);
      return;
    }
    if (!filteredClientSupportRequests.some((ticket) => ticket.id === selectedClientSupportTicketId)) {
      setSelectedClientSupportTicketId(filteredClientSupportRequests[0].id);
    }
  }, [filteredClientSupportRequests, selectedClientSupportTicketId]);

  useEffect(() => {
    if (!currentClientPortalUser) return;
    const c = clients.find((cl) => cl.id === currentClientPortalUser.clientId);
    if (!c) return;
    const bv = c.brandVoice || {};
    setBrandVoiceDraft({ description: bv.description || "", tone: bv.tone || "", keywords: bv.keywords || "", language: bv.language || "" });
  }, [currentClientPortalUser?.clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedClientId === null) return;
    if (!visibleAgencyClients.some((client) => client.id === selectedClientId)) {
      setSelectedClientId(null);
    }
  }, [selectedClientId, visibleAgencyClients]);

  useEffect(() => {
    setClientPage(1);
  }, [clientSearch, clientStatusFilter, clientSort]);

  useEffect(() => {
    if (!mailboxAccessAllowed || dashboardMailbox.loading || dashboardMailbox.hasLoaded) return;
    loadDashboardMailbox();
  }, [mailboxAccessAllowed, dashboardMailbox.loading, dashboardMailbox.hasLoaded]);

  useEffect(() => {
    if (!mailboxAccessAllowed || mailboxSentMessages.length) return;
    loadSentMailboxMessages();
  }, [mailboxAccessAllowed, mailboxSentMessages.length]);

  // Always-on mailbox poll — runs regardless of which tab is active so the
  // mailbox sound fires even when the user is on a different tab.
  useEffect(() => {
    if (!currentUserId || !mailboxAccessAllowed) return;
    const intervalId = setInterval(() => {
      loadDashboardMailbox(true);
    }, 60000);
    return () => clearInterval(intervalId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, mailboxAccessAllowed]);

  useEffect(() => {
    if (tab !== "mailbox" || !mailboxAccessAllowed || dashboardMailbox.loading) return;
    if (dashboardMailbox.hasLoaded && !dashboardMailbox.error) return;
    loadDashboardMailbox();
  }, [tab, mailboxAccessAllowed, dashboardMailbox.loading, dashboardMailbox.hasLoaded, dashboardMailbox.error]);

  useEffect(() => {
    if (tab !== "mailbox" || !mailboxAccessAllowed) return;
    const timer = setInterval(() => {
      loadDashboardMailbox(true);
      loadSentMailboxMessages();
    }, 60000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, mailboxAccessAllowed]);

  useEffect(() => {
    if (tab !== "security-events") return;
    loadBackendActivityAudit();
  }, [tab, loadBackendActivityAudit]);

  useEffect(() => {
    setClientPlanChangeForm((prev) => ({
      ...prev,
      plan: clientPortalClient?.pendingPlanChange?.newPlan || clientPortalClient?.plan || "starter",
    }));
  }, [clientPortalClient]);

  const managedRequestCounts = {
    pending: managedAccountRequests.filter((item) => String(item.status || "pending").toLowerCase() === "pending").length,
    converted: managedAccountRequests.filter((item) => String(item.status || "").toLowerCase() === "converted").length,
    closed: managedAccountRequests.filter((item) => String(item.status || "").toLowerCase() === "closed").length,
  };
  const filteredManagedRequests = managedAccountRequests.filter((item) => String(item.status || "pending").toLowerCase() === managedRequestFilter);
  const clientPortalFilteredReviews = clientPortalReviews.filter((r) => {
    const statusOk = clientPortalFilterStatus === "all" || r.status === clientPortalFilterStatus;
    const platformOk = clientPortalFilterPlatform === "all" || String(r.platform || "").toLowerCase() === clientPortalFilterPlatform.toLowerCase();
    const q = clientPortalSearch.trim().toLowerCase();
    const searchOk = !q || [r.author, r.text, r.aiResponse, r.platform].some((v) => String(v || "").toLowerCase().includes(q));
    return statusOk && platformOk && searchOk;
  });
  const clientPortalStats = {
    total: clientPortalReviews.length,
    pending: clientPortalReviews.filter((r) => r.status === "pending").length,
    approved: clientPortalReviews.filter((r) => r.status === "approved").length,
    published: clientPortalReviews.filter((r) => r.status === "published").length,
    avgRating: clientPortalReviews.length ? Number((clientPortalReviews.reduce((s, r) => s + Number(r.rating || 0), 0) / clientPortalReviews.length).toFixed(2)) : 0,
  };
  const clientPortalCoverage = clientPortalStats.total ? Math.round((clientPortalStats.published / clientPortalStats.total) * 100) : 0;
  const clientPortalSentiment = {
    positive: clientPortalReviews.filter((r) => Number(r.rating || 0) >= 4).length,
    neutral: clientPortalReviews.filter((r) => Number(r.rating || 0) === 3).length,
    negative: clientPortalReviews.filter((r) => Number(r.rating || 0) <= 2).length,
  };
  const selectedClientReviewStats = {
    total: selectedClientReviews.length,
    pending: selectedClientReviews.filter((r) => r.status === "pending").length,
    worked: selectedClientReviews.filter((r) => r.actionById || ["approved", "published", "rejected"].includes(r.status)).length,
  };
  const filteredSelectedClientReviews = selectedClientReviews.filter((r) => {
    const statusOk = clientReviewFilterStatus === "all" || r.status === clientReviewFilterStatus;
    const platformOk = clientReviewFilterPlatform === "all" || String(r.platform || "").toLowerCase() === clientReviewFilterPlatform.toLowerCase();
    const q = clientReviewSearch.trim().toLowerCase();
    const searchOk = !q || [r.author, r.text, r.aiResponse, r.platform].some((v) => String(v || "").toLowerCase().includes(q));
    return statusOk && platformOk && searchOk;
  });
  const mrr = activeClients.reduce((s, c) => s + (PLANS.find(p => p.id === c.plan)?.price || 0), 0);
  const filteredContracts = visibleAgencyClients.filter(c => contractFilter === "all" ? true : c.contractStatus === contractFilter);
  const unreadNotificationCount = reviewNotifications.filter((n) => !n.read).length;
  const mailboxInboxMessages = useMemo(() => (
    dashboardMailbox.messages.filter((message) => !message?.archived && !message?.deleted)
  ), [dashboardMailbox.messages]);
  const mailboxArchivedMessages = useMemo(() => (
    dashboardMailbox.messages.filter((message) => !!message?.archived && !message?.deleted)
  ), [dashboardMailbox.messages]);
  const mailboxDeletedInboxMessages = useMemo(() => (
    dashboardMailbox.messages.filter((message) => !!message?.deleted)
  ), [dashboardMailbox.messages]);
  const mailboxDeletedSentMessages = useMemo(() => (
    mailboxSentMessages.filter((message) => !!message?.deleted)
  ), [mailboxSentMessages]);
  const mailboxActiveSentMessages = useMemo(() => (
    mailboxSentMessages.filter((message) => !message?.deleted)
  ), [mailboxSentMessages]);
  const mailboxMessageCount = mailboxInboxMessages.filter((message) => !message?.read).length;
  const mailboxCurrentFolderMessages = useMemo(() => {
    if (mailboxFolder === "archived") return mailboxArchivedMessages;
    if (mailboxFolder === "trash") return [...mailboxDeletedInboxMessages, ...mailboxDeletedSentMessages];
    if (mailboxFolder === "sent") return mailboxActiveSentMessages;
    return mailboxInboxMessages;
  }, [mailboxFolder, mailboxArchivedMessages, mailboxDeletedInboxMessages, mailboxDeletedSentMessages, mailboxActiveSentMessages, mailboxInboxMessages]);
  const mailboxCurrentFolderThreads = useMemo(() => {
    const sortedMessages = [...mailboxCurrentFolderMessages].sort((a, b) => getMailboxTimestamp(b?.date) - getMailboxTimestamp(a?.date));
    const messageToThread = new Map();
    const threadBuckets = new Map();

    sortedMessages.forEach((message) => {
      const identityTokens = Array.from(new Set([
        ...extractMailboxReferenceTokens(message?.references),
        ...extractMailboxReferenceTokens(message?.inReplyTo),
        ...extractMailboxReferenceTokens(message?.messageId),
      ].filter(Boolean)));
      let threadId = identityTokens.map((token) => messageToThread.get(token)).find(Boolean);
      if (!threadId) {
        threadId = identityTokens[0] || buildMailboxFallbackThreadKey(message, dashboardMailbox.mailbox);
      }
      const bucket = threadBuckets.get(threadId) || [];
      bucket.push(message);
      threadBuckets.set(threadId, bucket);
      identityTokens.forEach((token) => messageToThread.set(token, threadId));
    });

    return Array.from(threadBuckets.entries()).map(([threadId, messages]) => {
      const chronologicalMessages = [...messages].sort((a, b) => getMailboxTimestamp(a?.date) - getMailboxTimestamp(b?.date));
      const latestMessage = chronologicalMessages[chronologicalMessages.length - 1] || null;
      const participants = Array.from(new Set(chronologicalMessages.flatMap((message) => {
        const entries = [message?.from, ...(String(message?.to || "").split(","))]
          .map((entry) => extractMailboxEmailAddress(entry) || String(entry || "").trim())
          .filter(Boolean);
        return entries;
      })));
      const previewTarget = [...chronologicalMessages].reverse().find((message) => message?.source !== "sent") || latestMessage;
      const attachmentCount = chronologicalMessages.reduce((sum, message) => sum + (Array.isArray(message?.attachments) ? message.attachments.length : 0), 0);
      return {
        id: threadId,
        messages: chronologicalMessages,
        latestMessage,
        previewTarget,
        subject: latestMessage?.subject || chronologicalMessages[0]?.subject || "(No subject)",
        snippet: latestMessage?.snippet || chronologicalMessages[chronologicalMessages.length - 1]?.body || "",
        participants,
        unreadCount: chronologicalMessages.filter((message) => !message?.read && message?.source !== "sent").length,
        starred: chronologicalMessages.some((message) => !!message?.starred),
        archived: chronologicalMessages.every((message) => message?.source === "sent" || !!message?.archived),
        deleted: chronologicalMessages.every((message) => !!message?.deleted),
        attachmentCount,
        latestTimestamp: getMailboxTimestamp(latestMessage?.date),
      };
    }).sort((a, b) => b.latestTimestamp - a.latestTimestamp);
  }, [mailboxCurrentFolderMessages, dashboardMailbox.mailbox]);
  const filteredMailboxThreads = useMemo(() => {
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const query = mailboxSearch.trim().toLowerCase();
    return mailboxCurrentFolderThreads.filter((thread) => {
      const matchesQuery = !query || thread.messages.some((message) => (
        [
          message?.from,
          message?.to,
          message?.subject,
          message?.snippet,
          message?.body,
          message?.date,
          ...(Array.isArray(message?.attachments) ? message.attachments.map((attachment) => attachment?.name) : []),
        ].some((value) => String(value || "").toLowerCase().includes(query))
      ));
      if (!matchesQuery) return false;
      if (mailboxThreadFilter === "unread") return thread.unreadCount > 0;
      if (mailboxThreadFilter === "starred") return thread.starred;
      if (mailboxThreadFilter === "attachments") return thread.attachmentCount > 0;
      if (mailboxSenderFilter !== "all") {
        const hasSender = thread.messages.some((message) => {
          const sender = extractMailboxEmailAddress(message?.from || "").toLowerCase();
          const recipientList = String(message?.to || "").split(",").map((entry) => extractMailboxEmailAddress(entry).toLowerCase()).filter(Boolean);
          return sender === mailboxSenderFilter || recipientList.includes(mailboxSenderFilter);
        });
        if (!hasSender) return false;
      }
      if (mailboxDateFilter === "today" && thread.latestTimestamp < startOfToday.getTime()) return false;
      if (mailboxDateFilter === "7d" && thread.latestTimestamp < (now - (7 * dayMs))) return false;
      if (mailboxDateFilter === "30d" && thread.latestTimestamp < (now - (30 * dayMs))) return false;
      return true;
    });
  }, [mailboxCurrentFolderThreads, mailboxSearch, mailboxThreadFilter, mailboxSenderFilter, mailboxDateFilter]);
  const MAILBOX_THREAD_PAGE_SIZE = 18;
  const mailboxThreadTotalPages = Math.max(1, Math.ceil(filteredMailboxThreads.length / MAILBOX_THREAD_PAGE_SIZE));
  const mailboxThreadPageClamped = Math.min(mailboxThreadPage, mailboxThreadTotalPages);
  const mailboxVisibleThreads = useMemo(() => {
    const start = (mailboxThreadPageClamped - 1) * MAILBOX_THREAD_PAGE_SIZE;
    return filteredMailboxThreads.slice(start, start + MAILBOX_THREAD_PAGE_SIZE);
  }, [filteredMailboxThreads, mailboxThreadPageClamped]);
  const mailboxSenderOptions = useMemo(() => {
    const map = new Map();
    mailboxCurrentFolderThreads.forEach((thread) => {
      thread.messages.forEach((message) => {
        const fromEmail = extractMailboxEmailAddress(message?.from || "");
        if (fromEmail) map.set(fromEmail.toLowerCase(), fromEmail);
        String(message?.to || "").split(",").forEach((entry) => {
          const toEmail = extractMailboxEmailAddress(entry || "");
          if (toEmail) map.set(toEmail.toLowerCase(), toEmail);
        });
      });
    });
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }));
  }, [mailboxCurrentFolderThreads]);
  const mailboxMessageKey = (message) => {
    const source = message?.source === "sent" ? "sent" : "inbox";
    return `${source}:${String(message?.id || "")}`;
  };
  const mailboxThreadsById = useMemo(() => {
    const map = new Map();
    mailboxCurrentFolderThreads.forEach((thread) => map.set(thread.id, thread));
    return map;
  }, [mailboxCurrentFolderThreads]);
  const filteredMailboxMessageKeys = useMemo(() => (
    filteredMailboxThreads.map((thread) => thread.id)
  ), [filteredMailboxThreads]);
  const selectedMailboxMessages = useMemo(() => (
    mailboxSelectedKeys
      .map((key) => mailboxThreadsById.get(key))
      .filter((thread) => !!thread)
      .flatMap((thread) => thread.messages)
  ), [mailboxSelectedKeys, mailboxThreadsById]);
  const allFilteredMailboxSelected = filteredMailboxMessageKeys.length > 0
    && filteredMailboxMessageKeys.every((key) => mailboxSelectedKeys.includes(key));
  const mailboxPreviewThread = useMemo(() => {
    if (mailboxPreviewMessageKey && mailboxThreadsById.has(mailboxPreviewMessageKey)) {
      return mailboxThreadsById.get(mailboxPreviewMessageKey);
    }
    return filteredMailboxThreads[0] || null;
  }, [mailboxPreviewMessageKey, mailboxThreadsById, filteredMailboxThreads]);
  const mailboxPreviewMessage = mailboxPreviewThread?.previewTarget || mailboxPreviewThread?.latestMessage || null;
  const mailboxPreviewTotalMessages = Array.isArray(mailboxPreviewThread?.messages) ? mailboxPreviewThread.messages.length : 0;
  const mailboxPreviewTotalPages = Math.max(1, Math.ceil(mailboxPreviewTotalMessages / MAILBOX_PREVIEW_PAGE_SIZE));
  const mailboxPreviewPageClamped = Math.min(mailboxPreviewPage, mailboxPreviewTotalPages);
  const mailboxPreviewVisibleMessages = useMemo(() => {
    const messages = Array.isArray(mailboxPreviewThread?.messages) ? mailboxPreviewThread.messages : [];
    if (!messages.length) return [];
    const end = messages.length - ((mailboxPreviewPageClamped - 1) * MAILBOX_PREVIEW_PAGE_SIZE);
    const start = Math.max(0, end - MAILBOX_PREVIEW_PAGE_SIZE);
    return messages.slice(start, end);
  }, [mailboxPreviewThread, mailboxPreviewPageClamped]);
  const mailboxDraftMailboxId = useMemo(() => (
    String(dashboardMailbox.mailbox || "hello@replai.work").trim().toLowerCase() || "hello@replai.work"
  ), [dashboardMailbox.mailbox]);

  useEffect(() => {
    const visible = new Set(filteredMailboxMessageKeys);
    setMailboxSelectedKeys((prev) => prev.filter((key) => visible.has(key)));
  }, [filteredMailboxMessageKeys]);

  useEffect(() => {
    setMailboxThreadPage(1);
  }, [mailboxFolder, mailboxSearch, mailboxThreadFilter, mailboxSenderFilter, mailboxDateFilter]);

  useEffect(() => {
    setActivityPage(1);
  }, [backendActivitySearch, activityActorFilter, activityActionFilter, activityDateFrom, activityDateTo]);

  useEffect(() => {
    if (mailboxPreviewThread && mailboxPreviewMessageKey !== mailboxPreviewThread.id) {
      setMailboxPreviewMessageKey(mailboxPreviewThread.id);
      return;
    }
    if (!mailboxPreviewThread && mailboxPreviewMessageKey) {
      setMailboxPreviewMessageKey("");
    }
  }, [mailboxPreviewThread, mailboxPreviewMessageKey]);

  useEffect(() => {
    setMailboxPreviewPage(1);
  }, [mailboxPreviewThread?.id]);

  useEffect(() => {
    if (!mailboxSenderOptions.some((option) => option.value === mailboxSenderFilter)) {
      setMailboxSenderFilter("all");
    }
  }, [mailboxSenderOptions, mailboxSenderFilter]);

  useEffect(() => {
    if (!mailboxAccessAllowed || !mailboxDraftMailboxId) return;
    if (mailboxDraftLoadRef.current === mailboxDraftMailboxId) return;
    mailboxDraftLoadRef.current = mailboxDraftMailboxId;
    mailboxDraftHydratedRef.current = false;
    setMailboxDraftStatus("loading");
    apiFetch(`/api/email/draft?mailbox=${encodeURIComponent(mailboxDraftMailboxId)}`, { cache: "no-store" })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        const draft = data?.draft;
        if (draft) {
          setMailboxCompose((prev) => ({
            ...prev,
            to: String(draft.to || ""),
            subject: String(draft.subject || ""),
            body: String(draft.body || ""),
            inReplyTo: String(draft.inReplyTo || ""),
            references: String(draft.references || ""),
            attachments: [],
          }));
          setMailboxDraftSavedAt(String(draft.savedAt || ""));
          setMailboxDraftStatus("saved");
        } else {
          setMailboxDraftSavedAt("");
          setMailboxDraftStatus("idle");
        }
      })
      .catch(() => {
        setMailboxDraftStatus("error");
      })
      .finally(() => {
        mailboxDraftHydratedRef.current = true;
      });
  }, [mailboxAccessAllowed, mailboxDraftMailboxId]);

  useEffect(() => {
    if (!mailboxDraftHydratedRef.current || !mailboxAccessAllowed) return;
    const draftPayload = {
      to: String(mailboxCompose.to || ""),
      subject: String(mailboxCompose.subject || ""),
      body: String(mailboxCompose.body || ""),
      inReplyTo: String(mailboxCompose.inReplyTo || ""),
      references: String(mailboxCompose.references || ""),
    };
    const hasDraft = Object.values(draftPayload).some((value) => String(value || "").trim());
    const timer = setTimeout(() => {
      setMailboxDraftStatus("saving");
      if (!hasDraft) {
        apiFetch('/api/email/draft', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mailbox: mailboxDraftMailboxId }),
        })
          .then(() => {
            setMailboxDraftSavedAt("");
            setMailboxDraftStatus("idle");
          })
          .catch(() => setMailboxDraftStatus("error"));
        return;
      }
      apiFetch('/api/email/draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mailbox: mailboxDraftMailboxId,
          ...draftPayload,
        }),
      })
        .then((res) => res.json().catch(() => ({})))
        .then((data) => {
          const savedAt = String(data?.draft?.savedAt || new Date().toISOString());
          setMailboxDraftSavedAt(savedAt);
          setMailboxDraftStatus("saved");
        })
        .catch(() => setMailboxDraftStatus("error"));
    }, 5000);
    return () => clearTimeout(timer);
  }, [mailboxCompose.to, mailboxCompose.subject, mailboxCompose.body, mailboxCompose.inReplyTo, mailboxCompose.references, mailboxAccessAllowed, mailboxDraftMailboxId]);

  useEffect(() => {
    if (!availableRegisterPaymentMethods.length) return;
    const methodStillAllowed = availableRegisterPaymentMethods.some((m) => m.id === clientRegisterForm.paymentMethod);
    if (!methodStillAllowed) {
      setClientRegisterForm((prev) => ({ ...prev, paymentMethod: availableRegisterPaymentMethods[0].id }));
    }
  }, [availableRegisterPaymentMethods, clientRegisterForm.paymentMethod]);

  const monthBuckets = Array.from({ length: 6 }, (_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() - (5 - i));
    return {
      month: d.getMonth(),
      year: d.getFullYear(),
      label: d.toLocaleDateString("en-US", { month: "short" }),
    };
  });
  const monthLabels = monthBuckets.map((m) => m.label);
  const clientPortalVolumeTrend = monthBuckets.map((_, idx) => {
    const series = clientPortalClient?.monthlyReviews || [];
    const fallback = Math.max(0, Math.round((clientPortalClient?.reviewCount || 0) / 3));
    return Number(series[idx - (6 - series.length)] ?? fallback ?? 0);
  });
  const clientPortalResponseTrend = clientPortalVolumeTrend.map((_, idx) => {
    const base = clientPortalCoverage / 100 || 0;
    const adjusted = Math.max(0.35, Math.min(0.99, base - 0.09 + idx * 0.02));
    return Number((adjusted * 100).toFixed(1));
  });
  const planTargetHours = { starter: 24, growth: 4, enterprise: 1 };

  // Returns { hoursAgo, slaHours, overdue } for a pending review
  const getReviewSlaInfo = (review, client) => {
    if (!review || review.status !== "pending") return null;
    const slaHours = planTargetHours[client?.plan] || 24;
    const reviewDate = review.date ? new Date(review.date) : null;
    if (!reviewDate || isNaN(reviewDate)) return { hoursAgo: null, slaHours, overdue: false };
    const hoursAgo = Math.floor((Date.now() - reviewDate.getTime()) / (1000 * 60 * 60));
    return { hoursAgo, slaHours, overdue: hoursAgo > slaHours };
  };
  const avgResponseHoursByClient = (client) => {
    const target = planTargetHours[client.plan] || 12;
    const coverage = client.reviewCount > 0 ? (client.responded || 0) / client.reviewCount : 1;
    const pressure = client.paymentStatus === "overdue" ? 0.55 : 0.2;
    return Number((target * (0.72 + (1 - coverage) * 0.7 + pressure)).toFixed(1));
  };
  const slaComplianceByClient = (client) => {
    const target = planTargetHours[client.plan] || 12;
    const avg = avgResponseHoursByClient(client);
    return avg <= target ? 100 : Number(Math.max(25, 100 - ((avg - target) / target) * 100).toFixed(1));
  };

  const mrrTrend = monthBuckets.map((m) => clients.reduce((sum, c) => {
    const planPrice = PLANS.find((p) => p.id === c.plan)?.price || 0;
    if (!planPrice) return sum;
    const join = c.joinDate ? new Date(c.joinDate) : null;
    const isJoined = !join || (join.getFullYear() < m.year || (join.getFullYear() === m.year && join.getMonth() <= m.month));
    const activeLike = c.status === "active" || c.contractStatus === "signed" || c.contractStatus === "sent";
    return isJoined && activeLike ? sum + planPrice : sum;
  }, 0));

  const reviewVolumeTrend = monthBuckets.map((_, idx) => activeClients.reduce((sum, c) => {
    const series = c.monthlyReviews || [];
    const fallback = Math.max(0, Math.round((c.reviewCount || 0) / 3));
    const pick = series[idx - (6 - series.length)] ?? fallback;
    return sum + Number(pick || 0);
  }, 0));

  const responseCoverageTrend = reviewVolumeTrend.map((v, idx) => {
    const totalCoverage = activeClients.length > 0
      ? activeClients.reduce((s, c) => s + (c.reviewCount > 0 ? (c.responded || 0) / c.reviewCount : 1), 0) / activeClients.length
      : 0;
    const adjusted = Math.max(0.65, Math.min(0.99, totalCoverage - 0.08 + (idx * 0.015)));
    return Number((adjusted * 100).toFixed(1));
  });

  const ratingTrend = monthBuckets.map((_, idx) => {
    const withRating = activeClients.filter((c) => c.avgRating > 0);
    const base = withRating.length ? withRating.reduce((s, c) => s + c.avgRating, 0) / withRating.length : 0;
    return Number(Math.max(0, Math.min(5, base - 0.18 + idx * 0.05)).toFixed(2));
  });

  const churnTrend = monthBuckets.map((_, idx) => {
    const overdue = visibleAgencyClients.filter((c) => c.paymentStatus === "overdue").length;
    const cancelled = visibleAgencyClients.filter((c) => c.status === "cancelled").length;
    const churn = ((cancelled + overdue * (0.35 + idx * 0.03)) / Math.max(visibleAgencyClients.length, 1)) * 100;
    return Number(churn.toFixed(1));
  });

  const revenueByPlan = PLANS.map((p) => ({
    id: p.id,
    label: p.id === "enterprise" ? "Pro" : p.name,
    value: visibleAgencyClients.filter((c) => c.status === "active" && c.plan === p.id).reduce((s) => s + p.price, 0),
  }));
  const topPlanRevenue = Math.max(...revenueByPlan.map((r) => r.value), 1);

  const revenueByGeography = Object.entries(visibleAgencyClients.reduce((acc, c) => {
    const country = c.country || "Unknown";
    const rev = PLANS.find((p) => p.id === c.plan)?.price || 0;
    acc[country] = (acc[country] || 0) + (c.status === "active" ? rev : 0);
    return acc;
  }, {})).map(([country, value]) => ({ country, value })).sort((a, b) => b.value - a.value);
  const topGeoRevenue = Math.max(...revenueByGeography.map((g) => g.value), 1);

  // ── SaaS financial metrics ──────────────────────────────────────────────────
  const arr = mrr * 12;
  const arpu = activeClients.length ? Math.round(mrr / activeClients.length) : 0;
  const currentChurnRate = churnTrend[churnTrend.length - 1] || 0;
  const ltv = currentChurnRate > 0 ? Math.round(arpu / (currentChurnRate / 100)) : arpu * 24;
  const _nowDt = new Date();
  const _nowMonth = _nowDt.getMonth();
  const _nowYear = _nowDt.getFullYear();
  const newClientsThisMonth = visibleAgencyClients.filter((c) => {
    if (!c.joinDate) return false;
    const d = new Date(c.joinDate);
    return d.getMonth() === _nowMonth && d.getFullYear() === _nowYear && c.status === "active";
  });
  const newMrr = newClientsThisMonth.reduce((s, c) => s + (PLANS.find((p) => p.id === c.plan)?.price || 0), 0);
  const churnedThisMonth = visibleAgencyClients.filter((c) => c.status === "cancelled");
  const churnedMrr = churnedThisMonth.reduce((s, c) => s + (PLANS.find((p) => p.id === c.plan)?.price || 0), 0);
  const netMrr = newMrr - churnedMrr;
  const trialClients = visibleAgencyClients.filter((c) => c.trialEndDate && new Date(c.trialEndDate) < new Date());
  const trialConverted = trialClients.filter((c) => c.status === "active").length;
  const trialConversionRate = trialClients.length > 0 ? Math.round((trialConverted / trialClients.length) * 100) : null;
  // Platform breakdown across all reviews
  const platformBreakdownObj = reviews.reduce((acc, r) => {
    const p = String(r.platform || "Google");
    acc[p] = (acc[p] || 0) + 1;
    return acc;
  }, {});
  const platformBreakdownArr = Object.entries(platformBreakdownObj).sort((a, b) => b[1] - a[1]);
  const topPlatformCount = Math.max(...platformBreakdownArr.map((x) => x[1]), 1);
  // Critical reviews (1-2★ unresponded)
  const criticalUnresponded = reviews.filter((r) => Number(r.rating || 0) <= 2 && r.status === "pending");
  // Staff performance (reviews actioned per staff)
  const staffLeaderboard = staffMembers.map((s) => ({
    name: s.firstName ? `${s.firstName} ${s.lastName || ""}`.trim() : s.username,
    count: reviews.filter((r) => String(r.actionById) === String(s.id) || String(r.assignedToId) === String(s.id)).length,
  })).filter((s) => s.count > 0).sort((a, b) => b.count - a.count).slice(0, 5);
  // Client count trend (active clients per month)
  const clientCountTrend = monthBuckets.map((m) => visibleAgencyClients.filter((c) => {
    const join = c.joinDate ? new Date(c.joinDate) : null;
    const isJoined = !join || (join.getFullYear() < m.year || (join.getFullYear() === m.year && join.getMonth() <= m.month));
    return isJoined && (c.status === "active" || c.contractStatus === "signed");
  }).length);

  const pipelineValue = prospects.reduce((s, p) => {
    const weight = p.status === "replied" ? 0.7 : p.status === "contacted" ? 0.45 : 0.2;
    const expectedPlan = p.rating >= 4.2 ? 199 : 99;
    return s + expectedPlan * weight;
  }, 0);

  const PROSPECTS_PER_PAGE = 10;
  const filteredProspects = prospects.filter((p) => {
    const statusOk = prospectStatusFilter === "all" || String(p.status || "").toLowerCase() === prospectStatusFilter;
    const q = prospectSearch.trim().toLowerCase();
    const searchOk = !q || [p.name, p.contact, p.email, p.location, p.type, p.notes]
      .some((v) => String(v || "").toLowerCase().includes(q));
    return statusOk && searchOk;
  });
  const sortedProspects = [...filteredProspects].sort((a, b) => {
    if (prospectSort === "unanswered-desc") return Number(b.unrespondedReviews || 0) - Number(a.unrespondedReviews || 0);
    if (prospectSort === "rating-desc") return Number(b.rating || 0) - Number(a.rating || 0);
    if (prospectSort === "name-asc") return String(a.name || "").localeCompare(String(b.name || ""));
    if (prospectSort === "name-desc") return String(b.name || "").localeCompare(String(a.name || ""));
    const aTime = a.lastContact ? new Date(a.lastContact).getTime() : 0;
    const bTime = b.lastContact ? new Date(b.lastContact).getTime() : 0;
    return bTime - aTime;
  });
  const totalProspectPages = Math.max(1, Math.ceil(sortedProspects.length / PROSPECTS_PER_PAGE));
  const currentProspectPage = Math.min(prospectPage, totalProspectPages);
  const pagedProspects = sortedProspects.slice((currentProspectPage - 1) * PROSPECTS_PER_PAGE, currentProspectPage * PROSPECTS_PER_PAGE);

  const datePickerFieldProps = {
    readOnly: true,
    onKeyDown: (e) => e.preventDefault(),
    onPaste: (e) => e.preventDefault(),
    onFocus: (e) => e.currentTarget.showPicker?.(),
    onClick: (e) => e.currentTarget.showPicker?.(),
  };

  const slaAverageHours = activeClients.length ? Number((activeClients.reduce((s, c) => s + avgResponseHoursByClient(c), 0) / activeClients.length).toFixed(1)) : 0;
  const slaCompliance = activeClients.length ? Number((activeClients.reduce((s, c) => s + slaComplianceByClient(c), 0) / activeClients.length).toFixed(1)) : 0;

  const selectedClientSentiment = selectedClientReviews.reduce((acc, r) => {
    if ((r.rating || 0) >= 4) acc.positive += 1;
    else if ((r.rating || 0) <= 2) acc.negative += 1;
    else acc.neutral += 1;
    return acc;
  }, { positive: 0, neutral: 0, negative: 0 });
  const selectedClientCoverage = selectedClientReviewStats.total > 0
    ? Math.round((selectedClientReviewStats.worked / selectedClientReviewStats.total) * 100)
    : 0;
  const selectedClientSlaAvg = selectedClient ? avgResponseHoursByClient(selectedClient) : 0;
  const selectedClientSlaCompliance = selectedClient ? slaComplianceByClient(selectedClient) : 0;
  const selectedClientPlatforms = Object.entries(selectedClientReviews.reduce((acc, r) => {
    const key = r.platform || "Unknown";
    if (!acc[key]) acc[key] = { reviews: 0, responded: 0, ratingTotal: 0, ratingCount: 0 };
    acc[key].reviews += 1;
    if (["approved", "published"].includes(r.status) || r.actionById) acc[key].responded += 1;
    if (r.rating > 0) {
      acc[key].ratingTotal += r.rating;
      acc[key].ratingCount += 1;
    }
    return acc;
  }, {})).map(([platform, data]) => ({
    platform,
    reviews: data.reviews,
    responded: data.responded,
    coverage: data.reviews > 0 ? Math.round((data.responded / data.reviews) * 100) : 0,
    rating: data.ratingCount > 0 ? Number((data.ratingTotal / data.ratingCount).toFixed(2)) : 0,
  }));

  const openClientBoardFromNotification = (notif) => {
    setTab("clients");
    setSelectedClientId(notif.clientId);
    setClientReviewFilterStatus("pending");
    setClientReviewFilterPlatform("all");
    setClientReviewSearch("");
    setHighlightedReviewId(notif.reviewId);
    setReviewNotifications((prev) => prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n)));
    setNotifOpen(false);
    setTimeout(() => setHighlightedReviewId(null), 2200);
  };

  const can = (permission) => !!currentUser && (currentUser.role === "admin" || (currentUser.permissions || []).includes(permission));
  const canClientPublish = () => !!currentClientPortalUser && (currentClientPortalUser.role || "owner") === "owner";
  const canClientManageUsers = () => !!currentClientPortalUser && (currentClientPortalUser.role || "owner") === "owner";
  const canSelfServeManage = can("selfserve.clients.manage") || can("client.manage");
  const canSelfServeReport = can("selfserve.reports.generate") || can("analytics.view");
  const canSupportRespond = can("support.ticket.respond") || can("support.ticket.manage") || canSelfServeManage;
  const canStaffMessage = can("staff.chat") || can("staff.manage") || can("staff.password.reset") || can("support.ticket.manage");
  const canResetAllSecurityLayers = can("security.layers.reset");
  const canViewAllSecurityEvents = !!currentUser && (currentUser.role === "admin" || can("staff.manage"));
  const currentSecurityAccount = normalizeSecurityAccount(currentUser || currentClientPortalUser || {});
  const currentSecurityLayers = normalizeSecurityLayers(currentSecurityAccount.securityLayers);
  const currentSecurityState = normalizeAccountSecurityState(currentSecurityAccount.securityState);
  const currentSecurityProgress = recommendedSecurityProgress(currentSecurityAccount);
  const adminSecurityEvents = useMemo(() => {
    const flatten = (accounts, scope) => accounts.flatMap((account) => {
      const normalized = normalizeSecurityAccount(account);
      const alerts = normalizeAccountSecurityState(normalized.securityState).loginAlerts || [];
      return alerts.map((alert) => ({
        id: `${scope}-${normalized.id}-${alert.id || alert.at || Math.random().toString(16).slice(2)}`,
        scope,
        userId: normalized.id,
        userName: normalized.name || normalized.username || "Unknown",
        username: normalized.username || "",
        role: normalized.role || scope,
        status: String(alert.status || "unknown").toLowerCase(),
        detail: String(alert.detail || ""),
        at: alert.at || new Date().toISOString(),
        deviceLabel: alert.deviceLabel || "Unknown Device",
        deviceId: alert.deviceId || "",
      }));
    });

    return [
      ...flatten(staffMembers, "staff"),
      ...flatten(clientPortalUsers, "client"),
    ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [staffMembers, clientPortalUsers]);

  const visibleSecurityEvents = useMemo(() => {
    if (canViewAllSecurityEvents) return adminSecurityEvents;
    if (!currentUser) return [];
    return adminSecurityEvents.filter((event) => event.scope === "staff" && event.userId === currentUser.id);
  }, [adminSecurityEvents, canViewAllSecurityEvents, currentUser]);

  const filteredAdminSecurityEvents = useMemo(() => {
    const query = securityEventSearch.trim().toLowerCase();
    return visibleSecurityEvents.filter((event) => {
      const statusOk = securityEventStatusFilter === "all" || event.status === securityEventStatusFilter;
      const scopeOk = securityEventScopeFilter === "all" || event.scope === securityEventScopeFilter;
      const ownerOk = securityEventOwnerFilter === "all" || (currentUser && event.scope === "staff" && event.userId === currentUser.id);
      const queryOk = !query || [
        event.userName,
        event.username,
        event.detail,
        event.deviceLabel,
        event.status,
        event.scope,
      ].some((value) => String(value || "").toLowerCase().includes(query));
      return statusOk && scopeOk && ownerOk && queryOk;
    });
  }, [visibleSecurityEvents, securityEventStatusFilter, securityEventScopeFilter, securityEventOwnerFilter, securityEventSearch, currentUser]);

  const filteredBackendActivity = useMemo(() => {
    const query = backendActivitySearch.trim().toLowerCase();
    const actorQuery = activityActorFilter.trim().toLowerCase();
    const fromTs = activityDateFrom ? new Date(activityDateFrom).getTime() : null;
    const toTs = activityDateTo ? new Date(activityDateTo + "T23:59:59").getTime() : null;
    return (backendActivityAudit || []).filter((entry) => {
      if (query && ![entry?.action, entry?.detail, entry?.actor?.name, entry?.actor?.username, entry?.actor?.role].some((v) => String(v || "").toLowerCase().includes(query))) return false;
      if (actorQuery && ![entry?.actor?.name, entry?.actor?.username].some((v) => String(v || "").toLowerCase().includes(actorQuery))) return false;
      if (activityActionFilter !== "all") {
        const action = String(entry?.action || "");
        if (activityActionFilter === "mailbox" && !action.startsWith("mailbox.")) return false;
        else if (activityActionFilter === "staff" && !action.startsWith("staff.")) return false;
        else if (activityActionFilter === "dashboard" && !action.startsWith("dashboard.")) return false;
        else if (activityActionFilter === "client" && !action.startsWith("client.")) return false;
        else if (activityActionFilter === "stripe" && !action.startsWith("stripe.")) return false;
        else if (activityActionFilter === "system" && !["mailbox.", "staff.", "dashboard.", "client.", "stripe."].some((p) => action.startsWith(p))) return false;
      }
      if (fromTs && entry?.at && new Date(entry.at).getTime() < fromTs) return false;
      if (toTs && entry?.at && new Date(entry.at).getTime() > toTs) return false;
      return true;
    });
  }, [backendActivityAudit, backendActivitySearch, activityActorFilter, activityActionFilter, activityDateFrom, activityDateTo]);

  const ACTIVITY_PAGE_SIZE = 50;
  const activityTotalPages = Math.max(1, Math.ceil(filteredBackendActivity.length / ACTIVITY_PAGE_SIZE));
  const activityPageClamped = Math.min(activityPage, activityTotalPages);
  const pagedBackendActivity = filteredBackendActivity.slice((activityPageClamped - 1) * ACTIVITY_PAGE_SIZE, activityPageClamped * ACTIVITY_PAGE_SIZE);

  const commitStaffSecurityAccount = (nextAccount) => {
    setStaffMembers((prev) => prev.map((member) => (member.id === nextAccount.id ? nextAccount : member)));
    if (currentUser?.id === nextAccount.id) setCurrentUser(nextAccount);
  };

  const commitClientSecurityAccount = (nextAccount) => {
    setClientPortalUsers((prev) => prev.map((user) => (user.id === nextAccount.id ? nextAccount : user)));
    if (currentClientPortalUser?.id === nextAccount.id) setCurrentClientPortalUser(nextAccount);
  };

  const appendLoginAlertEntry = (account, status, detail) => {
    const normalized = normalizeSecurityAccount(account);
    const layers = normalizeSecurityLayers(normalized.securityLayers);
    if (!layers.loginAlerts) return normalized;
    const state = normalizeAccountSecurityState(normalized.securityState);
    const nextAlerts = [{
      id: `alert-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
      status,
      detail,
      deviceId,
      deviceLabel,
      at: new Date().toISOString(),
    }, ...state.loginAlerts].slice(0, 50);
    return {
      ...normalized,
      securityState: {
        ...state,
        loginAlerts: nextAlerts,
      },
    };
  };

  const consumeBackupCode = async (account, rawCode) => {
    const code = String(rawCode || "").trim().toUpperCase();
    if (!code) return { valid: false, account: normalizeSecurityAccount(account) };
    const normalized = normalizeSecurityAccount(account);
    const state = normalizeAccountSecurityState(normalized.securityState);
    const codeHash = await hashBackupCode(code);
    const index = state.backupCodeHashes.findIndex((value) => value === codeHash);
    if (index < 0) return { valid: false, account: normalized };
    const nextHashes = state.backupCodeHashes.filter((_, idx) => idx !== index);
    return {
      valid: true,
      account: {
        ...normalized,
        securityState: {
          ...state,
          backupCodeHashes: nextHashes,
        },
      },
    };
  };

  const ensureSecurityArtifactsForLayers = async (account, layerKeys = []) => {
    let next = normalizeSecurityAccount(account);
    let nextLayers = normalizeSecurityLayers(next.securityLayers);
    let nextState = normalizeAccountSecurityState(next.securityState);
    let generatedBackupCodes = null;

    for (const key of layerKeys) {
      nextLayers = { ...nextLayers, [key]: true };
      if (key === "mfaApp" && !nextState.mfaSecret) {
        nextState = { ...nextState, mfaSecret: generateSecretHex(20) };
      }
      if (key === "backupCodes" && (!Array.isArray(nextState.backupCodeHashes) || !nextState.backupCodeHashes.length)) {
        generatedBackupCodes = generateBackupCodes(8);
        const hashes = await Promise.all(generatedBackupCodes.map((item) => hashBackupCode(item)));
        nextState = {
          ...nextState,
          backupCodeHashes: hashes,
          backupCodesGeneratedAt: new Date().toISOString(),
        };
      }
      if (key === "suspiciousLoginLock") {
        const alreadyTrusted = nextState.trustedDevices.some((entry) => entry.id === deviceId);
        if (!alreadyTrusted) {
          nextState = {
            ...nextState,
            trustedDevices: [{ id: deviceId, label: deviceLabel, trustedAt: new Date().toISOString() }, ...nextState.trustedDevices],
          };
        }
      }
    }

    next = {
      ...next,
      securityLayers: nextLayers,
      securityState: nextState,
    };
    return { account: next, generatedBackupCodes };
  };

  const updateOwnSecurityLayer = async (layerKey, enabled) => {
    if (!SECURITY_LAYER_DEFINITIONS.some((item) => item.key === layerKey)) return;
    if (!enabled) return;

    // Authenticator MFA requires a guided setup flow with QR code verification
    if (layerKey === "mfaApp") {
      const account = normalizeSecurityAccount(currentUser || currentClientPortalUser);
      if (!account) return;
      const secretHex = generateSecretHex(20);
      const accountName = account.username || account.name || "user";
      const uri = buildTotpUri(secretHex, accountName);
      let qrDataUrl = null;
      try { qrDataUrl = await generateQrDataUrl(uri); } catch (_) {}
      const scope = currentUser ? "staff" : "client";
      setMfaSetupModal({ scope, pendingSecretHex: secretHex, qrDataUrl, base32Secret: hexToBase32(secretHex), accountName, error: "" });
      setMfaSetupCode("");
      return;
    }

    if (currentUser) {
      const current = normalizeSecurityAccount(currentUser);
      const { account, generatedBackupCodes } = await ensureSecurityArtifactsForLayers(current, [layerKey]);
      commitStaffSecurityAccount(account);
      if (generatedBackupCodes) {
        setRevealedBackupCodes({ ownerName: account.name, codes: generatedBackupCodes, generatedAt: new Date().toISOString() });
      }
      return;
    }

    if (currentClientPortalUser) {
      const current = normalizeSecurityAccount(currentClientPortalUser);
      const { account, generatedBackupCodes } = await ensureSecurityArtifactsForLayers(current, [layerKey]);
      commitClientSecurityAccount(account);
      if (generatedBackupCodes) {
        setRevealedBackupCodes({ ownerName: account.name, codes: generatedBackupCodes, generatedAt: new Date().toISOString() });
      }
    }
  };

  // Complete the MFA setup: verify the TOTP code the user entered, then persist the secret and mark layer active
  const completeMfaSetup = async () => {
    if (!mfaSetupModal) return;
    const code = normalizeOtpCode(mfaSetupCode);
    if (!code || code.length < 6) {
      setMfaSetupModal((prev) => ({ ...prev, error: "Enter the 6-digit code from your authenticator app." }));
      return;
    }
    const valid = await verifyTotpCode(mfaSetupModal.pendingSecretHex, code);
    if (!valid) {
      setMfaSetupModal((prev) => ({ ...prev, error: "Code is incorrect or expired. Make sure your app shows the latest code and try again." }));
      return;
    }
    // Code verified — activate MFA, save secret
    if (mfaSetupModal.scope === "staff" && currentUser) {
      const next = normalizeSecurityAccount(currentUser);
      const updated = {
        ...next,
        securityLayers: { ...normalizeSecurityLayers(next.securityLayers), mfaApp: true },
        securityState: { ...normalizeAccountSecurityState(next.securityState), mfaSecret: mfaSetupModal.pendingSecretHex },
      };
      commitStaffSecurityAccount(updated);
    } else if (mfaSetupModal.scope === "client" && currentClientPortalUser) {
      const next = normalizeSecurityAccount(currentClientPortalUser);
      const updated = {
        ...next,
        securityLayers: { ...normalizeSecurityLayers(next.securityLayers), mfaApp: true },
        securityState: { ...normalizeAccountSecurityState(next.securityState), mfaSecret: mfaSetupModal.pendingSecretHex },
      };
      commitClientSecurityAccount(updated);
    }
    setMfaSetupModal(null);
    setMfaSetupCode("");
    notify("Authenticator MFA activated");
  };

  const cancelMfaSetup = () => {
    setMfaSetupModal(null);
    setMfaSetupCode("");
  };

  const activateAllRecommendedSecurityLayers = async () => {
    // Activate all non-MFA layers immediately; MFA needs the guided QR flow
    const nonMfaKeys = SECURITY_LAYER_DEFINITIONS.map((item) => item.key).filter((k) => k !== "mfaApp");
    const scope = currentUser ? "staff" : "client";
    const rawAccount = currentUser || currentClientPortalUser;
    if (!rawAccount) return;
    const current = normalizeSecurityAccount(rawAccount);
    const mfaAlreadyActive = !!current.securityLayers?.mfaApp;

    // Activate the three non-MFA layers
    const { account, generatedBackupCodes } = await ensureSecurityArtifactsForLayers(current, nonMfaKeys);
    if (scope === "staff") {
      commitStaffSecurityAccount(account);
    } else {
      commitClientSecurityAccount(account);
    }
    if (generatedBackupCodes) {
      setRevealedBackupCodes({ ownerName: account.name, codes: generatedBackupCodes, generatedAt: new Date().toISOString() });
    }

    if (mfaAlreadyActive) {
      notify("All recommended security layers activated");
      return;
    }

    // Open MFA setup modal for the remaining layer
    const secretHex = generateSecretHex(20);
    const accountName = account.username || account.name || "user";
    const uri = buildTotpUri(secretHex, accountName);
    let qrDataUrl = null;
    try { qrDataUrl = await generateQrDataUrl(uri); } catch (_) {}
    setMfaSetupModal({ scope, pendingSecretHex: secretHex, qrDataUrl, base32Secret: hexToBase32(secretHex), accountName, error: "" });
    setMfaSetupCode("");
    notify("3 layers activated — set up Authenticator MFA to complete security setup");
  };

  const pickSecurityLayerKeyForReset = () => {
    if (typeof window === "undefined") return null;
    const promptText = [
      "Choose a security layer to reset:",
      ...SECURITY_LAYER_DEFINITIONS.map((layer, idx) => `${idx + 1}. ${layer.label} (${layer.key})`),
      "",
      "Type number or key (example: 1 or mfaApp)",
    ].join("\n");
    const raw = window.prompt(promptText, "mfaApp");
    if (raw === null) return null;
    const value = String(raw || "").trim();
    if (!value) return null;
    const index = Number(value);
    if (Number.isInteger(index) && index >= 1 && index <= SECURITY_LAYER_DEFINITIONS.length) {
      return SECURITY_LAYER_DEFINITIONS[index - 1].key;
    }
    const byKey = SECURITY_LAYER_DEFINITIONS.find((layer) => layer.key.toLowerCase() === value.toLowerCase());
    if (byKey) return byKey.key;
    notify("Invalid layer selection", "error");
    return null;
  };

  const resetSingleSecurityLayer = (account, layerKey) => {
    const normalized = normalizeSecurityAccount(account);
    const nextLayers = { ...normalizeSecurityLayers(normalized.securityLayers), [layerKey]: false };
    const nextState = { ...normalizeAccountSecurityState(normalized.securityState) };

    if (layerKey === "mfaApp") {
      nextState.mfaSecret = "";
    } else if (layerKey === "backupCodes") {
      nextState.backupCodeHashes = [];
      nextState.backupCodesGeneratedAt = null;
    } else if (layerKey === "suspiciousLoginLock") {
      nextState.trustedDevices = [];
    } else if (layerKey === "loginAlerts") {
      nextState.loginAlerts = [];
    }

    return {
      ...normalized,
      securityLayers: nextLayers,
      securityState: nextState,
    };
  };

  const resetSecurityLayerForStaffMember = (staffId) => {
    if (!canResetAllSecurityLayers) return notify("No permission", "error");
    const target = staffMembers.find((member) => member.id === staffId);
    if (!target) return notify("Staff account not found", "error");
    const layerKey = pickSecurityLayerKeyForReset();
    if (!layerKey) return;
    const layer = SECURITY_LAYER_DEFINITIONS.find((item) => item.key === layerKey);
    const ok = typeof window === "undefined" || window.confirm(`Reset ${layer?.label || layerKey} for staff ${target.name || target.username}?`);
    if (!ok) return;

    const nextStaffMembers = staffMembers.map((member) => (
      member.id === staffId ? resetSingleSecurityLayer(member, layerKey) : member
    ));
    setStaffMembers(nextStaffMembers);
    if (currentUser?.id === staffId) {
      const synced = nextStaffMembers.find((member) => member.id === staffId);
      if (synced) setCurrentUser(synced);
    }
    setMfaSetupModal(null);
    setMfaSetupCode("");
    setRevealedBackupCodes(null);
    notify(`${layer?.label || layerKey} reset for staff ${target.name || target.username}`);
  };

  const resetSecurityLayerForClientWorkspace = (clientId) => {
    if (!canResetAllSecurityLayers) return notify("No permission", "error");
    const targetClient = clients.find((client) => client.id === clientId);
    const impactedCount = clientPortalUsers.filter((account) => account.clientId === clientId).length;
    if (!impactedCount) return notify("No client portal users found for this client", "error");
    const layerKey = pickSecurityLayerKeyForReset();
    if (!layerKey) return;
    const layer = SECURITY_LAYER_DEFINITIONS.find((item) => item.key === layerKey);
    const ok = typeof window === "undefined" || window.confirm(`Reset ${layer?.label || layerKey} for client workspace ${targetClient?.name || clientId}? This applies to ${impactedCount} account(s).`);
    if (!ok) return;

    const nextClientPortalUsers = clientPortalUsers.map((account) => (
      account.clientId === clientId ? resetSingleSecurityLayer(account, layerKey) : account
    ));
    setClientPortalUsers(nextClientPortalUsers);
    if (currentClientPortalUser?.clientId === clientId) {
      const syncedPortal = nextClientPortalUsers.find((account) => account.id === currentClientPortalUser.id);
      if (syncedPortal) setCurrentClientPortalUser(syncedPortal);
    }
    setMfaSetupModal(null);
    setMfaSetupCode("");
    setRevealedBackupCodes(null);
    notify(`${layer?.label || layerKey} reset for ${impactedCount} account(s) in ${targetClient?.name || "client workspace"}`);
  };

  const scopeRank = (user) => {
    if (!user) return 0;
    if (user.role === "admin") return 4;
    if (user.scopeLevel === "global") return 3;
    if (user.scopeLevel === "country") return 2;
    return 1;
  };

  const canMessageByHierarchy = (sender, recipient) => {
    if (!sender || !recipient) return false;
    if (sender.id === recipient.id) return true;
    if (sender.role === "admin") return true;
    if (recipient.role === "admin") return true;

    const senderRank = scopeRank(sender);
    const recipientRank = scopeRank(recipient);

    if (sender.scopeLevel === "global") return true;

    const sameCountry = String(sender.country || "").trim().toLowerCase() && String(sender.country || "").trim().toLowerCase() === String(recipient.country || "").trim().toLowerCase();
    const sameCity = sameCountry && String(sender.city || "").trim().toLowerCase() && String(sender.city || "").trim().toLowerCase() === String(recipient.city || "").trim().toLowerCase();

    if (sender.scopeLevel === "country") {
      if (recipientRank > senderRank) return true;
      return sameCountry;
    }

    if (sender.scopeLevel === "city") {
      if (recipientRank > senderRank) return sameCountry || recipient.scopeLevel === "global";
      return sameCity;
    }

    return false;
  };

  const loadDashboardMailbox = async (forceRefresh = false) => {
    setDashboardMailbox((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      // Set up a Promise that rejects after 35 seconds (server timeout is 30s + 5s buffer)
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request took too long - POP3 server may be slow')), 35000)
      );
      const params = new URLSearchParams({ limit: "12" });
      if (forceRefresh) params.set("refresh", "true");
      const fetchPromise = apiFetch(`/api/email/inbox?${params.toString()}`, { cache: "no-store" });
      
      const res = await Promise.race([fetchPromise, timeoutPromise]);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        setDashboardMailbox({ messages: [], configured: !!data?.configured, loading: false, error: data?.message || "Unable to load mailbox", mailbox: data?.mailbox || "", hasLoaded: true, cached: !!data?.cached });
        return;
      }
      setDashboardMailbox({ 
        messages: Array.isArray(data.messages) ? data.messages : [], 
        configured: !!data.configured, 
        loading: false, 
        error: "", 
        mailbox: data.mailbox || "",
        hasLoaded: true,
        cached: !!data.cached 
      });
    } catch (err) {
      const errorMsg = err?.message?.includes('too long') ? 'POP3 server is responding slowly. Displaying cached results if available. Try refreshing again.' : "Unable to load mailbox";
      setDashboardMailbox((prev) => ({ ...prev, loading: false, error: errorMsg, hasLoaded: true }));
    }
  };

  const loadSentMailboxMessages = async () => {
    try {
      const res = await apiFetch('/api/email/sent', { cache: 'no-store' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) return;
      setMailboxSentMessages(Array.isArray(data.messages) ? data.messages : []);
    } catch (_) {
      // Keep inbox available even if sent endpoint fails.
    }
  };

  const applyMailboxActionToCollection = (messages, targetId, action) => {
    const id = String(targetId || "");
    if (!id) return messages;
    if (action === "purge") {
      return messages.filter((message) => String(message?.id || "") !== id);
    }
    return messages.map((message) => {
      if (String(message?.id || "") !== id) return message;
      if (action === "read") return { ...message, read: true };
      if (action === "unread") return { ...message, read: false };
      if (action === "star") return { ...message, starred: true };
      if (action === "unstar") return { ...message, starred: false };
      if (action === "archive") return { ...message, archived: true };
      if (action === "unarchive") return { ...message, archived: false };
      if (action === "delete") return { ...message, deleted: true };
      if (action === "restore") return { ...message, deleted: false };
      return message;
    });
  };

  const applyMailboxActionLocally = (message, action) => {
    const id = String(message?.id || "");
    if (!id) return;
    if (message?.source === "sent") {
      setMailboxSentMessages((prev) => applyMailboxActionToCollection(prev, id, action));
      return;
    }
    setDashboardMailbox((prev) => ({
      ...prev,
      messages: applyMailboxActionToCollection(Array.isArray(prev.messages) ? prev.messages : [], id, action),
    }));
  };

  const addMailboxAttachments = async (files) => {
    const pickedFiles = Array.from(files || []);
    if (!pickedFiles.length) return;
    const totalBytes = pickedFiles.reduce((sum, file) => sum + Number(file?.size || 0), 0);
    if (pickedFiles.length > 5 || totalBytes > 7_500_000) {
      notify('Use up to 5 attachments with a combined size below 7.5 MB', 'error');
      return;
    }
    try {
      const nextAttachments = await Promise.all(pickedFiles.map((file) => readMailboxAttachmentFile(file)));
      setMailboxCompose((prev) => ({
        ...prev,
        attachments: [...(prev.attachments || []), ...nextAttachments].slice(0, 5),
      }));
    } catch (err) {
      notify(err?.message || 'Unable to attach file', 'error');
    }
  };

  const removeMailboxAttachment = (name) => {
    setMailboxCompose((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((attachment, index) => `${attachment.name}-${index}` !== name),
    }));
  };

  const clearMailboxDraftOnServer = async () => {
    try {
      await apiFetch('/api/email/draft', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mailbox: mailboxDraftMailboxId }),
      });
      setMailboxDraftSavedAt("");
      setMailboxDraftStatus("idle");
    } catch (_) {
      setMailboxDraftStatus("error");
    }
  };

  const clearMailboxComposer = () => {
    setMailboxCompose(createEmptyMailboxCompose());
    clearMailboxDraftOnServer();
  };

  const postMailboxAction = async (message, action) => {
    const res = await apiFetch('/api/email/message-action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messageId: String(message?.id || ""),
        action,
        source: message?.source === 'sent' ? 'sent' : 'inbox',
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.success) {
      throw new Error(data?.message || 'Unable to update message');
    }
    return data;
  };

  const getThreadActionMessages = (thread, action) => {
    const messages = Array.isArray(thread?.messages) ? thread.messages : [];
    if (["archive", "unarchive", "read", "unread"].includes(action)) {
      return messages.filter((message) => message?.source !== "sent");
    }
    return messages;
  };

  const toggleMailboxSelection = (thread) => {
    const key = String(thread?.id || "");
    if (!key) return;
    setMailboxSelectedKeys((prev) => (
      prev.includes(key) ? prev.filter((entry) => entry !== key) : [...prev, key]
    ));
  };

  const toggleMailboxSelectAllFiltered = () => {
    if (!filteredMailboxThreads.length) return;
    if (allFilteredMailboxSelected) {
      const filteredSet = new Set(filteredMailboxMessageKeys);
      setMailboxSelectedKeys((prev) => prev.filter((key) => !filteredSet.has(key)));
      return;
    }
    setMailboxSelectedKeys((prev) => Array.from(new Set([...prev, ...filteredMailboxMessageKeys])));
  };

  const runMailboxBulkAction = async (action) => {
    if (!selectedMailboxMessages.length) return;
    setMailboxActionLoadingId(`bulk:${action}`);
    try {
      selectedMailboxMessages.forEach((message) => applyMailboxActionLocally(message, action));
      await Promise.all(selectedMailboxMessages.map((message) => postMailboxAction(message, action)));
      setMailboxSelectedKeys([]);
      await Promise.all([loadDashboardMailbox(true), loadSentMailboxMessages()]);
      notify(`Applied ${action} to ${selectedMailboxMessages.length} message${selectedMailboxMessages.length === 1 ? "" : "s"}`);
    } catch (err) {
      notify(err?.message || 'Unable to apply bulk action', 'error');
    } finally {
      setMailboxActionLoadingId("");
    }
  };

  const runMailboxThreadAction = async (thread, action, options = {}) => {
    const { silent = false, refresh = true } = options;
    const messages = getThreadActionMessages(thread, action);
    if (!messages.length) {
      if (!silent) notify('No messages in this thread can use that action', 'error');
      return;
    }
    setMailboxActionLoadingId(`thread:${thread.id}:${action}`);
    try {
      messages.forEach((message) => applyMailboxActionLocally(message, action));
      await Promise.all(messages.map((message) => postMailboxAction(message, action)));
      if (refresh) {
        await Promise.all([loadDashboardMailbox(true), loadSentMailboxMessages()]);
      }
      if (!silent) notify(`Applied ${action} to ${messages.length} message${messages.length === 1 ? "" : "s"} in this thread`);
    } catch (err) {
      if (refresh) {
        await Promise.all([loadDashboardMailbox(true), loadSentMailboxMessages()]);
      }
      if (!silent) notify(err?.message || 'Unable to apply thread action', 'error');
    } finally {
      setMailboxActionLoadingId("");
    }
  };

  useEffect(() => {
    if (mailboxFolder !== "inbox" || !mailboxPreviewThread?.id || !mailboxPreviewThread.unreadCount) return;
    if (mailboxAutoReadThreadIds.current.has(mailboxPreviewThread.id)) return;
    mailboxAutoReadThreadIds.current.add(mailboxPreviewThread.id);
    runMailboxThreadAction(mailboxPreviewThread, "read", { silent: true, refresh: false })
      .finally(() => {
        mailboxAutoReadThreadIds.current.delete(mailboxPreviewThread.id);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailboxFolder, mailboxPreviewThread]);

  const openMailboxReplyComposer = (message) => {
    const targetEmail = extractMailboxEmailAddress(message?.replyTo || message?.from || "");
    if (!targetEmail) {
      notify("Unable to detect sender email for reply", "error");
      return;
    }
    const quoteSource = String(message?.body || message?.snippet || "")
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, 10)
      .join("\n");
    const quoted = quoteSource ? `\n\nOn ${message?.date || "recent email"}, ${message?.from || "sender"} wrote:\n> ${quoteSource.replace(/\n/g, "\n> ")}` : "";
    setMailboxCompose({
      to: targetEmail,
      subject: normalizeReplySubject(message?.subject),
      body: quoted,
      inReplyTo: String(message?.messageId || ""),
      references: String(message?.references || message?.messageId || ""),
      attachments: [],
    });
  };

  const sendMailboxMessage = async () => {
    const to = String(mailboxCompose.to || "").trim();
    const subject = String(mailboxCompose.subject || "").trim();
    const body = String(mailboxCompose.body || "").trim();
    const attachments = Array.isArray(mailboxCompose.attachments) ? mailboxCompose.attachments : [];
    if (!to || !subject || !body) {
      notify("To, Subject, and Message are required", "error");
      return;
    }
    setMailboxSending(true);
    setMailboxSendStatus(null);
    try {
      const res = await apiFetch('/api/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to,
          subject,
          text: body,
          inReplyTo: mailboxCompose.inReplyTo || undefined,
          references: mailboxCompose.references || undefined,
          attachments,
          auditActor: getAuditActor(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 202 && data?.queued) {
        setMailboxSendStatus({ type: "queued", msg: "Email transport not configured — message saved to retry queue. Set SUPPORT_EMAIL_PASS on your Railway backend to enable sending." });
        return;
      }
      if (!res.ok || !data?.success) {
        const msg = data?.message || "Unable to send email";
        const hint = msg.toLowerCase().includes("transport") || msg.toLowerCase().includes("smtp")
          ? `${msg}. Ensure SUPPORT_SMTP_HOST, SUPPORT_SMTP_USER, SUPPORT_SMTP_PASS are set on Railway.`
          : msg;
        setMailboxSendStatus({ type: "error", msg: hint });
        return;
      }
      setMailboxSendStatus({ type: "success", msg: "Message sent successfully." });
      setTimeout(() => setMailboxSendStatus(null), 5000);
      setMailboxCompose(createEmptyMailboxCompose());
      await clearMailboxDraftOnServer();
      await Promise.all([loadDashboardMailbox(true), loadSentMailboxMessages()]);
      setMailboxFolder("sent");
    } catch (_) {
      setMailboxSendStatus({ type: "error", msg: "Network error — unable to reach server. Check your connection." });
    } finally {
      setMailboxSending(false);
    }
  };

  const getAvailableProblemRecipients = () => {
    if (!currentUser) return [];
    return staffMembers.filter((member) => canMessageByHierarchy(currentUser, member));
  };

  const canAccessProblemThread = (thread, user) => {
    if (!thread || !user) return false;
    if (user.role === "admin") return true;
    if (thread.senderId === user.id) return true;
    if ((thread.recipientIds || []).includes(user.id)) return true;
    return (thread.replies || []).some((reply) => reply.senderId === user.id);
  };

  const onStaffProblemAttachmentsChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const maxMb = 8;
    const allowedFiles = files.filter((file) => file.size <= maxMb * 1024 * 1024).slice(0, 5);
    if (allowedFiles.length !== files.length) notify(`Some files were skipped (max ${maxMb}MB each)`, "error");
    const next = (await Promise.all(allowedFiles.map(async (file) => {
      try {
        return await readMailboxAttachmentFile(file);
      } catch {
        return null;
      }
    }))).filter(Boolean);
    setStaffProblemAttachments((prev) => [...prev, ...next].slice(0, 5));
    event.target.value = "";
  };

  const removeStaffProblemAttachment = (name) => {
    setStaffProblemAttachments((prev) => prev.filter((file) => file.name !== name));
  };

  const onStaffReplyAttachmentsChange = async (threadId, event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const maxMb = 8;
    const allowedFiles = files.filter((file) => file.size <= maxMb * 1024 * 1024).slice(0, 3);
    if (allowedFiles.length !== files.length) notify(`Some files were skipped (max ${maxMb}MB each)`, "error");
    const next = (await Promise.all(allowedFiles.map(async (file) => {
      try {
        return await readMailboxAttachmentFile(file);
      } catch {
        return null;
      }
    }))).filter(Boolean);
    setStaffReplyAttachments((prev) => ({ ...prev, [threadId]: [...(prev[threadId] || []), ...next].slice(0, 3) }));
    event.target.value = "";
  };

  const removeStaffReplyAttachment = (threadId, name) => {
    setStaffReplyAttachments((prev) => ({ ...prev, [threadId]: (prev[threadId] || []).filter((file) => file.name !== name) }));
  };

  const startStaffVoiceNoteRecording = async (target) => {
    if (!navigator?.mediaDevices?.getUserMedia || typeof window?.MediaRecorder === "undefined") {
      notify("Voice notes are not supported in this browser", "error");
      return;
    }
    if (mediaRecorderRef.current) {
      notify("A voice note recording is already in progress", "error");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorderStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      mediaRecorderChunksRef.current = [];
      setVoiceRecordingTarget(target);

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) mediaRecorderChunksRef.current.push(event.data);
      };

      recorder.onstop = async () => {
        const blob = new Blob(mediaRecorderChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        mediaRecorderChunksRef.current = [];
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = String(reader.result || "");
          const content = dataUrl.split(",").pop() || "";
          const file = {
            name: `voice-note-${Date.now()}.webm`,
            type: blob.type || "audio/webm",
            size: Number(blob.size || 0),
            content,
            dataUrl,
            voiceNote: true,
          };
          if (target?.scope === "compose") {
            setStaffProblemAttachments((prev) => [...prev, file].slice(0, 5));
          } else if (target?.scope === "reply" && target.threadId) {
            setStaffReplyAttachments((prev) => ({
              ...prev,
              [target.threadId]: [...(prev[target.threadId] || []), file].slice(0, 3),
            }));
          }
          setVoiceRecordingTarget(null);
          mediaRecorderRef.current = null;
          if (mediaRecorderStreamRef.current) {
            mediaRecorderStreamRef.current.getTracks().forEach((track) => track.stop());
            mediaRecorderStreamRef.current = null;
          }
          notify("Voice note attached");
        };
        reader.readAsDataURL(blob);
      };

      recorder.onerror = () => {
        setVoiceRecordingTarget(null);
        mediaRecorderRef.current = null;
        if (mediaRecorderStreamRef.current) {
          mediaRecorderStreamRef.current.getTracks().forEach((track) => track.stop());
          mediaRecorderStreamRef.current = null;
        }
        notify("Voice note recording failed", "error");
      };

      recorder.start();
    } catch {
      setVoiceRecordingTarget(null);
      notify("Microphone access is required for voice notes", "error");
    }
  };

  const stopStaffVoiceNoteRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    if (recorder.state === "recording") recorder.stop();
  };

  const canDeleteStaffThread = (thread, user) => {
    if (!thread || !user) return false;
    return user.role === "admin" || thread.senderId === user.id;
  };

  const canDeleteStaffReply = (reply, user) => {
    if (!reply || !user) return false;
    return user.role === "admin" || reply.senderId === user.id;
  };

  const registerStaffDeleteUndo = useCallback((payload) => {
    if (staffDeleteUndoTimerRef.current) clearTimeout(staffDeleteUndoTimerRef.current);
    setStaffDeleteUndo({
      ...payload,
      expiresAt: Date.now() + 10_000,
    });
    staffDeleteUndoTimerRef.current = setTimeout(() => {
      setStaffDeleteUndo(null);
      staffDeleteUndoTimerRef.current = null;
    }, 10_000);
  }, []);

  const undoStaffDelete = useCallback(() => {
    if (!staffDeleteUndo) return;
    if (staffDeleteUndo.type === "thread" && staffDeleteUndo.threadSnapshot) {
      setStaffProblemThreads((prev) => mergeStaffProblemThreads(prev, [staffDeleteUndo.threadSnapshot]));
      notify("Thread restored");
    } else if (staffDeleteUndo.type === "reply" && staffDeleteUndo.threadId && staffDeleteUndo.replySnapshot) {
      setStaffProblemThreads((prev) => prev.map((thread) => {
        if (thread.id !== staffDeleteUndo.threadId) return thread;
        const existingReplies = Array.isArray(thread.replies) ? thread.replies : [];
        if (existingReplies.some((reply) => reply.id === staffDeleteUndo.replySnapshot.id)) return thread;
        const nextReplies = [...existingReplies, staffDeleteUndo.replySnapshot]
          .sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
        return {
          ...thread,
          replies: nextReplies,
          updatedAt: new Date().toISOString(),
        };
      }));
      notify("Reply restored");
    }
    if (staffDeleteUndoTimerRef.current) clearTimeout(staffDeleteUndoTimerRef.current);
    staffDeleteUndoTimerRef.current = null;
    setStaffDeleteUndo(null);
  }, [staffDeleteUndo, notify]);

  const deleteStaffProblemThread = (threadId) => {
    const thread = staffProblemThreads.find((item) => item.id === threadId);
    if (!thread || !canDeleteStaffThread(thread, currentUser)) return notify("No permission", "error");
    registerStaffDeleteUndo({ type: "thread", threadId, threadSnapshot: thread });
    setStaffProblemThreads((prev) => prev.filter((item) => item.id !== threadId));
    setStaffReplyDrafts((prev) => {
      const next = { ...prev };
      delete next[threadId];
      return next;
    });
    setStaffReplyAttachments((prev) => {
      const next = { ...prev };
      delete next[threadId];
      return next;
    });
    logBackendActivity("staff.chat.thread.delete", `Deleted staff thread ${threadId}`, { threadId });
    notify("Thread deleted. Undo available for 10 seconds.");
  };

  const deleteStaffReply = (threadId, replyId) => {
    const thread = staffProblemThreads.find((item) => item.id === threadId);
    const reply = (thread?.replies || []).find((item) => item.id === replyId);
    if (!thread || !reply || !canDeleteStaffReply(reply, currentUser)) return notify("No permission", "error");
    registerStaffDeleteUndo({ type: "reply", threadId, replyId, replySnapshot: reply });
    setStaffProblemThreads((prev) => prev.map((item) => {
      if (item.id !== threadId) return item;
      return {
        ...item,
        replies: (item.replies || []).filter((r) => r.id !== replyId),
        updatedAt: new Date().toISOString(),
      };
    }));
    logBackendActivity("staff.chat.reply.delete", `Deleted reply ${replyId} from ${threadId}`, { threadId, replyId });
    notify("Reply deleted. Undo available for 10 seconds.");
  };

  const submitStaffProblemMessage = () => {
    if (!currentUser) return;
    const subject = staffProblemForm.subject.trim();
    const message = staffProblemForm.message.trim();
    if (!subject || !message) return notify("Enter a subject and message", "error");

    const availableRecipients = getAvailableProblemRecipients().filter((member) => member.id !== currentUser.id);
    let recipients = [];
    const mode = staffProblemForm.mode;

    if (mode === "direct") {
      recipients = availableRecipients.filter((member) => member.id === staffProblemForm.recipientId);
    } else if (mode === "area") {
      recipients = availableRecipients.filter((member) =>
        String(member.country || "").trim().toLowerCase() === String(currentUser.country || "").trim().toLowerCase() &&
        String(member.city || "").trim().toLowerCase() === String(currentUser.city || "").trim().toLowerCase()
      );
    } else if (mode === "country") {
      recipients = availableRecipients.filter((member) =>
        String(member.country || "").trim().toLowerCase() === String(currentUser.country || "").trim().toLowerCase()
      );
    } else {
      if (!(currentUser.role === "admin" || currentUser.scopeLevel === "global")) {
        return notify("Across messages are only allowed for global managers/admin", "error");
      }
      recipients = availableRecipients;
    }

    recipients = recipients.filter((member) => member.id !== currentUser.id);
    if (!recipients.length) return notify("No recipients found for selected scope", "error");

    const thread = {
      id: `staff-problem-${Date.now()}`,
      subject,
      message,
      senderId: currentUser.id,
      senderName: currentUser.name,
      senderUsername: currentUser.username,
      senderCity: currentUser.city || "",
      senderCountry: currentUser.country || "",
      senderScopeLevel: currentUser.scopeLevel || "country",
      senderScopeValue: currentUser.scopeValue || "Unassigned",
      recipientIds: recipients.map((member) => member.id),
      recipientNames: recipients.map((member) => member.name),
      recipientLocations: recipients.map((member) => `${member.city || ""}${member.city && member.country ? ", " : ""}${member.country || ""}`),
      mode,
      clientId: staffProblemForm.clientId || "",
      attachments: staffProblemAttachments,
      status: "open",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      replies: [],
    };

    setStaffProblemThreads((prev) => [thread, ...prev]);
    setStaffProblemForm({ subject: "", message: "", mode: "area", recipientId: "", clientId: "" });
    setStaffProblemAttachments([]);
    setStaffComposeOpen(false);
    logBackendActivity("staff.chat.thread.create", `Created staff thread "${subject}"`, {
      mode,
      recipientCount: recipients.length,
    });
    notify("Problem message sent");
  };

  const replyStaffProblemMessage = (threadId) => {
    if (!currentUser) return;
    const text = String(staffReplyDrafts[threadId] || "").trim();
    if (!text) return notify("Reply cannot be empty", "error");

    const thread = staffProblemThreads.find((item) => item.id === threadId);
    if (!thread || !canAccessProblemThread(thread, currentUser)) return notify("No permission", "error");

    const reply = {
      id: `staff-reply-${Date.now()}`,
      senderId: currentUser.id,
      senderName: currentUser.name,
      senderUsername: currentUser.username,
      senderCity: currentUser.city || "",
      senderCountry: currentUser.country || "",
      message: text,
      attachments: staffReplyAttachments[threadId] || [],
      createdAt: new Date().toISOString(),
    };

    setStaffProblemThreads((prev) => prev.map((item) => {
      if (item.id !== threadId) return item;
      return {
        ...item,
        replies: [...(item.replies || []), reply],
        updatedAt: new Date().toISOString(),
      };
    }));
    setStaffReplyDrafts((prev) => ({ ...prev, [threadId]: "" }));
    setStaffReplyAttachments((prev) => ({ ...prev, [threadId]: [] }));
    logBackendActivity("staff.chat.reply", `Replied in staff thread ${threadId}`, { threadId });
    notify("Reply sent");
  };

  const makeSupportTicketNumber = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    const seq = `${Math.floor(1000 + Math.random() * 9000)}`;
    return `RPL-${y}${m}${day}-${seq}`;
  };

  const onClientSupportAttachmentsChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const maxMb = 8;
    const next = files
      .filter((file) => file.size <= maxMb * 1024 * 1024)
      .slice(0, 5)
      .map((file) => ({ name: file.name, size: file.size, type: file.type || "application/octet-stream" }));
    if (next.length !== files.length) notify(`Some files were skipped (max ${maxMb}MB each)`, "error");
    setClientSupportAttachments((prev) => [...prev, ...next].slice(0, 5));
    event.target.value = "";
  };

  const removeClientSupportAttachment = (name) => {
    setClientSupportAttachments((prev) => prev.filter((file) => file.name !== name));
  };

  const submitClientSupportRequest = async () => {
    if (!clientPortalClient || !currentClientPortalUser) return;
    const subject = clientSupportForm.subject.trim();
    const message = clientSupportForm.message.trim();
    if (!subject || !message) return notify("Enter a subject and message", "error");
    const ticketNumber = makeSupportTicketNumber();
    const item = {
      id: `support-${Date.now()}`,
      ticketNumber,
      clientId: clientPortalClient.id,
      clientName: clientPortalClient.name,
      requesterId: currentClientPortalUser.id,
      requesterName: currentClientPortalUser.name,
      requesterRole: currentClientPortalUser.role || "owner",
      category: clientSupportForm.category,
      priority: clientSupportForm.priority,
      subject,
      message,
      status: "open",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      adminResponse: "",
      attachments: clientSupportAttachments,
      messages: [
        {
          id: `support-msg-${Date.now()}`,
          senderType: "client",
          senderId: currentClientPortalUser.id,
          senderName: currentClientPortalUser.name,
          senderUsername: currentClientPortalUser.username || "",
          senderRole: currentClientPortalUser.role || "owner",
          message,
          createdAt: new Date().toISOString(),
        },
      ],
      acknowledgementEmail: { sent: false, to: clientPortalClient.email || "", sentAt: null, provider: "none" },
    };

    try {
      const res = await apiFetch("/api/support/ticket-ack", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ticketNumber,
          toEmail: clientPortalClient.email,
          toName: currentClientPortalUser.name,
          clientName: clientPortalClient.name,
          subject,
          priority: clientSupportForm.priority,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success) {
        item.acknowledgementEmail = {
          sent: !!data?.emailSent,
          to: clientPortalClient.email || "",
          sentAt: new Date().toISOString(),
          provider: data?.provider || "simulated",
        };
      }
    } catch (_) {
      // keep local ticket even if email endpoint fails
    }

    setSupportRequests((prev) => [item, ...prev]);
    setClientSupportForm({ category: "technical", priority: "medium", subject: "", message: "" });
    setClientSupportAttachments([]);
    notify(`Support request sent (#${ticketNumber})`);
  };

  const updateSupportRequest = (ticketId, updates) => {
    setSupportRequests((prev) => prev.map((ticket) => ticket.id === ticketId ? { ...ticket, ...updates, updatedAt: new Date().toISOString() } : ticket));
  };

  const buildSupportTicketMessageSnapshot = (messages = []) => {
    const clientMessage = messages.find((msg) => msg.senderType === "client");
    const adminMessages = messages.filter((msg) => msg.senderType === "admin");
    const latestAdminMessage = adminMessages[adminMessages.length - 1] || null;
    return {
      message: clientMessage?.message || "",
      adminResponse: latestAdminMessage?.message || "",
      requesterUsername: clientMessage?.senderUsername || "",
    };
  };

  const getSupportTicketMessages = (ticket) => {
    if (!ticket) return [];
    if (Array.isArray(ticket.messages) && ticket.messages.length) return ticket.messages;
    const fallback = [];
    if (ticket.message) {
      fallback.push({
        id: `${ticket.id}-seed-client`,
        senderType: "client",
        senderId: ticket.requesterId,
        senderName: ticket.requesterName || "Client",
        senderUsername: ticket.requesterUsername || "",
        senderRole: ticket.requesterRole || "owner",
        message: ticket.message,
        createdAt: ticket.createdAt,
      });
    }
    if (ticket.adminResponse) {
      fallback.push({
        id: `${ticket.id}-seed-admin`,
        senderType: "admin",
        senderId: "admin",
        senderName: "Replai Support",
        senderUsername: "",
        senderRole: "support",
        message: ticket.adminResponse,
        createdAt: ticket.updatedAt || ticket.createdAt,
      });
    }
    return fallback;
  };

  const deleteSupportTicketMessage = (ticketId, messageId) => {
    const ticket = supportRequests.find((item) => item.id === ticketId);
    if (!ticket) return notify("Ticket not found", "error");
    const nextMessages = getSupportTicketMessages(ticket).filter((msg) => msg.id !== messageId);
    const snapshot = buildSupportTicketMessageSnapshot(nextMessages);
    updateSupportRequest(ticketId, {
      messages: nextMessages,
      message: snapshot.message,
      adminResponse: snapshot.adminResponse,
      requesterUsername: snapshot.requesterUsername,
    });
    notify("Support message deleted");
  };

  const deleteSupportTicket = (ticketId) => {
    const ticket = supportRequests.find((item) => item.id === ticketId);
    if (!ticket) return notify("Ticket not found", "error");
    if (!window.confirm(`Delete support ticket ${ticket.ticketNumber || ticket.id}?`)) return;
    setSupportRequests((prev) => prev.filter((item) => item.id !== ticketId));
    setClientSupportReplyDrafts((prev) => {
      const next = { ...prev };
      delete next[ticketId];
      return next;
    });
    setAdminSupportReplyDrafts((prev) => {
      const next = { ...prev };
      delete next[ticketId];
      return next;
    });
    if (selectedClientSupportTicketId === ticketId) setSelectedClientSupportTicketId(null);
    notify("Support ticket deleted");
  };

  const sendSupportReplyFromAdmin = (ticketId, overrideText = "") => {
    const text = String(overrideText || adminSupportReplyDrafts[ticketId] || "").trim();
    if (!text) return notify("Reply cannot be empty", "error");
    const ticket = supportRequests.find((item) => item.id === ticketId);
    if (!ticket) return notify("Ticket not found", "error");
    const nextMessage = {
      id: `support-msg-${Date.now()}`,
      senderType: "admin",
      senderId: currentUser?.id || "admin",
      senderName: currentUser?.name || "Replai Support",
      senderUsername: currentUser?.username || "",
      senderRole: currentUser?.role || "support",
      message: text,
      createdAt: new Date().toISOString(),
    };
    const existing = getSupportTicketMessages(ticket);
    updateSupportRequest(ticketId, {
      messages: [...existing, nextMessage],
      adminResponse: text,
      status: "in-progress",
    });
    setAdminSupportReplyDrafts((prev) => ({ ...prev, [ticketId]: "" }));
    notify("Support reply sent");
  };

  const sendSupportReplyFromClient = (ticketId) => {
    if (!currentClientPortalUser || !clientPortalClient) return;
    const text = String(clientSupportReplyDrafts[ticketId] || "").trim();
    if (!text) return notify("Reply cannot be empty", "error");
    const ticket = supportRequests.find((item) => item.id === ticketId && item.clientId === clientPortalClient.id);
    if (!ticket) return notify("Ticket not found", "error");
    const nextMessage = {
      id: `support-msg-${Date.now()}`,
      senderType: "client",
      senderId: currentClientPortalUser.id,
      senderName: currentClientPortalUser.name,
      senderUsername: currentClientPortalUser.username || "",
      senderRole: currentClientPortalUser.role || "owner",
      message: text,
      createdAt: new Date().toISOString(),
    };
    const existing = getSupportTicketMessages(ticket);
    updateSupportRequest(ticketId, {
      messages: [...existing, nextMessage],
      status: ticket.status === "resolved" || ticket.status === "closed" ? "open" : ticket.status,
    });
    setClientSupportReplyDrafts((prev) => ({ ...prev, [ticketId]: "" }));
    notify("Reply sent to Replai Support");
  };

  const resetSelfServeOwnerPassword = async (clientId) => {
    const owner = clientPortalUsers.find((u) => u.clientId === clientId && (u.role || "owner") === "owner");
    if (!owner) return notify("No owner account found", "error");
    const tempPassword = `Replai!${Math.random().toString(36).slice(2, 8)}`;
    const salt = generateSalt();
    const passwordHash = await hashPassword(tempPassword, salt);
    setClientPortalUsers((prev) => prev.map((u) => u.id === owner.id ? { ...u, passwordHash, passwordSalt: salt, mustChangePassword: true } : u));
    setTempPasswordModal({ username: owner.username, tempPassword });
    notify("Owner password reset.");
    const ownerEmail = owner.email || clients.find((c) => c.id === clientId)?.email;
    if (ownerEmail && agencySecret) {
      apiFetch("/api/client/password-changed-email", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
        body: JSON.stringify({
          clientEmail: ownerEmail,
          contactName: owner.name || owner.username,
          username: owner.username,
          changedBy: currentUser?.name || "Administrator",
          dashboardUrl: window.location.origin + window.location.pathname + "?workspace=client",
        }),
      }).catch(() => {});
    }
  };

  const adminChangeClientPortalUsername = (clientId, newUsername) => {
    const trimmed = (newUsername || "").trim().toLowerCase();
    if (!trimmed) return notify("Username cannot be empty", "error");
    if (trimmed.length < 3) return notify("Username must be at least 3 characters", "error");
    if (!/^[a-z0-9_]+$/.test(trimmed)) return notify("Username can only contain letters, numbers and underscores", "error");
    const owner = clientPortalUsers.find((u) => u.clientId === clientId && (u.role || "owner") === "owner");
    if (!owner) return notify("No owner account found", "error");
    if (isUsernameTaken(trimmed, { excludePortalUserId: owner.id })) return notify("Username is already taken", "error");
    setClientPortalUsers((prev) => prev.map((u) => u.id === owner.id ? { ...u, username: trimmed } : u));
    notify("Owner username updated");
  };

  const requestForgotPassword = async () => {
    const identifier = forgotPasswordForm.usernameOrEmail.trim();
    if (!identifier) return notify("Please enter your username or email", "error");
    try {
      await fetch("/api/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier }),
      });
    } catch (_) {
      // best-effort; still show success to avoid username enumeration
    }
    setForgotPasswordSent(true);
  };

  const adminResetStaffMemberPassword = async (staffId) => {
    const member = staffMembers.find((s) => s.id === staffId);
    if (!member) return notify("Staff member not found", "error");
    const tempPassword = `Replai!${Math.random().toString(36).slice(2, 8)}`;
    const salt = generateSalt();
    const passwordHash = await hashPassword(tempPassword, salt);
    setStaffMembers((prev) => {
      const next = prev.map((s) => s.id === staffId ? { ...s, passwordHash, passwordSalt: salt, mustChangePassword: true } : s);
      if (agencySecret) {
        apiFetch("/api/staff/accounts", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
          body: JSON.stringify({ staff: next }),
        }).catch(() => {});
      }
      return next;
    });
    setTempPasswordModal({ username: member.username, tempPassword });
    notify("Staff password reset.");
    if (member.email && agencySecret) {
      apiFetch("/api/staff/password-changed-email", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
        body: JSON.stringify({
          staffEmail: member.email,
          staffName: member.name,
          username: member.username,
          changedBy: currentUser?.name || "Administrator",
          dashboardUrl: window.location.origin + window.location.pathname,
          auditActor: getAuditActor(),
        }),
      }).catch(() => {});
    }
  };

  const adminChangeStaffMemberUsername = (staffId, newUsername) => {
    const trimmed = (newUsername || "").trim().toLowerCase();
    if (!trimmed) return notify("Username cannot be empty", "error");
    if (trimmed.length < 3) return notify("Username must be at least 3 characters", "error");
    if (!/^[a-z0-9_]+$/.test(trimmed)) return notify("Username can only contain letters, numbers and underscores", "error");
    const member = staffMembers.find((s) => s.id === staffId);
    if (!member) return notify("Staff member not found", "error");
    if (isUsernameTaken(trimmed, { excludeStaffId: staffId })) return notify("Username already taken", "error");
    setStaffMembers((prev) => prev.map((s) => s.id === staffId ? { ...s, username: trimmed } : s));
    notify("Staff username updated");
  };

  const adminResetAnyPortalUserPassword = async (userId) => {
    const user = clientPortalUsers.find((u) => u.id === userId);
    if (!user) return notify("Portal user not found", "error");
    const tempPassword = `Replai!${Math.random().toString(36).slice(2, 8)}`;
    const salt = generateSalt();
    const passwordHash = await hashPassword(tempPassword, salt);
    setClientPortalUsers((prev) => prev.map((u) => u.id === userId ? { ...u, passwordHash, passwordSalt: salt, mustChangePassword: true } : u));
    setTempPasswordModal({ username: user.username, tempPassword });
    notify("Portal user password reset.");
    const userEmail = user.email || clients.find((c) => c.id === user.clientId)?.email;
    if (userEmail && agencySecret) {
      apiFetch("/api/client/password-changed-email", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
        body: JSON.stringify({
          clientEmail: userEmail,
          contactName: user.name || user.username,
          username: user.username,
          changedBy: currentUser?.name || "Administrator",
          dashboardUrl: window.location.origin + window.location.pathname + "?workspace=client",
        }),
      }).catch(() => {});
    }
  };

  const adminChangeAnyPortalUserUsername = (userId, newUsername) => {
    const trimmed = (newUsername || "").trim().toLowerCase();
    if (!trimmed) return notify("Username cannot be empty", "error");
    if (trimmed.length < 3) return notify("Username must be at least 3 characters", "error");
    if (!/^[a-z0-9_]+$/.test(trimmed)) return notify("Username can only contain letters, numbers and underscores", "error");
    const user = clientPortalUsers.find((u) => u.id === userId);
    if (!user) return notify("Portal user not found", "error");
    if (isUsernameTaken(trimmed, { excludePortalUserId: userId })) return notify("Username already taken", "error");
    setClientPortalUsers((prev) => prev.map((u) => u.id === userId ? { ...u, username: trimmed } : u));
    notify("Portal user username updated");
  };

  const toggleSelfServeBan = (clientId, banned) => {
    setClientPortalUsers((prev) => prev.map((u) => u.clientId === clientId ? { ...u, banned } : u));
    setClients((prev) => prev.map((c) => c.id === clientId ? { ...c, status: banned ? "suspended" : "active" } : c));
    notify(banned ? "Self-serve account suspended" : "Self-serve account reactivated", banned ? "error" : "success");
  };

  const deleteSelfServeAccount = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    if (!client) return;
    if (!window.confirm(`Delete self-serve account for ${client.name}? This removes client users, reviews, and support tickets.`)) return;
    setClients((prev) => prev.filter((c) => c.id !== clientId));
    setClientPortalUsers((prev) => prev.filter((u) => u.clientId !== clientId));
    setReviews((prev) => prev.filter((r) => r.clientId !== clientId));
    setSupportRequests((prev) => prev.filter((ticket) => ticket.clientId !== clientId));
    notify("Self-serve account deleted", "error");
  };

  const deleteAgencyClient = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    if (!client) return;
    if (!window.confirm(`Permanently delete "${client.name}" from the agency dashboard? This also removes their portal users, reviews, and support tickets. This cannot be undone.`)) return;
    setClients((prev) => prev.filter((c) => c.id !== clientId));
    setClientPortalUsers((prev) => prev.filter((u) => u.clientId !== clientId));
    setReviews((prev) => prev.filter((r) => r.clientId !== clientId));
    setSupportRequests((prev) => prev.filter((ticket) => ticket.clientId !== clientId));
    if (selectedClientId === clientId) setSelectedClientId(null);
    notify(`${client.name} deleted from agency dashboard`, "error");
  };

  const respondToSupportRequest = (ticketId) => {
    const ticket = supportRequests.find((item) => item.id === ticketId);
    if (!ticket) return;
    const response = window.prompt(`Reply to ${ticket.clientName}:`, ticket.adminResponse || "");
    if (response === null || !String(response).trim()) return;
    sendSupportReplyFromAdmin(ticketId, String(response).trim());
  };

  const setSupportRequestStatus = (ticketId, status) => {
    updateSupportRequest(ticketId, { status });
    notify(`Support request marked ${status}`);
  };

  const applyBillingChangeFromTicket = (ticketId) => {
    const ticket = supportRequests.find((item) => item.id === ticketId);
    const request = ticket?.billingChangeRequest;
    if (!ticket || !request) return;
    const methodLabel = CLIENT_PAYMENT_METHODS.find((m) => m.id === request.newMethod)?.label || request.newMethod;
    if (!window.confirm(`Apply billing change for ${ticket.clientName} to ${methodLabel}?`)) return;
    updateSelfServeBilling(ticket.clientId, {
      paymentMethod: request.newMethod,
      paymentDetailMasked: request.paymentDetailMasked || undefined,
      paymentStatus: "pending",
    });
    updateSupportRequest(ticketId, {
      status: "resolved",
      adminResponse: ticket.adminResponse || `Billing method changed to ${methodLabel}.`,
      messages: [
        ...getSupportTicketMessages(ticket),
        {
          id: `support-msg-${Date.now()}`,
          senderType: "admin",
          senderId: currentUser?.id || "admin",
          senderName: currentUser?.name || "Replai Support",
          senderUsername: currentUser?.username || "",
          senderRole: currentUser?.role || "support",
          message: ticket.adminResponse || `Billing method changed to ${methodLabel}.`,
          createdAt: new Date().toISOString(),
        },
      ],
      billingChangeRequest: {
        ...request,
        appliedAt: new Date().toISOString(),
        appliedBy: currentUser?.name || "Admin",
      },
    });
    notify(`Billing method updated for ${ticket.clientName}`);
  };

  const applyPlanChangeFromTicket = (ticketId) => {
    const ticket = supportRequests.find((item) => item.id === ticketId);
    const request = ticket?.planChangeRequest;
    if (!ticket || !request) return;
    if (!window.confirm(`Apply ${request.newPlanLabel || request.newPlan} for ${ticket.clientName}?`)) return;
    setClients((prev) => prev.map((client) => client.id === ticket.clientId ? {
      ...client,
      plan: request.newPlan,
      platformsManaged: getPlatformsForPlan(request.newPlan),
      pendingPlanChange: null,
      updatedAt: new Date().toISOString(),
    } : client));
    updateSupportRequest(ticketId, {
      status: "resolved",
      adminResponse: ticket.adminResponse || `Plan updated to ${request.newPlanLabel || request.newPlan}.`,
      messages: [
        ...getSupportTicketMessages(ticket),
        {
          id: `support-msg-${Date.now()}`,
          senderType: "admin",
          senderId: currentUser?.id || "admin",
          senderName: currentUser?.name || "Replai Support",
          senderUsername: currentUser?.username || "",
          senderRole: currentUser?.role || "support",
          message: ticket.adminResponse || `Plan updated to ${request.newPlanLabel || request.newPlan}.`,
          createdAt: new Date().toISOString(),
        },
      ],
      planChangeRequest: {
        ...request,
        appliedAt: new Date().toISOString(),
        appliedBy: currentUser?.name || "Admin",
        status: "applied",
      },
    });
    notify(`Plan updated for ${ticket.clientName}`);
  };

  const updateSelfServeBilling = (clientId, updates) => {
    setClients((prev) => prev.map((client) => client.id === clientId ? { ...client, ...updates } : client));
    notify("Billing settings updated");
  };

  const setAppLanguage = (nextLang) => {
    const normalized = normalizeUiLanguageCode(nextLang);
    setUiLanguage(normalized);
  };

  const buildAttachmentNote = (file) => {
    if (!file?.name) return "";
    const sizeKb = file.size ? `${Math.max(1, Math.round(Number(file.size) / 1024))}KB` : null;
    const type = file.type || null;
    const meta = [sizeKb, type].filter(Boolean).join(" • ");
    return meta ? `[Attachment selected: ${file.name} (${meta})]` : `[Attachment selected: ${file.name}]`;
  };

  const setManagedRequestStatus = async (requestId, status, extras = {}) => {
    try {
      const res = await apiFetch("/api/managed-account-request/status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requestId, status, ...extras }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) return false;
      setManagedAccountRequests((prev) => prev.map((item) => item.id === requestId ? data.request : item));
      return true;
    } catch (_) {
      return false;
    }
  };

  const resendManagedRequestContract = async (requestItem) => {
    if (!requestItem) return;
    if (!canSelfServeManage) {
      notify("You do not have permission to resend contracts", "error");
      return;
    }
    try {
      const res = await apiFetch("/api/managed-account-request/resend-contract", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requestId: requestItem.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.request) {
        notify(data?.message || "Unable to resend contract", "error");
        return;
      }
      setManagedAccountRequests((prev) => prev.map((item) => item.id === requestItem.id ? data.request : item));
      notify(data.request.contractStatus === "sent" ? "Contract sent successfully" : "Contract resend attempted but delivery failed", data.request.contractStatus === "sent" ? "success" : "error");
    } catch (_) {
      notify("Unable to resend contract", "error");
    }
  };

  const convertManagedRequestToClient = async (requestItem, sendContractNow = false) => {
    if (!requestItem) return;
    if (!canSelfServeManage) {
      notify("You do not have permission to convert managed requests", "error");
      return;
    }
    const existing = clients.find((c) => String(c.email || "").toLowerCase() === String(requestItem.email || "").toLowerCase());
    if (existing) {
      notify(`Client already exists for ${requestItem.email}`, "error");
      return;
    }

    const nextClientId = Math.max(...clients.map((c) => Number(c.id) || 0), 0) + 1;
    const planId = PLANS.some((p) => p.id === requestItem.preferredPlan) ? requestItem.preferredPlan : "starter";
    const todayIso = new Date().toISOString().split("T")[0];
    const paymentMethod = requestItem.paymentMethod || "bank-transfer";
    const autoChargeAfterTrial = AUTO_BILLING_METHODS.includes(paymentMethod);
    const trialEndDate = addDaysISO(todayIso, CLIENT_FREE_TRIAL_DAYS);
    const newClient = {
      id: nextClientId,
      name: requestItem.businessName || "Managed Client",
      contact: requestItem.contact || "",
      email: requestItem.email || "",
      phone: requestItem.phone || "",
      addressLine1: requestItem.addressLine1 || "",
      postcode: requestItem.postcode || "",
      city: requestItem.city || "",
      country: requestItem.country || "",
      location: [requestItem.city, requestItem.country].filter(Boolean).join(", "),
      avatar: "🏢",
      plan: planId,
      platformsManaged: getPlatformsForPlan(planId),
      status: "active",
      contractStatus: requestItem.contractStatus === "sent" ? "sent" : (sendContractNow ? "sent" : "none"),
      connected: false,
      trustpilotConnected: false,
      googleLocations: [],
      reviewCount: 0,
      avgRating: 0,
      ratingAtOnboarding: 0,
      lastPayment: null,
      nextPayment: autoChargeAfterTrial ? trialEndDate : null,
      paymentStatus: autoChargeAfterTrial ? "trialing" : "pending",
      joinDate: todayIso,
      monthlyReviews: [],
      responded: 0,
      serviceMode: "agency",
      paymentMethod,
      paymentDetailMasked: requestItem.paymentDetail ? maskPaymentDetail(requestItem.paymentDetail) : "",
      trialStartDate: autoChargeAfterTrial ? todayIso : null,
      trialEndDate: autoChargeAfterTrial ? trialEndDate : null,
      autoChargeAfterTrial,
    };

    setClients((prev) => [...prev, newClient]);
    const statusOk = await setManagedRequestStatus(requestItem.id, "converted", { clientId: String(newClient.id) });
    if (!statusOk) {
      setManagedAccountRequests((prev) => prev.map((item) => item.id === requestItem.id ? { ...item, status: "converted", clientId: String(newClient.id), updatedAt: new Date().toISOString() } : item));
    }
    if (sendContractNow) {
      await sendContractViaDocuSign(newClient);
      notify(`Managed client created and contract sent for ${newClient.name}`);
      return;
    }
    notify(`Managed client created for ${newClient.name}`);
  };

  const login = async () => {
    if (!authReady) return;
    setLoginError("");
    setCurrentClientPortalUser(null);
    setAuthChallenge(null);
    const username = loginForm.username.trim().toLowerCase();
    let latestStaff = staffMembers;
    // If user not found locally and we have a secret, fetch from backend directly
    // (handles the case where staff sync hasn't completed yet or a different device added staff)
    let user = latestStaff.find((s) => (s.username || "").toLowerCase() === username);
    if (!user && agencySecret) {
      try {
        const r = await apiFetch("/api/staff/accounts", { headers: { "X-Agency-Secret": agencySecret } });
        const d = await r.json().catch(() => ({}));
        if (d?.success && Array.isArray(d.staff) && d.staff.length) {
          setStaffMembers(d.staff);
          latestStaff = d.staff;
          user = d.staff.find((s) => (s.username || "").toLowerCase() === username);
        }
      } catch (_) {}
    }
    if (!user) {
      setLoginError("Invalid username or password");
      return;
    }
    const ok = await verifyPassword(loginForm.password, user.passwordSalt, user.passwordHash);
    if (!ok) {
      setLoginError("Invalid username or password");
      const alerted = appendLoginAlertEntry(user, "failed", "Invalid password");
      commitStaffSecurityAccount(alerted);
      return;
    }

    let nextUser = normalizeSecurityAccount(user);
    const layers = normalizeSecurityLayers(nextUser.securityLayers);
    const state = normalizeAccountSecurityState(nextUser.securityState);

    if (layers.suspiciousLoginLock) {
      const knownDevice = state.trustedDevices.some((entry) => entry.id === deviceId);
      if (!knownDevice) {
        setAuthChallenge({
          scope: "staff",
          userId: nextUser.id,
          username,
          mode: "deviceLock",
          title: "Suspicious Login Lock",
          message: "This device is new. Enter a valid authenticator code or backup code to trust this device and continue.",
        });
        setAuthChallengeForm({ otpCode: "", backupCode: "" });
        const alerted = appendLoginAlertEntry(nextUser, "blocked", "Unknown device blocked");
        commitStaffSecurityAccount(alerted);
        return;
      }
    }

    if (layers.mfaApp || layers.backupCodes) {
      setAuthChallenge({
        scope: "staff",
        userId: nextUser.id,
        username,
        mode: layers.mfaApp ? "mfa" : "backup",
        title: "Two-Step Verification",
        message: layers.mfaApp ? "Enter your 6-digit authenticator code. You can also use a backup code." : "Enter one of your backup codes to complete login.",
      });
      setAuthChallengeForm({ otpCode: "", backupCode: "" });
      return;
    }

    nextUser = appendLoginAlertEntry(nextUser, "success", "Login successful");
    commitStaffSecurityAccount(nextUser);
    setCurrentUser(nextUser);
    setLoginForm({ username: "", password: "" });
    setLoginError("");
    notify(`Welcome ${nextUser.name}`);
    // Post-login sync: if we have an agency secret and the server has no staff yet,
    // push local staff list now so other devices can log in immediately.
    if (agencySecret) {
      setTimeout(() => {
        apiFetch("/api/staff/accounts", { headers: { "X-Agency-Secret": agencySecret } })
          .then((r) => r.json().catch(() => ({})))
          .then((data) => {
            if (!data?.success || !Array.isArray(data.staff) || !data.staff.length) {
              setStaffMembers((prev) => {
                if (prev.length > 0) {
                  apiFetch("/api/staff/accounts", {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
                    body: JSON.stringify({ staff: prev }),
                  }).catch(() => {});
                }
                return prev;
              });
            }
          })
          .catch(() => {});
      }, 500);
    }
  };

  const loginClientPortal = async () => {
    if (!authReady) return;
    setClientLoginError("");
    setClientRegisterError("");
    setCurrentUser(null);
    setAuthChallenge(null);
    const username = clientLoginForm.username.trim().toLowerCase();
    const account = clientPortalUsers.find((u) => String(u.username || "").toLowerCase() === username);
    if (!account) {
      setClientLoginError("Invalid username or password");
      return;
    }
    const ok = await verifyPassword(clientLoginForm.password, account.passwordSalt, account.passwordHash);
    if (!ok) {
      setClientLoginError("Invalid username or password");
      const alerted = appendLoginAlertEntry(account, "failed", "Invalid password");
      commitClientSecurityAccount(alerted);
      return;
    }
    const client = clients.find((c) => c.id === account.clientId);
    if (!client) {
      setClientLoginError("Client account is not linked yet. Please contact Replai support.");
      return;
    }
    if (account.banned || client.status === "suspended") {
      setClientLoginError("This self-serve account has been suspended. Contact Replai support.");
      return;
    }

    let nextAccount = normalizeSecurityAccount({ ...account, role: account.role === "member" ? "member" : "owner", clientName: client.name });
    const layers = normalizeSecurityLayers(nextAccount.securityLayers);
    const state = normalizeAccountSecurityState(nextAccount.securityState);

    if (layers.suspiciousLoginLock) {
      const knownDevice = state.trustedDevices.some((entry) => entry.id === deviceId);
      if (!knownDevice) {
        setAuthChallenge({
          scope: "client",
          userId: nextAccount.id,
          username,
          mode: "deviceLock",
          title: "Suspicious Login Lock",
          message: "This device is new. Enter a valid authenticator code or backup code to trust this device and continue.",
        });
        setAuthChallengeForm({ otpCode: "", backupCode: "" });
        const alerted = appendLoginAlertEntry(nextAccount, "blocked", "Unknown device blocked");
        commitClientSecurityAccount(alerted);
        return;
      }
    }

    if (layers.mfaApp || layers.backupCodes) {
      setAuthChallenge({
        scope: "client",
        userId: nextAccount.id,
        username,
        mode: layers.mfaApp ? "mfa" : "backup",
        title: "Two-Step Verification",
        message: layers.mfaApp ? "Enter your 6-digit authenticator code. You can also use a backup code." : "Enter one of your backup codes to complete login.",
      });
      setAuthChallengeForm({ otpCode: "", backupCode: "" });
      return;
    }

    nextAccount = appendLoginAlertEntry(nextAccount, "success", "Login successful");
    commitClientSecurityAccount(nextAccount);
    setCurrentClientPortalUser(nextAccount);
    setClientPortalTab("overview");
    setClientLoginForm({ username: "", password: "" });
    setClientLoginError("");
    notify(`Welcome ${nextAccount.name}`);
  };

  const verifyAuthChallenge = async () => {
    if (!authChallenge) return;
    const isStaff = authChallenge.scope === "staff";
    const source = isStaff ? staffMembers : clientPortalUsers;
    const account = source.find((item) => item.id === authChallenge.userId);
    if (!account) {
      if (isStaff) setLoginError("Session expired. Please log in again.");
      else setClientLoginError("Session expired. Please log in again.");
      setAuthChallenge(null);
      return;
    }

    let nextAccount = normalizeSecurityAccount(account);
    const layers = normalizeSecurityLayers(nextAccount.securityLayers);
    const state = normalizeAccountSecurityState(nextAccount.securityState);
    const otpCode = normalizeOtpCode(authChallengeForm.otpCode);
    const backupCode = String(authChallengeForm.backupCode || "").trim().toUpperCase();

    let verified = false;
    if (layers.mfaApp && state.mfaSecret && otpCode) {
      verified = await verifyTotpCode(state.mfaSecret, otpCode);
    }
    if (!verified && layers.backupCodes && backupCode) {
      const consumed = await consumeBackupCode(nextAccount, backupCode);
      if (consumed.valid) {
        nextAccount = consumed.account;
        verified = true;
      }
    }

    if (!verified) {
      const alerted = appendLoginAlertEntry(nextAccount, "failed", "Failed two-step verification");
      if (isStaff) {
        commitStaffSecurityAccount(alerted);
        setLoginError("Invalid verification code");
      } else {
        commitClientSecurityAccount(alerted);
        setClientLoginError("Invalid verification code");
      }
      return;
    }

    const nextTrustedDevices = normalizeAccountSecurityState(nextAccount.securityState).trustedDevices;
    if (!nextTrustedDevices.some((entry) => entry.id === deviceId)) {
      nextAccount = {
        ...nextAccount,
        securityState: {
          ...normalizeAccountSecurityState(nextAccount.securityState),
          trustedDevices: [{ id: deviceId, label: deviceLabel, trustedAt: new Date().toISOString() }, ...nextTrustedDevices],
        },
      };
    }

    nextAccount = appendLoginAlertEntry(nextAccount, "success", "Login verified with second factor");
    if (isStaff) {
      commitStaffSecurityAccount(nextAccount);
      setCurrentUser(nextAccount);
      setLoginForm({ username: "", password: "" });
      setLoginError("");
    } else {
      const linkedClient = clients.find((c) => c.id === nextAccount.clientId);
      if (!linkedClient) {
        setClientLoginError("Client account is not linked yet. Please contact Replai support.");
        return;
      }
      const withClientName = { ...nextAccount, clientName: linkedClient.name };
      commitClientSecurityAccount(withClientName);
      setCurrentClientPortalUser(withClientName);
      setClientPortalTab("overview");
      setClientLoginForm({ username: "", password: "" });
      setClientLoginError("");
    }

    setAuthChallenge(null);
    setAuthChallengeForm({ otpCode: "", backupCode: "" });
    notify(`Welcome ${nextAccount.name}`);
  };

  const regenerateBackupCodesForCurrentUser = async () => {
    if (!currentUser && !currentClientPortalUser) return;
    const target = normalizeSecurityAccount(currentUser || currentClientPortalUser);
    const nextLayers = { ...normalizeSecurityLayers(target.securityLayers), backupCodes: true };
    const codes = generateBackupCodes(8);
    const hashes = await Promise.all(codes.map((item) => hashBackupCode(item)));
    const nextAccount = {
      ...target,
      securityLayers: nextLayers,
      securityState: {
        ...normalizeAccountSecurityState(target.securityState),
        backupCodeHashes: hashes,
        backupCodesGeneratedAt: new Date().toISOString(),
      },
    };

    if (currentUser) commitStaffSecurityAccount(nextAccount);
    else commitClientSecurityAccount(nextAccount);

    setRevealedBackupCodes({ ownerName: nextAccount.name, codes, generatedAt: new Date().toISOString() });
    notify("New backup codes generated. Store them securely.");
  };

  const clearAllSecurityEventLogs = () => {
    if (currentUser?.role !== "admin") return;
    if (!window.confirm("Clear all stored login alert events for staff and client accounts?")) return;

    setStaffMembers((prev) => prev.map((member) => {
      const normalized = normalizeSecurityAccount(member);
      return {
        ...normalized,
        securityState: {
          ...normalizeAccountSecurityState(normalized.securityState),
          loginAlerts: [],
        },
      };
    }));

    setClientPortalUsers((prev) => prev.map((account) => {
      const normalized = normalizeSecurityAccount(account);
      return {
        ...normalized,
        securityState: {
          ...normalizeAccountSecurityState(normalized.securityState),
          loginAlerts: [],
        },
      };
    }));

    notify("Security event log cleared");
  };

  const completeClientRegistration = useCallback(async (form, billing = {}) => {
    const salt = generateSalt();
    const passwordHash = await hashPassword(form.password, salt);
    const nextClientId = Math.max(...clients.map((c) => Number(c.id) || 0), 0) + 1;
    const packageInfo = PLANS.find((p) => p.id === form.plan) || PLANS[0];
    const todayIso = new Date().toISOString().split("T")[0];
    const trialEndDate = billing.trialEndDate || addDaysISO(todayIso, CLIENT_FREE_TRIAL_DAYS);
    const newClient = {
      id: nextClientId,
      name: form.businessName,
      location: [form.city, form.country].filter(Boolean).join(", ") || form.country,
      addressLine1: form.addressLine1,
      postcode: form.postcode,
      city: form.city,
      country: form.country,
      phone: form.phone,
      avatar: "🏢",
      email: form.email,
      contact: form.contact,
      plan: packageInfo.id,
      platformsManaged: getPlatformsForPlan(packageInfo.id),
      status: "active",
      contractStatus: "signed",
      connected: false,
      trustpilotConnected: false,
      googleLocations: [],
      reviewCount: 0,
      avgRating: 0,
      ratingAtOnboarding: 0,
      lastPayment: billing.lastPayment || null,
      nextPayment: billing.nextPayment || trialEndDate,
      paymentStatus: billing.paymentStatus || (AUTO_BILLING_METHODS.includes(form.paymentMethod) ? "trialing" : "pending"),
      joinDate: todayIso,
      monthlyReviews: [0, 0, 0],
      responded: 0,
      serviceMode: "self-serve",
      paymentMethod: form.paymentMethod,
      paymentDetailMasked: billing.paymentDetailMasked || maskPaymentDetail(form.paymentDetail),
      trialStartDate: billing.trialStartDate || todayIso,
      trialEndDate,
      autoChargeAfterTrial: billing.autoChargeAfterTrial ?? AUTO_BILLING_METHODS.includes(form.paymentMethod),
      stripeCustomerId: billing.stripeCustomerId || "",
      stripeSubscriptionId: billing.stripeSubscriptionId || "",
    };
    const newUser = {
      id: `client-user-${Date.now()}`,
      clientId: nextClientId,
      name: form.contact,
      username: form.username,
      passwordHash,
      passwordSalt: salt,
      role: "owner",
      mustChangePassword: false,
      email: form.email,
      securityLayers: buildDefaultSecurityLayers(),
    };
    setClients((prev) => [...prev, newClient]);
    setClientPortalUsers((prev) => [...prev, newUser]);
    setCurrentClientPortalUser({ ...newUser, clientName: newClient.name });
    setClientPortalTab("overview");
    setClientAuthScreen("login");
    setClientRegisterForm({ businessName: "", contact: "", email: "", phone: "", addressLine1: "", postcode: "", country: "", city: "", username: "", password: "", confirmPassword: "", plan: "starter", paymentMethod: "paypal", paymentDetail: "", acceptTerms: false });

    // Add registration notification for admin dashboard
    const registrationEntry = {
      id: `reg-${Date.now()}`,
      clientId: nextClientId,
      businessName: form.businessName,
      contactName: form.contact,
      email: form.email,
      phone: form.phone,
      username: form.username,
      plan: packageInfo.name,
      paymentMethod: form.paymentMethod,
      registeredAt: new Date().toISOString(),
    };
    setRegistrationNotifications((prev) => [registrationEntry, ...prev]);

    // Fire-and-forget registration notification email to hello@replai.work
    apiFetch("/api/admin/new-registration-notify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(registrationEntry),
    }).catch(() => {});

    // Fire-and-forget welcome email to the new client
    apiFetch("/api/client/welcome-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientEmail: form.email,
        contactName: form.contact,
        businessName: form.businessName,
        username: form.username,
        planName: packageInfo.name,
        planPrice: packageInfo.price,
        paymentMethod: form.paymentMethod,
        trialEndDate,
        dashboardUrl: window.location.origin + window.location.pathname + "?workspace=client",
      }),
    }).catch(() => {});
  }, [clients]);

  const registerClientPortalAccount = async () => {
    if (clientRegisterProcessing) return;
    setClientRegisterError("");
    const form = {
      ...clientRegisterForm,
      businessName: clientRegisterForm.businessName.trim(),
      contact: clientRegisterForm.contact.trim(),
      email: clientRegisterForm.email.trim(),
      phone: clientRegisterForm.phone.trim(),
      addressLine1: clientRegisterForm.addressLine1.trim(),
      postcode: clientRegisterForm.postcode.trim(),
      country: clientRegisterForm.country.trim(),
      city: clientRegisterForm.city.trim(),
      username: clientRegisterForm.username.trim().toLowerCase(),
      paymentDetail: clientRegisterForm.paymentDetail.trim(),
    };
    if (!form.businessName || !form.contact || !form.email || !form.username || !form.password || !form.confirmPassword || !form.country || !form.addressLine1) {
      setClientRegisterError("Please complete all required fields");
      return;
    }
    if (form.password.length < 8) {
      setClientRegisterError("Password must be at least 8 characters");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setClientRegisterError("Password confirmation does not match");
      return;
    }
    if (!form.acceptTerms) {
      setClientRegisterError("You must accept the self-serve terms to continue");
      return;
    }
    if (isUsernameTaken(form.username)) {
      setClientRegisterError("That username is already in use");
      return;
    }

    // For Stripe: check if payment was processed
    if (form.paymentMethod === "stripe") {
      if (!form.paymentProcessed || !form.stripeCustomerId) {
        setClientRegisterError("Please complete the credit card payment to continue");
        return;
      }
    } else if (!AUTO_BILLING_METHODS.includes(form.paymentMethod) && !form.paymentDetail) {
      setClientRegisterError("Please enter your payment details for post-trial auto-billing");
      return;
    }

    // For Stripe: auto-complete registration since payment is processed
    if (form.paymentMethod === "stripe" && form.paymentProcessed) {
      setClientRegisterProcessing(true);
      try {
        await completeClientRegistration(form, {
          paymentStatus: "trialing",
          autoChargeAfterTrial: true,
          stripeCustomerId: form.stripeCustomerId,
          stripeSubscriptionId: form.stripeSubscriptionId,
        });
        notify(`Account created. Your 7-day trial has started. Payment method is set up for automatic charging.`);
        setClientRegisterProcessing(false);
        return;
      } catch (error) {
        setClientRegisterError(error.message);
        setClientRegisterProcessing(false);
        return;
      }
    }

    if (!AUTO_BILLING_METHODS.includes(form.paymentMethod)) {
      await completeClientRegistration(form, {
        paymentStatus: "pending",
        autoChargeAfterTrial: false,
      });
      notify(`Account created. Billing setup for ${CLIENT_PAYMENT_METHODS.find((m) => m.id === form.paymentMethod)?.label || "selected method"} will be completed by Replai support.`);
      return;
    }

    setClientRegisterProcessing(true);
    try {
      const successUrl = `${window.location.origin}${window.location.pathname}?workspace=client&register=1&checkout=success`;
      const cancelUrl = `${window.location.origin}${window.location.pathname}?workspace=client&register=1&checkout=cancel`;
      sessionStorage.setItem("replai_pending_client_registration", JSON.stringify(form));
      const res = await apiFetch("/api/billing/checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planId: form.plan,
          paymentMethod: form.paymentMethod,
          businessName: form.businessName,
          contactName: form.contact,
          email: form.email,
          successUrl,
          cancelUrl,
          browserLocale: typeof navigator !== "undefined" ? (navigator.language || "") : "",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success || !data?.checkoutUrl) {
        sessionStorage.removeItem("replai_pending_client_registration");
        setClientRegisterError(data?.message || "Unable to start secure billing checkout");
        return;
      }
      window.location.href = data.checkoutUrl;
    } catch (_) {
      sessionStorage.removeItem("replai_pending_client_registration");
      setClientRegisterError("Unable to start secure billing checkout");
    } finally {
      setClientRegisterProcessing(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutState = params.get("checkout");
    if (!checkoutState) return;
    if (checkoutState === "cancel") {
      setClientRegisterError("Checkout was cancelled. You can try again anytime.");
      window.history.replaceState({}, "", `${window.location.pathname}?workspace=client&register=1`);
      return;
    }
    if (checkoutState !== "success") return;
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setClientRegisterError("Missing checkout session id. Please try registration again.");
      return;
    }
    let active = true;
    const finalize = async () => {
      try {
        const rawPending = sessionStorage.getItem("replai_pending_client_registration");
        if (!rawPending) {
          if (active) setClientRegisterError("Registration session expired. Please register again.");
          return;
        }
        const pending = JSON.parse(rawPending);
        const res = await apiFetch(`/api/billing/session-status?sessionId=${encodeURIComponent(sessionId)}`, { cache: "no-store" });
        const data = await res.json().catch(() => ({}));
        if (!active) return;
        if (!res.ok || !data?.success || !data?.completed) {
          setClientRegisterError(data?.message || "Payment verification failed. Please contact support.");
          return;
        }
        await completeClientRegistration(pending, {
          paymentStatus: data.paymentStatus || "trialing",
          trialStartDate: data.trialStartDate,
          trialEndDate: data.trialEndDate,
          nextPayment: data.nextPayment,
          lastPayment: data.lastPayment,
          autoChargeAfterTrial: true,
          paymentDetailMasked: data.paymentDetailMasked || "Card on file",
          stripeCustomerId: data.stripeCustomerId || "",
          stripeSubscriptionId: data.stripeSubscriptionId || "",
        });
        sessionStorage.removeItem("replai_pending_client_registration");
        window.history.replaceState({}, "", `${window.location.pathname}?workspace=client`);
        notify("Registration complete. Subscription is active with free trial.");
      } catch (_) {
        if (active) setClientRegisterError("Payment verification failed. Please contact support.");
      }
    };
    finalize();
    return () => {
      active = false;
    };
  }, [completeClientRegistration, notify]);

  const registerManagedAccount = async () => {
    if (managedRegisterProcessing) return;
    setManagedRegisterError("");
    const form = {
      ...managedRegisterForm,
      businessName: managedRegisterForm.businessName.trim(),
      contact: managedRegisterForm.contact.trim(),
      email: managedRegisterForm.email.trim(),
      phone: managedRegisterForm.phone.trim(),
      addressLine1: managedRegisterForm.addressLine1.trim(),
      postcode: managedRegisterForm.postcode.trim(),
      country: managedRegisterForm.country.trim(),
      city: managedRegisterForm.city.trim(),
      paymentMethod: String(managedRegisterForm.paymentMethod || "bank-transfer").trim().toLowerCase(),
      paymentDetail: managedRegisterForm.paymentDetail.trim(),
      browserLocale: typeof navigator !== "undefined" ? (navigator.language || "") : "",
    };
    if (!form.businessName || !form.contact || !form.email || !form.country || !form.addressLine1) {
      setManagedRegisterError("Please complete all required fields");
      return;
    }
    
    setManagedRegisterProcessing(true);
    try {
      const res = await apiFetch("/api/managed-account-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setManagedRegisterError(data?.message || "Unable to submit managed account request");
        return;
      }
      notify("Request submitted! Replai will contact you within 24 hours to complete setup.");
      setManagedRegisterForm({ businessName: "", contact: "", email: "", phone: "", addressLine1: "", postcode: "", country: "", city: "", preferredPlan: "starter", paymentMethod: "bank-transfer", paymentDetail: "" });
      setClientAuthScreen("login");
    } catch (_) {
      setManagedRegisterError("Unable to submit managed account request");
    } finally {
      setManagedRegisterProcessing(false);
    }
  };

  const logout = () => {
    setCurrentUser(null);
    setCurrentClientPortalUser(null);
    setTab("queue");
    setClientPortalTab("overview");
    setOnboardingStep(1);
  };

  const clientPortalApproveReview = (reviewId) => {
    setReviews((prev) => prev.map((r) => (r.id === reviewId ? { ...r, status: "approved", actionById: currentClientPortalUser?.id, actionByName: currentClientPortalUser?.name } : r)));
    notify("Response approved");
  };

  const clientPortalPublishReview = (reviewId) => {
    if (!canClientPublish()) {
      notify("Only owner accounts can publish responses", "error");
      return;
    }
    setReviews((prev) => prev.map((r) => (r.id === reviewId ? { ...r, status: "published", actionById: currentClientPortalUser?.id, actionByName: currentClientPortalUser?.name } : r)));
    notify("Response published");
  };

  const clientPortalOpenEdit = (review) => {
    setClientPortalEditReviewId(review.id);
    setClientPortalEditText(review.aiResponse || "");
  };

  const clientPortalSaveEdit = () => {
    if (!clientPortalEditReviewId) return;
    setReviews((prev) => prev.map((r) => (r.id === clientPortalEditReviewId ? { ...r, aiResponse: clientPortalEditText } : r)));
    setClientPortalEditReviewId(null);
    setClientPortalEditText("");
    notify("Draft updated");
  };

  const clientPortalReopenReview = (reviewId) => {
    setReviews((prev) => prev.map((r) => (r.id === reviewId ? { ...r, status: "pending" } : r)));
    notify("Review moved back to pending");
  };

  const addClientPortalMember = async () => {
    if (!canClientManageUsers()) {
      notify("Only owner accounts can manage users", "error");
      return;
    }
    if (!currentClientPortalUser?.clientId) return;
    const name = clientPortalMemberForm.name.trim();
    const username = clientPortalMemberForm.username.trim().toLowerCase();
    const password = clientPortalMemberForm.password;
    if (!name || !username || !password) {
      notify("Enter name, username and password", "error");
      return;
    }
    if (isUsernameTaken(username)) {
      notify("Username already exists", "error");
      return;
    }
    const salt = generateSalt();
    const passwordHash = await hashPassword(password, salt);
    const item = {
      id: `client-user-${Date.now()}`,
      clientId: currentClientPortalUser.clientId,
      name,
      username,
      passwordHash,
      passwordSalt: salt,
      role: "member",
      securityLayers: buildDefaultSecurityLayers(),
    };
    setClientPortalUsers((prev) => [...prev, item]);
    setClientPortalMemberForm({ name: "", username: "", password: "" });
    notify("Member user added");
  };

  const removeClientPortalMember = (userId) => {
    if (!canClientManageUsers()) {
      notify("Only owner accounts can manage users", "error");
      return;
    }
    if (!window.confirm("Remove this member account?")) return;
    setClientPortalUsers((prev) => prev.filter((u) => u.id !== userId));
    notify("Member removed", "error");
  };

  const isUsernameTaken = (username, options = {}) => {
    const normalized = String(username || "").trim().toLowerCase();
    if (!normalized) return false;
    const { excludeStaffId = null, excludePortalUserId = null } = options;
    const staffTaken = staffMembers.some((member) => member.id !== excludeStaffId && String(member.username || "").toLowerCase() === normalized);
    if (staffTaken) return true;
    return clientPortalUsers.some((user) => user.id !== excludePortalUserId && String(user.username || "").toLowerCase() === normalized);
  };

  const saveBusinessInfoDraft = () => {
    notify("Business settings saved");
  };

  const saveCurrentUserProfileDraft = () => {
    if (!currentUser) return;
    if (!staffProfileDraft.firstName.trim() && !staffProfileDraft.name.trim()) return notify("First name is required", "error");
    const newUsername = staffProfileDraft.username.trim().toLowerCase();
    if (newUsername && newUsername !== currentUser.username) {
      if (newUsername.length < 3) return notify("Username must be at least 3 characters", "error");
      if (!/^[a-z0-9_]+$/.test(newUsername)) return notify("Username can only contain letters, numbers and underscores", "error");
      if (isUsernameTaken(newUsername, { excludeStaffId: currentUser.id })) return notify("Username already taken", "error");
    }
    const firstName = staffProfileDraft.firstName.trim();
    const lastName = staffProfileDraft.lastName.trim();
    const next = {
      ...currentUser,
      ...staffProfileDraft,
      firstName,
      lastName,
      name: firstName && lastName ? firstName + " " + lastName : staffProfileDraft.name.trim() || currentUser.name,
      gender: staffProfileDraft.gender,
      username: newUsername || currentUser.username,
      email: staffProfileDraft.email.trim(),
      phone: staffProfileDraft.phone.trim(),
      city: staffProfileDraft.city.trim(),
      country: staffProfileDraft.country.trim(),
      bio: staffProfileDraft.bio.trim(),
    };
    setCurrentUser(next);
    setStaffMembers((prev) => prev.map((staff) => (staff.id === currentUser.id ? { ...staff, ...next } : staff)));
    notify("Profile saved");
  };

  const updateCurrentUserDraft = (updates) => {
    if (!currentUser) return;
    const next = { ...currentUser, ...updates };
    setCurrentUser(next);
    setStaffProfileDraft(buildProfileDraft(next));
    setStaffMembers((prev) => prev.map((staff) => (staff.id === currentUser.id ? { ...staff, ...updates } : staff)));
  };

  const saveClientPortalProfileDraft = () => {
    if (!currentClientPortalUser) return;
    if (!clientProfileDraft.name.trim()) return notify("Name is required", "error");
    const next = {
      ...currentClientPortalUser,
      ...clientProfileDraft,
      name: clientProfileDraft.name.trim(),
      email: clientProfileDraft.email.trim(),
      phone: clientProfileDraft.phone.trim(),
      city: clientProfileDraft.city.trim(),
      country: clientProfileDraft.country.trim(),
      bio: clientProfileDraft.bio.trim(),
    };
    setCurrentClientPortalUser(next);
    setClientPortalUsers((prev) => prev.map((user) => (user.id === currentClientPortalUser.id ? { ...user, ...next } : user)));
    notify("Profile saved");
  };

  const saveBrandVoice = () => {
    if (!clientPortalClient) return;
    setClients((prev) => prev.map((c) => c.id === clientPortalClient.id ? { ...c, brandVoice: { ...brandVoiceDraft } } : c));
    notify("Brand voice saved — responses will use these settings");
  };

  const completeOnboarding = () => {
    if (!currentUser) return;
    if (!currentUser.city || !currentUser.country || !currentUser.phone || !currentUser.email || !currentUser.preferredLanguage) {
      return notify("Please complete city, country, phone, email and language", "error");
    }
    setStaffMembers((prev) => prev.map((s) => s.id === currentUser.id ? { ...s, profileCompleted: true, onboardingSeenVersion: ONBOARDING_VERSION } : s));
    setOnboardingStep(1);
    notify("Profile completed");
  };

  const relaunchOnboarding = () => {
    if (!currentUser) return;
    setOnboardingStep(1);
    setStaffMembers((prev) => prev.map((s) => (s.id === currentUser.id ? { ...s, profileCompleted: false, onboardingSeenVersion: null } : s)));
    notify("Onboarding reopened");
  };

  const updatePasswordOnFirstLogin = async () => {
    if (!currentUser) return;
    if (!mustChangeForm.currentPassword || !mustChangeForm.nextPassword || !mustChangeForm.confirmPassword) {
      return notify("Please fill all password fields", "error");
    }
    const validCurrent = await verifyPassword(mustChangeForm.currentPassword, currentUser.passwordSalt, currentUser.passwordHash);
    if (!validCurrent) return notify("Current password is incorrect", "error");
    if (mustChangeForm.nextPassword.length < 8) return notify("New password must be at least 8 characters", "error");
    if (mustChangeForm.nextPassword !== mustChangeForm.confirmPassword) return notify("Password confirmation does not match", "error");
    const salt = generateSalt();
    const passwordHash = await hashPassword(mustChangeForm.nextPassword, salt);
    setStaffMembers((prev) => {
      const next = prev.map((s) => (s.id === currentUser.id ? { ...s, passwordHash, passwordSalt: salt, mustChangePassword: false } : s));
      if (agencySecret) {
        apiFetch("/api/staff/accounts", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
          body: JSON.stringify({ staff: next }),
        }).catch(() => {});
      }
      return next;
    });
    setMustChangeForm({ currentPassword: "", nextPassword: "", confirmPassword: "" });
    notify("Password updated successfully");
  };

  const updateClientOwnerPasswordOnFirstLogin = async () => {
    if (!currentClientPortalUser) return;
    if (!clientMustChangeForm.currentPassword || !clientMustChangeForm.nextPassword || !clientMustChangeForm.confirmPassword) {
      return notify("Please fill all password fields", "error");
    }
    const validCurrent = await verifyPassword(clientMustChangeForm.currentPassword, currentClientPortalUser.passwordSalt, currentClientPortalUser.passwordHash);
    if (!validCurrent) return notify("Current password is incorrect", "error");
    if (clientMustChangeForm.nextPassword.length < 8) return notify("New password must be at least 8 characters", "error");
    if (clientMustChangeForm.nextPassword !== clientMustChangeForm.confirmPassword) return notify("Password confirmation does not match", "error");
    const salt = generateSalt();
    const passwordHash = await hashPassword(clientMustChangeForm.nextPassword, salt);
    setClientPortalUsers((prev) => prev.map((u) => (u.id === currentClientPortalUser.id ? { ...u, passwordHash, passwordSalt: salt, mustChangePassword: false } : u)));
    setClientMustChangeForm({ currentPassword: "", nextPassword: "", confirmPassword: "" });
    notify("Password updated successfully");
  };

  const changeClientOwnPassword = async () => {
    if (!currentClientPortalUser) return;
    if (!clientSettingsPasswordForm.currentPassword || !clientSettingsPasswordForm.nextPassword || !clientSettingsPasswordForm.confirmPassword) {
      return notify("Please fill all password fields", "error");
    }
    const validCurrent = await verifyPassword(clientSettingsPasswordForm.currentPassword, currentClientPortalUser.passwordSalt, currentClientPortalUser.passwordHash);
    if (!validCurrent) return notify("Current password is incorrect", "error");
    if (clientSettingsPasswordForm.nextPassword.length < 8) return notify("New password must be at least 8 characters", "error");
    if (clientSettingsPasswordForm.nextPassword !== clientSettingsPasswordForm.confirmPassword) return notify("Password confirmation does not match", "error");
    const salt = generateSalt();
    const passwordHash = await hashPassword(clientSettingsPasswordForm.nextPassword, salt);
    setClientPortalUsers((prev) => prev.map((u) => (u.id === currentClientPortalUser.id ? { ...u, passwordHash, passwordSalt: salt, mustChangePassword: false } : u)));
    setClientSettingsPasswordForm({ currentPassword: "", nextPassword: "", confirmPassword: "" });
    notify("Your password has been changed");
  };

  // eslint-disable-next-line no-unused-vars
  const updateClientPortalAccount = (updates) => {
    if (!clientPortalClient) return;
    setClients((prev) => prev.map((c) => (c.id === clientPortalClient.id ? { ...c, ...updates } : c)));
  };

  const requestClientPaymentMethodChange = () => {
    if (!clientPortalClient || !currentClientPortalUser) return;
    const newMethod = String(clientBillingChangeForm.newMethod || "").toLowerCase();
    const reason = clientBillingChangeForm.reason.trim();
    if (!newMethod) return notify("Select a new payment method", "error");
    if (newMethod === String(clientPortalClient.paymentMethod || "bank-transfer").toLowerCase() && !reason) {
      return notify("Select a different method or add reason", "error");
    }

    const ticketNumber = makeSupportTicketNumber();
    const newLabel = CLIENT_PAYMENT_METHODS.find((m) => m.id === newMethod)?.label || newMethod;
    const detailMasked = clientBillingChangeForm.paymentDetail ? maskPaymentDetail(clientBillingChangeForm.paymentDetail) : "";
    const message = [
      `Please change our billing method from ${clientPortalPaymentMethodLabel} to ${newLabel}.`,
      reason ? `Reason: ${reason}` : "",
      detailMasked ? `Updated billing detail: ${detailMasked}` : "",
    ].filter(Boolean).join("\n");

    const item = {
      id: `support-${Date.now()}`,
      ticketNumber,
      clientId: clientPortalClient.id,
      clientName: clientPortalClient.name,
      requesterId: currentClientPortalUser.id,
      requesterName: currentClientPortalUser.name,
      requesterRole: currentClientPortalUser.role || "owner",
      category: "billing",
      priority: "medium",
      subject: `Billing method change: ${clientPortalPaymentMethodLabel} → ${newLabel}`,
      message,
      status: "open",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      adminResponse: "",
      attachments: clientSupportAttachments,
      messages: [
        {
          id: `support-msg-${Date.now()}`,
          senderType: "client",
          senderId: currentClientPortalUser.id,
          senderName: currentClientPortalUser.name,
          senderUsername: currentClientPortalUser.username || "",
          senderRole: currentClientPortalUser.role || "owner",
          message,
          createdAt: new Date().toISOString(),
        },
      ],
      billingChangeRequest: {
        oldMethod: clientPortalClient.paymentMethod || "bank-transfer",
        oldMethodLabel: clientPortalPaymentMethodLabel,
        newMethod,
        newMethodLabel: newLabel,
        paymentDetailMasked: detailMasked,
        reason,
      },
      acknowledgementEmail: { sent: false, to: clientPortalClient.email || "", sentAt: null, provider: "none" },
    };

    setSupportRequests((prev) => [item, ...prev]);
    setClientSupportForm((prev) => ({ ...prev, category: "technical", priority: "medium", subject: "", message: "" }));
    setClientSupportAttachments([]);
    setClientBillingChangeForm((prev) => ({ ...prev, paymentDetail: "", reason: "" }));
    setClientPortalTab("support");
    notify(`Payment change request submitted (#${ticketNumber})`);
  };

  const requestClientPlanChange = async () => {
    if (!clientPortalClient || !currentClientPortalUser) return;
    const requestedPlan = String(clientPlanChangeForm.plan || "").trim().toLowerCase();
    const reason = clientPlanChangeForm.reason.trim();
    if (!PLANS.some((plan) => plan.id === requestedPlan)) return notify("Select a valid plan", "error");
    if (requestedPlan === clientPortalClient.plan) return notify("Select a different plan to request a change", "error");

    const currentPlan = PLANS.find((plan) => plan.id === clientPortalClient.plan) || null;
    const nextPlan = PLANS.find((plan) => plan.id === requestedPlan) || null;
    const effectiveDate = clientPortalClient.nextPayment || new Date(Date.now() + (30 * 24 * 60 * 60 * 1000)).toISOString().split("T")[0];
    const ticketNumber = makeSupportTicketNumber();
    const message = [
      `Please ${nextPlan?.price >= (currentPlan?.price || 0) ? "upgrade" : "downgrade"} our plan from ${currentPlan?.name || "Current plan"} to ${nextPlan?.name || requestedPlan}.`,
      `Requested effective date: ${effectiveDate}.`,
      reason ? `Reason: ${reason}` : "",
    ].filter(Boolean).join("\n");

    const item = {
      id: `support-${Date.now()}`,
      ticketNumber,
      clientId: clientPortalClient.id,
      clientName: clientPortalClient.name,
      requesterId: currentClientPortalUser.id,
      requesterName: currentClientPortalUser.name,
      requesterRole: currentClientPortalUser.role || "owner",
      category: "plan-change",
      priority: "medium",
      subject: `Plan change request: ${currentPlan?.name || "Current"} → ${nextPlan?.name || requestedPlan}`,
      message,
      status: "open",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      adminResponse: "",
      attachments: [],
      messages: [
        {
          id: `support-msg-${Date.now()}`,
          senderType: "client",
          senderId: currentClientPortalUser.id,
          senderName: currentClientPortalUser.name,
          senderUsername: currentClientPortalUser.username || "",
          senderRole: currentClientPortalUser.role || "owner",
          message,
          createdAt: new Date().toISOString(),
        },
      ],
      planChangeRequest: {
        oldPlan: currentPlan?.id || clientPortalClient.plan,
        oldPlanLabel: currentPlan?.name || clientPortalClient.plan,
        newPlan: requestedPlan,
        newPlanLabel: nextPlan?.name || requestedPlan,
        effectiveDate,
        reason,
        requestedAt: new Date().toISOString(),
        requestedBy: currentClientPortalUser.name,
        requestedByUsername: currentClientPortalUser.username || "",
        status: "scheduled",
      },
      acknowledgementEmail: { sent: false, to: clientPortalClient.email || "", sentAt: null, provider: "none" },
    };

    try {
      const res = await apiFetch("/api/support/ticket-ack", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ticketNumber,
          toEmail: clientPortalClient.email,
          toName: currentClientPortalUser.name,
          clientName: clientPortalClient.name,
          subject: item.subject,
          priority: item.priority,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success) {
        item.acknowledgementEmail = {
          sent: !!data?.emailSent,
          to: clientPortalClient.email || "",
          sentAt: new Date().toISOString(),
          provider: data?.provider || "simulated",
        };
      }
    } catch (_) {
      // keep local request even if email endpoint fails
    }

    setClients((prev) => prev.map((client) => client.id === clientPortalClient.id ? {
      ...client,
      pendingPlanChange: {
        oldPlan: currentPlan?.id || clientPortalClient.plan,
        oldPlanLabel: currentPlan?.name || clientPortalClient.plan,
        newPlan: requestedPlan,
        newPlanLabel: nextPlan?.name || requestedPlan,
        effectiveDate,
        requestedAt: new Date().toISOString(),
        reason,
      },
    } : client));
    setSupportRequests((prev) => [item, ...prev]);
    setClientPlanChangeForm({ plan: requestedPlan, reason: "" });
    setClientPortalTab("account");
    notify(`Plan change scheduled for next billing date (${effectiveDate})`);
  };

  const requestCancelSubscription = async () => {
    if (!clientPortalClient || !currentClientPortalUser || !isClientPortalOwner) return;
    setCancelSubModal((prev) => ({ ...prev, processing: true }));
    const reason = cancelSubModal.reason.trim();
    const ticketNumber = makeSupportTicketNumber();
    const planLabel = PLANS.find((p) => p.id === clientPortalClient.plan)?.name || clientPortalClient.plan;
    const message = [
      "I would like to cancel my Replai subscription and stop future billing.",
      reason ? `Reason: ${reason}` : "",
      `Current plan: ${planLabel}`,
      `Next billing / trial end: ${clientPortalClient.nextPayment || clientPortalClient.trialEndDate || "—"}`,
    ].filter(Boolean).join("\n");
    const now = new Date().toISOString();
    const item = {
      id: `support-${Date.now()}`,
      ticketNumber,
      clientId: clientPortalClient.id,
      clientName: clientPortalClient.name,
      requesterId: currentClientPortalUser.id,
      requesterName: currentClientPortalUser.name,
      requesterRole: currentClientPortalUser.role || "owner",
      category: "account-cancellation",
      priority: "high",
      subject: `Subscription cancellation request — ${clientPortalClient.name}`,
      message,
      status: "open",
      createdAt: now,
      updatedAt: now,
      adminResponse: "",
      attachments: [],
      messages: [{
        id: `support-msg-${Date.now()}`,
        senderType: "client",
        senderId: currentClientPortalUser.id,
        senderName: currentClientPortalUser.name,
        senderUsername: currentClientPortalUser.username || "",
        senderRole: "owner",
        message,
        createdAt: now,
      }],
      cancellationRequest: {
        plan: clientPortalClient.plan,
        planLabel,
        reason,
        requestedAt: now,
        requestedBy: currentClientPortalUser.name,
        requestedByUsername: currentClientPortalUser.username || "",
        status: "pending",
      },
      acknowledgementEmail: { sent: false, to: clientPortalClient.email || "", sentAt: null, provider: "none" },
    };
    try {
      const ackRes = await apiFetch("/api/support/ticket-ack", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientEmail: clientPortalClient.email,
          clientName: clientPortalClient.name,
          ticketNumber,
          subject: item.subject,
          category: "account-cancellation",
          priority: "high",
          dashboardUrl: window.location.origin,
        }),
      });
      const ackData = await ackRes.json().catch(() => ({}));
      if (ackData?.sent) {
        item.acknowledgementEmail = { sent: true, to: clientPortalClient.email, sentAt: now, provider: ackData.provider || "smtp" };
      }
    } catch (_) {}
    setSupportRequests((prev) => [item, ...prev]);
    setClients((prev) => prev.map((c) => c.id === clientPortalClient.id ? { ...c, status: "cancelling" } : c));
    setCancelSubModal({ open: false, reason: "", processing: false });
    notify("Cancellation request submitted. Our team will reach out within 24 hours.");
    setClientPortalTab("support");
  };

  const requestDeleteOwnAccount = async () => {
    if (!clientPortalClient || !currentClientPortalUser || !isClientPortalOwner) return;
    if (deleteAccountModal.confirmText !== "DELETE") {
      setDeleteAccountModal((prev) => ({ ...prev, error: 'Type DELETE in capitals to confirm.' }));
      return;
    }
    if (!deleteAccountModal.password) {
      setDeleteAccountModal((prev) => ({ ...prev, error: "Enter your current password to confirm." }));
      return;
    }
    setDeleteAccountModal((prev) => ({ ...prev, processing: true, error: "" }));
    const ok = await verifyPassword(deleteAccountModal.password, currentClientPortalUser.passwordSalt, currentClientPortalUser.passwordHash);
    if (!ok) {
      setDeleteAccountModal((prev) => ({ ...prev, processing: false, error: "Password is incorrect. Account not deleted." }));
      return;
    }
    const clientId = clientPortalClient.id;
    setClients((prev) => prev.filter((c) => c.id !== clientId));
    setClientPortalUsers((prev) => prev.filter((u) => u.clientId !== clientId));
    setReviews((prev) => prev.filter((r) => r.clientId !== clientId));
    setSupportRequests((prev) => prev.filter((t) => t.clientId !== clientId));
    setCurrentClientPortalUser(null);
    setDeleteAccountModal({ open: false, confirmText: "", password: "", error: "", processing: false });
  };

  const changeOwnPasswordFromSettings = async () => {
    if (!currentUser) return;
    if (!settingsPasswordForm.currentPassword || !settingsPasswordForm.nextPassword || !settingsPasswordForm.confirmPassword) {
      return notify("Please fill all password fields", "error");
    }
    const validCurrent = await verifyPassword(settingsPasswordForm.currentPassword, currentUser.passwordSalt, currentUser.passwordHash);
    if (!validCurrent) return notify("Current password is incorrect", "error");
    if (settingsPasswordForm.nextPassword.length < 8) return notify("New password must be at least 8 characters", "error");
    if (settingsPasswordForm.nextPassword !== settingsPasswordForm.confirmPassword) return notify("Password confirmation does not match", "error");
    const salt = generateSalt();
    const passwordHash = await hashPassword(settingsPasswordForm.nextPassword, salt);
    setStaffMembers((prev) => {
      const next = prev.map((s) => (s.id === currentUser.id ? { ...s, passwordHash, passwordSalt: salt, mustChangePassword: false } : s));
      if (agencySecret) {
        apiFetch("/api/staff/accounts", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
          body: JSON.stringify({ staff: next }),
        }).catch(() => {});
      }
      return next;
    });
    setSettingsPasswordForm({ currentPassword: "", nextPassword: "", confirmPassword: "" });
    notify("Your password has been changed");
  };

  const addStaff = async () => {
    if (!can("staff.manage")) return notify("No permission", "error");
    if (!staffForm.firstName || !staffForm.lastName || !staffForm.username || !staffForm.password) return notify("First name, last name, username and password are required", "error");
    if (isUsernameTaken(staffForm.username)) return notify("Username already exists", "error");
    const salt = generateSalt();
    const passwordHash = await hashPassword(staffForm.password, salt);
    const newRole = currentUser?.role === "admin" ? (staffForm.role || "staff") : "staff";
    const item = {
      id: `staff-${Date.now()}`,
      role: newRole,
      firstName: staffForm.firstName.trim(),
      lastName: staffForm.lastName.trim(),
      name: (staffForm.firstName.trim() + " " + staffForm.lastName.trim()),
      gender: staffForm.gender,
      username: staffForm.username,
      passwordHash,
      passwordSalt: salt,
      email: staffForm.email,
      phone: staffForm.phone,
      city: staffForm.city,
      country: staffForm.country,
      title: staffForm.title || DEFAULT_STAFF_TITLE,
      scopeLevel: staffForm.scopeLevel || "country",
      scopeValue: staffForm.scopeValue || (staffForm.scopeLevel === "city" ? staffForm.city : staffForm.country) || "Unassigned",
      preferredLanguage: staffForm.preferredLanguage || "English",
      timezone: "Europe/Berlin",
      notificationPreference: "dashboard",
      bio: "",
      avatar: staffForm.avatar,
      permissions: newRole === "admin" ? ALL_PERMISSIONS : staffForm.permissions,
      profileCompleted: false,
      onboardingSeenVersion: null,
      mustChangePassword: true,
      securityLayers: buildDefaultSecurityLayers(),
    };
    setStaffMembers((prev) => {
      const next = [...prev, item];
      // Immediately push to server so other devices can log in right away.
      if (agencySecret) {
        apiFetch("/api/staff/accounts", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
          body: JSON.stringify({ staff: next }),
        }).catch(() => {});
      }
      return next;
    });
    logBackendActivity("staff.account.create", `Created staff account for ${item.name}`, { staffId: item.id, role: item.role });
    setStaffForm({ firstName: "", lastName: "", gender: "", username: "", password: "", email: "", phone: "", city: "", country: "", title: DEFAULT_STAFF_TITLE, scopeLevel: "country", scopeValue: "", preferredLanguage: "English", avatar: "", role: "staff", permissions: ["review.respond", "review.escalate"] });
    notify("Staff account created");

    // Send welcome email if staff has an email address
    if (item.email && agencySecret) {
      const dashboardUrl = window.location.origin + window.location.pathname + (agencySecret ? `?workspace=agency&secret=${encodeURIComponent(agencySecret)}` : "");
      apiFetch("/api/staff/welcome-email", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
        body: JSON.stringify({
          staffEmail: item.email,
          staffName: item.name,
          username: item.username,
          password: staffForm.password,
          dashboardUrl,
          role: item.role,
          title: item.title,
          permissions: item.permissions,
          auditActor: getAuditActor(),
        }),
      })
        .then((r) => r.json().catch(() => ({})))
        .then((data) => {
          if (data?.sent) notify(`Welcome email sent to ${item.email}`);
          else if (data?.message) notify(`Welcome email: ${data.message}`, "error");
        })
        .catch(() => {});
    }
  };

  const startEditStaff = (staff) => {
    setEditingStaffId(staff.id);
    setStaffEditForm({
      firstName: staff.firstName || "",
      lastName: staff.lastName || "",
      gender: staff.gender || "",
      name: staff.name || "",
      email: staff.email || "",
      phone: staff.phone || "",
      city: staff.city || "",
      country: staff.country || "",
      title: staff.title || DEFAULT_STAFF_TITLE,
      scopeLevel: staff.scopeLevel || "country",
      scopeValue: staff.scopeValue || (staff.scopeLevel === "city" ? staff.city : staff.country) || "Unassigned",
      preferredLanguage: staff.preferredLanguage || "English",
      role: staff.role || "staff",
      permissions: staff.permissions || [],
      resetPassword: "",
      newUsername: "",
    });
  };

  const saveStaffEdit = async () => {
    const canManageStaff = can("staff.manage");
    const canResetStaffPassword = canManageStaff || can("staff.password.reset");
    if (!canManageStaff && !canResetStaffPassword) return notify("No permission", "error");
    if (!editingStaffId) return;
    const target = staffMembers.find((s) => s.id === editingStaffId);
    if (!target) return;
    if (target.role === "admin" && currentUser?.role !== "admin") return notify("Only admin can edit admin account", "error");
    let passwordSalt = target.passwordSalt;
    let passwordHash = target.passwordHash;
    let mustChangePassword = target.mustChangePassword;

    if (!canManageStaff && !staffEditForm.resetPassword.trim()) {
      return notify("You can only reset password from this page", "error");
    }

    if (staffEditForm.resetPassword.trim()) {
      if (!canResetStaffPassword) return notify("No permission to reset staff password", "error");
      if (staffEditForm.resetPassword.trim().length < 8) return notify("Reset password must be at least 8 characters", "error");
      passwordSalt = generateSalt();
      passwordHash = await hashPassword(staffEditForm.resetPassword.trim(), passwordSalt);
      mustChangePassword = true;
    }
    const nextUsername = staffEditForm.newUsername.trim().toLowerCase();
    if (canManageStaff && nextUsername) {
      if (nextUsername.length < 3) return notify("Username must be at least 3 characters", "error");
      if (!/^[a-z0-9_]+$/.test(nextUsername)) return notify("Username can only contain letters, numbers and underscores", "error");
      if (isUsernameTaken(nextUsername, { excludeStaffId: editingStaffId })) {
        return notify("Username already exists", "error");
      }
    }
    setStaffMembers((prev) => {
      const next = prev.map((s) => {
        if (s.id !== editingStaffId) return s;
        if (!canManageStaff) {
          return { ...s, passwordSalt, passwordHash, mustChangePassword };
        }
        const updatedRole = currentUser?.role === "admin" ? (staffEditForm.role || s.role || "staff") : s.role;
        return {
          ...s,
          username: nextUsername || s.username,
          firstName: staffEditForm.firstName,
          lastName: staffEditForm.lastName,
          name: (staffEditForm.firstName.trim() + " " + staffEditForm.lastName.trim()).trim() || staffEditForm.name,
          gender: staffEditForm.gender,
          email: staffEditForm.email,
          phone: staffEditForm.phone,
          city: staffEditForm.city,
          country: staffEditForm.country,
          title: staffEditForm.title,
          scopeLevel: staffEditForm.scopeLevel,
          scopeValue: staffEditForm.scopeValue || (staffEditForm.scopeLevel === "city" ? staffEditForm.city : staffEditForm.country) || "Unassigned",
          preferredLanguage: staffEditForm.preferredLanguage || "English",
          role: updatedRole,
          permissions: updatedRole === "admin" ? ALL_PERMISSIONS : staffEditForm.permissions,
          passwordSalt,
          passwordHash,
          mustChangePassword,
        };
      });
      if (agencySecret) {
        apiFetch("/api/staff/accounts", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
          body: JSON.stringify({ staff: next }),
        }).catch(() => {});
      }
      return next;
    });
    setEditingStaffId(null);
    logBackendActivity("staff.account.update", `Updated staff account for ${target.name || target.username || target.id}`, { staffId: target.id });
    notify("Staff profile updated");

    // If a password was reset via edit, email the staff member
    if (staffEditForm.resetPassword.trim() && target.email && agencySecret) {
      apiFetch("/api/staff/password-changed-email", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
        body: JSON.stringify({
          staffEmail: target.email,
          staffName: target.name || target.username,
          username: staffEditForm.newUsername.trim().toLowerCase() || target.username,
          changedBy: currentUser?.name || "Administrator",
          dashboardUrl: window.location.origin + window.location.pathname,
          auditActor: getAuditActor(),
        }),
      }).catch(() => {});
    }
  };

  const deleteStaff = (staffId) => {
    if (!can("staff.manage")) return notify("No permission", "error");
    const staff = staffMembers.find((s) => s.id === staffId);
    if (!staff) return;
    if (staff.role === "admin") return notify("Admin account cannot be deleted", "error");
    if (currentUser?.id === staffId) return notify("You cannot delete your own account", "error");
    if (!window.confirm(`Delete staff account for ${staff.name}?`)) return;
    setStaffMembers((prev) => {
      const next = prev.filter((s) => s.id !== staffId);
      if (agencySecret) {
        apiFetch("/api/staff/accounts", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
          body: JSON.stringify({ staff: next }),
        }).catch(() => {});
      }
      return next;
    });
    if (editingStaffId === staffId) setEditingStaffId(null);
    logBackendActivity("staff.account.delete", `Deleted staff account for ${staff.name || staff.username || staff.id}`, { staffId });
    notify("Staff account deleted", "error");
  };

  const filteredStaff = staffMembers.filter((s) => {
    if (s.role === "admin") return true;
    const cityOk = staffFilter.city === "all" || (s.city || "").toLowerCase() === staffFilter.city.toLowerCase();
    const countryOk = staffFilter.country === "all" || (s.country || "").toLowerCase() === staffFilter.country.toLowerCase();
    const q = staffFilter.query.trim().toLowerCase();
    const queryOk = !q || [s.name, s.username, s.email, s.phone, s.title, s.scopeValue].some((v) => String(v || "").toLowerCase().includes(q));
    return cityOk && countryOk && queryOk;
  });

  const stampReviewAction = (review, actionType, updates = {}) => ({
    ...review,
    ...updates,
    actionType,
    actionAt: new Date().toISOString(),
    actionById: currentUser?.id || null,
    actionByName: currentUser?.name || "System",
    actionByUsername: currentUser?.username || "system",
    actionByAvatar: currentUser?.avatar || "",
  });

  const workboardColumns = {
    todo: reviews.filter((r) => !r.actionById && r.status === "pending"),
    inprogress: reviews.filter((r) => r.actionById && ["pending"].includes(r.status)),
    done: reviews.filter((r) => ["approved", "published", "rejected"].includes(r.status)),
  };
  const workboardAudit = reviews
    .filter((r) => r.actionAt)
    .sort((a, b) => new Date(b.actionAt).getTime() - new Date(a.actionAt).getTime());

  const exportWorkboardAuditCsv = () => {
    if (!workboardAudit.length) return notify("No audit activity yet", "error");
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      ["Timestamp", "Action", "Status", "Client", "Reviewer", "Staff Name", "Staff Username", "Platform"].map(esc).join(","),
      ...workboardAudit.map((r) => {
        const client = clients.find((c) => c.id === r.clientId);
        return [
          r.actionAt,
          r.actionType || "updated",
          r.status,
          client?.name || "Unknown",
          r.author,
          r.actionByName || "System",
          r.actionByUsername || "system",
          r.platform,
        ].map(esc).join(",");
      }),
    ];
    downloadBlob(lines.join("\n"), `workboard-audit-${new Date().toISOString().slice(0, 10)}.csv`, "text/csv;charset=utf-8");
    notify("Audit exported as CSV");
  };

  const exportWorkboardAuditPdf = () => {
    if (!workboardAudit.length) return notify("No audit activity yet", "error");
    const rows = workboardAudit.map((r) => {
      const client = clients.find((c) => c.id === r.clientId);
      return `<tr>
        <td>${new Date(r.actionAt).toLocaleString()}</td>
        <td>${r.actionType || "updated"}</td>
        <td>${r.status}</td>
        <td>${client?.name || "Unknown"}</td>
        <td>${r.author}</td>
        <td>${r.actionByName || "System"} (@${r.actionByUsername || "system"})</td>
      </tr>`;
    }).join("");
    const html = `<!doctype html>
<html><head><meta charset="utf-8" /><title>Workboard Audit</title>
<style>
body{font-family:Arial,sans-serif;color:#111;margin:24px}
h1{margin:0 0 6px 0} .small{font-size:12px;color:#666;margin-bottom:16px}
table{border-collapse:collapse;width:100%} th,td{border:1px solid #ddd;padding:8px;font-size:12px;text-align:left;vertical-align:top}
th{background:#f3f4f6}
</style></head><body>
<h1>Replai Workboard Audit</h1>
<div class="small">Generated ${new Date().toLocaleString()} • Total actions: ${workboardAudit.length}</div>
<table>
<thead><tr><th>Timestamp</th><th>Action</th><th>Status</th><th>Client</th><th>Reviewer</th><th>Performed By</th></tr></thead>
<tbody>${rows}</tbody>
</table>
</body></html>`;
    const w = window.open("", "_blank", "width=1024,height=900");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 300);
    notify("Audit PDF preview opened");
  };

  const canRespond = can("review.respond");
  const canEditReview = can("review.edit");
  const canPublish = can("review.publish");
  const canEscalate = can("review.escalate");
  const canManageStaff = can("staff.manage");
  const canResetStaffPassword = canManageStaff || can("staff.password.reset");

  const getBankTransferConfigForCountry = useCallback((countryValue) => {
    const key = normalizeCountryKey(countryValue);
    if (!key) return null;
    return bankTransferConfigs.find((cfg) => {
      if (cfg.enabled === false) return false;
      const code = normalizeCountryKey(cfg.countryCode);
      const name = normalizeCountryKey(cfg.countryName);
      return key === code || key === name;
    }) || null;
  }, [bankTransferConfigs]);

  const addBankTransferConfig = () => {
    const countryCode = normalizeCountryKey(bankTransferConfigForm.countryCode);
    const countryName = String(bankTransferConfigForm.countryName || "").trim();
    if (!countryCode || !countryName || !bankTransferConfigForm.accountHolder || !bankTransferConfigForm.ibanOrAccount) {
      notify("Country code, name, account holder and account/IBAN are required", "error");
      return;
    }
    const id = `bt-${countryCode.toLowerCase()}-${Date.now()}`;
    setBankTransferConfigs((prev) => ([...prev, { ...bankTransferConfigForm, id, countryCode, countryName }]));
    setBankTransferConfigForm({ countryCode: "", countryName: "", accountHolder: "", bankName: "", ibanOrAccount: "", swiftBic: "", routingCode: "", currency: "EUR", referencePrefix: "RPL", enabled: true });
    notify(`Bank transfer config added for ${countryCode}`);
  };

  const startEditBankTransferConfig = (cfg) => {
    setEditingBankTransferConfigId(cfg.id);
    setBankTransferEditForm({
      countryCode: cfg.countryCode || "",
      countryName: cfg.countryName || "",
      accountHolder: cfg.accountHolder || "",
      bankName: cfg.bankName || "",
      ibanOrAccount: cfg.ibanOrAccount || "",
      swiftBic: cfg.swiftBic || "",
      routingCode: cfg.routingCode || "",
      currency: cfg.currency || "EUR",
      referencePrefix: cfg.referencePrefix || "RPL",
      enabled: cfg.enabled !== false,
    });
  };

  const cancelEditBankTransferConfig = () => {
    setEditingBankTransferConfigId(null);
    setBankTransferEditForm({ countryCode: "", countryName: "", accountHolder: "", bankName: "", ibanOrAccount: "", swiftBic: "", routingCode: "", currency: "EUR", referencePrefix: "RPL", enabled: true });
  };

  const saveBankTransferConfigEdit = () => {
    if (!editingBankTransferConfigId) return;
    const countryCode = normalizeCountryKey(bankTransferEditForm.countryCode);
    const countryName = String(bankTransferEditForm.countryName || "").trim();
    if (!countryCode || !countryName || !bankTransferEditForm.accountHolder || !bankTransferEditForm.ibanOrAccount) {
      notify("Country code, name, account holder and account/IBAN are required", "error");
      return;
    }
    setBankTransferConfigs((prev) => prev.map((item) => item.id === editingBankTransferConfigId ? {
      ...item,
      ...bankTransferEditForm,
      countryCode,
      countryName,
    } : item));
    cancelEditBankTransferConfig();
    notify("Bank transfer config updated");
  };

  const removeBankTransferConfig = (id) => {
    if (editingBankTransferConfigId === id) {
      cancelEditBankTransferConfig();
    }
    setBankTransferConfigs((prev) => prev.filter((item) => item.id !== id));
    notify("Bank transfer config removed");
  };

  const fillTemplate = (template, target, isProspect = false) => {
    let body = template.body;
    let subj = template.subject;
    const bankCfg = getBankTransferConfigForCountry(target.country);
    const planAmount = PLANS.find(p => p.id === target.plan)?.price || "99";
    const reminderReference = `${(bankCfg?.referencePrefix || "RPL")}-${String(target.id || "NEW")}`;
    const bankInstructions = buildBankTransferInstructionText(bankCfg, reminderReference);
    const replacements = {
      "{contact}": target.contact || "",
      "{business}": target.name || "",
      "{email}": target.email || "",
      "{business_type}": target.type || "businesses",
      "{amount}": planAmount,
      "{current_plan}": PLANS.find(p => p.id === target.plan)?.name || "Starter",
      "{plan_limit}": PLANS.find(p => p.id === target.plan)?.reviews || "30",
      "{suggested_plan}": "Growth",
      "{suggested_price}": "199",
      "{suggested_reviews}": "100",
      "{suggested_platforms}": "Google + Facebook + Trustpilot",
      "{suggested_response}": "4-hour",
      "{month}": new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" }),
      "{total_reviews}": String(target.reviewCount || target.monthlyReviews?.[target.monthlyReviews.length-1] || "0"),
      "{responded}": String(target.responded || "0"),
      "{avg_time}": "2.4 hours",
      "{positive}": String(Math.round((target.reviewCount || 10) * 0.78)),
      "{negative}": String(Math.round((target.reviewCount || 10) * 0.12)),
      "{rating}": String(target.avgRating || "N/A"),
      "{highlight_1}": "Response rate maintained at 100%",
      "{highlight_2}": "Average rating improved from last month",
      "{recommendation}": "Consider sharing your best recent reviews on social media to boost visibility.",
      "{sample_response}": '"Thank you for your kind words! We take pride in delivering the best experience possible. We look forward to seeing you again soon!"',
      "{testimonial}": TESTIMONIALS[0].text,
      "{testimonial_author}": TESTIMONIALS[0].contact,
      "{testimonial_business}": TESTIMONIALS[0].client,
      "{client_count}": String(activeClients.length),
      "{stars}": "1",
      "{reviewer}": "Recent Customer",
      "{review_text}": "Example review text",
      "{draft_response}": "Draft response text",
      "{update_message}": "We've made some improvements to our response process.",
      "{bank_details}": bankInstructions || "[No bank transfer details configured for this country]",
      "{payment_reference}": reminderReference,
    };
    Object.entries(replacements).forEach(([k, v]) => { body = body.split(k).join(v); subj = subj.split(k).join(v); });
    return { body, subject: subj };
  };

  const isDateInRange = (dateValue, from, to) => {
    if (!dateValue) return false;
    const value = new Date(dateValue);
    const start = from ? new Date(from) : null;
    const end = to ? new Date(to) : null;
    if (Number.isNaN(value.getTime())) return false;
    if (start && !Number.isNaN(start.getTime()) && value < start) return false;
    if (end && !Number.isNaN(end.getTime()) && value > new Date(`${to}T23:59:59`)) return false;
    return true;
  };

  const buildScopedMetrics = (scopeClientIds, from, to) => {
    const scopedReviews = reviews.filter((r) => scopeClientIds.includes(r.clientId) && isDateInRange(r.date, from, to));
    const totalReviews = scopedReviews.length;
    const responded = scopedReviews.filter((r) => ["approved", "published"].includes(r.status) || (r.aiResponse || "").trim()).length;
    const responseRate = totalReviews > 0 ? Math.round((responded / totalReviews) * 100) : 0;
    const avgRatingBase = scopedReviews.filter((r) => Number(r.rating) > 0);
    const avgRating = avgRatingBase.length ? (avgRatingBase.reduce((sum, r) => sum + Number(r.rating || 0), 0) / avgRatingBase.length).toFixed(2) : "0.00";
    const outstanding = Math.max(totalReviews - responded, 0);
    return { scopedReviews, totalReviews, responded, responseRate, avgRating, outstanding };
  };

  const isTrustpilotConnected = (client) => Boolean(client?.trustpilotConnected);

  const pollGoogleConnectionStatus = async (clientId, attempts = 8) => {
    for (let i = 0; i < attempts; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 2500));
      try {
        const statusRes = await apiFetch(`/api/google/status?clientId=${encodeURIComponent(String(clientId))}`, { cache: "no-store" });
        if (!statusRes.ok) continue;
        const statusData = await statusRes.json().catch(() => ({}));
        if (statusData?.success && statusData.connected) {
          setClients((prev) => prev.map((c) => c.id === clientId ? { ...c, connected: true } : c));
          notify("Google connection confirmed ✅");
          return true;
        }
      } catch (_) {
        // continue polling
      }
    }
    notify("Google auth window opened. Complete consent, then click Sync Reviews.", "error");
    return false;
  };

  const startGoogleConnection = async (client) => {
    setGoogleBusyClientId(client.id);
    try {
      const res = await apiFetch("/api/google/connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: String(client.id) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success || !data?.authUrl) {
        notify(data?.message || "Google connect endpoint is not configured", "error");
        return;
      }
      window.open(data.authUrl, "_blank", "noopener,noreferrer,width=980,height=760");
      notify(`Google OAuth started for ${client.name}`);
      await pollGoogleConnectionStatus(client.id);
    } catch (_) {
      notify("Failed to start Google connection", "error");
    } finally {
      setGoogleBusyClientId(null);
    }
  };

  const fetchGoogleLocationsForClient = async (client) => {
    setGoogleBusyClientId(client.id);
    try {
      const res = await apiFetch(`/api/google/locations?clientId=${encodeURIComponent(String(client.id))}`, { cache: "no-store" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        notify(data?.message || "Could not fetch Google locations", "error");
        return;
      }
      const locations = Array.isArray(data.locations) ? data.locations : [];
      setClients((prev) => prev.map((c) => c.id === client.id ? { ...c, googleLocations: locations } : c));
      const cap = getLocationCap(client.plan);
      notify(`Found ${locations.length} location(s) — plan allows up to ${cap}`);
    } catch (_) {
      notify("Failed to fetch Google locations", "error");
    } finally {
      setGoogleBusyClientId(null);
    }
  };

  const syncGoogleReviewsForClient = async (client) => {
    setGoogleBusyClientId(client.id);
    try {
      const storedLocations = Array.isArray(client.googleLocations) ? client.googleLocations : [];
      const cap = getLocationCap(client.plan);
      const cappedLocations = storedLocations.slice(0, cap);

      const body = { clientId: String(client.id) };
      // Use multi-location path only when we have stored locations
      if (cappedLocations.length > 0) {
        body.locations = cappedLocations;
        body.plan = client.plan || "starter";
      }

      const res = await apiFetch("/api/google/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        notify(data?.message || "Google review sync failed", "error");
        return;
      }

      const incoming = Array.isArray(data.reviews) ? data.reviews : [];
      const baseId = Date.now();
      setReviews((prev) => {
        const existingKeys = new Set(prev.map((r) => `${r.clientId}::${r.externalReviewId || `${r.author}::${r.date}::${r.text}`}`));
        const mapped = incoming
          .map((g, index) => ({
            id: baseId + index,
            clientId: client.id,
            externalReviewId: g.externalReviewId || g.reviewName || `google-${baseId + index}`,
            author: g.author || "Google User",
            rating: Number(g.rating || 0),
            text: g.text || "",
            date: g.date || new Date().toISOString().split("T")[0],
            platform: "Google",
            status: g.status || "pending",
            aiResponse: g.aiResponse || "",
            locationId: g.locationId || null,
            locationLabel: g.locationLabel || null,
          }))
          .filter((r) => {
            const key = `${r.clientId}::${r.externalReviewId || `${r.author}::${r.date}::${r.text}`}`;
            if (existingKeys.has(key)) return false;
            existingKeys.add(key);
            return true;
          });
        return [...prev, ...mapped];
      });

      const stats = data.stats || {};
      setClients((prev) => prev.map((c) => c.id === client.id ? {
        ...c,
        connected: true,
        reviewCount: Number(stats.totalReviews ?? c.reviewCount ?? 0),
        responded: Number(stats.responded ?? c.responded ?? 0),
        avgRating: Number(stats.avgRating ?? c.avgRating ?? 0),
      } : c));

      if (data.multiLocation && Array.isArray(data.locationResults)) {
        const summary = data.locationResults.map((lr) => `${lr.label}: ${lr.count}`).join(", ");
        notify(`Synced ${incoming.length} review(s) across ${data.locationResults.length} location(s) — ${summary}`);
      } else {
        notify(`Synced ${incoming.length} Google review(s) for ${client.name}`);
      }
    } catch (_) {
      notify("Google sync failed", "error");
    } finally {
      setGoogleBusyClientId(null);
    }
  };

  const disconnectGoogleClient = (client) => {
    if (!window.confirm(`Disconnect Google for ${client.name}?`)) return;
    setClients((prev) => prev.map((c) => c.id === client.id ? { ...c, connected: false } : c));
    notify(`Disconnected Google for ${client.name}`);
  };

  const pollTrustpilotConnectionStatus = async (clientId, attempts = 8) => {
    for (let i = 0; i < attempts; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 2500));
      try {
        const statusRes = await apiFetch(`/api/trustpilot/status?clientId=${encodeURIComponent(String(clientId))}`, { cache: "no-store" });
        if (!statusRes.ok) continue;
        const statusData = await statusRes.json().catch(() => ({}));
        if (statusData?.success && statusData.connected) {
          setClients((prev) => prev.map((c) => c.id === clientId ? { ...c, trustpilotConnected: true } : c));
          notify("Trustpilot connection confirmed ✅");
          return true;
        }
      } catch (_) {
        // continue polling
      }
    }
    notify("Trustpilot auth window opened. Complete consent, then click Sync Reviews.", "error");
    return false;
  };

  const startTrustpilotConnection = async (client) => {
    setTrustpilotBusyClientId(client.id);
    try {
      const res = await apiFetch("/api/trustpilot/connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: String(client.id) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success || !data?.authUrl) {
        notify(data?.message || "Trustpilot connect endpoint is not configured", "error");
        return;
      }
      window.open(data.authUrl, "_blank", "noopener,noreferrer,width=980,height=760");
      notify(tp("Trustpilot OAuth started for {{client}}").replace("{{client}}", client.name));
      await pollTrustpilotConnectionStatus(client.id);
    } catch (_) {
      notify("Failed to start Trustpilot connection", "error");
    } finally {
      setTrustpilotBusyClientId(null);
    }
  };

  const syncTrustpilotReviewsForClient = async (client) => {
    setTrustpilotBusyClientId(client.id);
    try {
      const res = await apiFetch("/api/trustpilot/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: String(client.id) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        notify(data?.message || "Trustpilot review sync failed", "error");
        return;
      }

      const incoming = Array.isArray(data.reviews) ? data.reviews : [];
      const baseId = Date.now();
      setReviews((prev) => {
        const existingKeys = new Set(prev.map((r) => `${r.clientId}::${r.externalReviewId || `${r.author}::${r.date}::${r.text}`}`));
        const mapped = incoming
          .map((entry, index) => ({
            id: baseId + index,
            clientId: client.id,
            externalReviewId: entry.externalReviewId || `trustpilot-${baseId + index}`,
            author: entry.author || "Trustpilot User",
            rating: Number(entry.rating || 0),
            text: entry.text || "",
            date: entry.date || new Date().toISOString().split("T")[0],
            platform: "Trustpilot",
            status: entry.status || "pending",
            aiResponse: entry.aiResponse || "",
          }))
          .filter((r) => {
            const key = `${r.clientId}::${r.externalReviewId || `${r.author}::${r.date}::${r.text}`}`;
            if (existingKeys.has(key)) return false;
            existingKeys.add(key);
            return true;
          });
        return [...prev, ...mapped];
      });

      const stats = data.stats || {};
      setClients((prev) => prev.map((c) => c.id === client.id ? {
        ...c,
        trustpilotConnected: true,
        reviewCount: Number(stats.totalReviews ?? c.reviewCount ?? 0),
        responded: Number(stats.responded ?? c.responded ?? 0),
        avgRating: Number(stats.avgRating ?? c.avgRating ?? 0),
      } : c));

      notify(
        tp("Synced {{count}} Trustpilot review(s) for {{client}}")
          .replace("{{count}}", String(incoming.length))
          .replace("{{client}}", client.name)
      );
    } catch (_) {
      notify("Trustpilot sync failed", "error");
    } finally {
      setTrustpilotBusyClientId(null);
    }
  };

  const disconnectTrustpilotClient = (client) => {
    if (!window.confirm(tp("Disconnect Trustpilot for {{client}}?").replace("{{client}}", client.name))) return;
    setClients((prev) => prev.map((c) => c.id === client.id ? { ...c, trustpilotConnected: false } : c));
    notify(tp("Disconnected Trustpilot for {{client}}").replace("{{client}}", client.name));
  };

  // ── Facebook integration ──────────────────────────────────────────────────
  const pollFacebookConnectionStatus = async (clientId, attempts = 8) => {
    for (let i = 0; i < attempts; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 2500));
      try {
        const statusRes = await apiFetch(`/api/facebook/status?clientId=${encodeURIComponent(String(clientId))}`, { cache: "no-store" });
        if (!statusRes.ok) continue;
        const statusData = await statusRes.json().catch(() => ({}));
        if (statusData?.success && statusData.connected) {
          setClients((prev) => prev.map((c) => c.id === clientId ? { ...c, facebookConnected: true } : c));
          notify("Facebook connection confirmed ✅");
          return true;
        }
      } catch (_) {
        // continue polling
      }
    }
    notify("Facebook auth window opened. Complete consent, then click Sync Reviews.", "error");
    return false;
  };

  const startFacebookConnection = async (client) => {
    setFacebookBusyClientId(client.id);
    try {
      const res = await apiFetch("/api/facebook/connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: String(client.id) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success || !data?.authUrl) {
        notify(data?.message || "Facebook connect endpoint is not configured", "error");
        return;
      }
      window.open(data.authUrl, "_blank", "noopener,noreferrer,width=980,height=760");
      notify(tp("Facebook OAuth started for {{client}}").replace("{{client}}", client.name));
      await pollFacebookConnectionStatus(client.id);
    } catch (_) {
      notify("Failed to start Facebook connection", "error");
    } finally {
      setFacebookBusyClientId(null);
    }
  };

  const syncFacebookReviewsForClient = async (client) => {
    setFacebookBusyClientId(client.id);
    try {
      const res = await apiFetch("/api/facebook/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: String(client.id) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        notify(data?.message || "Facebook review sync failed", "error");
        return;
      }
      const incoming = Array.isArray(data.reviews) ? data.reviews : [];
      const baseId = Date.now();
      setReviews((prev) => {
        const existingKeys = new Set(prev.map((r) => `${r.clientId}::${r.externalReviewId || `${r.author}::${r.date}::${r.text}`}`));
        const mapped = incoming
          .map((entry, index) => ({
            id: baseId + index,
            clientId: client.id,
            externalReviewId: entry.externalReviewId || `facebook-${baseId + index}`,
            author: entry.author || "Facebook User",
            rating: Number(entry.rating || 0),
            text: entry.text || "",
            date: entry.date || new Date().toISOString().split("T")[0],
            platform: "Facebook",
            status: entry.status || "pending",
            aiResponse: entry.aiResponse || "",
          }))
          .filter((r) => {
            const key = `${r.clientId}::${r.externalReviewId || `${r.author}::${r.date}::${r.text}`}`;
            if (existingKeys.has(key)) return false;
            existingKeys.add(key);
            return true;
          });
        return [...prev, ...mapped];
      });
      const stats = data.stats || {};
      setClients((prev) => prev.map((c) => c.id === client.id ? {
        ...c,
        facebookConnected: true,
        reviewCount: Number(stats.totalReviews ?? c.reviewCount ?? 0),
        responded: Number(stats.responded ?? c.responded ?? 0),
        avgRating: Number(stats.avgRating ?? c.avgRating ?? 0),
      } : c));
      notify(
        tp("Synced {{count}} Facebook review(s) for {{client}}")
          .replace("{{count}}", String(incoming.length))
          .replace("{{client}}", client.name)
      );
    } catch (_) {
      notify("Facebook sync failed", "error");
    } finally {
      setFacebookBusyClientId(null);
    }
  };

  const disconnectFacebookClient = (client) => {
    if (!window.confirm(tp("Disconnect Facebook for {{client}}?").replace("{{client}}", client.name))) return;
    setClients((prev) => prev.map((c) => c.id === client.id ? { ...c, facebookConnected: false } : c));
    notify(tp("Disconnected Facebook for {{client}}").replace("{{client}}", client.name));
  };

  const openOutreach = (prospect) => {
    const tmpl = OUTREACH_TEMPLATES[0];
    const { body, subject } = fillTemplate(tmpl, prospect, true);
    setOutreachTemplate(tmpl);
    setOutreachRecipient(prospect);
    setOutreachBody(body);
    setOutreachSubject(subject);
    setOutreachAttachment(null);
    setModal("outreach");
  };

  const changeOutreachTemplate = (tmplId) => {
    const tmpl = OUTREACH_TEMPLATES.find(t => t.id === tmplId);
    if (tmpl && outreachRecipient) {
      const { body, subject } = fillTemplate(tmpl, outreachRecipient, true);
      setOutreachTemplate(tmpl);
      setOutreachBody(body);
      setOutreachSubject(subject);
    }
  };

  const sendOutreach = () => {
    const subj = encodeURIComponent(outreachSubject);
    const bodyWithAttachment = outreachAttachment ? `${outreachBody}\n\n${buildAttachmentNote(outreachAttachment)}` : outreachBody;
    const body = encodeURIComponent(bodyWithAttachment);
    window.open(`mailto:${outreachRecipient.email}?subject=${subj}&body=${body}`, "_blank");
    setProspects(p => p.map(pr => pr.id === outreachRecipient.id ? { ...pr, status: "contacted", lastContact: new Date().toISOString().split("T")[0] } : pr));
    notify(`Outreach sent to ${outreachRecipient.contact}${outreachAttachment ? " (add attachment manually in email draft)" : ""}`);
    setModal(null);
  };

  // eslint-disable-next-line no-unused-vars
  const openClientMsg = (client, templateId) => {
    const tmpl = CLIENT_MSG_TEMPLATES.find(t => t.id === templateId) || CLIENT_MSG_TEMPLATES[0];
    const { body, subject } = fillTemplate(tmpl, client);
    setClientMsgTemplate(tmpl);
    setClientMsgRecipient(client);
    setClientMsgBody(body);
    setClientMsgSubject(subject);
    setClientMsgAttachment(null);
    setClientMsgPreview(null);
    setModal("clientMsg");
  };

  const changeClientMsgTemplate = (tmplId) => {
    const tmpl = CLIENT_MSG_TEMPLATES.find(t => t.id === tmplId);
    if (tmpl && clientMsgRecipient) {
      const { body, subject } = fillTemplate(tmpl, clientMsgRecipient);
      setClientMsgTemplate(tmpl);
      setClientMsgBody(body);
      setClientMsgSubject(subject);
      setClientMsgPreview(null);
    }
  };

  const buildClientReminderPayload = (bodyWithAttachment) => ({
    toEmail: clientMsgRecipient.email,
    toName: clientMsgRecipient.contact,
    clientName: clientMsgRecipient.name,
    country: clientMsgRecipient.country,
    amountDisplay: `€${PLANS.find((p) => p.id === clientMsgRecipient.plan)?.price || "99"}`,
    dueDate: clientMsgRecipient.nextPayment || "",
    reference: `${(getBankTransferConfigForCountry(clientMsgRecipient.country)?.referencePrefix || "RPL")}-${String(clientMsgRecipient.id || "NEW")}`,
    subject: clientMsgSubject,
    message: bodyWithAttachment,
  });

  const previewPaymentReminderMsg = async () => {
    if (!clientMsgRecipient) return;
    const bodyWithAttachment = clientMsgAttachment ? `${clientMsgBody}\n\n${buildAttachmentNote(clientMsgAttachment)}` : clientMsgBody;
    try {
      const res = await apiFetch("/api/billing/send-bank-transfer-reminder", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...buildClientReminderPayload(bodyWithAttachment), previewOnly: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        notify(data?.message || "Failed to build reminder preview", "error");
        return;
      }
      setClientMsgPreview({
        subject: data.subject || clientMsgSubject,
        message: data.preview || bodyWithAttachment,
        country: data.country || clientMsgRecipient.country || "",
      });
    } catch (_) {
      notify("Failed to build reminder preview", "error");
    }
  };

  const sendClientMsg = async () => {
    const bodyWithAttachment = clientMsgAttachment ? `${clientMsgBody}\n\n${buildAttachmentNote(clientMsgAttachment)}` : clientMsgBody;

    if (clientMsgTemplate.id === "payment-reminder") {
      try {
        const res = await apiFetch("/api/billing/send-bank-transfer-reminder", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildClientReminderPayload(bodyWithAttachment)),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.success) {
          notify(data?.message || "Failed to send reminder email", "error");
          return;
        }
        notify(`Reminder sent to ${clientMsgRecipient.contact} via ${data.provider || "email"}`);
        setModal(null);
        return;
      } catch (_) {
        notify("Failed to send reminder email", "error");
        return;
      }
    }

    const subj = encodeURIComponent(clientMsgSubject);
    const body = encodeURIComponent(bodyWithAttachment);
    window.open(`mailto:${clientMsgRecipient.email}?subject=${subj}&body=${body}`, "_blank");
    notify(`Message sent to ${clientMsgRecipient.contact}${clientMsgAttachment ? " (add attachment manually in email draft)" : ""}`);
    setModal(null);
  };

  const generateReport = (client) => {
    const plan = PLANS.find(p => p.id === client.plan);
    setModalData({ client, plan });
    setReportFrom(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0]);
    setReportTo(new Date().toISOString().split("T")[0]);
    setReportIntro("Hi {contact},\n\nPlease find your detailed Replai performance report below. This covers your review activity, response performance, and account metrics for the selected period.");
    setModal("report");
  };

  const buildReportMetrics = (client, from = reportFrom, to = reportTo) => {
    const metrics = buildScopedMetrics([client.id], from, to);
    const totalReviews = metrics.totalReviews;
    const responded = metrics.responded;
    const outstanding = Math.max(totalReviews - responded, 0);
    const responseRate = totalReviews > 0 ? Math.round((responded / totalReviews) * 100) : 0;
    const avgResponseTime = client.plan === "enterprise" ? "1.9 hours" : client.plan === "growth" ? "3.2 hours" : "7.1 hours";
    return { totalReviews, responded, outstanding, responseRate, avgResponseTime };
  };

  const buildReportHtml = (client, plan) => {
    const prepared = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    const periodLabel = `${reportFrom} to ${reportTo}`;
    const metrics = buildReportMetrics(client, reportFrom, reportTo);
    const intro = reportIntro.replaceAll("{contact}", client.contact || "there");
    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Replai Report - ${client.name}</title>
  <style>
    body{font-family:Arial,sans-serif;color:#111;margin:32px;line-height:1.6}
    .logo{font-weight:700;font-size:24px;color:#10B981;margin-bottom:4px}
    .logo .dot{color:#6366F1}
    .muted{color:#6b7280;font-size:12px}
    .card{border:1px solid #e5e7eb;border-radius:10px;padding:14px;margin-top:14px}
    .grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}
    .metric{border:1px solid #e5e7eb;border-radius:8px;padding:10px;text-align:center}
    .metric .v{font-size:22px;font-weight:700}
    .metric .l{font-size:11px;color:#6b7280;text-transform:uppercase}
  </style>
</head>
<body>
  <div class="logo">Replai<span class="dot">.</span>work</div>
  <div class="muted">Prepared: ${prepared}</div>
  <h2>Client Performance Report</h2>
  <p><strong>Client:</strong> ${client.name}<br/>
  <strong>Contact:</strong> ${client.contact} (${client.email})<br/>
  <strong>Phone:</strong> ${client.phone || "—"}<br/>
  <strong>Address:</strong> ${formatAddress(client)}<br/>
  <strong>Plan:</strong> ${plan?.name || "N/A"} (€${plan?.price || 0}/mo)<br/>
  <strong>Platforms Managed:</strong> ${(client.platformsManaged || getPlatformsForPlan(client.plan)).join(", ")}<br/>
  <strong>Reporting Period:</strong> ${periodLabel}</p>

  <div class="card">
    <strong>Intro Message</strong>
    <p>${intro.replaceAll("\n", "<br/>")}</p>
  </div>

  <div class="grid">
    <div class="metric"><div class="v">${metrics.totalReviews}</div><div class="l">Reviews Received</div></div>
    <div class="metric"><div class="v">${metrics.responded}</div><div class="l">Responses Generated</div></div>
    <div class="metric"><div class="v">${metrics.responseRate}%</div><div class="l">Response Rate</div></div>
    <div class="metric"><div class="v">${client.avgRating || "—"}</div><div class="l">Average Rating</div></div>
  </div>

  <div class="card">
    <strong>Operational Summary</strong>
    <ul>
      <li>Outstanding reviews requiring response: ${metrics.outstanding}</li>
      <li>Average response turnaround: ${metrics.avgResponseTime}</li>
      <li>Plan SLA: ${plan?.response || "N/A"}</li>
      <li>Current month trend samples: ${(client.monthlyReviews || []).join(" → ") || "No trend data"}</li>
    </ul>
  </div>

  <div class="card">
    <strong>Recommendations</strong>
    <ul>
      <li>Keep weekly review monitoring cadence to sustain response rate above 95%.</li>
      <li>Escalate reviews rated 1–2 stars within 2 hours to client contact.</li>
      <li>Encourage happy customers to leave follow-up Google reviews.</li>
    </ul>
  </div>
</body>
</html>`;
  };

  const openReportPdfWindow = (download = false) => {
    if (!modalData.client) return;
    const html = buildReportHtml(modalData.client, modalData.plan);
    const w = window.open("", "_blank", "width=960,height=800");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
    if (download) {
      setTimeout(() => w.print(), 300);
    }
  };

  const sendReport = () => {
    const metrics = buildReportMetrics(modalData.client, reportFrom, reportTo);
    const intro = reportIntro.replaceAll("{contact}", modalData.client.contact || "there");
    const body = `${intro}\n\nREPORT SUMMARY (${reportFrom} to ${reportTo})\n- Reviews received: ${metrics.totalReviews}\n- Responses generated: ${metrics.responded}\n- Response rate: ${metrics.responseRate}%\n- Average rating: ${modalData.client.avgRating || "N/A"}\n\nPrepared on ${new Date().toLocaleDateString()} by Replai.`;
    const subj = encodeURIComponent(`Detailed report — ${modalData.client.name} (${reportFrom} to ${reportTo})`);
    window.open(`mailto:${modalData.client.email}?subject=${subj}&body=${encodeURIComponent(body)}`, "_blank");
    notify(`Report prepared for ${modalData.client.contact}`);
    setModal(null);
  };

  const getContractEnvelopeForClient = (clientId) => contractEnvelopes.find((env) => env.clientId === clientId) || null;

  const sendContractNotification = async ({ eventType, client, envelope, signedBy = "", signedAt = "" }) => {
    try {
      const browserLang = typeof navigator !== "undefined" ? (navigator.language || "en") : "en";
      const res = await apiFetch("/api/contracts/notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          eventType,
          toEmail: client?.email || "",
          toName: client?.contact || client?.name || "Client",
          clientName: client?.name || "",
          envelopeId: envelope?.envelopeId || envelope?.id || "",
          signingUrl: envelope?.signingUrl || "",
          sentAt: envelope?.sentAt || "",
          signedAt,
          signedBy,
          language: browserLang,
          includeInternalCopy: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        console.warn("Contract notification failed", data);
      }
      return data;
    } catch (error) {
      console.warn("Contract notification error", error);
      return null;
    }
  };

  const sendContractViaDocuSign = async (client) => {
    if (!client) return;
    const existing = getContractEnvelopeForClient(client.id);
    const envelope = existing || {
      id: `env-${client.id}-${Date.now()}`,
      clientId: client.id,
      provider: "DocuSign",
      envelopeId: `DS-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}-${String(client.id).padStart(4, "0")}`,
      signerName: client.contact || "Client Signer",
      signerEmail: client.email || "",
    };
    let nextEnvelope = {
      ...envelope,
      status: "sent",
      effectiveDate: contractEffectiveDate,
      sentAt: new Date().toISOString(),
      signedAt: null,
      signedBy: null,
      businessInfoSnapshot: buildBusinessInfoDraft(businessInfoDraft),
      paymentMethod: client.paymentMethod || "",
      paymentDetail: client.paymentDetailMasked || client.paymentDetail || "",
    };

    try {
      const browserLang = typeof navigator !== "undefined" ? (navigator.language || "en") : "en";
      const linkRes = await apiFetch("/api/contracts/create-sign-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          envelopeId: nextEnvelope.envelopeId,
          clientId: String(client.id),
          clientName: client.name,
          toEmail: client.email,
          toName: client.contact || client.name,
          language: browserLang,
          effectiveDate: contractEffectiveDate,
          paymentMethod: client.paymentMethod || "",
          paymentDetail: client.paymentDetailMasked || client.paymentDetail || "",
          planId: client.plan || "starter",
        }),
      });
      const linkData = await linkRes.json().catch(() => ({}));
      if (linkRes.ok && linkData?.success) {
        nextEnvelope = {
          ...nextEnvelope,
          signingUrl: linkData.signingUrl || "",
          signingToken: linkData.token || "",
        };
      }
    } catch (_) {
      // keep sending flow even if link generation failed
    }

    setContractEnvelopes((prev) => {
      const others = prev.filter((item) => item.clientId !== client.id);
      return [nextEnvelope, ...others];
    });
    setClients((prev) => prev.map((item) => item.id === client.id ? { ...item, contractStatus: "sent" } : item));
    sendContractNotification({ eventType: "contract_sent", client, envelope: nextEnvelope });
    notify(`Contract request prepared for ${client.name}`);
  };

  const openClientContractSigning = (client) => {
    const envelope = getContractEnvelopeForClient(client.id);
    if (!envelope) {
      notify("No DocuSign request has been sent yet", "error");
      return;
    }
    setContractESignForm({ fullName: client.contact || currentClientPortalUser?.name || "", title: "Owner", accept: false });
    setModalData({ client, envelope });
    setModal("contract-sign");
  };

  const completeClientContractSigning = async () => {
    const client = modalData.client;
    const envelope = modalData.envelope;
    if (!client || !envelope) return;
    if (!contractESignForm.fullName.trim() || !contractESignForm.accept) {
      notify("Enter signer name and accept the agreement", "error");
      return;
    }
    const signedAt = new Date().toISOString();
    const signerName = contractESignForm.fullName.trim();
    setContractEnvelopes((prev) => prev.map((item) => item.id === envelope.id ? {
      ...item,
      status: "signed",
      signedAt,
      signedBy: signerName,
      signerTitle: contractESignForm.title.trim() || "Owner",
    } : item));
    setClients((prev) => prev.map((item) => item.id === client.id ? { ...item, contractStatus: "signed" } : item));
    sendContractNotification({
      eventType: "contract_signed",
      client,
      envelope: { ...envelope, signedAt },
      signedBy: signerName,
      signedAt,
    });
    setModal(null);
    notify(`Contract signed via DocuSign for ${client.name}`);
    // If client uses Stripe/card/PayPal and has no subscription yet, send payment setup email
    if (AUTO_BILLING_METHODS.includes(String(client.paymentMethod || "").toLowerCase()) && !client.stripeSubscriptionId) {
      try {
        const browserLang = typeof navigator !== "undefined" ? (navigator.language || "en") : "en";
        await apiFetch("/api/billing/send-payment-setup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clientId: String(client.id),
            clientName: client.name,
            clientEmail: client.email,
            contactName: client.contact || client.name,
            planId: client.plan || "starter",
            paymentMethod: client.paymentMethod,
            envelopeId: envelope.envelopeId || "",
            language: browserLang,
          }),
        });
        notify(`Payment setup email sent to ${client.name}`);
      } catch (_) {
        // Non-fatal
      }
    }
  };

  const buildAnalyticsReport = () => {
    const scopedClients = analyticsReportScope === "all"
      ? activeClients
      : activeClients.filter((c) => String(c.id) === String(analyticsReportScope));
    const scopeClientIds = scopedClients.map((c) => c.id);
    const metrics = buildScopedMetrics(scopeClientIds, analyticsReportFrom, analyticsReportTo);
    const rows = scopedClients.map((client) => {
      const m = buildScopedMetrics([client.id], analyticsReportFrom, analyticsReportTo);
      return {
        client,
        reviews: m.totalReviews,
        responded: m.responded,
        responseRate: m.responseRate,
        avgRating: m.avgRating,
      };
    });
    return {
      scopeLabel: analyticsReportScope === "all" ? "All active clients" : (scopedClients[0]?.name || "Selected client"),
      metrics,
      rows,
      from: analyticsReportFrom,
      to: analyticsReportTo,
    };
  };

  const buildAnalyticsReportHtml = () => {
    const data = buildAnalyticsReport();
    const rowHtml = data.rows.map((r) => `<tr><td>${r.client.name}</td><td>${r.reviews}</td><td>${r.responded}</td><td>${r.responseRate}%</td><td>${r.avgRating}</td></tr>`).join("");
    return `<!doctype html><html><head><meta charset="utf-8"/><title>Analytics Report</title><style>
      body{font-family:Arial,sans-serif;color:#111;margin:28px;line-height:1.6}
      h1{margin:0 0 6px 0} .muted{font-size:12px;color:#6b7280;margin-bottom:14px}
      .grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:14px 0}
      .card{border:1px solid #e5e7eb;border-radius:10px;padding:12px;text-align:center}
      .v{font-size:20px;font-weight:700}.l{font-size:11px;color:#6b7280;text-transform:uppercase}
      table{border-collapse:collapse;width:100%;margin-top:14px}th,td{border:1px solid #e5e7eb;padding:8px;font-size:12px;text-align:left}
      th{background:#f9fafb}
    </style></head><body>
      <h1>Replai Analytics Report</h1>
      <div class="muted">Scope: ${data.scopeLabel} • Period: ${data.from} to ${data.to} • Prepared ${new Date().toLocaleString()}</div>
      <p>${analyticsReportIntro.replaceAll("\n", "<br/>")}</p>
      <div class="grid">
        <div class="card"><div class="v">${data.metrics.totalReviews}</div><div class="l">Reviews</div></div>
        <div class="card"><div class="v">${data.metrics.responded}</div><div class="l">Responses</div></div>
        <div class="card"><div class="v">${data.metrics.responseRate}%</div><div class="l">Response Rate</div></div>
        <div class="card"><div class="v">${data.metrics.avgRating}</div><div class="l">Avg Rating</div></div>
      </div>
      <table><thead><tr><th>Client</th><th>Reviews</th><th>Responses</th><th>Response Rate</th><th>Avg Rating</th></tr></thead><tbody>${rowHtml || '<tr><td colspan="5">No data in selected range</td></tr>'}</tbody></table>
    </body></html>`;
  };

  const openAnalyticsReportWindow = (download = false) => {
    const html = buildAnalyticsReportHtml();
    const w = window.open("", "_blank", "width=960,height=800");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
    if (download) setTimeout(() => w.print(), 300);
  };

  const sendAnalyticsReport = () => {
    const data = buildAnalyticsReport();
    const recipient = analyticsReportRecipient.trim() || (analyticsReportScope !== "all" ? activeClients.find((c) => String(c.id) === String(analyticsReportScope))?.email : "");
    if (!recipient) return notify("Provide recipient email or choose a single client", "error");
    const body = `${analyticsReportIntro}\n\nAnalytics Summary (${data.from} to ${data.to})\n- Scope: ${data.scopeLabel}\n- Reviews: ${data.metrics.totalReviews}\n- Responses: ${data.metrics.responded}\n- Response rate: ${data.metrics.responseRate}%\n- Avg rating: ${data.metrics.avgRating}`;
    const subject = encodeURIComponent(`Analytics report — ${data.scopeLabel} (${data.from} to ${data.to})`);
    window.open(`mailto:${recipient}?subject=${subject}&body=${encodeURIComponent(body)}`, "_blank");
    notify("Analytics report prepared");
  };

  const buildContractHtml = (client, selectedEffectiveDate = contractEffectiveDate) => {
    const plan = PLANS.find(p => p.id === client.plan);
    const envelope = getContractEnvelopeForClient(client.id);
    const providerBusinessInfo = (() => {
      const envelopeStatus = String(envelope?.status || "").toLowerCase();
      if (envelope && (envelopeStatus === "sent" || envelopeStatus === "signed")) {
        if (envelope.businessInfoSnapshot) return buildBusinessInfoDraft(envelope.businessInfoSnapshot);
        return buildBusinessInfoDraft(BIZ);
      }
      return buildBusinessInfoDraft(businessInfoDraft);
    })();
    const preparedDate = new Date().toISOString().split("T")[0];
    const prepared = formatLongDate(preparedDate);
    const effectiveDate = selectedEffectiveDate || preparedDate;
    const effective = formatLongDate(effectiveDate);
    const country = client.country || "Client jurisdiction";
    const privacyLaw = getPrivacyLawByCountry(country);
    const includedPlatforms = client.platformsManaged || getPlatformsForPlan(client.plan);
    const hasManualPlatforms = includedPlatforms.some((p) => p !== "Google Business Profile");
    const escalationContact = `${client.contact || "Client"} | ${client.email || "—"} | ${client.phone || "—"}`;
     const providerSig = envelope?.status === "signed"
      ? `<div style="margin-top:8px;font-weight:700">Executed electronically via ${envelope.provider || "DocuSign"}</div>
        <div class="small">Envelope ID: ${envelope.envelopeId || envelope.id}</div>
        <div class="small">Completed: ${formatLongDate((envelope.signedAt || "").slice(0, 10) || effectiveDate)}</div>`
      : `<div style="margin-top:8px;font-weight:700">Electronic signature routing via ${envelope?.provider || "DocuSign"}</div>
        <div class="small">Provider signature image is not embedded in this PDF.</div>
        <div class="small">Envelope ID: ${envelope?.envelopeId || "Pending generation"}</div>`;
     const clientSignatureBlock = envelope?.status === "signed"
      ? `<div style="margin-top:8px;font-weight:700">Signed electronically by ${envelope.signedBy || envelope.signerName || client.contact || "Client Contact"}</div>
        <div class="small">Provider: ${envelope.provider || "DocuSign"}</div>
        <div class="small">Date: ${formatLongDate((envelope.signedAt || "").slice(0, 10) || effectiveDate)}</div>`
      : `<div style="margin-top:8px;border-bottom:1px solid #000;width:220px"></div>
        <div class="small">Name: ${client.contact || "Client Contact"}</div>
        <div class="small">Date: To be completed in ${envelope?.provider || "DocuSign"}</div>`;
    const contractPlanName = plan?.id === "enterprise" ? "Pro" : (plan?.name || "N/A");
    const contractPaymentMethod = CLIENT_PAYMENT_METHODS.find((m) => m.id === String(client.paymentMethod || "").toLowerCase())?.label || "Not specified";
    const contractPaymentDetail = client.paymentDetailMasked || (client.paymentDetail ? maskPaymentDetail(client.paymentDetail) : "");
    const contractPaymentMethodId = String(client.paymentMethod || "").toLowerCase();
    const contractBankConfig = contractPaymentMethodId === "bank-transfer"
      ? getBankTransferConfigForCountry(client.country)
      : null;
    const contractBankReference = `${(contractBankConfig?.referencePrefix || "RPL")}-${String(client.id || "NEW")}`;
    const contractBankDetails = buildBankTransferInstructionText(contractBankConfig, contractBankReference);
    const contractBankDetailHtml = contractBankDetails
      ? `<li>Local bank transfer details for ${client.country || "the selected country"}:<br/><span style="white-space:pre-line">${contractBankDetails}</span></li>`
      : (contractPaymentMethodId === "bank-transfer"
        ? `<li><strong>Local bank transfer details are not available for ${client.country || "the selected country"}. Please update the payment method.</strong></li>`
        : "");
    const isAutoBilling = AUTO_BILLING_METHODS.includes(String(client.paymentMethod || "").toLowerCase());
    const contractTrialNote = isAutoBilling
      ? `<li>A ${CLIENT_FREE_TRIAL_DAYS}-day free trial applies from the Effective Date; the first charge occurs automatically after the trial ends</li>`
      : "";
    const selectedPlanSla = plan?.id === "enterprise" ? "1 hour" : (plan?.response || "As agreed");
    const selectedPlanMonitoring = getMonitoringFrequencyByPlan(plan?.id);
    const planRows = [
      { id: "starter", name: "Starter", sla: "24 hours", monitoring: "Once daily" },
      { id: "growth", name: "Growth", sla: "4 hours", monitoring: "3× daily" },
      { id: "enterprise", name: "Pro", sla: "1 hour", monitoring: "Continuous monitoring" },
    ].map((p) => {
      const isSelected = p.id === plan?.id;
      return `<tr${isSelected ? ' style="background:#F9FAFB"' : ""}><td>${p.name}</td><td>${p.sla}</td><td>${p.monitoring}</td></tr>`;
    }).join("");
    const manualPlatforms = includedPlatforms.filter((p) => p !== "Google Business Profile");

    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Master Service Agreement - ${client.name}</title>
  <style>
    body{font-family:Arial,sans-serif;color:#111;line-height:1.6;margin:32px}
    .logo{font-size:24px;font-weight:700;color:#10B981}.dot{color:#6366F1}
    h1{margin:8px 0 2px 0} h2{margin:18px 0 8px 0;font-size:17px}
    .box{border:1px solid #e5e7eb;border-radius:10px;padding:12px;margin-top:10px}
    .small{font-size:12px;color:#6b7280}
    table{width:100%;border-collapse:collapse;margin-top:8px}
    th,td{border:1px solid #e5e7eb;padding:8px;text-align:left;font-size:13px}
    th{background:#F3F4F6}
    ul{margin-top:6px}
  </style>
</head>
<body>
  <div class="logo">Replai<span class="dot">.</span>work</div>
  <div class="small">Prepared: ${prepared}</div>
  <h1>Master Service Agreement (MSA)</h1>

  <h2>1. Parties</h2>
  <div class="box">
    <strong>Service Provider</strong><br/>
    ${providerBusinessInfo.name || BIZ.name} (trading as ${providerBusinessInfo.tradingAs || BIZ.tradingAs})<br/>
    ${providerBusinessInfo.address || BIZ.address}<br/>
    Email: ${providerBusinessInfo.email || BIZ.email}<br/>
    Phone: ${providerBusinessInfo.phone || BIZ.phone}
  </div>
  <div class="box">
    <strong>Client</strong><br/>
    ${client.name || "—"}<br/>
    Contact Person: ${client.contact || "—"}<br/>
    Email: ${client.email}<br/>
    Phone: ${client.phone || "—"}<br/>
    Address: ${formatAddress(client)}
  </div>
  <p>Both parties are referred to individually as a “Party” and collectively as the “Parties.”</p>

  <h2>2. Purpose of Agreement</h2>
  <p>This Service Agreement (“Agreement”) governs the provision of review response management services by Replai to the Client for online review platforms selected by the Client.</p>
  <p>The Agreement is prepared on ${prepared} and becomes effective on <strong>${effective}</strong> (“Effective Date”).</p>

  <h2>3. Scope of Services</h2>
  <h3>3.1 Included Services</h3>
  <ul>
    <li>Monitoring of new reviews on supported platforms</li>
    <li>Drafting and publishing responses to reviews</li>
    <li>Tone-matched, brand-aligned communication</li>
    <li>Daily review checks (frequency depends on plan)</li>
    <li>SLA-based response times (as per selected plan)</li>
    <li>Escalation of sensitive reviews before publishing</li>
    <li>Optional multi-platform handling (manual or integrated)</li>
  </ul>

  <h3>3.2 Selected Plan</h3>
  <p>Plan: <strong>${contractPlanName}</strong><br/>
  Monthly Fee: <strong>€${plan?.price || 0}</strong><br/>
  Response SLA: <strong>${selectedPlanSla}</strong><br/>
  Monitoring Frequency: <strong>${selectedPlanMonitoring}</strong></p>

  <p><strong>Included Platforms:</strong></p>
  <ul>
    ${includedPlatforms.map((p) => `<li>${p}</li>`).join("")}
  </ul>

  <h3>3.3 Manual Platform Handling</h3>
  <p>For platforms without API integration, the Client grants Replai limited account access solely for review management.</p>
  <p>Replai will not modify business information, post unrelated content, change ownership/admin roles, access private messages, or perform actions outside review response duties.</p>
  ${hasManualPlatforms ? `<p><strong>Manual platforms in this contract:</strong> ${manualPlatforms.join(", ")}.</p>` : ""}

  <h3>3.4 Billing Preference</h3>
  <p>Preferred payment method: <strong>${contractPaymentMethod}</strong>${contractPaymentDetail ? `<br/>Payment detail on file: <strong>${contractPaymentDetail}</strong>` : ""}</p>

  <h2>4. Client Responsibilities</h2>
  <h3>4.1 Provide Accurate Access</h3>
  <ul>
    <li>Grant necessary access to review platforms</li>
    <li>Maintain valid login credentials</li>
    <li>Notify Replai of any access changes</li>
  </ul>
  <h3>4.2 Respond to Escalations</h3>
  <p>Replai will escalate reviews containing legal threats, refund demands, severe complaints, allegations of misconduct, or safety/health concerns.</p>
  <p>Client must respond within 48 hours to avoid delays.</p>
  <p><strong>Escalation Contact:</strong> ${escalationContact}</p>
  <h3>4.3 Provide Brand Guidelines (Optional)</h3>
  <ul>
    <li>Brand voice guidelines</li>
    <li>Example responses</li>
    <li>Prohibited phrases</li>
  </ul>

  <h2>5. Service Levels (SLA)</h2>
  <p>Replai will respond to new reviews within the SLA defined in the selected plan:</p>
  <table>
    <thead><tr><th>Plan</th><th>SLA</th><th>Monitoring Frequency</th></tr></thead>
    <tbody>${planRows}</tbody>
  </table>
  <p><strong>SLA applies during business hours: 09:00–18:00 CET, Monday–Friday.</strong></p>
  <p>Extended hours can be added as a paid add-on.</p>

  <h2>6. Data Protection & Privacy</h2>
  <h3>6.1 Compliance</h3>
  <p>Replai processes Client data strictly for service delivery and complies with <strong>${privacyLaw}</strong>, as applicable to ${country}.</p>
  <h3>6.2 No Data Selling</h3>
  <ul>
    <li>No sale of client data</li>
    <li>No sharing with third parties except essential subprocessors</li>
    <li>No use of private review content for AI model training</li>
  </ul>
  <h3>6.3 Confidentiality</h3>
  <p>Both Parties agree to keep all non-public information confidential.</p>

  <h2>7. Intellectual Property</h2>
  <p><strong>7.1 Ownership of Responses:</strong> All review responses created by Replai become the property of the Client upon publication.</p>
  <p><strong>7.2 License to Use Client Materials:</strong> The Client grants Replai a limited license to use brand guidelines, logos, and tone-of-voice materials solely to deliver the service.</p>

  <h2>8. Fees & Payment Terms</h2>
  <ul>
    <li>Monthly subscription billed in advance</li>
    <li>Payment method: <strong>${contractPaymentMethod}</strong>${contractPaymentDetail ? ` (${contractPaymentDetail})` : ""}</li>
    ${contractBankDetailHtml}
    ${contractTrialNote}
    <li>Prices listed in EUR unless otherwise stated</li>
    <li>Late payment: services may be paused after 7 days until payment is received</li>
    <li>No refunds for partial months or unused service periods</li>
  </ul>

  <h2>9. Term & Termination</h2>
  <ul>
    <li>Agreement continues month-to-month unless terminated</li>
    <li>Client may cancel with 7-day notice before the next billing cycle</li>
    <li>Replai may terminate with 14-day notice or immediately for abusive behavior, illegal content, non-payment, or misuse of access</li>
    <li>On termination, access permissions must be revoked and no further responses are published</li>
  </ul>

  <h2>10. Liability & Disclaimers</h2>
  <ul>
    <li>Replai’s total liability is limited to one month of service fees</li>
    <li>No guarantee of improved ratings, review removal, or customer satisfaction outcomes</li>
    <li>Replai is not responsible for platform outages, API limitations, or policy changes</li>
  </ul>

  <h2>11. Governing Law</h2>
  <p>This Agreement is governed by the laws of Germany, unless the Parties mutually agree otherwise in writing. Disputes will be resolved in the courts of Munich, Germany.</p>

  <h2>12. Entire Agreement</h2>
  <p>This Agreement supersedes all prior proposals, emails, or verbal discussions.</p>

  <h2>13. Signatures</h2>
  <div class="box">
    <strong>Service Provider Signature</strong>
    ${providerSig}
    <div class="small">Name: ${providerBusinessInfo.name || BIZ.name}</div>
    <div class="small">Date: ${effective}</div>
  </div>
  <div class="box">
    <strong>Client Signature</strong>
    ${clientSignatureBlock}
  </div>
</body>
</html>`;
  };

  const openContractPdf = (client, download = false, effectiveDate = contractEffectiveDate) => {
    const html = buildContractHtml(client, effectiveDate);
    const w = window.open("", "_blank", "width=960,height=800");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
    if (download) setTimeout(() => w.print(), 300);
  };

  const deleteUnsignedContract = (clientId) => {
    const c = clients.find(x => x.id === clientId);
    if (!c || c.contractStatus === "signed") return;
    if (!window.confirm(`Delete unsigned contract for ${c.name}?`)) return;
    setClients((prev) => prev.map((x) => (x.id === clientId ? { ...x, contractStatus: "none", plan: null } : x)));
    setContractEnvelopes((prev) => prev.filter((item) => item.clientId !== clientId));
    notify("Unsigned contract deleted", "error");
  };

  const navItems = [
    { id: "queue", icon: "📥", label: t("tabs.queue", "Reviews"), badge: pendingCount },
    { id: "workboard", icon: "🗂️", label: t("tabs.workboard", "Workboard") },
    { id: "staff", icon: "🧑‍💼", label: t("tabs.staff", "Staff") },
    { id: "messages", icon: "✉️", label: "Staff Messages", badge: staffProblemThreads.filter((thread) => canAccessProblemThread(thread, currentUser) && thread.senderId !== currentUser?.id).length },
    { id: "clients", icon: "👥", label: t("tabs.clients", "Clients") },
    { id: "selfserve", icon: "🛟", label: t("tabs.selfserve", "Self-Serve Ops"), badge: openSupportRequests.length },
    { id: "outreach", icon: "📣", label: t("tabs.outreach", "Outreach") },
    { id: "contracts", icon: "📄", label: t("tabs.contracts", "Contracts") },
    { id: "analytics", icon: "📊", label: t("tabs.analytics", "Analytics") },
    { id: "mailbox", icon: "📧", label: "Mailbox", badge: dashboardMailbox.messages.length > 0 ? dashboardMailbox.messages.length : 0 },
    { id: "security-events", icon: "🛡️", label: "Security Events" },
    { id: "registrations", icon: "📋", label: "Registrations", badge: registrationNotifications.filter((n) => !registrationsSeenIds.includes(n.id)).length || 0 },
    { id: "cms", icon: "🖊️", label: "Landing Page CMS" },
    { id: "settings", icon: "⚙️", label: t("tabs.settings", "Settings") },
    { id: "credentials", icon: "🔐", label: "Credentials" },
  ];

  const tabPermissions = {
    queue: "review.respond",
    workboard: "review.respond",
    staff: "staff.password.reset",
    messages: "staff.chat",
    clients: "client.view",
    selfserve: "selfserve.clients.view",
    outreach: "client.manage",
    contracts: "contract.view",
    analytics: "analytics.view",
    mailbox: "mailbox.access",
    "security-events": "settings.view",
    registrations: "registrations.view",
    cms: "cms.edit",
    settings: "settings.view",
    credentials: "credentials.console",
  };
  const visibleNavItems = navItems.filter((item) => {
    if (item.id === "staff") return can("staff.manage") || can("staff.password.reset");
    if (item.id === "messages") return canStaffMessage;
    if (item.id === "mailbox") return mailboxAccessAllowed;
    if (item.id === "security-events") return currentUser?.role === "admin";
    if (item.id === "credentials") return currentUser?.role === "admin" || can("credentials.console");
    if (item.id === "registrations") return currentUser?.role === "admin" || can("registrations.view");
    if (item.id === "cms") return currentUser?.role === "admin" || can("cms.edit");
    return can(tabPermissions[item.id]);
  });

  if (!authReady || !agencyAccessChecked) {
    return (
      <div id="replai-dashboard-root" style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#0A0A0F", color: "#9CA3AF", fontFamily: "'DM Sans', sans-serif" }}>
        <div>{tp("Preparing secure login...")}</div>
      </div>
    );
  }

  if (!currentUser && !currentClientPortalUser) {
    return (
      <div id="replai-dashboard-root" style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#0A0A0F", color: "#E5E5E5", fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ width: agencyAccessEnabled ? 460 : 560, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 22, color: brandColor }}>{brandLogo ? `${brandLogo} ` : ""}{brandName}{agencyBranding.enabled ? "" : <span style={{ color: "#6366F1" }}>.</span>}{agencyBranding.enabled ? "" : "work"}</div>
            <select value={uiLanguage} onChange={(e) => setAppLanguage(e.target.value)} style={{ background: "#1E1E2A", border: "1px solid #2D2D3A", borderRadius: 8, color: "#E5E5E5", padding: "6px 8px", fontSize: 11 }}>
              {UI_LANGUAGE_OPTIONS.map((lang) => <option key={`login-lang-${lang.code}`} value={lang.code}>{lang.label}</option>)}
            </select>
          </div>
          <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 16 }}>{agencyAccessEnabled ? tp("Choose your workspace") : tp("Client self-serve portal")}</div>

          {requestedAgencyAccess && !agencyAccessEnabled && <div style={{ marginBottom: 16, padding: 12, background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, fontSize: 12, color: "#FCA5A5" }}>Private agency access link is invalid or expired.</div>}

          {agencyAccessEnabled && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
            <button onClick={() => { setLoginMode("staff"); setClientLoginError(""); setClientRegisterError(""); setForgotPasswordMode(false); setForgotPasswordSent(false); }} style={{ background: loginMode === "staff" ? "#1E1E2A" : "#0A0A0F", color: "#E5E5E5", border: `1px solid ${loginMode === "staff" ? "#6366F1" : "#2D2D3A"}`, borderRadius: 8, padding: "10px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Agency Dashboard</button>
            <button onClick={() => { setLoginMode("client"); setLoginError(""); setForgotPasswordMode(false); setForgotPasswordSent(false); }} style={{ background: loginMode === "client" ? "#1E1E2A" : "#0A0A0F", color: "#E5E5E5", border: `1px solid ${loginMode === "client" ? "#10B981" : "#2D2D3A"}`, borderRadius: 8, padding: "10px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Client Self-Serve</button>
          </div>}

          {loginMode === "staff" && agencyAccessEnabled && <>
            {!forgotPasswordMode && <>
              <div style={{ marginBottom: 10 }}><label style={lbl}>Username</label><input value={loginForm.username} onChange={(e) => { setLoginForm((p) => ({ ...p, username: e.target.value })); setLoginError(""); }} style={inp} /></div>
              <div style={{ marginBottom: 14 }}><label style={lbl}>Password</label><div style={{ position: "relative" }}><input type={showStaffLoginPassword ? "text" : "password"} value={loginForm.password} onChange={(e) => { setLoginForm((p) => ({ ...p, password: e.target.value })); setLoginError(""); }} style={{ ...inp, paddingRight: 36 }} /><button type="button" onClick={() => setShowStaffLoginPassword((v) => !v)} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 14, padding: 0, lineHeight: 1 }}>{showStaffLoginPassword ? "🙈" : "👁"}</button></div></div>
              {loginError && <div style={{ marginBottom: 14, padding: 10, background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 6, fontSize: 13, color: "#FCA5A5" }}>{loginError}</div>}
              <button onClick={login} disabled={!authReady || staffSyncLoading} style={{ width: "100%", background: "linear-gradient(135deg,#6366F1,#8B5CF6)", color: "#fff", border: "none", borderRadius: 8, padding: "11px 14px", fontWeight: 700, cursor: (authReady && !staffSyncLoading) ? "pointer" : "not-allowed", opacity: (authReady && !staffSyncLoading) ? 1 : 0.6 }}>{staffSyncLoading ? "Loading accounts..." : "Log In to Agency Dashboard"}</button>
              <button onClick={() => { setForgotPasswordMode(true); setForgotPasswordSent(false); }} style={{ marginTop: 10, background: "none", border: "none", color: "#93C5FD", fontSize: 12, cursor: "pointer", padding: 0 }}>Forgot password?</button>
            </>}
            {forgotPasswordMode && <>
              <div style={{ marginBottom: 10 }}><label style={lbl}>Username or Email</label><input value={forgotPasswordForm.usernameOrEmail} onChange={(e) => setForgotPasswordForm({ usernameOrEmail: e.target.value })} style={inp} /></div>
              <button onClick={requestForgotPassword} style={{ width: "100%", background: "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "11px 14px", fontWeight: 700, cursor: "pointer" }}>Request New Password via Email</button>
              {forgotPasswordSent && <div style={{ marginTop: 10, fontSize: 12, color: "#A7F3D0" }}>If the account exists, a reset email request has been sent.</div>}
              <button onClick={() => { setForgotPasswordMode(false); setForgotPasswordSent(false); }} style={{ marginTop: 10, background: "none", border: "none", color: "#93C5FD", fontSize: 12, cursor: "pointer", padding: 0 }}>Back to login</button>
            </>}
          </>}

          {loginMode === "client" && <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 16 }}>
              <button onClick={() => { setClientAuthScreen("login"); setClientRegisterError(""); setManagedRegisterError(""); }} style={{ background: clientAuthScreen === "login" ? "#1E1E2A" : "#0A0A0F", color: "#E5E5E5", border: `1px solid ${clientAuthScreen === "login" ? "#10B981" : "#2D2D3A"}`, borderRadius: 8, padding: "10px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Log In</button>
              <button onClick={() => { setClientAuthScreen("register"); setClientLoginError(""); setManagedRegisterError(""); }} style={{ background: clientAuthScreen === "register" ? "#1E1E2A" : "#0A0A0F", color: "#E5E5E5", border: `1px solid ${clientAuthScreen === "register" ? "#10B981" : "#2D2D3A"}`, borderRadius: 8, padding: "10px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Self-Serve</button>
              <button onClick={() => { setClientAuthScreen("managed-register"); setClientLoginError(""); setClientRegisterError(""); }} style={{ background: clientAuthScreen === "managed-register" ? "#1E1E2A" : "#0A0A0F", color: "#E5E5E5", border: `1px solid ${clientAuthScreen === "managed-register" ? "#10B981" : "#2D2D3A"}`, borderRadius: 8, padding: "10px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Managed</button>
            </div>

            {clientAuthScreen === "login" && <>
              {!forgotPasswordMode && <>
                <div style={{ marginBottom: 10 }}><label style={lbl}>Client Username</label><input value={clientLoginForm.username} onChange={(e) => { setClientLoginForm((p) => ({ ...p, username: e.target.value })); setClientLoginError(""); }} style={inp} /></div>
                <div style={{ marginBottom: 14 }}><label style={lbl}>Password</label><div style={{ position: "relative" }}><input type={showClientLoginPassword ? "text" : "password"} value={clientLoginForm.password} onChange={(e) => { setClientLoginForm((p) => ({ ...p, password: e.target.value })); setClientLoginError(""); }} style={{ ...inp, paddingRight: 36 }} /><button type="button" onClick={() => setShowClientLoginPassword((v) => !v)} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 14, padding: 0, lineHeight: 1 }}>{showClientLoginPassword ? "🙈" : "👁"}</button></div></div>
                {clientLoginError && <div style={{ marginBottom: 14, padding: 10, background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 6, fontSize: 13, color: "#FCA5A5" }}>{clientLoginError}</div>}
                <button onClick={loginClientPortal} disabled={!authReady} style={{ width: "100%", background: "linear-gradient(135deg,#10B981,#14B8A6)", color: "#fff", border: "none", borderRadius: 8, padding: "11px 14px", fontWeight: 700, cursor: authReady ? "pointer" : "not-allowed", opacity: authReady ? 1 : 0.6 }}>Log In to Client Dashboard</button>
                <button onClick={() => { setForgotPasswordMode(true); setForgotPasswordSent(false); }} style={{ marginTop: 10, background: "none", border: "none", color: "#93C5FD", fontSize: 12, cursor: "pointer", padding: 0 }}>Forgot password?</button>
              </>}
              {forgotPasswordMode && <>
                <div style={{ marginBottom: 10 }}><label style={lbl}>Username or Email</label><input value={forgotPasswordForm.usernameOrEmail} onChange={(e) => setForgotPasswordForm({ usernameOrEmail: e.target.value })} style={inp} /></div>
                <button onClick={requestForgotPassword} style={{ width: "100%", background: "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "11px 14px", fontWeight: 700, cursor: "pointer" }}>Request New Password via Email</button>
                {forgotPasswordSent && <div style={{ marginTop: 10, fontSize: 12, color: "#A7F3D0" }}>If the account exists, a reset email request has been sent.</div>}
                <button onClick={() => { setForgotPasswordMode(false); setForgotPasswordSent(false); }} style={{ marginTop: 10, background: "none", border: "none", color: "#93C5FD", fontSize: 12, cursor: "pointer", padding: 0 }}>Back to login</button>
              </>}
            </>}

            {clientAuthScreen === "register" && <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                <div><label style={lbl}>Business name</label><input value={clientRegisterForm.businessName} onChange={(e) => setClientRegisterForm((p) => ({ ...p, businessName: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>Contact person</label><input value={clientRegisterForm.contact} onChange={(e) => setClientRegisterForm((p) => ({ ...p, contact: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>Email</label><input value={clientRegisterForm.email} onChange={(e) => setClientRegisterForm((p) => ({ ...p, email: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>Phone</label><input value={clientRegisterForm.phone} onChange={(e) => setClientRegisterForm((p) => ({ ...p, phone: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>Street address + number</label><input value={clientRegisterForm.addressLine1} onChange={(e) => setClientRegisterForm((p) => ({ ...p, addressLine1: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>Postcode</label><input value={clientRegisterForm.postcode} onChange={(e) => setClientRegisterForm((p) => ({ ...p, postcode: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>Country</label><input value={clientRegisterForm.country} onChange={(e) => setClientRegisterForm((p) => ({ ...p, country: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>City</label><input value={clientRegisterForm.city} onChange={(e) => setClientRegisterForm((p) => ({ ...p, city: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>Username</label><input value={clientRegisterForm.username} onChange={(e) => setClientRegisterForm((p) => ({ ...p, username: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>Package</label><select value={clientRegisterForm.plan} onChange={(e) => setClientRegisterForm((p) => ({ ...p, plan: e.target.value }))} style={inp}>{PLANS.map((plan) => {
                  const localizedPlan = localizedPricing?.[plan.id];
                  const labelPrice = localizedPlan?.formatted || `€${plan.price}`;
                  return <option key={`register-plan-${plan.id}`} value={plan.id}>{plan.name} — {labelPrice}/mo</option>;
                })}</select></div>
                <div><label style={lbl}>Password</label><input type="password" value={clientRegisterForm.password} onChange={(e) => setClientRegisterForm((p) => ({ ...p, password: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>Confirm password</label><input type="password" value={clientRegisterForm.confirmPassword} onChange={(e) => setClientRegisterForm((p) => ({ ...p, confirmPassword: e.target.value }))} style={inp} /></div>
                <div style={{ gridColumn: "1 / span 2" }}>
                  <label style={lbl}>Payment method</label>
                  <select value={clientRegisterForm.paymentMethod} onChange={(e) => setClientRegisterForm((p) => ({ ...p, paymentMethod: e.target.value }))} style={inp}>
                    {availableRegisterPaymentMethods.map((method) => <option key={`register-pay-${method.id}`} value={method.id}>{method.label}</option>)}
                  </select>
                  <div style={{ marginTop: 6, fontSize: 11, color: "#9CA3AF" }}>
                    {tp("Billing country is verified server-side")}{billingConfig.detectedCountry ? ` (${billingConfig.detectedCountry})` : ""}. {tp("Country field edits do not reduce pricing.")}
                  </div>
                </div>
                <div style={{ gridColumn: "1 / span 2" }}>
                  {clientRegisterForm.paymentMethod === "stripe" ? (
                    <div>
                      <label style={lbl}>Credit Card Payment</label>
                      <Elements stripe={stripePromise}>
                        <StripePaymentForm
                          planId={clientRegisterForm.plan}
                          country={clientRegisterForm.country}
                          email={clientRegisterForm.email}
                          businessName={clientRegisterForm.businessName}
                          onSuccess={(result) => {
                            setClientRegisterForm((p) => ({
                              ...p,
                              stripeCustomerId: result.stripeCustomerId,
                              stripeSubscriptionId: result.stripeSubscriptionId,
                              paymentProcessed: true,
                              paymentDetail: "•••• •••• •••• processed",
                            }));
                          }}
                          onError={(error) => {
                            alert(`Payment Error: ${error}`);
                          }}
                        />
                      </Elements>
                    </div>
                  ) : (
                    <div>
                      <label style={lbl}>{registerPaymentMeta.label}</label>
                      <input value={clientRegisterForm.paymentDetail} onChange={(e) => setClientRegisterForm((p) => ({ ...p, paymentDetail: e.target.value }))} placeholder={registerPaymentMeta.placeholder} style={inp} />
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: 10, background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 10, padding: 10, fontSize: 12, color: "#A7F3D0", lineHeight: 1.7 }}>
                Free trial is <strong>{CLIENT_FREE_TRIAL_DAYS} days</strong>. Your selected payment method will be automatically charged when the trial ends unless cancelled before renewal.
              </div>
              {!AUTO_BILLING_METHODS.includes(clientRegisterForm.paymentMethod) && <div style={{ marginBottom: 10, background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 10, padding: 10, fontSize: 12, color: "#FDE68A", lineHeight: 1.7 }}>Automatic backend charging is currently available for Stripe/Credit Card/PayPal. For {CLIENT_PAYMENT_METHODS.find((m) => m.id === clientRegisterForm.paymentMethod)?.label || "this method"}, Replai support will complete billing setup after registration.</div>}

              <div style={{ marginBottom: 10, background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 8 }}>Self-Serve Terms & Conditions</div>
                <div style={{ maxHeight: 180, overflowY: "auto", fontSize: 12, color: "#D1D5DB", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                  {CLIENT_SELF_SERVE_TERMS_TEXT}
                </div>
                <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: "#E5E5E5", marginTop: 10 }}>
                  <input type="checkbox" checked={clientRegisterForm.acceptTerms} onChange={(e) => setClientRegisterForm((p) => ({ ...p, acceptTerms: e.target.checked }))} style={{ marginTop: 3 }} />
                  <span>I accept the self-serve terms and authorize Replai to provision my workspace.</span>
                </label>
              </div>

              {clientRegisterError && <div style={{ marginBottom: 14, padding: 10, background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 6, fontSize: 13, color: "#FCA5A5" }}>{clientRegisterError}</div>}
              <button onClick={registerClientPortalAccount} disabled={clientRegisterProcessing} style={{ width: "100%", background: "linear-gradient(135deg,#10B981,#14B8A6)", color: "#fff", border: "none", borderRadius: 8, padding: "11px 14px", fontWeight: 700, cursor: clientRegisterProcessing ? "wait" : "pointer", opacity: clientRegisterProcessing ? 0.75 : 1 }}>{clientRegisterProcessing ? "Opening secure checkout..." : "Create Self-Serve Account"}</button>
            </>}

            {clientAuthScreen === "managed-register" && <>
              <div style={{ marginBottom: 10, background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.35)", borderRadius: 10, padding: 10, fontSize: 12, color: "#BFD3FE", lineHeight: 1.7 }}>
                Tell us about your business and we'll reach out within 24 hours to set up your managed account, including trial access and billing.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                <div><label style={lbl}>Business name</label><input value={managedRegisterForm.businessName} onChange={(e) => setManagedRegisterForm((p) => ({ ...p, businessName: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>Contact person</label><input value={managedRegisterForm.contact} onChange={(e) => setManagedRegisterForm((p) => ({ ...p, contact: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>Email</label><input value={managedRegisterForm.email} onChange={(e) => setManagedRegisterForm((p) => ({ ...p, email: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>Phone</label><input value={managedRegisterForm.phone} onChange={(e) => setManagedRegisterForm((p) => ({ ...p, phone: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>Street address + number</label><input value={managedRegisterForm.addressLine1} onChange={(e) => setManagedRegisterForm((p) => ({ ...p, addressLine1: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>Postcode</label><input value={managedRegisterForm.postcode} onChange={(e) => setManagedRegisterForm((p) => ({ ...p, postcode: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>Country</label><input value={managedRegisterForm.country} onChange={(e) => setManagedRegisterForm((p) => ({ ...p, country: e.target.value }))} style={inp} /></div>
                <div><label style={lbl}>City</label><input value={managedRegisterForm.city} onChange={(e) => setManagedRegisterForm((p) => ({ ...p, city: e.target.value }))} style={inp} /></div>
                <div style={{ gridColumn: "1 / span 2" }}><label style={lbl}>Preferred package</label><select value={managedRegisterForm.preferredPlan} onChange={(e) => setManagedRegisterForm((p) => ({ ...p, preferredPlan: e.target.value }))} style={inp}>{PLANS.map((plan) => <option key={`managed-plan-${plan.id}`} value={plan.id}>{plan.name} — €{plan.price}/mo</option>)}</select></div>
                <div><label style={lbl}>Preferred payment method</label><select value={managedRegisterForm.paymentMethod} onChange={(e) => setManagedRegisterForm((p) => ({ ...p, paymentMethod: e.target.value }))} style={inp}>{CLIENT_PAYMENT_METHODS.map((method) => <option key={`managed-payment-${method.id}`} value={method.id}>{method.label}</option>)}</select></div>
                <div><label style={lbl}>{getPaymentDetailMeta(managedRegisterForm.paymentMethod).label}</label><input value={managedRegisterForm.paymentDetail} onChange={(e) => setManagedRegisterForm((p) => ({ ...p, paymentDetail: e.target.value }))} placeholder={getPaymentDetailMeta(managedRegisterForm.paymentMethod).placeholder} style={inp} /></div>
              </div>
              {managedRegisterError && <div style={{ marginBottom: 14, padding: 10, background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 6, fontSize: 13, color: "#FCA5A5" }}>{managedRegisterError}</div>}
              <button onClick={registerManagedAccount} disabled={managedRegisterProcessing} style={{ width: "100%", background: "linear-gradient(135deg,#10B981,#14B8A6)", color: "#fff", border: "none", borderRadius: 8, padding: "11px 14px", fontWeight: 700, cursor: managedRegisterProcessing ? "wait" : "pointer", opacity: managedRegisterProcessing ? 0.75 : 1 }}>{managedRegisterProcessing ? "Submitting..." : "Request Managed Account"}</button>
            </>}
          </>}

          {authChallenge && <div style={{ marginTop: 14, padding: 12, background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.35)", borderRadius: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#E5E5E5", marginBottom: 4 }}>{authChallenge.title}</div>
            <div style={{ fontSize: 12, color: "#C7D2FE", marginBottom: 10 }}>{authChallenge.message}</div>
            <div style={{ display: "grid", gap: 8 }}>
              <input
                placeholder="Authenticator code (6 digits)"
                value={authChallengeForm.otpCode}
                onChange={(e) => setAuthChallengeForm((prev) => ({ ...prev, otpCode: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                style={inp}
              />
              <input
                placeholder="Backup code (e.g. ABCD-EFGH)"
                value={authChallengeForm.backupCode}
                onChange={(e) => setAuthChallengeForm((prev) => ({ ...prev, backupCode: e.target.value.toUpperCase() }))}
                style={inp}
              />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button onClick={verifyAuthChallenge} style={{ flex: 1, background: "linear-gradient(135deg,#6366F1,#8B5CF6)", color: "#fff", border: "none", borderRadius: 8, padding: "10px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Verify & Continue</button>
              <button onClick={() => { setAuthChallenge(null); setAuthChallengeForm({ otpCode: "", backupCode: "" }); }} style={{ flex: 1, background: "transparent", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "10px 12px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
            </div>
          </div>}

          {revealedBackupCodes && <div style={{ marginTop: 14, padding: 12, background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#A7F3D0", marginBottom: 6 }}>Recovery Backup Codes</div>
            <div style={{ fontSize: 11, color: "#D1FAE5", marginBottom: 8 }}>Generated for {revealedBackupCodes.ownerName}. Save these now, they are shown only once.</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 }}>
              {revealedBackupCodes.codes.map((code) => (
                <div key={`shown-backup-code-${code}`} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: "7px 8px", fontSize: 12, color: "#E5E5E5", fontFamily: "'Space Mono', monospace" }}>{code}</div>
              ))}
            </div>
            <button onClick={() => setRevealedBackupCodes(null)} style={{ background: "transparent", color: "#A7F3D0", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 8, padding: "8px 10px", fontSize: 11, cursor: "pointer" }}>I have saved these codes</button>
          </div>}
        </div>
      </div>
    );
  }

  if (currentClientPortalUser?.mustChangePassword) {
    return (
      <div id="replai-dashboard-root" style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#0A0A0F", color: "#E5E5E5", fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ width: 520, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 24 }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 20, color: "#10B981", marginBottom: 8 }}>Replai<span style={{ color: "#6366F1" }}>.</span>work</div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{tp("Set a new password to continue")}</div>
          <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 14 }}>{tp("For security, owner accounts must change the initial password on first login.")}</div>
          <div style={{ marginBottom: 10 }}><label style={lbl}>{tp("Current password")}</label><div style={{ position: "relative" }}><input type={showClientMustChangePw.current ? "text" : "password"} value={clientMustChangeForm.currentPassword} onChange={(e) => setClientMustChangeForm((p) => ({ ...p, currentPassword: e.target.value }))} style={{ ...inp, paddingRight: 36 }} /><button type="button" onClick={() => setShowClientMustChangePw((v) => ({ ...v, current: !v.current }))} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 14 }}>{showClientMustChangePw.current ? "🙈" : "👁"}</button></div></div>
          <div style={{ marginBottom: 10 }}><label style={lbl}>{tp("New password")}</label><div style={{ position: "relative" }}><input type={showClientMustChangePw.next ? "text" : "password"} value={clientMustChangeForm.nextPassword} onChange={(e) => setClientMustChangeForm((p) => ({ ...p, nextPassword: e.target.value }))} style={{ ...inp, paddingRight: 36 }} /><button type="button" onClick={() => setShowClientMustChangePw((v) => ({ ...v, next: !v.next }))} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 14 }}>{showClientMustChangePw.next ? "🙈" : "👁"}</button></div></div>
          <div style={{ marginBottom: 14 }}><label style={lbl}>{tp("Confirm new password")}</label><div style={{ position: "relative" }}><input type={showClientMustChangePw.confirm ? "text" : "password"} value={clientMustChangeForm.confirmPassword} onChange={(e) => setClientMustChangeForm((p) => ({ ...p, confirmPassword: e.target.value }))} style={{ ...inp, paddingRight: 36 }} /><button type="button" onClick={() => setShowClientMustChangePw((v) => ({ ...v, confirm: !v.confirm }))} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 14 }}>{showClientMustChangePw.confirm ? "🙈" : "👁"}</button></div></div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={logout} style={{ flex: 1, background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: 10, cursor: "pointer" }}>{tp("Logout")}</button>
            <button onClick={updateClientOwnerPasswordOnFirstLogin} style={{ flex: 1, background: "#10B981", color: "#fff", border: "none", borderRadius: 8, padding: 10, fontWeight: 700, cursor: "pointer" }}>{tp("Update password")}</button>
          </div>
        </div>
      </div>
    );
  }

  if (currentClientPortalUser && !hasAllRecommendedSecurityLayers(currentClientPortalUser)) {
    const progress = recommendedSecurityProgress(currentClientPortalUser);
    return (
      <>
      <div id="replai-dashboard-root" style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#0A0A0F", color: "#E5E5E5", fontFamily: "'DM Sans', sans-serif", padding: 20 }}>
        <div style={{ width: "min(760px, 96vw)", background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 24 }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 20, color: "#10B981", marginBottom: 8 }}>{tp("Security Setup Required")}</div>
          <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 12 }}>{tp("Every account must enable all recommended protection layers before continuing.")}</div>
          <div style={{ marginBottom: 14, fontSize: 12, color: "#D1D5DB" }}>Progress: <strong>{progress.enabled}/{progress.total}</strong> enabled ({progress.pct}%)</div>
          <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
            {SECURITY_LAYER_DEFINITIONS.map((layer) => {
              const active = !!currentSecurityLayers[layer.key];
              return (
                <div key={`client-sec-gate-${layer.key}`} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{layer.label}</div>
                    <div style={{ fontSize: 11, color: "#9CA3AF" }}>{layer.detail}</div>
                  </div>
                  <button disabled={active} onClick={() => updateOwnSecurityLayer(layer.key, true)} style={{ background: active ? "rgba(16,185,129,0.15)" : "#1E1E2A", color: active ? "#34D399" : "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 11px", fontSize: 12, fontWeight: 700, cursor: active ? "default" : "pointer" }}>{active ? "Active" : "Activate"}</button>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={logout} style={{ flex: 1, background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: 10, cursor: "pointer" }}>{tp("Logout")}</button>
            <button onClick={activateAllRecommendedSecurityLayers} style={{ flex: 1, background: "linear-gradient(135deg,#10B981,#14B8A6)", color: "#fff", border: "none", borderRadius: 8, padding: 10, fontWeight: 700, cursor: "pointer" }}>{tp("Activate All Recommended Layers")}</button>
          </div>
        </div>
      </div>
      {mfaSetupModal && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}><div style={{ width: "min(500px, 96vw)", background: "#111118", border: "1px solid #1E1E2A", borderRadius: 16, padding: 26, maxHeight: "90vh", overflowY: "auto" }}><div style={{ fontSize: 11, color: "#10B981", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, fontFamily: "'Space Mono', monospace" }}>Authenticator MFA</div><div style={{ fontSize: 19, fontWeight: 700, color: "#fff", marginBottom: 6 }}>Set Up Your Authenticator App</div><div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 18, lineHeight: 1.7 }}>Use Google Authenticator, Microsoft Authenticator, Authy, or any TOTP app to scan this QR code. A new 6-digit code will be generated every 30 seconds.</div><div style={{ display: "grid", gap: 14 }}><div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: "12px 14px" }}><div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}><div style={{ width: 24, height: 24, borderRadius: 99, background: "#10B981", color: "#fff", fontSize: 11, fontWeight: 700, display: "grid", placeItems: "center", flexShrink: 0 }}>1</div><div style={{ fontSize: 12, color: "#D1D5DB" }}>Download an authenticator app on your smartphone:<br/><span style={{ color: "#9CA3AF" }}>Google Authenticator, Microsoft Authenticator, Authy, or similar.</span></div></div></div><div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: "12px 14px" }}><div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 4 }}><div style={{ width: 24, height: 24, borderRadius: 99, background: "#10B981", color: "#fff", fontSize: 11, fontWeight: 700, display: "grid", placeItems: "center", flexShrink: 0 }}>2</div><div style={{ fontSize: 12, color: "#D1D5DB" }}>Open your app → tap + → <strong style={{ color: "#fff" }}>Scan a QR code</strong></div></div>{mfaSetupModal.qrDataUrl && <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginTop: 10 }}><img src={mfaSetupModal.qrDataUrl} alt="MFA QR Code" style={{ width: 200, height: 200, borderRadius: 10, background: "#fff", padding: 8 }} /></div>}<div style={{ marginTop: 10 }}><div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 4 }}>Or enter this key manually in your app:</div><div style={{ background: "#1E1E2A", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 12px", fontFamily: "'Space Mono', monospace", fontSize: 12, color: "#E5E5E5", letterSpacing: "0.08em", wordBreak: "break-all" }}>{formatBase32ForDisplay(mfaSetupModal.base32Secret)}</div></div></div><div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: "12px 14px" }}><div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}><div style={{ width: 24, height: 24, borderRadius: 99, background: "#10B981", color: "#fff", fontSize: 11, fontWeight: 700, display: "grid", placeItems: "center", flexShrink: 0 }}>3</div><div style={{ fontSize: 12, color: "#D1D5DB" }}>Enter the <strong style={{ color: "#fff" }}>6-digit code</strong> from your app to confirm setup</div></div><input placeholder="000000" value={mfaSetupCode} onChange={(e) => setMfaSetupCode(e.target.value.replace(/\D/g,"").slice(0,6))} onKeyDown={(e) => { if (e.key==="Enter") completeMfaSetup(); }} maxLength={6} style={{ background: "#1E1E2A", border: "1px solid #2D2D3A", borderRadius: 8, color: "#fff", padding: "10px 14px", fontSize: 24, fontFamily: "'Space Mono', monospace", letterSpacing: "0.3em", width: "100%", outline: "none", textAlign: "center", boxSizing: "border-box" }} />{mfaSetupModal.error && <div style={{ marginTop: 8, padding: "8px 10px", background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, fontSize: 12, color: "#FCA5A5" }}>{mfaSetupModal.error}</div>}</div></div><div style={{ display: "flex", gap: 10, marginTop: 18 }}><button onClick={completeMfaSetup} disabled={mfaSetupCode.length < 6} style={{ flex: 1, background: mfaSetupCode.length < 6 ? "#374151" : "linear-gradient(135deg,#10B981,#14B8A6)", color: "#fff", border: "none", borderRadius: 10, padding: "12px 16px", fontWeight: 700, fontSize: 14, cursor: mfaSetupCode.length < 6 ? "not-allowed" : "pointer", opacity: mfaSetupCode.length < 6 ? 0.7 : 1 }}>Verify & Activate MFA</button><button onClick={cancelMfaSetup} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 10, padding: "12px 16px", cursor: "pointer", fontSize: 13 }}>Cancel</button></div></div></div>}
      </>
    );
  }

  if (currentClientPortalUser) {
    return (
      <div id="replai-dashboard-root" style={{ minHeight: "100vh", background: "#0A0A0F", color: "#E5E5E5", fontFamily: "'DM Sans', sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap" rel="stylesheet" />
        {notif && <div style={{ position: "fixed", top: 20, right: 20, zIndex: 999, background: notif.type === "success" ? "#065F46" : "#991B1B", color: "#fff", padding: "12px 20px", borderRadius: 10, fontSize: 13, fontWeight: 500, boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}>{notif.type === "success" ? "✓" : "✕"} {notif.msg}</div>}

        <div style={{ borderBottom: "1px solid #1E1E2A", background: "#111118", position: "sticky", top: 0, zIndex: 10 }}>
          <div style={{ maxWidth: 1200, margin: "0 auto", padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 20, color: brandColor }}>{brandLogo ? `${brandLogo} ` : ""}{brandName}{agencyBranding.enabled ? "" : <span style={{ color: "#6366F1" }}>.</span>}{agencyBranding.enabled ? "" : "work"}</div>
              <div style={{ fontSize: 12, color: "#9CA3AF" }}>Client Dashboard • {clientPortalClient?.name || currentClientPortalUser.clientName} • <span style={{ color: isClientPortalOwner ? "#A7F3D0" : "#BFDBFE" }}>{isClientPortalOwner ? "Owner" : "Member"}</span></div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <select value={uiLanguage} onChange={(e) => setAppLanguage(e.target.value)} style={{ background: "#1E1E2A", border: "1px solid #2D2D3A", borderRadius: 8, color: "#E5E5E5", padding: "8px 10px", fontSize: 12 }}>
                {UI_LANGUAGE_OPTIONS.map((lang) => <option key={`client-ui-lang-${lang.code}`} value={lang.code}>{lang.label}</option>)}
              </select>
              {[{ id: "overview", label: "Overview" }, { id: "reviews", label: "Reviews" }, { id: "analytics", label: "Analytics" }, { id: "support", label: "Support" }, { id: "account", label: "Account" }].map((item) => (
                <button key={item.id} onClick={() => setClientPortalTab(item.id)} style={{ background: clientPortalTab === item.id ? "#1E1E2A" : "transparent", color: clientPortalTab === item.id ? "#fff" : "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 11px", fontSize: 12, cursor: "pointer" }}>{item.label}</button>
              ))}
              <button onClick={logout} style={{ background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "8px 11px", fontSize: 12, cursor: "pointer" }}>Logout</button>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px" }}>
          {!clientPortalClient && <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18, color: "#FCA5A5" }}>No client profile is linked to this account yet.</div>}

          {clientPortalClient && clientPortalTab === "overview" && <>
            {clientPortalStats.pending > 0 && (
              <div onClick={() => setClientPortalTab("reviews")} style={{ marginBottom: 14, padding: "12px 18px", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 12, display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                <span style={{ fontSize: 22 }}>📋</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#FCD34D" }}>{clientPortalStats.pending} response{clientPortalStats.pending !== 1 ? "s" : ""} awaiting your approval</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>Click to review and approve — responses can't be published until you approve them.</div>
                </div>
                <span style={{ fontSize: 18, color: "#FCD34D" }}>→</span>
              </div>
            )}
            {(() => {
              const negRevs = clientPortalReviews.filter((r) => Number(r.rating || 0) <= 2 && r.status === "pending");
              if (!negRevs.length) return null;
              return (
                <div onClick={() => setClientPortalTab("reviews")} style={{ marginBottom: 14, padding: "12px 18px", background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 12, display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                  <span style={{ fontSize: 22 }}>⚠️</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#FCA5A5" }}>{negRevs.length} negative review{negRevs.length !== 1 ? "s" : ""} (1-2★) need{negRevs.length === 1 ? "s" : ""} attention</div>
                    <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>Responding quickly to low-star reviews protects your reputation. Click to view.</div>
                  </div>
                  <span style={{ fontSize: 18, color: "#FCA5A5" }}>→</span>
                </div>
              );
            })()}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 16 }}>
              {[{ l: "Total Reviews", v: clientPortalStats.total, c: "#6366F1" }, { l: "Pending", v: clientPortalStats.pending, c: "#F59E0B" }, { l: "Published", v: clientPortalStats.published, c: "#10B981" }, { l: "Coverage", v: `${clientPortalCoverage}%`, c: "#0EA5E9" }, { l: "Rating Growth", v: formatSignedDelta(getClientRatingGrowth(clientPortalClient)), c: getClientRatingGrowth(clientPortalClient) >= 0 ? "#34D399" : "#F87171" }].map((s) => (
                <div key={s.l} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{s.l}</div>
                  <div style={{ fontSize: 22, color: s.c, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{s.v}</div>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 14 }}>
              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 8 }}>Review Volume (6 months)</div>
                <TinyBars data={clientPortalVolumeTrend} color="#6366F1" maxValue={Math.max(...clientPortalVolumeTrend, 1)} />
                <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", fontSize: 11, color: "#6B7280" }}>{monthLabels.map((m) => <span key={`client-vol-${m}`}>{m}</span>)}</div>
              </div>
              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 8 }}>Response Coverage Trend</div>
                <TinyTrend data={clientPortalResponseTrend} color="#10B981" fill="rgba(16,185,129,0.18)" height={94} />
                <div style={{ marginTop: 10, fontSize: 12, color: "#9CA3AF" }}>Current: <span style={{ color: "#fff", fontWeight: 700 }}>{clientPortalCoverage}%</span></div>
              </div>
            </div>

            {/* Per-platform health cards */}
            {(() => {
              const platforms = clientPortalClient.platformsManaged || getPlatformsForPlan(clientPortalClient.plan);
              const platformMeta = {
                "Google Business Profile": { icon: "🔵", short: "Google",      color: "#4285F4", connected: clientPortalClient.connected },
                "Facebook":                { icon: "🟦", short: "Facebook",    color: "#1877F2", connected: !!clientPortalClient.facebookConnected },
                "Trustpilot":              { icon: "🟢", short: "Trustpilot",  color: "#00B67A", connected: clientPortalClient.trustpilotConnected },
                "Yelp":                    { icon: "🔴", short: "Yelp",        color: "#D32323", connected: !!clientPortalClient.yelpConnected },
                "TripAdvisor":             { icon: "🟤", short: "TripAdvisor", color: "#34E0A1", connected: !!clientPortalClient.tripadvisorConnected },
              };
              const connectedCount = platforms.filter((p) => (platformMeta[p] || {}).connected).length;
              const totalCount = platforms.length;
              const coveragePct = totalCount ? Math.round((connectedCount / totalCount) * 100) : 0;
              return (
                <div style={{ marginTop: 14, marginBottom: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>Platform Coverage</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ fontSize: 11, color: "#9CA3AF" }}>{connectedCount} of {totalCount} connected</div>
                      <div style={{ width: 120, height: 7, background: "#1E1E2A", borderRadius: 99, overflow: "hidden" }}>
                        <div style={{ width: `${coveragePct}%`, height: "100%", borderRadius: 99, background: coveragePct === 100 ? "#10B981" : coveragePct >= 50 ? "#F59E0B" : "#EF4444" }} />
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: coveragePct === 100 ? "#10B981" : coveragePct >= 50 ? "#F59E0B" : "#EF4444", fontFamily: "'Space Mono', monospace" }}>{coveragePct}%</div>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(platforms.length, 3)}, 1fr)`, gap: 10 }}>
                    {platforms.map((p) => {
                      const meta = platformMeta[p] || { icon: "⚪", short: p, color: "#6B7280", connected: false };
                      const pReviews = clientPortalReviews.filter((r) => r.platform === (meta.short === "Google" ? "Google" : meta.short) || r.platform === p);
                      const pTotal = pReviews.length;
                      const pRating = pTotal ? Number((pReviews.reduce((s, r) => s + Number(r.rating || 0), 0) / pTotal).toFixed(1)) : null;
                      const pPublished = pReviews.filter((r) => r.status === "published").length;
                      return (
                        <div key={p} style={{ background: "#111118", border: `1px solid ${meta.connected ? "rgba(99,102,241,0.2)" : "#1E1E2A"}`, borderRadius: 12, padding: 14 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
                            <span style={{ fontSize: 16 }}>{meta.icon}</span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{meta.short}</span>
                            <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99, background: meta.connected ? "rgba(52,211,153,0.12)" : "rgba(107,114,128,0.15)", color: meta.connected ? "#34D399" : "#6B7280" }}>{meta.connected ? "Active" : "Not connected"}</span>
                          </div>
                          {meta.connected ? <>
                            <div style={{ fontSize: 22, fontWeight: 700, color: meta.color, fontFamily: "'Space Mono',monospace" }}>{pRating !== null ? `⭐ ${pRating}` : "—"}</div>
                            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>{pTotal} review{pTotal !== 1 ? "s" : ""} · {pPublished} published</div>
                          </> : <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>Contact support to activate</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {(() => {
              const curRating = Number(clientPortalClient.avgRating || 0);
              const ratingGoal = curRating >= 4.8 ? 5.0 : curRating >= 4.5 ? 4.8 : curRating >= 4.0 ? 4.5 : curRating >= 3.5 ? 4.0 : 3.8;
              const ratingPct = Math.min(100, ratingGoal > 0 ? Math.round((curRating / ratingGoal) * 100) : 0);
              const reviewMilestones = [10, 25, 50, 100, 250, 500, 1000];
              const curCount = clientPortalStats.total;
              const nextMilestone = reviewMilestones.find((m) => m > curCount) || curCount + 50;
              const prevMilestone = [...reviewMilestones].reverse().find((m) => m <= curCount) || 0;
              const milestonePct = nextMilestone > prevMilestone ? Math.round(((curCount - prevMilestone) / (nextMilestone - prevMilestone)) * 100) : 100;
              const coverageGoal = 90;
              const coveragePct2 = Math.min(100, Math.round((clientPortalCoverage / coverageGoal) * 100));
              return (
                <div style={{ marginTop: 14, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 18 }}>
                  <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>Review Goals & Milestones</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <div style={{ fontSize: 12, color: "#E5E5E5", fontWeight: 600 }}>Star Rating Goal</div>
                        <div style={{ fontSize: 12, color: ratingPct >= 100 ? "#10B981" : "#F59E0B", fontFamily: "'Space Mono', monospace" }}>{ratingPct >= 100 ? "Achieved!" : `${ratingPct}%`}</div>
                      </div>
                      <div style={{ height: 8, background: "#1E1E2A", borderRadius: 99, overflow: "hidden", marginBottom: 6 }}>
                        <div style={{ width: `${ratingPct}%`, height: "100%", borderRadius: 99, background: ratingPct >= 100 ? "#10B981" : "linear-gradient(90deg,#F59E0B,#FBBF24)" }} />
                      </div>
                      <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                        Current: <strong style={{ color: "#fff" }}>⭐ {curRating > 0 ? curRating.toFixed(1) : "—"}</strong> · Goal: <strong style={{ color: "#FCD34D" }}>⭐ {ratingGoal.toFixed(1)}</strong>
                      </div>
                      {ratingPct >= 100 && <div style={{ marginTop: 4, fontSize: 11, color: "#10B981", fontWeight: 700 }}>Goal reached! New target: ⭐ {Math.min(5, ratingGoal + 0.3).toFixed(1)}</div>}
                    </div>
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <div style={{ fontSize: 12, color: "#E5E5E5", fontWeight: 600 }}>Review Milestone</div>
                        <div style={{ fontSize: 12, color: milestonePct >= 100 ? "#10B981" : "#6366F1", fontFamily: "'Space Mono', monospace" }}>{milestonePct}%</div>
                      </div>
                      <div style={{ height: 8, background: "#1E1E2A", borderRadius: 99, overflow: "hidden", marginBottom: 6 }}>
                        <div style={{ width: `${milestonePct}%`, height: "100%", borderRadius: 99, background: "linear-gradient(90deg,#6366F1,#8B5CF6)" }} />
                      </div>
                      <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                        You have: <strong style={{ color: "#fff" }}>{curCount} reviews</strong> · Next milestone: <strong style={{ color: "#A5B4FC" }}>{nextMilestone}</strong>
                      </div>
                    </div>
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <div style={{ fontSize: 12, color: "#E5E5E5", fontWeight: 600 }}>Response Coverage</div>
                        <div style={{ fontSize: 12, color: coveragePct2 >= 100 ? "#10B981" : "#0EA5E9", fontFamily: "'Space Mono', monospace" }}>{coveragePct2 >= 100 ? "Goal met!" : `${coveragePct2}%`}</div>
                      </div>
                      <div style={{ height: 8, background: "#1E1E2A", borderRadius: 99, overflow: "hidden", marginBottom: 6 }}>
                        <div style={{ width: `${coveragePct2}%`, height: "100%", borderRadius: 99, background: coveragePct2 >= 100 ? "#10B981" : "linear-gradient(90deg,#0EA5E9,#38BDF8)" }} />
                      </div>
                      <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                        Current: <strong style={{ color: "#fff" }}>{clientPortalCoverage}%</strong> · Goal: <strong style={{ color: "#7DD3FC" }}>{coverageGoal}%</strong>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
              <div style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.16), rgba(20,184,166,0.1))", border: "1px solid rgba(16,185,129,0.22)", borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 11, color: "#A7F3D0", textTransform: "uppercase", marginBottom: 8 }}>Your Package</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 6 }}>{clientPortalPackage?.name || "Custom"}</div>
                <div style={{ fontSize: 13, color: "#D1FAE5" }}>€{clientPortalPackage?.price || 0}/month</div>
              </div>
              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 8 }}>Included Platforms</div>
                <div style={{ fontSize: 13, color: "#E5E5E5", lineHeight: 1.7 }}>{clientPortalPackage?.platforms || (clientPortalClient.platformsManaged || []).join(", ") || "Google Business Profile"}</div>
              </div>
              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 8 }}>Billing Method</div>
                <div style={{ fontSize: 16, color: "#fff", fontWeight: 700 }}>{clientPortalPaymentMethodLabel}</div>
                <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 6 }}>Stored detail: {clientPortalClient.paymentDetailMasked || "On file"}</div>
                <div style={{ fontSize: 12, color: clientTrialActive ? "#93C5FD" : "#9CA3AF", marginTop: 4 }}>{clientTrialActive ? `Free trial ends: ${clientPortalClient.trialEndDate || "—"}` : `Next charge: ${clientPortalClient.nextPayment || "—"}`}</div>
                <div style={{ fontSize: 11, color: "#A7F3D0", marginTop: 4 }}>{clientPortalClient.autoChargeAfterTrial ? "Auto-charge after trial is enabled" : "Auto-charge is disabled"}</div>
              </div>
            </div>
          </>}

          {clientPortalClient && clientPortalTab === "reviews" && <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
              <select value={clientPortalFilterStatus} onChange={(e) => setClientPortalFilterStatus(e.target.value)} style={inp}>
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="published">Published</option>
                <option value="rejected">Rejected</option>
              </select>
              <select value={clientPortalFilterPlatform} onChange={(e) => setClientPortalFilterPlatform(e.target.value)} style={inp}>
                <option value="all">All Platforms</option>
                {Array.from(new Set(clientPortalReviews.map((r) => r.platform))).map((p) => <option key={`cp-platform-${p}`} value={p}>{p}</option>)}
              </select>
              <input placeholder="Search reviews or responses" value={clientPortalSearch} onChange={(e) => setClientPortalSearch(e.target.value)} style={inp} />
            </div>

            {clientPortalFilteredReviews.map((r) => (
              <div key={`client-portal-review-${r.id}`} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, marginBottom: 12, overflow: "hidden" }}>
                <div style={{ padding: "10px 14px", borderBottom: "1px solid #1E1E2A", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{r.author}</div>
                    <div style={{ fontSize: 11, color: "#6B7280" }}>{r.platform} • {r.date}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}><Stars r={r.rating} /><Badge type={r.status} /></div>
                </div>
                <div style={{ padding: 14 }}>
                  <div style={{ marginBottom: 12, color: "#D1D5DB", fontSize: 13, lineHeight: 1.7 }}><strong style={{ color: "#fff" }}>Customer review:</strong><br />{r.text}</div>
                  <div style={{ marginBottom: 12, background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 12, color: "#A7F3D0", fontSize: 13, lineHeight: 1.7 }}><strong style={{ color: "#10B981" }}>Suggested response:</strong><br />{r.aiResponse || "No draft yet"}</div>

                  {clientPortalEditReviewId === r.id && (
                    <div style={{ marginBottom: 10 }}>
                      <textarea value={clientPortalEditText} onChange={(e) => setClientPortalEditText(e.target.value)} style={{ ...inp, minHeight: 90, resize: "vertical" }} />
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <button onClick={clientPortalSaveEdit} style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 8, padding: "7px 11px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Save Edit</button>
                        <button onClick={() => { setClientPortalEditReviewId(null); setClientPortalEditText(""); }} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
                      </div>
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <select value={getReviewTone(r.id)} onChange={(e) => setReviewTone(r.id, e.target.value)} style={{ ...inp, width: "auto", minWidth: 210, padding: "7px 10px", fontSize: 12 }}>
                      {REVIEW_RESPONSE_TONES.map((tone) => <option key={`client-portal-tone-${r.id}-${tone.id}`} value={tone.id}>{tone.label}</option>)}
                    </select>
                    <button onClick={() => regenerateResponse(r.id)} style={{ background: "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: "pointer" }}>Regenerate</button>
                    <button onClick={() => clientPortalOpenEdit(r)} style={{ background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: "pointer" }}>Edit Draft</button>
                    {r.status !== "approved" && <button onClick={() => clientPortalApproveReview(r.id)} style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 8, padding: "7px 11px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Approve</button>}
                    {r.status !== "published" && isClientPortalOwner && <button onClick={() => clientPortalPublishReview(r.id)} style={{ background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "7px 11px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Publish</button>}
                    {r.status !== "pending" && <button onClick={() => clientPortalReopenReview(r.id)} style={{ background: "transparent", color: "#F59E0B", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: "pointer" }}>Move to Pending</button>}
                  </div>
                  {!isClientPortalOwner && <div style={{ marginTop: 8, fontSize: 11, color: "#6B7280" }}>Member role can draft and approve. Publishing is restricted to owner.</div>}
                </div>
              </div>
            ))}

            {clientPortalFilteredReviews.length === 0 && <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 16, color: "#9CA3AF" }}>No reviews match your current filters.</div>}
          </>}

          {clientPortalClient && clientPortalTab === "analytics" && <>
            {/* KPI row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10 }}>
              {(() => {
                const cpAvgH = avgResponseHoursByClient(clientPortalClient);
                const cpSlaC = slaComplianceByClient(clientPortalClient);
                return [
                  { l: "Total Reviews", v: clientPortalStats.total, c: "#6366F1" },
                  { l: "Responded", v: clientPortalStats.published, c: "#10B981" },
                  { l: "Response Rate", v: `${clientPortalCoverage}%`, c: clientPortalCoverage >= 80 ? "#10B981" : clientPortalCoverage >= 50 ? "#F59E0B" : "#EF4444" },
                  { l: "Avg Response Time", v: `${cpAvgH}h`, c: "#0EA5E9" },
                  { l: "SLA Compliance", v: `${cpSlaC}%`, c: cpSlaC >= 80 ? "#22C55E" : cpSlaC >= 60 ? "#F59E0B" : "#EF4444" },
                ].map((s) => (
                  <div key={`cp-kpi-${s.l}`} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 10, padding: 14 }}>
                    <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{s.l}</div>
                    <div style={{ fontSize: 22, color: s.c, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{s.v}</div>
                  </div>
                ));
              })()}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 8 }}>Rating Trend</div>
                <TinyTrend data={clientPortalVolumeTrend.map((v, i) => Math.max(3.2, Math.min(5, clientPortalStats.avgRating - 0.2 + i * 0.08 + (v ? 0.04 : -0.06))))} color="#EC4899" fill="rgba(236,72,153,0.2)" height={104} />
                <div style={{ marginTop: 10, fontSize: 12, color: "#9CA3AF" }}>Onboarding rating: <strong style={{ color: "#E5E5E5" }}>{getClientOnboardingRating(clientPortalClient).toFixed(2)}</strong> • Current: <strong style={{ color: "#E5E5E5" }}>{Number(clientPortalClient.avgRating || 0).toFixed(2)}</strong> • Delta: <strong style={{ color: getClientRatingGrowth(clientPortalClient) >= 0 ? "#34D399" : "#F87171" }}>{formatSignedDelta(getClientRatingGrowth(clientPortalClient))}</strong></div>
              </div>
              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 8 }}>Sentiment Mix</div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  {[{ l: "Positive", v: clientPortalSentiment.positive, c: "#10B981" }, { l: "Neutral", v: clientPortalSentiment.neutral, c: "#F59E0B" }, { l: "Negative", v: clientPortalSentiment.negative, c: "#EF4444" }].map((s) => (
                    <div key={`cp-sent-${s.l}`} style={{ flex: 1, background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12 }}>
                      <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{s.l}</div>
                      <div style={{ fontSize: 20, color: s.c, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{s.v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 10, height: 8, display: "flex", borderRadius: 99, overflow: "hidden", background: "#1E1E2A" }}>
                  {clientPortalStats.total > 0 && <>
                    {clientPortalSentiment.positive > 0 && <div style={{ width: `${Math.round((clientPortalSentiment.positive / clientPortalStats.total) * 100)}%`, background: "#10B981" }} />}
                    {clientPortalSentiment.neutral > 0 && <div style={{ width: `${Math.round((clientPortalSentiment.neutral / clientPortalStats.total) * 100)}%`, background: "#F59E0B" }} />}
                    {clientPortalSentiment.negative > 0 && <div style={{ width: `${Math.round((clientPortalSentiment.negative / clientPortalStats.total) * 100)}%`, background: "#EF4444" }} />}
                  </>}
                </div>
              </div>
            </div>
            {/* Volume trend + platform breakdown */}
            <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 14 }}>
              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 8 }}>Review Volume</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 8 }}>Monthly Review Volume (6 months)</div>
                <TinyBars data={clientPortalVolumeTrend} color="#6366F1" maxValue={Math.max(...clientPortalVolumeTrend, 1)} />
                <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", fontSize: 10, color: "#6B7280" }}>{monthLabels.map((m) => <span key={`cp-vol-lbl-${m}`}>{m}</span>)}</div>
              </div>
              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 8 }}>Platform Breakdown</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 8 }}>Reviews by Platform</div>
                {(() => {
                  const platformMap = clientPortalReviews.reduce((acc, r) => {
                    const p = r.platform || "Google";
                    acc[p] = (acc[p] || 0) + 1;
                    return acc;
                  }, {});
                  const platformEntries = Object.entries(platformMap).sort((a, b) => b[1] - a[1]);
                  const topCount = Math.max(...platformEntries.map((x) => x[1]), 1);
                  if (!platformEntries.length) return <div style={{ fontSize: 12, color: "#6B7280" }}>No reviews yet.</div>;
                  return platformEntries.map(([p, cnt]) => (
                    <div key={`cp-plat-${p}`} style={{ marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#D1D5DB", marginBottom: 3 }}>
                        <span>{p}</span><span style={{ color: "#6366F1", fontWeight: 700 }}>{cnt} ({clientPortalStats.total ? Math.round((cnt / clientPortalStats.total) * 100) : 0}%)</span>
                      </div>
                      <div style={{ height: 7, background: "#1E1E2A", borderRadius: 99 }}><div style={{ width: `${Math.round((cnt / topCount) * 100)}%`, height: "100%", borderRadius: 99, background: "linear-gradient(90deg,#6366F1,#8B5CF6)" }} /></div>
                    </div>
                  ));
                })()}
              </div>
            </div>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
              <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 8 }}>Response Performance</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
                {(() => {
                  const cpAvgH = avgResponseHoursByClient(clientPortalClient);
                  const cpSlaC = slaComplianceByClient(clientPortalClient);
                  const cpTarget = planTargetHours[clientPortalClient.plan] || 12;
                  return [
                    { l: "Response Rate", v: `${clientPortalCoverage}%`, c: clientPortalCoverage >= 80 ? "#10B981" : clientPortalCoverage >= 50 ? "#F59E0B" : "#EF4444", desc: "Reviews responded to" },
                    { l: "Avg Response Time", v: `${cpAvgH}h`, c: "#0EA5E9", desc: `Target: ${cpTarget}h (${clientPortalClient.plan || "starter"} plan)` },
                    { l: "SLA Compliance", v: `${cpSlaC}%`, c: cpSlaC >= 80 ? "#22C55E" : cpSlaC >= 60 ? "#F59E0B" : "#EF4444", desc: "Responses within SLA" },
                  ].map((s) => (
                    <div key={`cp-resp-${s.l}`} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 14 }}>
                      <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>{s.l}</div>
                      <div style={{ fontSize: 24, color: s.c, fontWeight: 700, fontFamily: "'Space Mono', monospace", marginBottom: 2 }}>{s.v}</div>
                      <div style={{ fontSize: 10, color: "#6B7280" }}>{s.desc}</div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* ── Download Performance Report ── */}
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 4 }}>Download Performance Report</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF" }}>Export your review data and performance metrics as a CSV file.</div>
                </div>
                <button
                  onClick={() => {
                    const cpRevs = reviews.filter((r) => String(r.clientId) === String(clientPortalClient.id));
                    const cpAvgH = avgResponseHoursByClient(clientPortalClient);
                    const cpSlaC = slaComplianceByClient(clientPortalClient);
                    const responded = cpRevs.filter((r) => r.status === "approved" || r.status === "published").length;
                    const responseRate = cpRevs.length ? Math.round((responded / cpRevs.length) * 100) : 0;
                    const posCount = cpRevs.filter((r) => Number(r.rating || 0) >= 4).length;
                    const neuCount = cpRevs.filter((r) => Number(r.rating || 0) === 3).length;
                    const negCount = cpRevs.filter((r) => Number(r.rating || 0) <= 2).length;
                    const ratingGrowth = getClientRatingGrowth(clientPortalClient);
                    const now = new Date().toLocaleDateString("en-GB");

                    // Summary header rows
                    const summaryRows = [
                      ["Replai — Performance Report"],
                      [`Business: ${clientPortalClient.name || clientPortalClient.username}`],
                      [`Report generated: ${now}`],
                      [],
                      ["== Summary =="],
                      ["Metric", "Value"],
                      ["Total Reviews", cpRevs.length],
                      ["Responded", responded],
                      ["Response Rate", `${responseRate}%`],
                      ["Avg Response Time", `${cpAvgH}h`],
                      ["SLA Compliance", `${cpSlaC}%`],
                      ["Current Rating", Number(clientPortalClient.avgRating || 0).toFixed(2)],
                      ["Rating Change", ratingGrowth >= 0 ? `+${ratingGrowth.toFixed(2)}` : ratingGrowth.toFixed(2)],
                      ["Positive Reviews (4-5★)", posCount],
                      ["Neutral Reviews (3★)", neuCount],
                      ["Negative Reviews (1-2★)", negCount],
                      [],
                      ["== Reviews =="],
                      ["Date", "Rating", "Platform", "Author", "Status", "Response Time (h)", "Review Text"],
                    ];

                    const reviewRows = cpRevs.map((r) => {
                      const created = r.createdAt ? new Date(r.createdAt).toLocaleDateString("en-GB") : "";
                      const responded = r.respondedAt && r.createdAt ? Math.round((new Date(r.respondedAt) - new Date(r.createdAt)) / 3600000) : "";
                      const text = (r.reviewText || r.text || "").replace(/"/g, '""');
                      return [created, r.rating || "", r.platform || "Google", r.reviewer || r.authorName || "", r.status || "", responded, `"${text}"`];
                    });

                    const csvLines = [...summaryRows, ...reviewRows].map((row) => row.join(","));
                    const blob = new Blob([csvLines.join("\n")], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `replai-report-${(clientPortalClient.name || clientPortalClient.username || "client").replace(/\s+/g, "-").toLowerCase()}-${now.replace(/\//g, "-")}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                >
                  Download CSV Report
                </button>
              </div>
            </div>
          </>}

          {clientPortalClient && clientPortalTab === "support" && <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18 }}>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 10 }}>Escalate to Replai</div>
                <div style={{ display: "grid", gap: 10 }}>
                  <select value={clientSupportForm.category} onChange={(e) => setClientSupportForm((p) => ({ ...p, category: e.target.value }))} style={inp}>
                    {SUPPORT_CATEGORY_OPTIONS.map((option) => <option key={`support-cat-${option}`} value={option}>{option}</option>)}
                  </select>
                  <select value={clientSupportForm.priority} onChange={(e) => setClientSupportForm((p) => ({ ...p, priority: e.target.value }))} style={inp}>
                    {SUPPORT_PRIORITY_OPTIONS.map((option) => <option key={`support-pri-${option}`} value={option}>{option}</option>)}
                  </select>
                  <input placeholder="Subject" value={clientSupportForm.subject} onChange={(e) => setClientSupportForm((p) => ({ ...p, subject: e.target.value }))} style={inp} />
                  <textarea placeholder="Describe the issue or support needed" value={clientSupportForm.message} onChange={(e) => setClientSupportForm((p) => ({ ...p, message: e.target.value }))} style={{ ...inp, minHeight: 120, resize: "vertical" }} />
                  <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 8 }}>Attachments (up to 5 files, 8MB each)</div>
                    <input type="file" multiple onChange={onClientSupportAttachmentsChange} style={{ ...inp, padding: 8 }} />
                    {clientSupportAttachments.length > 0 && <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {clientSupportAttachments.map((file) => <button key={`support-file-${file.name}`} onClick={() => removeClientSupportAttachment(file.name)} style={{ background: "#111118", border: "1px solid #2D2D3A", color: "#D1D5DB", borderRadius: 99, fontSize: 11, padding: "4px 8px", cursor: "pointer" }}>📎 {file.name} ✕</button>)}
                    </div>}
                  </div>
                  <button onClick={submitClientSupportRequest} style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 8, padding: "10px 14px", fontWeight: 700, cursor: "pointer" }}>Send to Replai Support</button>
                </div>
              </div>
              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18 }}>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 10 }}>What you can escalate</div>
                <div style={{ fontSize: 13, color: "#D1D5DB", lineHeight: 1.9 }}>
                  • Billing issues and payment method changes<br />
                  • Account access or owner lockouts<br />
                  • Platform connection problems<br />
                  • Sensitive reviews needing operator support<br />
                  • Technical problems inside the workspace
                </div>
                <div style={{ marginTop: 10, background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 8 }}>Need to change your payment option? Send a billing request and our team will update it from the agency dashboard.</div>
                  <button onClick={requestClientPaymentMethodChange} style={{ background: "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Create Payment Change Request</button>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 14, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Support Inbox</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF" }}>Closed tickets move out of the default view, but you can still open them from Closed or All.</div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {SUPPORT_STATUS_FILTER_OPTIONS.map((option) => (
                    <button
                      key={`client-support-filter-${option.id}`}
                      onClick={() => setClientSupportStatusFilter(option.id)}
                      style={{ background: clientSupportStatusFilter === option.id ? "rgba(99,102,241,0.18)" : "#0A0A0F", color: "#E5E5E5", border: `1px solid ${clientSupportStatusFilter === option.id ? "#6366F1" : "#1E1E2A"}`, borderRadius: 999, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 14 }}>
                <div style={{ display: "grid", gap: 8, alignContent: "start" }}>
                  {filteredClientSupportRequests.map((ticket) => {
                    const selected = selectedClientSupportTicket?.id === ticket.id;
                    return (
                      <button
                        key={`client-support-item-${ticket.id}`}
                        onClick={() => setSelectedClientSupportTicketId(ticket.id)}
                        style={{ background: selected ? "rgba(99,102,241,0.12)" : "#0A0A0F", border: `1px solid ${selected ? "#6366F1" : "#1E1E2A"}`, borderRadius: 10, padding: 12, textAlign: "left", cursor: "pointer" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
                          <div style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>{ticket.subject}</div>
                          <Badge type={ticket.status === "resolved" ? "approved" : ticket.status === "closed" ? "rejected" : ticket.status === "in-progress" ? "sent" : "pending"} label={ticket.status} />
                        </div>
                        <div style={{ fontSize: 11, color: "#9CA3AF", lineHeight: 1.6 }}>{ticket.ticketNumber || ticket.id}<br />{ticket.category} • {new Date(ticket.updatedAt || ticket.createdAt).toLocaleString()}</div>
                      </button>
                    );
                  })}
                  {filteredClientSupportRequests.length === 0 && <div style={{ fontSize: 12, color: "#9CA3AF", background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 14 }}>No support requests in this view.</div>}
                </div>

                <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14, minHeight: 320 }}>
                  {selectedClientSupportTicket ? <>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 10, alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{selectedClientSupportTicket.subject}</div>
                        <div style={{ fontSize: 11, color: "#9CA3AF" }}>{selectedClientSupportTicket.ticketNumber || selectedClientSupportTicket.id} • {selectedClientSupportTicket.category} • {new Date(selectedClientSupportTicket.createdAt).toLocaleString()}</div>
                      </div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <Badge type={selectedClientSupportTicket.priority === "urgent" ? "overdue" : selectedClientSupportTicket.priority === "high" ? "sent" : "pending"} label={selectedClientSupportTicket.priority} />
                        <Badge type={selectedClientSupportTicket.status === "resolved" ? "approved" : selectedClientSupportTicket.status === "closed" ? "rejected" : selectedClientSupportTicket.status === "in-progress" ? "sent" : "pending"} label={selectedClientSupportTicket.status} />
                        <button onClick={() => deleteSupportTicket(selectedClientSupportTicket.id)} style={{ background: "transparent", color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>Delete Ticket</button>
                      </div>
                    </div>

                    <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
                      {getSupportTicketMessages(selectedClientSupportTicket).map((msg) => {
                        const isClientMsg = msg.senderType === "client";
                        return (
                          <div key={`${selectedClientSupportTicket.id}-${msg.id}`} style={{ display: "flex", justifyContent: isClientMsg ? "flex-start" : "flex-end" }}>
                            <div style={{ maxWidth: "86%", background: isClientMsg ? "#111118" : "rgba(16,185,129,0.10)", border: `1px solid ${isClientMsg ? "#2D2D3A" : "rgba(16,185,129,0.25)"}`, borderRadius: isClientMsg ? "8px 8px 8px 2px" : "8px 8px 2px 8px", padding: "8px 10px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                                <div style={{ fontSize: 10, color: "#6B7280" }}>
                                  {msg.senderName || (isClientMsg ? "Client" : "Replai Support")}
                                  {msg.senderUsername ? ` (@${msg.senderUsername})` : ""}
                                  {` • ${new Date(msg.createdAt).toLocaleString()}`}
                                </div>
                                <button onClick={() => deleteSupportTicketMessage(selectedClientSupportTicket.id, msg.id)} style={{ background: "transparent", border: "none", color: "#FCA5A5", fontSize: 10, cursor: "pointer" }}>Delete</button>
                              </div>
                              <div style={{ fontSize: 12, color: "#D1D5DB", whiteSpace: "pre-wrap", lineHeight: 1.7 }}>{msg.message}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {selectedClientSupportTicket.billingChangeRequest && <div style={{ marginBottom: 8, fontSize: 11, color: "#A7F3D0", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: 8, padding: 8 }}>
                      Billing request: {selectedClientSupportTicket.billingChangeRequest.oldMethodLabel || selectedClientSupportTicket.billingChangeRequest.oldMethod} → {selectedClientSupportTicket.billingChangeRequest.newMethodLabel || selectedClientSupportTicket.billingChangeRequest.newMethod}
                      {selectedClientSupportTicket.billingChangeRequest.appliedAt ? ` • Applied ${new Date(selectedClientSupportTicket.billingChangeRequest.appliedAt).toLocaleString()}` : " • Pending admin action"}
                    </div>}
                    {selectedClientSupportTicket.planChangeRequest && <div style={{ marginBottom: 8, fontSize: 11, color: "#BFDBFE", background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 8, padding: 8 }}>
                      Plan request: {selectedClientSupportTicket.planChangeRequest.oldPlanLabel || selectedClientSupportTicket.planChangeRequest.oldPlan} → {selectedClientSupportTicket.planChangeRequest.newPlanLabel || selectedClientSupportTicket.planChangeRequest.newPlan} • Effective {selectedClientSupportTicket.planChangeRequest.effectiveDate}
                    </div>}
                    {Array.isArray(selectedClientSupportTicket.attachments) && selectedClientSupportTicket.attachments.length > 0 && <div style={{ marginBottom: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {selectedClientSupportTicket.attachments.map((file) => <span key={`cp-ticket-file-${selectedClientSupportTicket.id}-${file.name}`} style={{ fontSize: 11, color: "#D1D5DB", background: "#111118", border: "1px solid #2D2D3A", borderRadius: 99, padding: "3px 8px" }}>📎 {file.name}</span>)}
                    </div>}
                    <div style={{ marginBottom: 8, fontSize: 11, color: selectedClientSupportTicket.acknowledgementEmail?.sent ? "#A7F3D0" : "#FCA5A5" }}>Acknowledgement: {selectedClientSupportTicket.acknowledgementEmail?.sent ? `Email sent to ${selectedClientSupportTicket.acknowledgementEmail.to}` : "Pending"}</div>
                    {!(["resolved", "closed"].includes(selectedClientSupportTicket.status)) && (
                      <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "flex-end" }}>
                        <textarea
                          placeholder="Reply to Replai Support"
                          value={clientSupportReplyDrafts[selectedClientSupportTicket.id] || ""}
                          onChange={(e) => setClientSupportReplyDrafts((prev) => ({ ...prev, [selectedClientSupportTicket.id]: e.target.value }))}
                          style={{ ...inp, minHeight: 56, resize: "vertical", flex: 1 }}
                        />
                        <button onClick={() => sendSupportReplyFromClient(selectedClientSupportTicket.id)} style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Send</button>
                      </div>
                    )}
                  </> : <div style={{ fontSize: 12, color: "#9CA3AF" }}>Select a support ticket to view the conversation.</div>}
                </div>
              </div>
            </div>
          </>}

          {clientPortalClient && clientPortalTab === "account" && <>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 10 }}>Business Profile</div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{clientPortalClient.avatar} {clientPortalClient.name}</div>
                <div style={{ fontSize: 13, color: "#9CA3AF", lineHeight: 1.8 }}>
                  Contact: {clientPortalClient.contact || "—"}<br />
                  Email: {clientPortalClient.email || "—"}<br />
                  Phone: {clientPortalClient.phone || "—"}<br />
                  Address: {formatAddress(clientPortalClient)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 10 }}>Plan & Service</div>
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 12 }}>Plan: <strong>{clientPortalPackage?.name || "Custom"}</strong></div>
                  <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 12 }}>Contract: <strong style={{ textTransform: "capitalize" }}>{clientPortalClient.contractStatus || "none"}</strong></div>
                  <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 12 }}>
                    Signature Provider: <strong>{getContractEnvelopeForClient(clientPortalClient.id)?.provider || "DocuSign"}</strong><br />
                    Envelope: <strong>{getContractEnvelopeForClient(clientPortalClient.id)?.envelopeId || "Not sent"}</strong>
                  </div>
                  <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 12 }}>{clientTrialActive ? <>Trial ends: <strong>{clientPortalClient.trialEndDate || "—"}</strong></> : <>Next payment: <strong>{clientPortalClient.nextPayment || "—"}</strong></>}</div>
                  <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 12 }}>Billing status: <strong style={{ textTransform: "capitalize" }}>{clientPortalClient.paymentStatus || "paid"}</strong></div>
                  <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 12 }}>Payment method: <strong>{clientPortalPaymentMethodLabel}</strong></div>
                  <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 12 }}>Platforms: <strong>{(clientPortalClient.platformsManaged || []).join(", ") || "Google"}</strong></div>
                  {clientPortalClient.inviteLink && (
                    <div style={{ background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.25)", borderRadius: 8, padding: 10 }}>
                      <div style={{ fontSize: 11, color: "#A5B4FC", marginBottom: 6, fontWeight: 700 }}>🔗 Your Review Invite Link</div>
                      <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 8 }}>Share this link with your customers — via WhatsApp, email, receipts, or your website — to invite them to leave you a review.</div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <input readOnly value={clientPortalClient.inviteLink} style={{ ...inp, flex: 1, marginBottom: 0, fontSize: 11, color: "#A5B4FC" }} />
                        <button
                          onClick={() => { navigator.clipboard.writeText(clientPortalClient.inviteLink); notify("Link copied! Share it with your customers."); }}
                          style={{ background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "0 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                        >Copy</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18 }}>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 10 }}>My Profile</div>
                <div style={{ display: "grid", gap: 8 }}>
                  <input placeholder="Full name" value={clientProfileDraft.name} onChange={(e) => setClientProfileDraft((prev) => ({ ...prev, name: e.target.value }))} style={inp} />
                  <input placeholder="Email" value={clientProfileDraft.email} onChange={(e) => setClientProfileDraft((prev) => ({ ...prev, email: e.target.value }))} style={inp} />
                  <input placeholder="Phone" value={clientProfileDraft.phone} onChange={(e) => setClientProfileDraft((prev) => ({ ...prev, phone: e.target.value }))} style={inp} />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <input placeholder="City" value={clientProfileDraft.city} onChange={(e) => setClientProfileDraft((prev) => ({ ...prev, city: e.target.value }))} style={inp} />
                    <input placeholder="Country" value={clientProfileDraft.country} onChange={(e) => setClientProfileDraft((prev) => ({ ...prev, country: e.target.value }))} style={inp} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <select value={clientProfileDraft.preferredLanguage} onChange={(e) => setClientProfileDraft((prev) => ({ ...prev, preferredLanguage: e.target.value }))} style={inp}>{LANGUAGE_OPTIONS.map((lang) => <option key={`client-profile-lang-${lang}`} value={lang}>{lang}</option>)}</select>
                    <select value={clientProfileDraft.timezone} onChange={(e) => setClientProfileDraft((prev) => ({ ...prev, timezone: e.target.value }))} style={inp}>{TIMEZONE_OPTIONS.map((tz) => <option key={`client-profile-tz-${tz}`} value={tz}>{tz}</option>)}</select>
                  </div>
                  <textarea placeholder="Short profile note" value={clientProfileDraft.bio} onChange={(e) => setClientProfileDraft((prev) => ({ ...prev, bio: e.target.value }))} style={{ ...inp, minHeight: 78, resize: "vertical" }} />
                  <div style={{ fontSize: 11, color: "#9CA3AF" }}>Username: <strong style={{ color: "#E5E7EB" }}>@{currentClientPortalUser?.username || ""}</strong> • Only admin or authorized staff can change usernames.</div>
                  <button onClick={saveClientPortalProfileDraft} style={{ alignSelf: "start", background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontWeight: 700, cursor: "pointer" }}>Save Profile</button>
                </div>
              </div>

              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18 }}>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 10 }}>Password</div>
                <div style={{ display: "grid", gap: 8 }}>
                  <input type="password" placeholder="Current password" value={clientSettingsPasswordForm.currentPassword} onChange={(e) => setClientSettingsPasswordForm((p) => ({ ...p, currentPassword: e.target.value }))} style={inp} />
                  <input type="password" placeholder="New password" value={clientSettingsPasswordForm.nextPassword} onChange={(e) => setClientSettingsPasswordForm((p) => ({ ...p, nextPassword: e.target.value }))} style={inp} />
                  <input type="password" placeholder="Confirm new password" value={clientSettingsPasswordForm.confirmPassword} onChange={(e) => setClientSettingsPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))} style={inp} />
                  <button onClick={changeClientOwnPassword} style={{ alignSelf: "start", background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontWeight: 700, cursor: "pointer" }}>Update Password</button>
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #1E1E2A", fontSize: 11, color: "#9CA3AF" }}>If you need a username change, contact an admin or authorized support staff.</div>
                </div>
              </div>

              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18 }}>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 10 }}>Billing & Payment</div>
                <div style={{ display: "grid", gap: 10 }}>
                  {isClientPortalOwner && clientPortalClient.contractStatus !== "signed" && getContractEnvelopeForClient(clientPortalClient.id) && <button onClick={() => openClientContractSigning(clientPortalClient)} style={{ alignSelf: "start", background: "linear-gradient(135deg,#10B981,#059669)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Sign Contract via DocuSign</button>}
                  <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 12, color: "#D1D5DB" }}>
                    Payment option on file: <strong>{clientPortalPaymentMethodLabel}</strong><br />
                    Billing detail: <strong>{clientPortalClient.paymentDetailMasked || "On file"}</strong>
                  </div>
                  <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 12, color: "#D1D5DB" }}>
                    <div style={{ marginBottom: 8 }}>Request billing method update</div>
                    <div style={{ display: "grid", gap: 8 }}>
                      <select value={clientBillingChangeForm.newMethod} onChange={(e) => setClientBillingChangeForm((prev) => ({ ...prev, newMethod: e.target.value }))} style={inp}>
                        {CLIENT_PAYMENT_METHODS.map((method) => <option key={`cp-billing-change-${method.id}`} value={method.id}>{method.label}</option>)}
                      </select>
                      <input placeholder={getPaymentDetailMeta(clientBillingChangeForm.newMethod).placeholder} value={clientBillingChangeForm.paymentDetail} onChange={(e) => setClientBillingChangeForm((prev) => ({ ...prev, paymentDetail: e.target.value }))} style={inp} />
                      <textarea placeholder="Reason for change (optional)" value={clientBillingChangeForm.reason} onChange={(e) => setClientBillingChangeForm((prev) => ({ ...prev, reason: e.target.value }))} style={{ ...inp, minHeight: 70, resize: "vertical" }} />
                    </div>
                  </div>
                  <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 12, color: "#D1D5DB" }}>
                    Active package: <strong>{clientPortalPackage?.name || "Custom"}</strong> • €{clientPortalPackage?.price || 0}/month
                  </div>
                  <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 12, color: "#D1D5DB" }}>
                    <div style={{ marginBottom: 8 }}>Request plan upgrade or downgrade</div>
                    <div style={{ display: "grid", gap: 8 }}>
                      <select value={clientPlanChangeForm.plan} onChange={(e) => setClientPlanChangeForm((prev) => ({ ...prev, plan: e.target.value }))} style={inp}>
                        {PLANS.map((plan) => <option key={`client-plan-change-${plan.id}`} value={plan.id}>{plan.name} — €{plan.price}/month</option>)}
                      </select>
                      <textarea placeholder="Reason for the plan change (optional)" value={clientPlanChangeForm.reason} onChange={(e) => setClientPlanChangeForm((prev) => ({ ...prev, reason: e.target.value }))} style={{ ...inp, minHeight: 70, resize: "vertical" }} />
                      <button onClick={requestClientPlanChange} style={{ alignSelf: "start", background: "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Request Plan Change</button>
                    </div>
                  </div>
                  <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 12, color: "#9CA3AF" }}>
                    {clientTrialActive ? `Free trial active until ${clientPortalClient.trialEndDate || "—"}.` : `Next automatic charge: ${clientPortalClient.nextPayment || "—"}.`} {clientPortalClient.autoChargeAfterTrial ? "Auto-charge is enabled." : "Auto-charge is disabled."}
                  </div>
                  {clientPendingPlanChange && <div style={{ background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: 8, padding: 10, fontSize: 12, color: "#BFDBFE" }}>
                    Pending change: {clientPendingPlanChange.oldPlanLabel || clientPendingPlanChange.oldPlan} → {clientPendingPlanChange.newPlanLabel || clientPendingPlanChange.newPlan}. This will take effect on {clientPendingPlanChange.effectiveDate} and the updated rate will apply from that billing month.
                  </div>}
                  <div style={{ fontSize: 11, color: "#9CA3AF" }}>To change your payment option, send a billing request to Replai support. Our team will update it from the agency dashboard.</div>
                  <button onClick={requestClientPaymentMethodChange} style={{ alignSelf: "start", background: "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Request Payment Option Change</button>
                </div>
              </div>
            </div>

            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18, marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase" }}>Security Dashboard</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF" }}>Recommended layers are mandatory for all accounts.</div>
                </div>
                <div style={{ fontSize: 12, color: currentSecurityProgress.enabled === currentSecurityProgress.total ? "#34D399" : "#F59E0B" }}>{currentSecurityProgress.enabled}/{currentSecurityProgress.total} Active</div>
              </div>
              <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
                {SECURITY_LAYER_DEFINITIONS.map((layer) => {
                  const active = !!currentSecurityLayers[layer.key];
                  return (
                    <div key={`client-security-layer-${layer.key}`} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 12, color: "#fff", fontWeight: 700 }}>{layer.label}</div>
                        <div style={{ fontSize: 11, color: "#9CA3AF" }}>{layer.detail}</div>
                      </div>
                      <button disabled={active} onClick={() => updateOwnSecurityLayer(layer.key, true)} style={{ background: active ? "rgba(16,185,129,0.15)" : "#1E1E2A", color: active ? "#34D399" : "#fff", border: "1px solid #2D2D3A", borderRadius: 7, padding: "6px 10px", fontSize: 11, fontWeight: 700, cursor: active ? "default" : "pointer" }}>{active ? "Active" : "Activate"}</button>
                    </div>
                  );
                })}
              </div>
              <button onClick={activateAllRecommendedSecurityLayers} style={{ background: "linear-gradient(135deg,#10B981,#14B8A6)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 12px", fontWeight: 700, cursor: "pointer" }}>Activate All Recommended Layers</button>
              <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                {currentSecurityLayers.mfaApp && <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 11, color: "#D1D5DB", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#34D399", fontWeight: 700 }}>✓</span> Authenticator MFA configured</span>
                  <button onClick={() => updateOwnSecurityLayer("mfaApp", true)} style={{ background: "transparent", color: "#A7F3D0", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>Re-enroll</button>
                </div>}
                {currentSecurityLayers.backupCodes && <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 11, color: "#D1D5DB", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                  <span>Backup codes remaining: <strong style={{ color: "#fff" }}>{Array.isArray(currentSecurityState.backupCodeHashes) ? currentSecurityState.backupCodeHashes.length : 0}</strong></span>
                  <button onClick={regenerateBackupCodesForCurrentUser} style={{ background: "transparent", color: "#A7F3D0", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>Regenerate</button>
                </div>}
                {currentSecurityLayers.suspiciousLoginLock && <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 11, color: "#D1D5DB" }}>
                  Trusted devices: <strong style={{ color: "#fff" }}>{(currentSecurityState.trustedDevices || []).length}</strong>
                </div>}
                {currentSecurityLayers.loginAlerts && <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 11, color: "#D1D5DB" }}>
                  Recent login alerts: <strong style={{ color: "#fff" }}>{(currentSecurityState.loginAlerts || []).length}</strong>
                </div>}
              </div>
            </div>

            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18, marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 10 }}>Review Account Connections</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 6 }}>Google Business Profile</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 10 }}>{clientPortalClient.connected ? "Connected and ready to sync reviews." : "Connect your Google Business Profile to start review sync."}</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button onClick={() => (clientPortalClient.connected ? disconnectGoogleClient(clientPortalClient) : startGoogleConnection(clientPortalClient))} disabled={googleBusyClientId === clientPortalClient.id || !isClientPortalOwner} style={{ background: clientPortalClient.connected ? "transparent" : "#10B981", color: clientPortalClient.connected ? "#D1D5DB" : "#fff", border: clientPortalClient.connected ? "1px solid #2D2D3A" : "none", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: googleBusyClientId === clientPortalClient.id || !isClientPortalOwner ? "not-allowed" : "pointer", opacity: googleBusyClientId === clientPortalClient.id || !isClientPortalOwner ? 0.6 : 1 }}>{clientPortalClient.connected ? "Disconnect" : "Connect Google"}</button>
                    <button onClick={() => syncGoogleReviewsForClient(clientPortalClient)} disabled={!clientPortalClient.connected || googleBusyClientId === clientPortalClient.id} style={{ background: "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: !clientPortalClient.connected || googleBusyClientId === clientPortalClient.id ? "not-allowed" : "pointer", opacity: !clientPortalClient.connected || googleBusyClientId === clientPortalClient.id ? 0.5 : 1 }}>Sync Reviews</button>
                  </div>
                </div>
                <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 6 }}>Trustpilot</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 10 }}>{isTrustpilotConnected(clientPortalClient) ? "Connected and ready to sync reviews." : "Connect your Trustpilot account to start review sync."}</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button onClick={() => (isTrustpilotConnected(clientPortalClient) ? disconnectTrustpilotClient(clientPortalClient) : startTrustpilotConnection(clientPortalClient))} disabled={trustpilotBusyClientId === clientPortalClient.id || !isClientPortalOwner} style={{ background: isTrustpilotConnected(clientPortalClient) ? "transparent" : "#10B981", color: isTrustpilotConnected(clientPortalClient) ? "#D1D5DB" : "#fff", border: isTrustpilotConnected(clientPortalClient) ? "1px solid #2D2D3A" : "none", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: trustpilotBusyClientId === clientPortalClient.id || !isClientPortalOwner ? "not-allowed" : "pointer", opacity: trustpilotBusyClientId === clientPortalClient.id || !isClientPortalOwner ? 0.6 : 1 }}>{isTrustpilotConnected(clientPortalClient) ? "Disconnect" : "Connect Trustpilot"}</button>
                    <button onClick={() => syncTrustpilotReviewsForClient(clientPortalClient)} disabled={!isTrustpilotConnected(clientPortalClient) || trustpilotBusyClientId === clientPortalClient.id} style={{ background: "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: !isTrustpilotConnected(clientPortalClient) || trustpilotBusyClientId === clientPortalClient.id ? "not-allowed" : "pointer", opacity: !isTrustpilotConnected(clientPortalClient) || trustpilotBusyClientId === clientPortalClient.id ? 0.5 : 1 }}>Sync Reviews</button>
                  </div>
                </div>
                <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 6 }}>Facebook</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 10 }}>{clientPortalClient.facebookConnected ? "Connected and ready to sync reviews." : "Connect your Facebook page to start syncing recommendations."}</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button onClick={() => (clientPortalClient.facebookConnected ? disconnectFacebookClient(clientPortalClient) : startFacebookConnection(clientPortalClient))} disabled={facebookBusyClientId === clientPortalClient.id || !isClientPortalOwner} style={{ background: clientPortalClient.facebookConnected ? "transparent" : "#1877F2", color: clientPortalClient.facebookConnected ? "#D1D5DB" : "#fff", border: clientPortalClient.facebookConnected ? "1px solid #2D2D3A" : "none", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: facebookBusyClientId === clientPortalClient.id || !isClientPortalOwner ? "not-allowed" : "pointer", opacity: facebookBusyClientId === clientPortalClient.id || !isClientPortalOwner ? 0.6 : 1 }}>{facebookBusyClientId === clientPortalClient.id ? "Connecting…" : clientPortalClient.facebookConnected ? "Disconnect" : "Connect Facebook"}</button>
                    <button onClick={() => syncFacebookReviewsForClient(clientPortalClient)} disabled={!clientPortalClient.facebookConnected || facebookBusyClientId === clientPortalClient.id} style={{ background: "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: !clientPortalClient.facebookConnected || facebookBusyClientId === clientPortalClient.id ? "not-allowed" : "pointer", opacity: !clientPortalClient.facebookConnected || facebookBusyClientId === clientPortalClient.id ? 0.5 : 1 }}>Sync Reviews</button>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18 }}>
              <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 10 }}>Client Workspace Users</div>
              <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
                {clientPortalLinkedUsers.map((u) => (
                  <div key={`client-user-row-${u.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10 }}>
                    <div>
                      <div style={{ fontSize: 13, color: "#E5E5E5", fontWeight: 600 }}>{u.name}</div>
                      <div style={{ fontSize: 11, color: "#9CA3AF" }}><span>@</span>{u.username} • {u.role === "member" ? "Member" : "Owner"}</div>
                    </div>
                    {isClientPortalOwner && u.role === "member" && <button onClick={() => removeClientPortalMember(u.id)} style={{ background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>Remove</button>}
                  </div>
                ))}
              </div>

              {isClientPortalOwner && <>
                <div style={{ fontSize: 12, color: "#D1D5DB", marginBottom: 10 }}>Add member user</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8 }}>
                  <input placeholder="Name" value={clientPortalMemberForm.name} onChange={(e) => setClientPortalMemberForm((p) => ({ ...p, name: e.target.value }))} style={inp} />
                  <input placeholder="Username" value={clientPortalMemberForm.username} onChange={(e) => setClientPortalMemberForm((p) => ({ ...p, username: e.target.value }))} style={inp} />
                  <input placeholder="Password" value={clientPortalMemberForm.password} onChange={(e) => setClientPortalMemberForm((p) => ({ ...p, password: e.target.value }))} style={inp} />
                  <button onClick={addClientPortalMember} style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Add</button>
                </div>
              </>}

              {!isClientPortalOwner && <div style={{ fontSize: 12, color: "#9CA3AF" }}>Member role can manage drafts but cannot change billing or workspace users.</div>}
            </div>

            {/* ── Brand Voice ── */}
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18, marginTop: 14 }}>
              <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 6 }}>Brand Voice</div>
              <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 14, lineHeight: 1.7 }}>
                Tell us how your brand communicates. These preferences are used by Replai's AI when drafting review responses for your account.
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                <div>
                  <label style={lbl}>Business description</label>
                  <textarea placeholder="e.g. A family-run Italian restaurant known for fresh pasta, warm service, and a cosy atmosphere." value={brandVoiceDraft.description} onChange={(e) => setBrandVoiceDraft((p) => ({ ...p, description: e.target.value }))} style={{ ...inp, minHeight: 80, resize: "vertical" }} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <label style={lbl}>Tone of voice</label>
                    <select value={brandVoiceDraft.tone} onChange={(e) => setBrandVoiceDraft((p) => ({ ...p, tone: e.target.value }))} style={inp}>
                      <option value="">Select tone…</option>
                      <option value="warm-friendly">Warm & Friendly</option>
                      <option value="professional">Professional</option>
                      <option value="formal">Formal</option>
                      <option value="casual">Casual</option>
                      <option value="empathetic">Empathetic</option>
                      <option value="enthusiastic">Enthusiastic</option>
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Response language</label>
                    <select value={brandVoiceDraft.language} onChange={(e) => setBrandVoiceDraft((p) => ({ ...p, language: e.target.value }))} style={inp}>
                      <option value="">Match reviewer language</option>
                      {["English","German","French","Spanish","Italian","Portuguese","Dutch","Polish"].map((l) => <option key={`bv-lang-${l}`} value={l}>{l}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label style={lbl}>Keywords / phrases to use (comma-separated)</label>
                  <input placeholder="e.g. fresh ingredients, family atmosphere, handmade" value={brandVoiceDraft.keywords} onChange={(e) => setBrandVoiceDraft((p) => ({ ...p, keywords: e.target.value }))} style={inp} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#E5E5E5" }}>Email me on negative reviews</div>
                    <div style={{ fontSize: 11, color: "#6B7280" }}>Get an email alert when a 1★ or 2★ review arrives on any connected platform.</div>
                  </div>
                  <button
                    onClick={() => {
                      const enabled = !(clientPortalClient.negativeReviewEmailAlerts);
                      setClients((prev) => prev.map((c) => c.id === clientPortalClient.id ? { ...c, negativeReviewEmailAlerts: enabled } : c));
                      notify(enabled ? "Email alerts enabled for negative reviews" : "Email alerts disabled");
                    }}
                    style={{ background: clientPortalClient.negativeReviewEmailAlerts ? "rgba(16,185,129,0.15)" : "#1E1E2A", color: clientPortalClient.negativeReviewEmailAlerts ? "#34D399" : "#9CA3AF", border: `1px solid ${clientPortalClient.negativeReviewEmailAlerts ? "rgba(16,185,129,0.4)" : "#2D2D3A"}`, borderRadius: 8, padding: "7px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                  >{clientPortalClient.negativeReviewEmailAlerts ? "On" : "Off"}</button>
                </div>
                <button onClick={saveBrandVoice} style={{ alignSelf: "start", background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontWeight: 700, cursor: "pointer" }}>Save Brand Voice</button>
              </div>
            </div>

            {(() => {
              const widgetRating = Number(clientPortalClient.avgRating || 0).toFixed(1);
              const widgetCount = clientPortalStats.total;
              const widgetName = clientPortalClient.name || "Your Business";
              const starsFilled = Math.round(Number(widgetRating));
              const starsHtml = Array.from({ length: 5 }, (_, i) => i < starsFilled ? "★" : "☆").join("");
              const inviteHref = clientPortalClient.inviteLink ? ` href="${clientPortalClient.inviteLink}"` : "";
              const embedCode = `<!-- Replai Review Badge — ${widgetName} -->\n<a${inviteHref} target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:10px;background:#fff;border:1px solid #e5e7eb;border-radius:10px;padding:12px 18px;font-family:sans-serif;text-decoration:none;box-shadow:0 1px 4px rgba(0,0,0,0.08)">\n  <span style="color:#f59e0b;font-size:20px;line-height:1">${starsHtml}</span>\n  <div>\n    <div style="font-size:15px;font-weight:700;color:#111">${widgetRating} / 5</div>\n    <div style="font-size:11px;color:#6b7280">${widgetCount > 0 ? `Based on ${widgetCount} review${widgetCount !== 1 ? "s" : ""}` : "Leave us a review"} · Powered by Replai</div>\n  </div>\n</a>`;
              return (
                <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18, marginTop: 14 }}>
                  <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 6 }}>Website Review Badge</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 12 }}>Copy the HTML snippet below and paste it anywhere on your website — email footer, homepage, or receipt page.</div>
                  <div style={{ marginBottom: 14, padding: 16, background: "#fff", borderRadius: 10, display: "inline-flex", alignItems: "center", gap: 12 }}>
                    <span style={{ color: "#f59e0b", fontSize: 22, lineHeight: 1 }}>{starsHtml}</span>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#111" }}>{widgetRating} / 5</div>
                      <div style={{ fontSize: 11, color: "#6b7280" }}>{widgetCount > 0 ? `Based on ${widgetCount} review${widgetCount !== 1 ? "s" : ""}` : "Leave us a review"} · Powered by Replai</div>
                    </div>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <textarea readOnly value={embedCode} style={{ ...inp, fontFamily: "'Space Mono', monospace", fontSize: 11, minHeight: 110, resize: "vertical", color: "#A5B4FC", whiteSpace: "pre" }} />
                  </div>
                  <button
                    onClick={() => { navigator.clipboard.writeText(embedCode); notify("Badge HTML copied! Paste it on your website."); }}
                    style={{ background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                  >Copy Badge HTML</button>
                </div>
              );
            })()}

            {isClientPortalOwner && (
              <div style={{ background: "#111118", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 12, padding: 18, marginTop: 14 }}>
                <div style={{ fontSize: 11, color: "#EF4444", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Danger Zone</div>
                <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 14, lineHeight: 1.7 }}>
                  Actions in this section are permanent or require Replai support to reverse. Please read each warning carefully before proceeding.
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div style={{ background: "#0A0A0F", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 10, padding: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#FCA5A5", marginBottom: 6 }}>Cancel Subscription</div>
                    <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 12, lineHeight: 1.6 }}>
                      Submit a cancellation request to our team. Billing stops after your current period ends. Your data remains accessible until the end of the period.
                    </div>
                    <button
                      onClick={() => setCancelSubModal({ open: true, reason: "", processing: false })}
                      style={{ background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.5)", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                    >Request Cancellation</button>
                  </div>
                  <div style={{ background: "#0A0A0F", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 10, padding: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#F87171", marginBottom: 6 }}>Delete Account & All Data</div>
                    <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 12, lineHeight: 1.6 }}>
                      Permanently removes your account, all reviews, support history, and workspace users. This cannot be undone. Requires password confirmation.
                    </div>
                    <button
                      onClick={() => setDeleteAccountModal({ open: true, confirmText: "", password: "", error: "", processing: false })}
                      style={{ background: "rgba(239,68,68,0.12)", color: "#F87171", border: "1px solid rgba(239,68,68,0.5)", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                    >Delete My Account</button>
                  </div>
                </div>
              </div>
            )}
          </>}
        </div>
        {mfaSetupModal && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}><div style={{ width: "min(500px, 96vw)", background: "#111118", border: "1px solid #1E1E2A", borderRadius: 16, padding: 26, maxHeight: "90vh", overflowY: "auto" }}><div style={{ fontSize: 11, color: "#10B981", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, fontFamily: "'Space Mono', monospace" }}>Authenticator MFA</div><div style={{ fontSize: 19, fontWeight: 700, color: "#fff", marginBottom: 6 }}>Set Up Your Authenticator App</div><div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 18, lineHeight: 1.7 }}>Use Google Authenticator, Microsoft Authenticator, Authy, or any TOTP app to scan this QR code. A new 6-digit code will be generated every 30 seconds.</div><div style={{ display: "grid", gap: 14 }}><div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: "12px 14px" }}><div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}><div style={{ width: 24, height: 24, borderRadius: 99, background: "#10B981", color: "#fff", fontSize: 11, fontWeight: 700, display: "grid", placeItems: "center", flexShrink: 0 }}>1</div><div style={{ fontSize: 12, color: "#D1D5DB" }}>Download an authenticator app on your smartphone:<br/><span style={{ color: "#9CA3AF" }}>Google Authenticator, Microsoft Authenticator, Authy, or similar.</span></div></div></div><div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: "12px 14px" }}><div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 4 }}><div style={{ width: 24, height: 24, borderRadius: 99, background: "#10B981", color: "#fff", fontSize: 11, fontWeight: 700, display: "grid", placeItems: "center", flexShrink: 0 }}>2</div><div style={{ fontSize: 12, color: "#D1D5DB" }}>Open your app → tap + → <strong style={{ color: "#fff" }}>Scan a QR code</strong></div></div>{mfaSetupModal.qrDataUrl && <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginTop: 10 }}><img src={mfaSetupModal.qrDataUrl} alt="MFA QR Code" style={{ width: 200, height: 200, borderRadius: 10, background: "#fff", padding: 8 }} /></div>}<div style={{ marginTop: 10 }}><div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 4 }}>Or enter this key manually in your app:</div><div style={{ background: "#1E1E2A", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 12px", fontFamily: "'Space Mono', monospace", fontSize: 12, color: "#E5E5E5", letterSpacing: "0.08em", wordBreak: "break-all" }}>{formatBase32ForDisplay(mfaSetupModal.base32Secret)}</div></div></div><div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: "12px 14px" }}><div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}><div style={{ width: 24, height: 24, borderRadius: 99, background: "#10B981", color: "#fff", fontSize: 11, fontWeight: 700, display: "grid", placeItems: "center", flexShrink: 0 }}>3</div><div style={{ fontSize: 12, color: "#D1D5DB" }}>Enter the <strong style={{ color: "#fff" }}>6-digit code</strong> from your app to confirm setup</div></div><input placeholder="000000" value={mfaSetupCode} onChange={(e) => setMfaSetupCode(e.target.value.replace(/\D/g,"").slice(0,6))} onKeyDown={(e) => { if (e.key==="Enter") completeMfaSetup(); }} maxLength={6} style={{ background: "#1E1E2A", border: "1px solid #2D2D3A", borderRadius: 8, color: "#fff", padding: "10px 14px", fontSize: 24, fontFamily: "'Space Mono', monospace", letterSpacing: "0.3em", width: "100%", outline: "none", textAlign: "center", boxSizing: "border-box" }} />{mfaSetupModal.error && <div style={{ marginTop: 8, padding: "8px 10px", background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, fontSize: 12, color: "#FCA5A5" }}>{mfaSetupModal.error}</div>}</div></div><div style={{ display: "flex", gap: 10, marginTop: 18 }}><button onClick={completeMfaSetup} disabled={mfaSetupCode.length < 6} style={{ flex: 1, background: mfaSetupCode.length < 6 ? "#374151" : "linear-gradient(135deg,#10B981,#14B8A6)", color: "#fff", border: "none", borderRadius: 10, padding: "12px 16px", fontWeight: 700, fontSize: 14, cursor: mfaSetupCode.length < 6 ? "not-allowed" : "pointer", opacity: mfaSetupCode.length < 6 ? 0.7 : 1 }}>Verify & Activate MFA</button><button onClick={cancelMfaSetup} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 10, padding: "12px 16px", cursor: "pointer", fontSize: 13 }}>Cancel</button></div></div></div>}
      </div>
    );
  }

  if (currentUser.mustChangePassword) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#0A0A0F", color: "#E5E5E5", fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ width: 520, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Change password before continuing</div>
          <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 14 }}>For security, you must set a new password on first login.</div>
          <div style={{ marginBottom: 10 }}><label style={lbl}>Current password</label><div style={{ position: "relative" }}><input type={showStaffMustChangePw.current ? "text" : "password"} value={mustChangeForm.currentPassword} onChange={(e) => setMustChangeForm((p) => ({ ...p, currentPassword: e.target.value }))} style={{ ...inp, paddingRight: 36 }} /><button type="button" onClick={() => setShowStaffMustChangePw((v) => ({ ...v, current: !v.current }))} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 14 }}>{showStaffMustChangePw.current ? "🙈" : "👁"}</button></div></div>
          <div style={{ marginBottom: 10 }}><label style={lbl}>New password</label><div style={{ position: "relative" }}><input type={showStaffMustChangePw.next ? "text" : "password"} value={mustChangeForm.nextPassword} onChange={(e) => setMustChangeForm((p) => ({ ...p, nextPassword: e.target.value }))} style={{ ...inp, paddingRight: 36 }} /><button type="button" onClick={() => setShowStaffMustChangePw((v) => ({ ...v, next: !v.next }))} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 14 }}>{showStaffMustChangePw.next ? "🙈" : "👁"}</button></div></div>
          <div style={{ marginBottom: 14 }}><label style={lbl}>Confirm new password</label><div style={{ position: "relative" }}><input type={showStaffMustChangePw.confirm ? "text" : "password"} value={mustChangeForm.confirmPassword} onChange={(e) => setMustChangeForm((p) => ({ ...p, confirmPassword: e.target.value }))} style={{ ...inp, paddingRight: 36 }} /><button type="button" onClick={() => setShowStaffMustChangePw((v) => ({ ...v, confirm: !v.confirm }))} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 14 }}>{showStaffMustChangePw.confirm ? "🙈" : "👁"}</button></div></div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={logout} style={{ flex: 1, background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: 10, cursor: "pointer" }}>Logout</button>
            <button onClick={updatePasswordOnFirstLogin} style={{ flex: 1, background: "#10B981", color: "#fff", border: "none", borderRadius: 8, padding: 10, fontWeight: 700, cursor: "pointer" }}>Update password</button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasAllRecommendedSecurityLayers(currentUser)) {
    const progress = recommendedSecurityProgress(currentUser);
    return (
      <>
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#0A0A0F", color: "#E5E5E5", fontFamily: "'DM Sans', sans-serif", padding: 20 }}>
        <div style={{ width: "min(760px, 96vw)", background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 24 }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 20, color: "#6366F1", marginBottom: 8 }}>Security Setup Required</div>
          <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 12 }}>All staff accounts must activate every recommended security layer before using the dashboard.</div>
          <div style={{ marginBottom: 14, fontSize: 12, color: "#D1D5DB" }}>Progress: <strong>{progress.enabled}/{progress.total}</strong> enabled ({progress.pct}%)</div>
          <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
            {SECURITY_LAYER_DEFINITIONS.map((layer) => {
              const active = !!currentSecurityLayers[layer.key];
              return (
                <div key={`staff-sec-gate-${layer.key}`} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{layer.label}</div>
                    <div style={{ fontSize: 11, color: "#9CA3AF" }}>{layer.detail}</div>
                  </div>
                  <button disabled={active} onClick={() => updateOwnSecurityLayer(layer.key, true)} style={{ background: active ? "rgba(16,185,129,0.15)" : "#1E1E2A", color: active ? "#34D399" : "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 11px", fontSize: 12, fontWeight: 700, cursor: active ? "default" : "pointer" }}>{active ? "Active" : "Activate"}</button>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={logout} style={{ flex: 1, background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: 10, cursor: "pointer" }}>Logout</button>
            <button onClick={activateAllRecommendedSecurityLayers} style={{ flex: 1, background: "linear-gradient(135deg,#6366F1,#8B5CF6)", color: "#fff", border: "none", borderRadius: 8, padding: 10, fontWeight: 700, cursor: "pointer" }}>Activate All Recommended Layers</button>
          </div>
        </div>
      </div>
      {mfaSetupModal && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}><div style={{ width: "min(500px, 96vw)", background: "#111118", border: "1px solid #1E1E2A", borderRadius: 16, padding: 26, maxHeight: "90vh", overflowY: "auto" }}><div style={{ fontSize: 11, color: "#6366F1", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, fontFamily: "'Space Mono', monospace" }}>Authenticator MFA</div><div style={{ fontSize: 19, fontWeight: 700, color: "#fff", marginBottom: 6 }}>Set Up Your Authenticator App</div><div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 18, lineHeight: 1.7 }}>Use Google Authenticator, Microsoft Authenticator, Authy, or any TOTP app to scan this QR code. A new 6-digit code will be generated every 30 seconds.</div><div style={{ display: "grid", gap: 14 }}><div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: "12px 14px" }}><div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}><div style={{ width: 24, height: 24, borderRadius: 99, background: "#6366F1", color: "#fff", fontSize: 11, fontWeight: 700, display: "grid", placeItems: "center", flexShrink: 0 }}>1</div><div style={{ fontSize: 12, color: "#D1D5DB" }}>Download an authenticator app on your smartphone:<br/><span style={{ color: "#9CA3AF" }}>Google Authenticator, Microsoft Authenticator, Authy, or similar.</span></div></div></div><div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: "12px 14px" }}><div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 4 }}><div style={{ width: 24, height: 24, borderRadius: 99, background: "#6366F1", color: "#fff", fontSize: 11, fontWeight: 700, display: "grid", placeItems: "center", flexShrink: 0 }}>2</div><div style={{ fontSize: 12, color: "#D1D5DB" }}>Open your app → tap + → <strong style={{ color: "#fff" }}>Scan a QR code</strong></div></div>{mfaSetupModal.qrDataUrl && <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginTop: 10 }}><img src={mfaSetupModal.qrDataUrl} alt="MFA QR Code" style={{ width: 200, height: 200, borderRadius: 10, background: "#fff", padding: 8 }} /></div>}<div style={{ marginTop: 10 }}><div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 4 }}>Or enter this key manually in your app:</div><div style={{ background: "#1E1E2A", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 12px", fontFamily: "'Space Mono', monospace", fontSize: 12, color: "#E5E5E5", letterSpacing: "0.08em", wordBreak: "break-all" }}>{formatBase32ForDisplay(mfaSetupModal.base32Secret)}</div></div></div><div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: "12px 14px" }}><div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}><div style={{ width: 24, height: 24, borderRadius: 99, background: "#6366F1", color: "#fff", fontSize: 11, fontWeight: 700, display: "grid", placeItems: "center", flexShrink: 0 }}>3</div><div style={{ fontSize: 12, color: "#D1D5DB" }}>Enter the <strong style={{ color: "#fff" }}>6-digit code</strong> from your app to confirm setup</div></div><input placeholder="000000" value={mfaSetupCode} onChange={(e) => setMfaSetupCode(e.target.value.replace(/\D/g,"").slice(0,6))} onKeyDown={(e) => { if (e.key==="Enter") completeMfaSetup(); }} maxLength={6} style={{ background: "#1E1E2A", border: "1px solid #2D2D3A", borderRadius: 8, color: "#fff", padding: "10px 14px", fontSize: 24, fontFamily: "'Space Mono', monospace", letterSpacing: "0.3em", width: "100%", outline: "none", textAlign: "center", boxSizing: "border-box" }} />{mfaSetupModal.error && <div style={{ marginTop: 8, padding: "8px 10px", background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, fontSize: 12, color: "#FCA5A5" }}>{mfaSetupModal.error}</div>}</div></div><div style={{ display: "flex", gap: 10, marginTop: 18 }}><button onClick={completeMfaSetup} disabled={mfaSetupCode.length < 6} style={{ flex: 1, background: mfaSetupCode.length < 6 ? "#374151" : "linear-gradient(135deg,#6366F1,#8B5CF6)", color: "#fff", border: "none", borderRadius: 10, padding: "12px 16px", fontWeight: 700, fontSize: 14, cursor: mfaSetupCode.length < 6 ? "not-allowed" : "pointer", opacity: mfaSetupCode.length < 6 ? 0.7 : 1 }}>Verify & Activate MFA</button><button onClick={cancelMfaSetup} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 10, padding: "12px 16px", cursor: "pointer", fontSize: 13 }}>Cancel</button></div></div></div>}
      </>
    );
  }

  if (!currentUser.profileCompleted || currentUser.onboardingSeenVersion !== ONBOARDING_VERSION) {
    const onboardingSteps = [
      { id: 1, title: "Welcome", subtitle: "Understand your role and access" },
      { id: 2, title: "Profile", subtitle: "Add contact and location details" },
      { id: 3, title: "Workspace", subtitle: "Choose language and notifications" },
      { id: 4, title: "Review", subtitle: "Confirm and launch dashboard" },
    ];
    const onboardingReady = !!currentUser.email && !!currentUser.phone && !!currentUser.city && !!currentUser.country && !!currentUser.preferredLanguage;
    return (
      <div style={{ minHeight: "100vh", background: "radial-gradient(circle at top right, rgba(99,102,241,0.24), transparent 24%), #0A0A0F", color: "#E5E5E5", fontFamily: "'DM Sans', sans-serif", padding: "42px 24px" }}>
        <div style={{ maxWidth: 1120, margin: "0 auto", display: "grid", gridTemplateColumns: "360px 1fr", gap: 18 }}>
          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 18, padding: 24, alignSelf: "start", position: "sticky", top: 24 }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 21, color: "#10B981", marginBottom: 10 }}>Replai<span style={{ color: "#6366F1" }}>.</span>work</div>
            <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Welcome aboard, {currentUser.name}</div>
            <div style={{ fontSize: 13, color: "#9CA3AF", lineHeight: 1.7, marginBottom: 18 }}>This setup gets each staff member ready with profile details, working preferences, and a clear view of responsibilities before entering the dashboard.</div>

            <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Your Assignment</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{currentUser.title || DEFAULT_STAFF_TITLE}</div>
              <div style={{ fontSize: 12, color: "#A78BFA", marginTop: 4 }}>{String(currentUser.scopeLevel || "country").toUpperCase()} • {currentUser.scopeValue || currentUser.country || currentUser.city || "Unassigned"}</div>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 10 }}>Permissions</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                {(currentUser.permissions || []).map((perm) => <span key={`onboarding-perm-${perm}`} style={{ fontSize: 10, color: "#D1D5DB", background: "#151522", border: "1px solid #2D2D3A", borderRadius: 99, padding: "4px 8px" }}>{perm}</span>)}
              </div>
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              {onboardingSteps.map((step) => {
                const active = onboardingStep === step.id;
                const done = onboardingStep > step.id;
                return (
                  <button key={`onboarding-step-${step.id}`} onClick={() => setOnboardingStep(step.id)} style={{ background: active ? "rgba(99,102,241,0.14)" : "transparent", border: `1px solid ${active ? "#6366F1" : "#1E1E2A"}`, borderRadius: 12, padding: 12, color: "#E5E5E5", textAlign: "left", cursor: "pointer" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 99, background: done ? "#10B981" : active ? "#6366F1" : "#1E1E2A", color: "#fff", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700 }}>{done ? "✓" : step.id}</div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{step.title}</div>
                        <div style={{ fontSize: 11, color: "#9CA3AF" }}>{step.subtitle}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 18, padding: 26 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Staff onboarding</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{onboardingSteps.find((s) => s.id === onboardingStep)?.title}</div>
              </div>
              <div style={{ width: 180, height: 8, background: "#0A0A0F", borderRadius: 99, overflow: "hidden" }}>
                <div style={{ width: `${(onboardingStep / onboardingSteps.length) * 100}%`, height: "100%", background: "linear-gradient(90deg,#6366F1,#10B981)" }} />
              </div>
            </div>

            {onboardingStep === 1 && <div>
              <div style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 14 }}>
                <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 14, padding: 18 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", marginBottom: 8 }}>What you will manage</div>
                  <div style={{ fontSize: 13, color: "#9CA3AF", lineHeight: 1.8 }}>
                    • Monitor incoming review activity for assigned accounts<br/>
                    • Draft and refine client-safe responses<br/>
                    • Escalate sensitive or legal-risk reviews quickly<br/>
                    • Keep workboard, client board, and queue updated in real time
                  </div>
                </div>
                <div style={{ background: "linear-gradient(135deg, rgba(99,102,241,0.18), rgba(16,185,129,0.12))", border: "1px solid rgba(99,102,241,0.24)", borderRadius: 14, padding: 18 }}>
                  <div style={{ fontSize: 11, color: "#C7D2FE", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Assigned Scope</div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "#fff", marginBottom: 6 }}>{currentUser.title || DEFAULT_STAFF_TITLE}</div>
                  <div style={{ fontSize: 13, color: "#D1D5DB", marginBottom: 10 }}>{currentUser.scopeLevel === "city" ? "City responsibility" : currentUser.scopeLevel === "country" ? "Country responsibility" : "Global responsibility"}</div>
                  <div style={{ fontSize: 12, color: "#E5E7EB" }}>{currentUser.scopeValue || currentUser.country || currentUser.city || "Unassigned"}</div>
                </div>
              </div>
            </div>}

            {onboardingStep === 2 && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label style={lbl}>Email</label><input value={currentUser.email || ""} onChange={(e) => updateCurrentUserDraft({ email: e.target.value })} style={inp} /></div>
              <div><label style={lbl}>Phone</label><input value={currentUser.phone || ""} onChange={(e) => updateCurrentUserDraft({ phone: e.target.value })} style={inp} /></div>
              <div><label style={lbl}>City</label><input value={currentUser.city || ""} onChange={(e) => updateCurrentUserDraft({ city: e.target.value })} style={inp} /></div>
              <div><label style={lbl}>Country</label><input value={currentUser.country || ""} onChange={(e) => updateCurrentUserDraft({ country: e.target.value })} style={inp} /></div>
              <div style={{ gridColumn: "1 / span 2" }}><label style={lbl}>Short Bio</label><textarea value={currentUser.bio || ""} onChange={(e) => updateCurrentUserDraft({ bio: e.target.value })} style={{ ...inp, minHeight: 88, resize: "vertical" }} placeholder="A short internal intro for the team." /></div>
            </div>}

            {onboardingStep === 3 && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label style={lbl}>Preferred Work Language</label><select value={currentUser.preferredLanguage || "English"} onChange={(e) => updateCurrentUserDraft({ preferredLanguage: e.target.value })} style={inp}>{LANGUAGE_OPTIONS.map((lang) => <option key={`onboarding-lang-${lang}`} value={lang}>{lang}</option>)}</select></div>
              <div><label style={lbl}>Timezone</label><select value={currentUser.timezone || "Europe/Berlin"} onChange={(e) => updateCurrentUserDraft({ timezone: e.target.value })} style={inp}>{TIMEZONE_OPTIONS.map((tz) => <option key={`onboarding-timezone-${tz}`} value={tz}>{tz}</option>)}</select></div>
              <div><label style={lbl}>Notification Preference</label><select value={currentUser.notificationPreference || "dashboard"} onChange={(e) => updateCurrentUserDraft({ notificationPreference: e.target.value })} style={inp}>{NOTIFICATION_PREFERENCES.map((pref) => <option key={`onboarding-pref-${pref.id}`} value={pref.id}>{pref.label}</option>)}</select></div>
              <div>
                <label style={lbl}>Profile Picture</label>
                <input type="file" accept="image/*" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => updateCurrentUserDraft({ avatar: String(reader.result || "") });
                  reader.readAsDataURL(file);
                }} style={{ ...inp, padding: 8 }} />
              </div>
              <div style={{ gridColumn: "1 / span 2", display: "flex", gap: 12, alignItems: "center", background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
                <div style={{ width: 58, height: 58, borderRadius: 99, background: "#1E1E2A", overflow: "hidden", display: "grid", placeItems: "center" }}>
                  {currentUser.avatar ? <img src={currentUser.avatar} alt={currentUser.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 18, color: "#fff" }}>{(currentUser.name || "?")[0]}</span>}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Your working preferences</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF", lineHeight: 1.7 }}>Responses will default to the original review language. Your preferred language mainly affects your workspace and editing comfort.</div>
                </div>
              </div>
            </div>}

            {onboardingStep === 4 && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 14, padding: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 10 }}>Profile Summary</div>
                <div style={{ fontSize: 12, color: "#9CA3AF", lineHeight: 1.9 }}>
                  <strong style={{ color: "#fff" }}>Email:</strong> {currentUser.email || "Missing"}<br/>
                  <strong style={{ color: "#fff" }}>Phone:</strong> {currentUser.phone || "Missing"}<br/>
                  <strong style={{ color: "#fff" }}>Location:</strong> {currentUser.city || "—"}, {currentUser.country || "—"}<br/>
                  <strong style={{ color: "#fff" }}>Language:</strong> {currentUser.preferredLanguage || "Missing"}<br/>
                  <strong style={{ color: "#fff" }}>Timezone:</strong> {currentUser.timezone || "Europe/Berlin"}<br/>
                  <strong style={{ color: "#fff" }}>Notifications:</strong> {NOTIFICATION_PREFERENCES.find((p) => p.id === (currentUser.notificationPreference || "dashboard"))?.label || "Dashboard only"}
                </div>
              </div>
              <div style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.12), rgba(99,102,241,0.12))", border: "1px solid rgba(16,185,129,0.22)", borderRadius: 14, padding: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 10 }}>Ready Checklist</div>
                {[
                  { ok: !!currentUser.email, text: "Email added" },
                  { ok: !!currentUser.phone, text: "Phone added" },
                  { ok: !!currentUser.city && !!currentUser.country, text: "Location completed" },
                  { ok: !!currentUser.preferredLanguage, text: "Language selected" },
                ].map((item) => (
                  <div key={item.text} style={{ fontSize: 12, color: item.ok ? "#D1FAE5" : "#FCA5A5", marginBottom: 8 }}>{item.ok ? "✓" : "✕"} {item.text}</div>
                ))}
                <div style={{ marginTop: 14, fontSize: 12, color: onboardingReady ? "#A7F3D0" : "#FCA5A5" }}>{onboardingReady ? "Everything looks good. Launch your dashboard." : "Complete the missing items before finishing onboarding."}</div>
              </div>
            </div>}

            <div style={{ marginTop: 20, display: "flex", justifyContent: "space-between", gap: 10 }}>
              <button onClick={logout} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 10, padding: "10px 14px", cursor: "pointer" }}>Logout</button>
              <div style={{ display: "flex", gap: 8 }}>
                {onboardingStep > 1 && <button onClick={() => setOnboardingStep((s) => Math.max(1, s - 1))} style={{ background: "transparent", color: "#E5E5E5", border: "1px solid #2D2D3A", borderRadius: 10, padding: "10px 14px", cursor: "pointer" }}>Back</button>}
                {onboardingStep < 4 && <button onClick={() => setOnboardingStep((s) => Math.min(4, s + 1))} style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)", color: "#fff", border: "none", borderRadius: 10, padding: "10px 16px", fontWeight: 700, cursor: "pointer" }}>Continue</button>}
                {onboardingStep === 4 && <button onClick={completeOnboarding} disabled={!onboardingReady} style={{ background: onboardingReady ? "#10B981" : "#374151", color: "#fff", border: "none", borderRadius: 10, padding: "10px 16px", fontWeight: 700, cursor: onboardingReady ? "pointer" : "not-allowed", opacity: onboardingReady ? 1 : 0.6 }}>Finish onboarding</button>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="replai-dashboard-root" style={{ minHeight: "100vh", background: "#0A0A0F", color: "#E5E5E5", fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap" rel="stylesheet" />

      {notif && <div style={{ position: "fixed", top: 20, right: 20, zIndex: 999, background: notif.type === "success" ? "#065F46" : "#991B1B", color: "#fff", padding: "12px 20px", borderRadius: 10, fontSize: 13, fontWeight: 500, boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}>{notif.type === "success" ? "✓" : "✕"} {notif.msg}</div>}

      {cancelSubModal.open && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ width: "min(480px,96vw)", background: "#111118", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 16, padding: 26 }}>
            <div style={{ fontSize: 11, color: "#EF4444", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Cancel Subscription</div>
            <div style={{ fontSize: 19, fontWeight: 700, color: "#fff", marginBottom: 10 }}>Are you sure you want to cancel?</div>
            <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 13, color: "#FCA5A5", lineHeight: 1.7 }}>
              <strong>What happens when you cancel:</strong><br />
              • Your subscription ends at the current billing period<br />
              • You lose access to all Replai features after that date<br />
              • Your review responses and history are removed from the platform<br />
              • This request cannot be reversed once processed by our team
            </div>
            <label style={{ fontSize: 12, color: "#9CA3AF", display: "block", marginBottom: 6 }}>Reason for cancelling (optional)</label>
            <textarea
              value={cancelSubModal.reason}
              onChange={(e) => setCancelSubModal((p) => ({ ...p, reason: e.target.value }))}
              placeholder="Help us improve — tell us why you're leaving..."
              style={{ width: "100%", background: "#0A0A0F", border: "1px solid #2D2D3A", borderRadius: 8, color: "#E5E5E5", padding: "10px 14px", fontSize: 13, minHeight: 80, resize: "vertical", outline: "none", boxSizing: "border-box", marginBottom: 16 }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={requestCancelSubscription}
                disabled={cancelSubModal.processing}
                style={{ flex: 1, background: cancelSubModal.processing ? "#374151" : "#EF4444", color: "#fff", border: "none", borderRadius: 10, padding: "11px 16px", fontWeight: 700, fontSize: 13, cursor: cancelSubModal.processing ? "not-allowed" : "pointer", opacity: cancelSubModal.processing ? 0.7 : 1 }}
              >{cancelSubModal.processing ? "Submitting…" : "Submit Cancellation Request"}</button>
              <button
                onClick={() => setCancelSubModal({ open: false, reason: "", processing: false })}
                style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 10, padding: "11px 16px", cursor: "pointer", fontSize: 13 }}
              >Keep My Account</button>
            </div>
          </div>
        </div>
      )}

      {deleteAccountModal.open && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ width: "min(500px,96vw)", background: "#111118", border: "1px solid rgba(239,68,68,0.6)", borderRadius: 16, padding: 26 }}>
            <div style={{ fontSize: 11, color: "#EF4444", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Permanent Deletion</div>
            <div style={{ fontSize: 19, fontWeight: 700, color: "#fff", marginBottom: 10 }}>Delete Account & All Data</div>
            <div style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 13, color: "#FCA5A5", lineHeight: 1.7 }}>
              <strong>This action is irreversible. It will permanently delete:</strong><br />
              • Your business account and all workspace users<br />
              • All reviews and AI-generated responses<br />
              • All support tickets and billing history<br />
              • All connected platform integrations<br /><br />
              <strong>There is no recovery option.</strong>
            </div>
            <label style={{ fontSize: 12, color: "#9CA3AF", display: "block", marginBottom: 6 }}>Type <strong style={{ color: "#F87171" }}>DELETE</strong> to confirm</label>
            <input
              value={deleteAccountModal.confirmText}
              onChange={(e) => setDeleteAccountModal((p) => ({ ...p, confirmText: e.target.value, error: "" }))}
              placeholder="DELETE"
              style={{ width: "100%", background: "#0A0A0F", border: `1px solid ${deleteAccountModal.confirmText === "DELETE" ? "rgba(239,68,68,0.6)" : "#2D2D3A"}`, borderRadius: 8, color: "#F87171", padding: "10px 14px", fontSize: 14, fontFamily: "'Space Mono', monospace", letterSpacing: "0.08em", outline: "none", boxSizing: "border-box", marginBottom: 12 }}
            />
            <label style={{ fontSize: 12, color: "#9CA3AF", display: "block", marginBottom: 6 }}>Current password</label>
            <input
              type="password"
              value={deleteAccountModal.password}
              onChange={(e) => setDeleteAccountModal((p) => ({ ...p, password: e.target.value, error: "" }))}
              placeholder="Enter your password"
              style={{ width: "100%", background: "#0A0A0F", border: "1px solid #2D2D3A", borderRadius: 8, color: "#E5E5E5", padding: "10px 14px", fontSize: 13, outline: "none", boxSizing: "border-box", marginBottom: 12 }}
            />
            {deleteAccountModal.error && (
              <div style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#FCA5A5", marginBottom: 12 }}>{deleteAccountModal.error}</div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={requestDeleteOwnAccount}
                disabled={deleteAccountModal.processing || deleteAccountModal.confirmText !== "DELETE" || !deleteAccountModal.password}
                style={{ flex: 1, background: deleteAccountModal.processing || deleteAccountModal.confirmText !== "DELETE" || !deleteAccountModal.password ? "#374151" : "#EF4444", color: "#fff", border: "none", borderRadius: 10, padding: "11px 16px", fontWeight: 700, fontSize: 13, cursor: deleteAccountModal.processing || deleteAccountModal.confirmText !== "DELETE" || !deleteAccountModal.password ? "not-allowed" : "pointer", opacity: deleteAccountModal.processing || deleteAccountModal.confirmText !== "DELETE" || !deleteAccountModal.password ? 0.6 : 1 }}
              >{deleteAccountModal.processing ? "Deleting…" : "Permanently Delete Everything"}</button>
              <button
                onClick={() => setDeleteAccountModal({ open: false, confirmText: "", password: "", error: "", processing: false })}
                style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 10, padding: "11px 16px", cursor: "pointer", fontSize: 13 }}
              >Cancel</button>
            </div>
          </div>
        </div>
      )}

      {chatDeleteConfirmModal.open && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ width: "min(420px,96vw)", background: "#111118", border: "1px solid #1E1E2A", borderRadius: 16, padding: 26 }}>
            <div style={{ fontSize: 11, color: "#EF4444", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, fontFamily: "'Space Mono', monospace" }}>Confirm Deletion</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#fff", marginBottom: 8 }}>{chatDeleteConfirmModal.type === "thread" ? "Delete Thread?" : "Delete Message?"}</div>
            <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 20, lineHeight: 1.6 }}>
              {chatDeleteConfirmModal.type === "thread"
                ? "This will permanently delete the entire thread and all its messages. This action cannot be undone."
                : "This will permanently delete this message. This action cannot be undone."}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => {
                  if (chatDeleteConfirmModal.type === "thread") {
                    deleteStaffProblemThread(chatDeleteConfirmModal.threadId);
                  } else {
                    deleteStaffReply(chatDeleteConfirmModal.threadId, chatDeleteConfirmModal.msgId);
                  }
                  setChatDeleteConfirmModal({ open: false, type: "", threadId: null, msgId: null });
                }}
                style={{ flex: 1, background: "#EF4444", color: "#fff", border: "none", borderRadius: 10, padding: "11px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
              >Yes, Delete</button>
              <button
                onClick={() => setChatDeleteConfirmModal({ open: false, type: "", threadId: null, msgId: null })}
                style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 10, padding: "11px 16px", cursor: "pointer", fontSize: 13 }}
              >Cancel</button>
            </div>
          </div>
        </div>
      )}

      {tempPasswordModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ width: "min(440px,96vw)", background: "#111118", border: "1px solid #1E1E2A", borderRadius: 16, padding: 26 }}>
            <div style={{ fontSize: 11, color: "#F59E0B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, fontFamily: "'Space Mono', monospace" }}>Temporary Password</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#fff", marginBottom: 6 }}>Password Reset</div>
            <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 18, lineHeight: 1.6 }}>
              A temporary password has been set for <strong style={{ color: "#E5E5E5" }}>@{tempPasswordModal.username}</strong>. Share it securely — they will be required to change it on next login.
            </div>
            <div style={{ background: "#0A0A0F", border: "1px solid #2D2D3A", borderRadius: 10, padding: "12px 16px", marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4 }}>Temporary password</div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 18, color: "#F59E0B", letterSpacing: "0.05em", wordBreak: "break-all" }}>{tempPasswordModal.tempPassword}</div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => { navigator.clipboard.writeText(tempPasswordModal.tempPassword); notify("Temporary password copied to clipboard."); }}
                style={{ flex: 1, background: "#F59E0B", color: "#0A0A0F", border: "none", borderRadius: 10, padding: "11px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
              >Copy Password</button>
              <button
                onClick={() => setTempPasswordModal(null)}
                style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 10, padding: "11px 16px", cursor: "pointer", fontSize: 13 }}
              >Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <div style={{ position: "fixed", left: 0, top: 0, bottom: 0, width: 220, background: "#111118", borderRight: "1px solid #1E1E2A", display: "flex", flexDirection: "column", zIndex: 10, overflow: "hidden" }}>
        <div style={{ padding: "22px 18px", borderBottom: "1px solid #1E1E2A" }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 19, fontWeight: 700, color: brandColor }}>{brandLogo ? `${brandLogo} ` : ""}{brandName}{agencyBranding.enabled ? "" : <span style={{ color: "#6366F1" }}>.</span>}{agencyBranding.enabled ? "" : "work"}</div>
          <div style={{ fontSize: 10, color: "#6B7280", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>Command Center</div>
        </div>
        <nav style={{ padding: "14px 10px", flex: 1, display: "flex", flexDirection: "column", overflowY: "auto" }}>
          {visibleNavItems.map(item => (
            <button key={item.id} onClick={() => setTab(item.id)} style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "9px 11px", marginBottom: 3, border: "none", borderRadius: 8, background: tab === item.id ? "#1E1E2A" : "transparent", color: tab === item.id ? "#fff" : "#9CA3AF", fontSize: 13, fontWeight: 500, cursor: "pointer", textAlign: "left" }}>
              <span style={{ fontSize: 15 }}>{item.icon}</span>{item.label}
              {item.badge > 0 && <span style={{ marginLeft: "auto", background: "#EF4444", color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99 }}>{item.badge}</span>}
            </button>
          ))}
        </nav>
        <div style={{ padding: 14, borderTop: "1px solid #1E1E2A" }}>
          <div style={{ background: "#1E1E2A", borderRadius: 10, padding: 12 }}>
            <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>MRR</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#10B981", fontFamily: "'Space Mono', monospace" }}>€{mrr}</div>
            <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 8 }}>{activeClients.length} active clients</div>
            <div style={{ fontSize: 10, color: "#9CA3AF", marginBottom: 8 }}>{currentUser.name} ({currentUser.role})</div>
            <button onClick={logout} style={{ width: "100%", background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>Logout</button>
          </div>
        </div>
      </div>

      {/* Main */}
      <div style={{ marginLeft: 220, minHeight: "100vh" }}>
        <div style={{ padding: "14px 28px", borderBottom: "1px solid #1E1E2A", display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(17,17,24,0.8)", backdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 5 }}>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#fff" }}>{{ queue: t("tabs.queue", "Review Queue"), workboard: t("tabs.workboard", "Staff Workboard"), staff: t("tabs.staff", "Staff Directory"), clients: t("tabs.clients", "Client Management"), selfserve: t("tabs.selfserve", "Self-Serve Operations"), outreach: t("tabs.outreach", "Outreach & Marketing"), contracts: t("tabs.contracts", "Contracts"), analytics: t("tabs.analytics", "Analytics"), mailbox: "Company Mailbox", "security-events": "Security Events", settings: t("tabs.settings", "Settings") }[tab]}</h1>
          <div style={{ display: "flex", gap: 8 }}>
            <select value={uiLanguage} onChange={(e) => setAppLanguage(e.target.value)} style={{ background: "#1E1E2A", border: "1px solid #2D2D3A", borderRadius: 8, color: "#E5E5E5", padding: "7px 10px", fontSize: 12, outline: "none" }}>
              {UI_LANGUAGE_OPTIONS.map((lang) => <option key={`agency-ui-lang-${lang.code}`} value={lang.code}>{lang.label}</option>)}
            </select>
            {tab === "queue" && <>
              <select value={selClient} onChange={e => setSelClient(e.target.value)} style={{ background: "#1E1E2A", border: "1px solid #2D2D3A", borderRadius: 8, color: "#E5E5E5", padding: "7px 10px", fontSize: 12, outline: "none" }}>
                <option value="all">All Clients</option>
                {visibleAgencyClients.filter(c => c.connected).map(c => <option key={c.id} value={c.id}>{c.avatar} {c.name}</option>)}
              </select>
              <select value={filterSt} onChange={e => setFilterSt(e.target.value)} style={{ background: "#1E1E2A", border: "1px solid #2D2D3A", borderRadius: 8, color: "#E5E5E5", padding: "7px 10px", fontSize: 12, outline: "none" }}>
                <option value="all">All Status</option>
                <option value="pending">⏳ Pending</option>
                <option value="approved">✅ Approved</option>
                <option value="published">🌐 Published</option>
                <option value="rejected">❌ Rejected</option>
              </select>
            </>}
            {tab === "outreach" && <button onClick={() => { setModalData({}); setModal("newProspect"); }} style={{ background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ Add Prospect</button>}
            {tab === "clients" && <button onClick={() => setModal("newClient")} style={{ background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ Add Client</button>}
            {tab === "workboard" && <>
              <button onClick={exportWorkboardAuditCsv} style={{ background: "#1E1E2A", color: "#E5E5E5", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 12px", fontSize: 12, cursor: "pointer" }}>Export CSV</button>
              <button onClick={exportWorkboardAuditPdf} style={{ background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Export PDF</button>
            </>}
            {tab === "contracts" && <select value={contractFilter} onChange={e => setContractFilter(e.target.value)} style={{ background: "#1E1E2A", border: "1px solid #2D2D3A", borderRadius: 8, color: "#E5E5E5", padding: "7px 10px", fontSize: 12, outline: "none" }}>
              <option value="all">All Contracts</option>
              <option value="signed">Signed</option>
              <option value="sent">Awaiting Signature</option>
              <option value="none">No Contract</option>
            </select>}
            {tab === "security-events" && currentUser?.role === "admin" && <button onClick={clearAllSecurityEventLogs} style={{ background: "transparent", color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "7px 12px", fontSize: 12, cursor: "pointer" }}>Clear Event Log</button>}
            {mailboxAccessAllowed && <button onClick={() => { setNotifOpen(false); setTab("mailbox"); }} style={{ background: tab === "mailbox" ? "#6366F1" : "#1E1E2A", color: "#E5E5E5", border: tab === "mailbox" ? "1px solid #6366F1" : "1px solid #2D2D3A", borderRadius: 8, padding: "7px 10px", fontSize: 12, cursor: "pointer", position: "relative", minWidth: 42 }} title={dashboardMailbox.mailbox ? `Open mailbox: ${dashboardMailbox.mailbox}` : "Open mailbox"}>
              📧
              {mailboxMessageCount > 0 && <span style={{ position: "absolute", top: -6, right: -6, background: "#EF4444", color: "#fff", borderRadius: 99, fontSize: 10, padding: "1px 6px", fontWeight: 700 }}>{mailboxMessageCount}</span>}
            </button>}
            <div style={{ position: "relative" }}>
              <button onClick={() => setNotifOpen((p) => !p)} style={{ background: "#1E1E2A", color: "#E5E5E5", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 10px", fontSize: 12, cursor: "pointer", position: "relative" }}>
                🔔 Notifications
                {unreadNotificationCount > 0 && <span style={{ position: "absolute", top: -6, right: -6, background: "#EF4444", color: "#fff", borderRadius: 99, fontSize: 10, padding: "1px 6px", fontWeight: 700 }}>{unreadNotificationCount}</span>}
              </button>
              {notifOpen && (
                <div style={{ position: "absolute", right: 0, marginTop: 6, width: 320, maxHeight: 360, overflowY: "auto", background: "#111118", border: "1px solid #1E1E2A", borderRadius: 10, zIndex: 20 }}>
                  {reviewNotifications.length === 0 && <div style={{ padding: 12, fontSize: 12, color: "#9CA3AF" }}>No pending review notifications.</div>}
                  {reviewNotifications.map((n) => (
                    <button key={n.id} onClick={() => openClientBoardFromNotification(n)} style={{ width: "100%", textAlign: "left", padding: 12, background: n.read ? "transparent" : "rgba(99,102,241,0.12)", border: "none", borderBottom: "1px solid #1E1E2A", color: "#E5E5E5", cursor: "pointer" }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{n.message}</div>
                      <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 3 }}>{new Date(n.createdAt).toLocaleString()}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ===== REVIEW QUEUE ===== */}
        {tab === "queue" && <div style={{ padding: "20px 28px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 20 }}>
            {[{ l: "Pending", v: pendingCount, c: "#F59E0B" }, { l: "Published", v: visibleAgencyReviews.filter(r => r.status === "published").length, c: "#10B981" }, { l: "Clients", v: activeClients.length, c: "#EC4899" }, { l: "MRR", v: `€${mrr}`, c: "#6366F1" }].map((s, i) => (
              <div key={i} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18 }}>
                <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{s.l}</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: s.c, fontFamily: "'Space Mono', monospace" }}>{s.v}</div>
              </div>
            ))}
          </div>
          {filteredReviews.map(review => {
            const cl = clients.find(c => c.id === review.clientId);
            const sla = getReviewSlaInfo(review, cl);
            return (
              <div key={review.id} style={{ background: "#111118", border: `1px solid ${sla?.overdue ? "rgba(239,68,68,0.45)" : "#1E1E2A"}`, borderRadius: 14, overflow: "hidden", marginBottom: 14 }}>
                <div style={{ padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #1E1E2A" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 20 }}>{cl?.avatar}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{cl?.name}</div>
                      <div style={{ fontSize: 11, color: "#6B7280" }}>{review.platform} • {review.date} • {review.reviewLanguage || detectReviewLanguage(review.text)}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {sla && sla.hoursAgo !== null && (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 99, background: sla.overdue ? "rgba(239,68,68,0.15)" : "rgba(245,158,11,0.12)", color: sla.overdue ? "#F87171" : "#FCD34D", border: `1px solid ${sla.overdue ? "rgba(239,68,68,0.35)" : "rgba(245,158,11,0.25)"}`, whiteSpace: "nowrap" }}>
                        {sla.overdue ? "⚠ " : "⏱ "}{sla.hoursAgo}h{sla.overdue ? ` — SLA ${sla.slaHours}h exceeded` : ` / ${sla.slaHours}h SLA`}
                      </span>
                    )}
                    <Badge type={review.status} />
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                  <div style={{ padding: 18, borderRight: "1px solid #1E1E2A" }}>
                    <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Customer Review</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}><div style={{ width: 28, height: 28, borderRadius: 99, background: `hsl(${review.author.charCodeAt(0)*7},50%,30%)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff" }}>{review.author[0]}</div><div><div style={{ fontSize: 12, fontWeight: 600, color: "#fff" }}>{review.author}</div><Stars r={review.rating} /></div></div>
                    <p style={{ fontSize: 13, lineHeight: 1.6, color: "#D1D5DB", margin: 0 }}>{review.text}</p>
                  </div>
                  <div style={{ padding: 18, background: "rgba(99,102,241,0.03)" }}>
                    <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}><span style={{ color: "#10B981" }}>AI</span> Response</div>
                    {editRev === review.id ? <div><textarea value={editTxt} onChange={e => setEditTxt(e.target.value)} style={{ ...inp, minHeight: 70, resize: "vertical", borderColor: "#6366F1" }} /><div style={{ display: "flex", gap: 6, marginTop: 8 }}><button disabled={!canEditReview} onClick={() => { if (!canEditReview) return; setReviews(p => p.map(r => r.id === review.id ? stampReviewAction(r, "edited", { aiResponse: editTxt }) : r)); setEditRev(null); notify("Updated"); }} style={{ background: "#6366F1", color: "#fff", border: "none", borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: canEditReview ? "pointer" : "not-allowed", opacity: canEditReview ? 1 : 0.5 }}>Save</button><button onClick={() => setEditRev(null)} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 7, padding: "7px 14px", fontSize: 12, cursor: "pointer" }}>Cancel</button></div></div>
                    : <p style={{ fontSize: 13, lineHeight: 1.6, color: "#D1D5DB", margin: 0 }}>{review.aiResponse}</p>}
                    {review.actionByName && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 8 }}>Handled by: <span style={{ color: "#fff" }}>{review.actionByName}</span> ({review.actionByUsername})</div>}
                    {review.status === "pending" && editRev !== review.id && <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                      {canRespond && <button onClick={() => { setReviews(p => p.map(r => r.id === review.id ? stampReviewAction(r, "approved", { status: "approved" }) : r)); notify("Approved"); }} style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>✓ Approve</button>}
                      {canEditReview && <button onClick={() => { setEditRev(review.id); setEditTxt(review.aiResponse); }} style={{ background: "transparent", color: "#6366F1", border: "1px solid #6366F1", borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>✎ Edit</button>}
                      {canRespond && <select value={getReviewTone(review.id)} onChange={e => setReviewTone(review.id, e.target.value)} style={{ ...inp, width: "auto", minWidth: 210, padding: "7px 10px", fontSize: 12 }}>
                        {REVIEW_RESPONSE_TONES.map((tone) => <option key={`queue-pending-tone-${review.id}-${tone.id}`} value={tone.id}>{tone.label}</option>)}
                      </select>}
                      {canRespond && <button onClick={() => regenerateResponse(review.id)} style={{ background: "transparent", color: "#F59E0B", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>🔄 Regenerate</button>}
                      {canEscalate && <button onClick={() => { setReviews(p => p.map(r => r.id === review.id ? stampReviewAction(r, "rejected", { status: "rejected" }) : r)); notify("Rejected", "error"); }} style={{ background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 7, padding: "7px 14px", fontSize: 12, cursor: "pointer" }}>✕ Reject</button>}
                    </div>}
                    {review.status === "approved" && <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                      {canPublish && <button onClick={() => { setReviews(p => p.map(r => r.id === review.id ? stampReviewAction(r, "published", { status: "published" }) : r)); notify("Published to Google ✓"); }} style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)", color: "#fff", border: "none", borderRadius: 7, padding: "7px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>🚀 Publish</button>}
                      {canRespond && <button onClick={() => regenerateResponse(review.id)} style={{ background: "transparent", color: "#F59E0B", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>🔄 Regenerate</button>}
                      {canEditReview && <button onClick={() => { setReviews(p => p.map(r => r.id === review.id ? stampReviewAction(r, "back_to_pending", { status: "pending" }) : r)); notify("Moved back to pending"); }} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 7, padding: "7px 14px", fontSize: 12, cursor: "pointer" }}>← Back</button>}
                    </div>}
                    {review.status === "published" && <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                      <div style={{ width: "100%", fontSize: 11, color: "#10B981", marginBottom: 4 }}>✓ Published on {review.platform} — {review.date}</div>
                      {canEscalate && <button onClick={() => { setReviews(p => p.map(r => r.id === review.id ? stampReviewAction(r, "recalled", { status: "approved" }) : r)); notify("Response recalled — now in Approved"); }} style={{ background: "transparent", color: "#F59E0B", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>↩️ Recall</button>}
                      {canEditReview && <button onClick={() => { setEditRev(review.id); setEditTxt(review.aiResponse); setReviews(p => p.map(r => r.id === review.id ? stampReviewAction(r, "edit_republish", { status: "approved" }) : r)); notify("Recalled for editing — approve to republish"); }} style={{ background: "transparent", color: "#6366F1", border: "1px solid #6366F1", borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>✎ Edit & Republish</button>}
                      {canEscalate && <button onClick={() => { if (window.confirm("Delete this published response from Google?")) { setReviews(p => p.map(r => r.id === review.id ? stampReviewAction(r, "deleted", { status: "rejected", aiResponse: "[Response deleted]" }) : r)); notify("Response deleted", "error"); }}} style={{ background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 7, padding: "7px 14px", fontSize: 12, cursor: "pointer" }}>🗑 Delete</button>}
                    </div>}
                    {review.status === "rejected" && <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                      <div style={{ width: "100%", fontSize: 11, color: "#EF4444", marginBottom: 4 }}>Rejected reviews can still be worked on and moved back into queue.</div>
                      {canRespond && <button onClick={() => regenerateResponse(review.id)} style={{ background: "transparent", color: "#F59E0B", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>🔄 Regenerate</button>}
                      {canEditReview && <button onClick={() => { setEditRev(review.id); setEditTxt(review.aiResponse); }} style={{ background: "transparent", color: "#6366F1", border: "1px solid #6366F1", borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>✎ Edit</button>}
                      {canRespond && <button onClick={() => { setReviews(p => p.map(r => r.id === review.id ? stampReviewAction(r, "reopened", { status: "pending" }) : r)); notify("Review moved back to pending"); }} style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>↩ Re-open</button>}
                    </div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>}

        {/* ===== WORKBOARD ===== */}
        {tab === "workboard" && <div style={{ padding: "20px 28px" }}>
          <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 12 }}>Automated board: reviews move based on staff actions. Staff identity is attached to each action.</div>
          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 12, marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>Recent Activity Audit</div>
              <div style={{ fontSize: 11, color: "#9CA3AF" }}>{workboardAudit.length} total actions</div>
            </div>
            {workboardAudit.slice(0, 6).map((r) => {
              const client = clients.find((c) => c.id === r.clientId);
              return (
                <div key={`audit-${r.id}-${r.actionAt}`} style={{ fontSize: 11, color: "#9CA3AF", padding: "6px 0", borderBottom: "1px solid #1E1E2A" }}>
                  <span style={{ color: "#fff" }}>{r.actionByName || "System"}</span> @{r.actionByUsername || "system"} → <span style={{ color: "#6366F1" }}>{r.actionType || "updated"}</span> on <span style={{ color: "#fff" }}>{client?.name || "Unknown"}</span> ({r.status}) • {new Date(r.actionAt).toLocaleString()}
                </div>
              );
            })}
            {!workboardAudit.length && <div style={{ fontSize: 11, color: "#6B7280" }}>No actions yet. Perform review actions to start audit logging.</div>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
            {[
              { id: "todo", title: "To Do", color: "#6B7280" },
              { id: "inprogress", title: "In Progress", color: "#F59E0B" },
              { id: "done", title: "Done", color: "#10B981" },
            ].map((col) => (
              <div key={col.id} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 12, minHeight: 420 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{col.title}</div>
                  <span style={{ fontSize: 11, color: col.color }}>{workboardColumns[col.id].length}</span>
                </div>
                {workboardColumns[col.id].map((r) => {
                  const c = clients.find((x) => x.id === r.clientId);
                  return (
                    <div key={r.id} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 10, marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#fff", marginBottom: 4 }}>{c?.name} • {r.author}</div>
                      <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 6 }}>{r.text.slice(0, 90)}{r.text.length > 90 ? "..." : ""}</div>
                      <div style={{ fontSize: 10, color: "#6B7280" }}>Status: {r.status}</div>
                      {r.actionByName && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                          <div style={{ width: 22, height: 22, borderRadius: 99, background: "#1E1E2A", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                            {r.actionByAvatar ? <img src={r.actionByAvatar} alt={r.actionByName} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 11, color: "#fff" }}>{(r.actionByName || "?")[0]}</span>}
                          </div>
                          <div style={{ fontSize: 10, color: "#9CA3AF" }}>
                            <div style={{ color: "#fff" }}>{r.actionByName}</div>
                            <div>@{r.actionByUsername} • {r.actionType || "updated"}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>}

        {/* ===== STAFF ===== */}
        {tab === "staff" && <div style={{ padding: "20px 28px", maxWidth: 1080 }}>
          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 8 }}>Suggested title ladder (lowest to highest)</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {STAFF_TITLES_LADDER.map((t, i) => (
                <span key={t} style={{ fontSize: 11, color: "#D1D5DB", background: "#0A0A0F", border: "1px solid #2D2D3A", borderRadius: 99, padding: "4px 10px" }}>
                  {i + 1}. {t}
                </span>
              ))}
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: "#6B7280" }}>Scope strategy: default is <strong style={{ color: "#9CA3AF" }}>country</strong> (recommended), with optional city-level or global responsibility.</div>
          </div>

          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 8 }}>Filter Staff</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              <input placeholder="Search name/username/email/phone" value={staffFilter.query} onChange={(e) => setStaffFilter((p) => ({ ...p, query: e.target.value }))} style={inp} />
              <input placeholder="City or all" value={staffFilter.city === "all" ? "" : staffFilter.city} onChange={(e) => setStaffFilter((p) => ({ ...p, city: e.target.value || "all" }))} style={inp} />
              <input placeholder="Country or all" value={staffFilter.country === "all" ? "" : staffFilter.country} onChange={(e) => setStaffFilter((p) => ({ ...p, country: e.target.value || "all" }))} style={inp} />
            </div>
          </div>

          {canManageStaff && <div style={{ marginBottom: 14, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 18 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 600, color: "#fff" }}>Create Staff</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div><label style={lbl}>First Name</label><input value={staffForm.firstName} onChange={(e) => setStaffForm((p) => ({ ...p, firstName: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Last Name</label><input value={staffForm.lastName} onChange={(e) => setStaffForm((p) => ({ ...p, lastName: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Gender</label><select value={staffForm.gender} onChange={(e) => setStaffForm((p) => ({ ...p, gender: e.target.value }))} style={inp}><option value="">Select gender</option><option value="female">Female</option><option value="male">Male</option><option value="other">Other</option></select></div>
              <div><label style={lbl}>Username</label><input value={staffForm.username} onChange={(e) => setStaffForm((p) => ({ ...p, username: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Password</label><input type="password" value={staffForm.password} onChange={(e) => setStaffForm((p) => ({ ...p, password: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Email</label><input value={staffForm.email} onChange={(e) => setStaffForm((p) => ({ ...p, email: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Phone</label><input value={staffForm.phone} onChange={(e) => setStaffForm((p) => ({ ...p, phone: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>City</label><input value={staffForm.city} onChange={(e) => setStaffForm((p) => ({ ...p, city: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Country</label><input value={staffForm.country} onChange={(e) => setStaffForm((p) => ({ ...p, country: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Title</label><select value={staffForm.title} onChange={(e) => setStaffForm((p) => ({ ...p, title: e.target.value }))} style={inp}>{STAFF_TITLES_LADDER.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
              <div><label style={lbl}>Scope</label><select value={staffForm.scopeLevel} onChange={(e) => setStaffForm((p) => ({ ...p, scopeLevel: e.target.value, scopeValue: "" }))} style={inp}>{STAFF_SCOPE_OPTIONS.map((v) => <option key={v} value={v}>{v[0].toUpperCase() + v.slice(1)}</option>)}</select></div>
              <div><label style={lbl}>Scope Value</label><input placeholder={staffForm.scopeLevel === "city" ? "e.g. Berlin" : staffForm.scopeLevel === "country" ? "e.g. Germany" : "All Regions"} value={staffForm.scopeValue} onChange={(e) => setStaffForm((p) => ({ ...p, scopeValue: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Preferred Language</label><select value={staffForm.preferredLanguage} onChange={(e) => setStaffForm((p) => ({ ...p, preferredLanguage: e.target.value }))} style={inp}>{LANGUAGE_OPTIONS.map((lang) => <option key={`create-lang-${lang}`} value={lang}>{lang}</option>)}</select></div>
              {currentUser?.role === "admin" && <div><label style={lbl}>Role</label><select value={staffForm.role} onChange={(e) => setStaffForm((p) => ({ ...p, role: e.target.value, permissions: e.target.value === "admin" ? ALL_PERMISSIONS : p.permissions }))} style={inp}><option value="staff">Staff</option><option value="admin">Admin</option></select></div>}
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={lbl}>{t("staff.privileges", "Privileges")}</label>
              {staffForm.role === "admin" ? <div style={{ fontSize: 12, color: "#10B981", padding: "8px 12px", background: "rgba(16,185,129,0.1)", borderRadius: 8, border: "1px solid rgba(16,185,129,0.25)" }}>Admin accounts receive all permissions automatically.</div> : <PermissionMatrix
                selected={staffForm.permissions}
                onChange={(permissions) => setStaffForm((p) => ({ ...p, permissions }))}
                t={t}
                idPrefix="staff-create"
              />}
            </div>
            <button onClick={addStaff} style={{ marginTop: 12, background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontWeight: 700, cursor: "pointer" }}>Create Staff Account</button>
          </div>}

          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 16 }}>
            {filteredStaff.map((s) => (
              <div key={`staff-page-${s.id}`} style={{ padding: "8px 0", borderBottom: "1px solid #1E1E2A" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <div style={{ width: 30, height: 30, borderRadius: 99, background: "#1E1E2A", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {s.avatar ? <img src={s.avatar} alt={s.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 11, color: "#fff" }}>{(s.name || "?")[0]}</span>}
                    </div>
                    <div style={{ fontSize: 12 }}>
                      <div style={{ color: "#fff" }}>{s.name} <span style={{ color: "#6B7280" }}>@{s.username}</span> <span style={{ color: "#6B7280" }}>• {s.city || "—"}, {s.country || "—"}</span></div>
                      <div style={{ color: "#A78BFA" }}>{s.title || DEFAULT_STAFF_TITLE} • {String(s.scopeLevel || "country").toUpperCase()}: {s.scopeValue || (s.scopeLevel === "city" ? s.city : s.country) || "Unassigned"}</div>
                      <div style={{ color: "#9CA3AF" }}>Language: {s.preferredLanguage || "English"}</div>
                      <div style={{ color: "#9CA3AF" }}>{s.email || "—"} • {s.phone || "—"}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ fontSize: 10, color: "#9CA3AF" }}>{s.permissions?.length || 0} perms</div>
                    {(s.role !== "admin" || currentUser?.role === "admin") && (canManageStaff || canResetStaffPassword) && <button onClick={() => startEditStaff(s)} style={{ background: "transparent", color: "#6366F1", border: "1px solid #6366F1", borderRadius: 7, padding: "4px 8px", fontSize: 10, cursor: "pointer" }}>Edit</button>}
                    {s.role !== "admin" && canResetAllSecurityLayers && <button onClick={() => resetSecurityLayerForStaffMember(s.id)} style={{ background: "transparent", color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.45)", borderRadius: 7, padding: "4px 8px", fontSize: 10, cursor: "pointer" }}>Reset Layer</button>}
                    {s.role !== "admin" && canManageStaff && <button onClick={() => deleteStaff(s.id)} style={{ background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 7, padding: "4px 8px", fontSize: 10, cursor: "pointer" }}>Delete</button>}
                  </div>
                </div>

                {editingStaffId === s.id && (
                  <div style={{ marginTop: 10, padding: 10, background: "#111118", border: "1px solid #2D2D3A", borderRadius: 8 }}>
                    {canManageStaff ? <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      <input placeholder="First Name" value={staffEditForm.firstName} onChange={(e) => setStaffEditForm((p) => ({ ...p, firstName: e.target.value }))} style={inp} />
                      <input placeholder="Last Name" value={staffEditForm.lastName} onChange={(e) => setStaffEditForm((p) => ({ ...p, lastName: e.target.value }))} style={inp} />
                      <select value={staffEditForm.gender} onChange={(e) => setStaffEditForm((p) => ({ ...p, gender: e.target.value }))} style={inp}><option value="">Select gender</option><option value="female">Female</option><option value="male">Male</option><option value="other">Other</option></select>
                      <input placeholder="Email" value={staffEditForm.email} onChange={(e) => setStaffEditForm((p) => ({ ...p, email: e.target.value }))} style={inp} />
                      <input placeholder="New username (leave blank to keep)" value={staffEditForm.newUsername} onChange={(e) => setStaffEditForm((p) => ({ ...p, newUsername: e.target.value }))} style={inp} />
                      <input placeholder="Phone" value={staffEditForm.phone} onChange={(e) => setStaffEditForm((p) => ({ ...p, phone: e.target.value }))} style={inp} />
                      <input placeholder="City" value={staffEditForm.city} onChange={(e) => setStaffEditForm((p) => ({ ...p, city: e.target.value }))} style={inp} />
                      <input placeholder="Country" value={staffEditForm.country} onChange={(e) => setStaffEditForm((p) => ({ ...p, country: e.target.value }))} style={inp} />
                      <select value={staffEditForm.title} onChange={(e) => setStaffEditForm((p) => ({ ...p, title: e.target.value }))} style={inp}>{STAFF_TITLES_LADDER.map((t) => <option key={`edit-title-${t}`} value={t}>{t}</option>)}</select>
                      <select value={staffEditForm.scopeLevel} onChange={(e) => setStaffEditForm((p) => ({ ...p, scopeLevel: e.target.value, scopeValue: "" }))} style={inp}>{STAFF_SCOPE_OPTIONS.map((v) => <option key={`edit-scope-${v}`} value={v}>{v[0].toUpperCase() + v.slice(1)}</option>)}</select>
                      <input placeholder={staffEditForm.scopeLevel === "city" ? "Scope city" : staffEditForm.scopeLevel === "country" ? "Scope country" : "All Regions"} value={staffEditForm.scopeValue} onChange={(e) => setStaffEditForm((p) => ({ ...p, scopeValue: e.target.value }))} style={inp} />
                      <select value={staffEditForm.preferredLanguage} onChange={(e) => setStaffEditForm((p) => ({ ...p, preferredLanguage: e.target.value }))} style={inp}>{LANGUAGE_OPTIONS.map((lang) => <option key={`edit-lang-${lang}`} value={lang}>{lang}</option>)}</select>
                      <input type="password" placeholder="Optional password reset" value={staffEditForm.resetPassword} onChange={(e) => setStaffEditForm((p) => ({ ...p, resetPassword: e.target.value }))} style={inp} />
                      {currentUser?.role === "admin" && s.id !== currentUser?.id && <select value={staffEditForm.role} onChange={(e) => setStaffEditForm((p) => ({ ...p, role: e.target.value, permissions: e.target.value === "admin" ? ALL_PERMISSIONS : p.permissions }))} style={inp}><option value="staff">Role: Staff</option><option value="admin">Role: Admin</option></select>}
                    </div> : <div>
                      <input type="password" placeholder="New password for staff" value={staffEditForm.resetPassword} onChange={(e) => setStaffEditForm((p) => ({ ...p, resetPassword: e.target.value }))} style={inp} />
                      <div style={{ marginTop: 6, fontSize: 11, color: "#9CA3AF" }}>Staff will be required to change this password on next login.</div>
                    </div>}

                    {canManageStaff && <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 6 }}>{t("staff.privileges", "Privileges")}</div>
                      {staffEditForm.role === "admin" ? <div style={{ fontSize: 12, color: "#10B981", padding: "8px 12px", background: "rgba(16,185,129,0.1)", borderRadius: 8, border: "1px solid rgba(16,185,129,0.25)" }}>Admin accounts receive all permissions automatically.</div> : <PermissionMatrix
                        selected={staffEditForm.permissions}
                        onChange={(permissions) => setStaffEditForm((p) => ({ ...p, permissions }))}
                        t={t}
                        idPrefix={`staff-edit-${s.id}`}
                      />}
                    </div>}

                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <button onClick={saveStaffEdit} style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 7, padding: "6px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Save</button>
                      <button onClick={() => setEditingStaffId(null)} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 7, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>}

        {tab === "messages" && (() => {
          const staffLocation = (m) => [m.city, m.country].filter(Boolean).join(", ") || "—";
          const availableRecipients = getAvailableProblemRecipients().filter((m) => m.id !== currentUser?.id);
          const recipientSearchQ = staffRecipientSearch.trim().toLowerCase();
          const filteredRecipients = recipientSearchQ
            ? availableRecipients.filter((m) =>
                m.username.toLowerCase().includes(recipientSearchQ)
                || m.name.toLowerCase().includes(recipientSearchQ)
                || (m.city || "").toLowerCase().includes(recipientSearchQ)
                || (m.country || "").toLowerCase().includes(recipientSearchQ))
            : availableRecipients;
          const selectedRecipient = availableRecipients.find((m) => m.id === staffProblemForm.recipientId);

          const visibleThreads = staffProblemThreads
            .filter((thread) => canAccessProblemThread(thread, currentUser))
            .sort((a, b) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime());

          const directoryQuery = staffDirectorySearch.trim().toLowerCase();
          const directoryContacts = directoryQuery
            ? availableRecipients.filter((m) =>
                m.username.toLowerCase().includes(directoryQuery)
                || m.name.toLowerCase().includes(directoryQuery)
                || (m.city || "").toLowerCase().includes(directoryQuery)
                || (m.country || "").toLowerCase().includes(directoryQuery))
            : availableRecipients;

          const activeThread = visibleThreads.find((thread) => thread.id === activeStaffThreadId) || visibleThreads[0] || null;
          const chatCompact = typeof window !== "undefined" && window.innerWidth < 980;
          const activeThreadMessages = activeThread
            ? [
                {
                  id: activeThread.id,
                  senderId: activeThread.senderId,
                  senderName: activeThread.senderName,
                  senderUsername: activeThread.senderUsername,
                  senderLoc: [activeThread.senderCity, activeThread.senderCountry].filter(Boolean).join(", ") || "—",
                  message: activeThread.message,
                  attachments: activeThread.attachments || [],
                  createdAt: activeThread.createdAt,
                  isOriginal: true,
                },
                ...(activeThread.replies || []).map((reply) => ({
                  id: reply.id,
                  senderId: reply.senderId,
                  senderName: reply.senderName,
                  senderUsername: reply.senderUsername,
                  senderLoc: [reply.senderCity, reply.senderCountry].filter(Boolean).join(", ") || "—",
                  message: reply.message,
                  attachments: reply.attachments || [],
                  createdAt: reply.createdAt,
                  isOriginal: false,
                })),
              ]
            : [];

          return (
            <div style={{ padding: "20px 28px", background: "radial-gradient(circle at 20% 10%, rgba(124,58,237,0.09), transparent 45%), radial-gradient(circle at 85% 85%, rgba(99,102,241,0.08), transparent 42%)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#F8FAFC", letterSpacing: "0.01em" }}>Staff Messenger</div>
                  <div style={{ fontSize: 12, color: "#CBD5E1" }}>Modern chat layout · voice notes · attachments · no call controls</div>
                </div>
                {canStaffMessage && (
                  <button
                    onClick={() => {
                      setStaffComposeOpen((p) => !p);
                      setStaffProblemForm({ subject: "", message: "", mode: "area", recipientId: "", clientId: "" });
                      setStaffProblemAttachments([]);
                      setStaffRecipientSearch("");
                    }}
                    style={{ background: staffComposeOpen ? "#1E1E2A" : "#6366F1", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 10, padding: "9px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                    {staffComposeOpen ? "✕ Close" : "+ New Chat"}
                  </button>
                )}
              </div>

              {staffDeleteUndo && (
                <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "10px 12px", background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 10 }}>
                  <div style={{ fontSize: 12, color: "#FDE68A" }}>
                    {staffDeleteUndo.type === "thread" ? "Thread deleted" : "Reply deleted"}. Undo is available for 10 seconds.
                  </div>
                  <button onClick={undoStaffDelete} style={{ background: "#F59E0B", color: "#111827", border: "none", borderRadius: 7, padding: "6px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Undo</button>
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: chatCompact ? "1fr" : "320px 1fr", gap: 14 }}>
                <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 24, overflow: "hidden", minHeight: 620, boxShadow: "0 18px 42px rgba(15,23,42,0.18)" }}>
                  <div style={{ padding: 12, borderBottom: "1px solid #E2E8F0" }}>
                    <input
                      value={staffDirectorySearch}
                      onChange={(e) => setStaffDirectorySearch(e.target.value)}
                      placeholder="Search contacts..."
                      style={{ ...inp, background: "#FFFFFF", border: "1px solid #D1D5DB", color: "#111827" }}
                    />
                  </div>
                  <div style={{ padding: 10, borderBottom: "1px solid #E2E8F0" }}>
                    <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.05em" }}>Contacts</div>
                    <div style={{ display: "grid", gap: 6, maxHeight: 180, overflowY: "auto" }}>
                      {directoryContacts.map((contact) => (
                        <button
                          key={`contact-${contact.id}`}
                          onClick={() => {
                            setStaffComposeOpen(true);
                            setStaffProblemForm((prev) => ({ ...prev, mode: "direct", recipientId: contact.id }));
                            setStaffRecipientSearch(`@${contact.username}`);
                          }}
                          style={{ display: "flex", alignItems: "center", gap: 8, background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 12, padding: "8px 10px", cursor: "pointer", textAlign: "left" }}>
                          <div style={{ width: 28, height: 28, borderRadius: 999, background: "linear-gradient(135deg,#4F46E5,#7C3AED)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>{(contact.name || "?")[0]}</div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12, color: "#111827", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{contact.name}</div>
                            <div style={{ fontSize: 10, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>@{contact.username} • {staffLocation(contact)}</div>
                          </div>
                        </button>
                      ))}
                      {!directoryContacts.length && <div style={{ fontSize: 11, color: "#9CA3AF", padding: 6 }}>No contacts found.</div>}
                    </div>
                  </div>
                  <div style={{ padding: 10 }}>
                    <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.05em" }}>Conversations</div>
                    <div style={{ display: "grid", gap: 6, maxHeight: 360, overflowY: "auto" }}>
                      {visibleThreads.map((thread) => {
                        const isActive = activeThread && thread.id === activeThread.id;
                        const lastReply = (thread.replies || [])[thread.replies.length - 1];
                        const lastText = lastReply?.message || thread.message || "";
                        return (
                          <button
                            key={`thread-list-${thread.id}`}
                            onClick={() => setActiveStaffThreadId(thread.id)}
                            style={{ background: isActive ? "linear-gradient(135deg, rgba(99,102,241,0.18), rgba(124,58,237,0.14))" : "#FFFFFF", border: `1px solid ${isActive ? "rgba(99,102,241,0.45)" : "#E5E7EB"}`, borderRadius: 12, padding: "9px 10px", cursor: "pointer", textAlign: "left" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                              <div style={{ fontSize: 12, color: "#111827", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{thread.subject || "No subject"}</div>
                              <div style={{ fontSize: 10, color: "#6B7280" }}>{new Date(thread.updatedAt || thread.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                            </div>
                            <div style={{ fontSize: 10, color: "#6B7280", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lastText}</div>
                          </button>
                        );
                      })}
                      {!visibleThreads.length && <div style={{ fontSize: 11, color: "#6B7280", padding: 6 }}>No chats yet.</div>}
                    </div>
                  </div>
                </div>

                <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 24, minHeight: 620, display: "grid", gridTemplateRows: staffComposeOpen && canStaffMessage ? "auto auto 1fr auto" : "auto 1fr auto", boxShadow: "0 18px 42px rgba(15,23,42,0.18)" }}>
                  {activeThread && (
                    <div style={{ padding: "14px 16px", borderBottom: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#FFFFFF" }}>
                      <div>
                        <div style={{ fontSize: 14, color: "#111827", fontWeight: 800 }}>{activeThread.subject}</div>
                        <div style={{ fontSize: 11, color: "#6B7280" }}>Created {new Date(activeThread.createdAt).toLocaleString()}</div>
                      </div>
                      {canDeleteStaffThread(activeThread, currentUser) && (
                        <button onClick={() => setChatDeleteConfirmModal({ open: true, type: "thread", threadId: activeThread.id, msgId: null })} style={{ background: "transparent", color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 7, padding: "5px 10px", fontSize: 11, cursor: "pointer" }}>Delete thread</button>
                      )}
                    </div>
                  )}

                  {staffComposeOpen && canStaffMessage && (
                    <div style={{ padding: 14, borderBottom: "1px solid #E2E8F0", display: "grid", gap: 8, background: "#F9FAFB" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 180px", gap: 8 }}>
                        <input placeholder="Subject" value={staffProblemForm.subject} onChange={(e) => setStaffProblemForm((prev) => ({ ...prev, subject: e.target.value }))} style={{ ...inp, background: "#FFFFFF", border: "1px solid #D1D5DB", color: "#111827" }} />
                        <select value={staffProblemForm.mode} onChange={(e) => { setStaffProblemForm((prev) => ({ ...prev, mode: e.target.value, recipientId: "" })); setStaffRecipientSearch(""); }} style={inp}>
                          <option value="area">Area</option>
                          <option value="country">Country</option>
                          <option value="across">Global</option>
                          <option value="direct">Direct</option>
                        </select>
                      </div>
                      {staffProblemForm.mode === "direct" && (
                        <input placeholder="Search direct recipient..." value={staffRecipientSearch} onChange={(e) => { setStaffRecipientSearch(e.target.value); setStaffProblemForm((prev) => ({ ...prev, recipientId: "" })); }} style={{ ...inp, background: "#FFFFFF", border: "1px solid #D1D5DB", color: "#111827" }} />
                      )}
                      {staffProblemForm.mode === "direct" && !selectedRecipient && filteredRecipients.length > 0 && (
                        <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, maxHeight: 130, overflowY: "auto" }}>
                          {filteredRecipients.map((m) => (
                            <button key={`compose-rec-${m.id}`} onClick={() => { setStaffProblemForm((prev) => ({ ...prev, recipientId: m.id })); setStaffRecipientSearch(`@${m.username}`); }} style={{ width: "100%", textAlign: "left", padding: "8px 10px", background: "transparent", color: "#E5E7EB", border: "none", borderBottom: "1px solid #1E1E2A", cursor: "pointer" }}>{m.name} @{m.username}</button>
                          ))}
                        </div>
                      )}
                      <textarea placeholder="Write your message..." value={staffProblemForm.message} onChange={(e) => setStaffProblemForm((prev) => ({ ...prev, message: e.target.value }))} style={{ ...inp, minHeight: 72, resize: "vertical", background: "#FFFFFF", border: "1px solid #D1D5DB", color: "#111827" }} />
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <label style={{ fontSize: 11, color: "#9CA3AF", cursor: "pointer" }}>📎 Attach<input type="file" multiple onChange={onStaffProblemAttachmentsChange} style={{ display: "none" }} /></label>
                        <button
                          onClick={() => (voiceRecordingTarget?.scope === "compose" ? stopStaffVoiceNoteRecording() : startStaffVoiceNoteRecording({ scope: "compose" }))}
                          style={{ background: voiceRecordingTarget?.scope === "compose" ? "#EF4444" : "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 999, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>
                          {voiceRecordingTarget?.scope === "compose" ? "■ Stop Voice" : "🎙 Voice Note"}
                        </button>
                        {staffProblemAttachments.map((file) => (
                          <button key={`compose-attach-${file.name}-${file.size}`} onClick={() => removeStaffProblemAttachment(file.name)} style={{ background: "rgba(99,102,241,0.16)", border: "1px solid rgba(99,102,241,0.35)", color: "#C7D2FE", borderRadius: 999, fontSize: 11, padding: "4px 10px", cursor: "pointer" }}>{file.name} ×</button>
                        ))}
                        <button onClick={submitStaffProblemMessage} style={{ marginLeft: "auto", background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, cursor: "pointer" }}>Send</button>
                      </div>
                    </div>
                  )}

                  <div style={{ padding: "16px", overflowY: "auto", display: "grid", gap: 12, background: "linear-gradient(180deg,#F8FAFC,#EEF2FF 70%)" }}>
                    {!activeThread && <div style={{ textAlign: "center", color: "#6B7280", paddingTop: 30 }}>Choose a conversation from the left panel.</div>}
                    {activeThreadMessages.map((msg) => {
                      const isMe = msg.senderId === currentUser?.id;
                      return (
                        <div key={`active-msg-${msg.id}`} style={{ display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start" }}>
                          <div style={{ fontSize: 11, color: "#64748B", marginBottom: 4 }}>{msg.senderName} @{msg.senderUsername} • {new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                          <div style={{ maxWidth: "74%", background: isMe ? "linear-gradient(135deg,#7C3AED,#4F46E5)" : "#FFFFFF", border: `1px solid ${isMe ? "rgba(124,58,237,0.4)" : "#E2E8F0"}`, borderRadius: isMe ? "16px 4px 16px 16px" : "4px 16px 16px 16px", color: isMe ? "#FFFFFF" : "#1F2937", padding: "11px 14px", fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap", boxShadow: isMe ? "0 10px 22px rgba(79,70,229,0.3)" : "0 8px 16px rgba(148,163,184,0.18)" }}>{msg.message}</div>
                          {(msg.attachments || []).length > 0 && (
                            <div style={{ marginTop: 6, display: "grid", gap: 6, justifyItems: isMe ? "end" : "start" }}>
                              {msg.attachments.map((file) => {
                                const fileUrl = getStaffAttachmentDataUrl(file);
                                const image = isImageAttachment(file) && !!fileUrl;
                                const audio = isAudioAttachment(file) && !!fileUrl;
                                return (
                                  <div key={`active-msg-att-${msg.id}-${file.name}`} style={{ display: "grid", gap: 4 }}>
                                    {image && <a href={fileUrl} target="_blank" rel="noreferrer"><img src={fileUrl} alt={file.name} style={{ width: 180, maxHeight: 140, objectFit: "cover", borderRadius: 8, border: "1px solid #2D2D3A" }} /></a>}
                                    {audio && <audio controls src={fileUrl} style={{ width: 220 }} />}
                                    {fileUrl ? <a href={fileUrl} download={file.name || "attachment"} style={{ fontSize: 11, color: "#C7D2FE", textDecoration: "none", background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.3)", borderRadius: 999, padding: "3px 9px" }}>📎 {file.name} (download)</a> : <span style={{ fontSize: 11, color: "#FCA5A5" }}>📎 {file.name} unavailable</span>}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {!msg.isOriginal && canDeleteStaffReply(msg, currentUser) && (
                            <button onClick={() => setChatDeleteConfirmModal({ open: true, type: "message", threadId: activeThread.id, msgId: msg.id })} style={{ marginTop: 4, background: "transparent", border: "1px solid rgba(239,68,68,0.35)", color: "#FCA5A5", borderRadius: 6, fontSize: 10, padding: "2px 7px", cursor: "pointer" }}>Delete</button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {activeThread && canAccessProblemThread(activeThread, currentUser) && (
                    <div style={{ borderTop: "1px solid #E2E8F0", padding: 12, display: "grid", gap: 8, background: "#FFFFFF" }}>
                      <textarea
                        placeholder="Type here..."
                        value={staffReplyDrafts[activeThread.id] || ""}
                        onChange={(e) => setStaffReplyDrafts((prev) => ({ ...prev, [activeThread.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) replyStaffProblemMessage(activeThread.id); }}
                        rows={2}
                        style={{ ...inp, resize: "vertical", minHeight: 56, background: "#FFFFFF", border: "1px solid #D1D5DB", color: "#111827" }}
                      />
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <label style={{ fontSize: 11, color: "#9CA3AF", cursor: "pointer" }}>📎 Attach<input type="file" multiple onChange={(e) => onStaffReplyAttachmentsChange(activeThread.id, e)} style={{ display: "none" }} /></label>
                        <button
                          onClick={() => (voiceRecordingTarget?.scope === "reply" && voiceRecordingTarget?.threadId === activeThread.id ? stopStaffVoiceNoteRecording() : startStaffVoiceNoteRecording({ scope: "reply", threadId: activeThread.id }))}
                          style={{ background: voiceRecordingTarget?.scope === "reply" && voiceRecordingTarget?.threadId === activeThread.id ? "#EF4444" : "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 999, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>
                          {voiceRecordingTarget?.scope === "reply" && voiceRecordingTarget?.threadId === activeThread.id ? "■ Stop Voice" : "🎙 Voice Note"}
                        </button>
                        {(staffReplyAttachments[activeThread.id] || []).map((file) => (
                          <button key={`reply-attach-${activeThread.id}-${file.name}-${file.size}`} onClick={() => removeStaffReplyAttachment(activeThread.id, file.name)} style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)", color: "#A7F3D0", borderRadius: 999, fontSize: 11, padding: "3px 9px", cursor: "pointer" }}>{file.name} ×</button>
                        ))}
                        <button onClick={() => replyStaffProblemMessage(activeThread.id)} style={{ marginLeft: "auto", background: "#10B981", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Send Reply</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ===== CLIENTS ===== */}
        {tab === "clients" && <div style={{ padding: "20px 28px" }}>
          {!selectedClient && <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 20 }}>
              {[{ l: "Active", v: activeClients.length, c: "#10B981" }, { l: "Overdue", v: visibleAgencyClients.filter(c => c.paymentStatus === "overdue").length, c: "#EF4444" }, { l: "Prospects", v: visibleAgencyClients.filter(c => c.status === "prospect").length, c: "#F59E0B" }, { l: "MRR", v: `€${mrr}`, c: "#6366F1" }].map((s, i) => (
                <div key={i} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18 }}>
                  <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{s.l}</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: s.c, fontFamily: "'Space Mono', monospace" }}>{s.v}</div>
                </div>
              ))}
            </div>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 12, marginBottom: 12 }}>
              <label style={lbl}>Client Directory</label>
              <div style={{ display: "grid", gridTemplateColumns: "1.4fr 180px 180px", gap: 8 }}>
                <input
                  placeholder="Search name, contact, email, city, country or plan"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  style={inp}
                />
                <select value={clientStatusFilter} onChange={(e) => setClientStatusFilter(e.target.value)} style={inp}>
                  <option value="all">All statuses</option>
                  <option value="active">Active</option>
                  <option value="prospect">Prospect</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="suspended">Suspended</option>
                </select>
                <select value={clientSort} onChange={(e) => setClientSort(e.target.value)} style={inp}>
                  <option value="name-asc">Sort: Name A-Z</option>
                  <option value="name-desc">Sort: Name Z-A</option>
                  <option value="reviews-desc">Sort: Most Reviews</option>
                  <option value="reviews-asc">Sort: Least Reviews</option>
                  <option value="newest">Sort: Newest Join Date</option>
                  <option value="oldest">Sort: Oldest Join Date</option>
                </select>
              </div>
              <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <div style={{ fontSize: 11, color: "#9CA3AF" }}>Showing {pagedClientDirectory.length} of {sortedClientDirectory.length} filtered ({visibleAgencyClients.length} total)</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <button
                    onClick={() => setClientPage((p) => Math.max(1, p - 1))}
                    disabled={currentClientPage <= 1}
                    style={{ background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: currentClientPage <= 1 ? "not-allowed" : "pointer", opacity: currentClientPage <= 1 ? 0.5 : 1 }}
                  >Prev</button>
                  <button
                    onClick={() => setClientPage((p) => Math.min(totalClientPages, p + 1))}
                    disabled={currentClientPage >= totalClientPages}
                    style={{ background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: currentClientPage >= totalClientPages ? "not-allowed" : "pointer", opacity: currentClientPage >= totalClientPages ? 0.5 : 1 }}
                  >Next</button>
                  <span style={{ fontSize: 11, color: "#9CA3AF" }}>Page {currentClientPage} of {totalClientPages}</span>
                </div>
              </div>
            </div>
            {pagedClientDirectory.map(c => {
              const plan = PLANS.find(p => p.id === c.plan);
              const clientPending = visibleAgencyReviews.filter((r) => r.clientId === c.id && r.status === "pending").length;
              const ratingGrowth = getClientRatingGrowth(c);
              return (
                <div key={c.id} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 20, marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      <div style={{ width: 42, height: 42, borderRadius: 10, background: "#1E1E2A", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{c.avatar}</div>
                      <div>
                        <button onClick={() => setSelectedClientId(c.id)} style={{ background: "none", border: "none", padding: 0, margin: 0, color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", textAlign: "left" }}>{c.name}</button>
                        <div style={{ fontSize: 11, color: "#6B7280" }}>{c.contact} • {c.email}</div>
                        <div style={{ fontSize: 11, color: "#6B7280" }}>{c.phone || "No phone"} • Joined {c.joinDate}</div>
                        <div style={{ fontSize: 11, color: "#6B7280" }}>{formatAddress(c)}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      {clientPending > 0 && <span style={{ background: "rgba(245,158,11,0.2)", color: "#F59E0B", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 99, padding: "4px 10px", fontSize: 11 }}>{clientPending} new</span>}
                      {c.paymentStatus === "overdue" && <Badge type="overdue" label="Payment Overdue" />}
                      <Badge type={c.contractStatus} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10, marginBottom: 14 }}>
                    {[{ l: "Plan", v: plan?.name || "—", c: "#6366F1" }, { l: "Price", v: `€${plan?.price || 0}/mo`, c: "#10B981" }, { l: "Reviews", v: c.reviewCount, c: "#F59E0B" }, { l: "Growth", v: formatSignedDelta(ratingGrowth), c: ratingGrowth >= 0 ? "#34D399" : "#F87171" }, { l: "Next Payment", v: c.nextPayment || "—", c: c.paymentStatus === "overdue" ? "#EF4444" : "#6B7280" }].map((s, i) => (
                      <div key={i} style={{ background: "#0A0A0F", borderRadius: 8, padding: 10, textAlign: "center" }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: s.c, fontFamily: "'Space Mono', monospace" }}>{s.v}</div>
                        <div style={{ fontSize: 9, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.l}</div>
                      </div>
                    ))}
                  </div>
                  {(() => {
                    const cRevs = reviews.filter((r) => String(r.clientId) === String(c.id));
                    const cTotal = cRevs.length;
                    if (cTotal === 0) return null;
                    const cPos = cRevs.filter((r) => Number(r.rating || 0) >= 4).length;
                    const cNeu = cRevs.filter((r) => Number(r.rating || 0) === 3).length;
                    const cPctPos = Math.round((cPos / cTotal) * 100);
                    const cPctNeu = Math.round((cNeu / cTotal) * 100);
                    const cPctNeg = 100 - cPctPos - cPctNeu;
                    return (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                          <span>Sentiment ({cTotal} reviews)</span>
                          <span><span style={{ color: "#10B981" }}>{cPctPos}% pos</span> · <span style={{ color: "#F59E0B" }}>{cPctNeu}% neu</span> · <span style={{ color: "#EF4444" }}>{cPctNeg}% neg</span></span>
                        </div>
                        <div style={{ display: "flex", height: 7, borderRadius: 99, overflow: "hidden", background: "#0A0A0F" }}>
                          {cPctPos > 0 && <div style={{ width: `${cPctPos}%`, background: "#10B981" }} />}
                          {cPctNeu > 0 && <div style={{ width: `${cPctNeu}%`, background: "#F59E0B" }} />}
                          {cPctNeg > 0 && <div style={{ width: `${cPctNeg}%`, background: "#EF4444" }} />}
                        </div>
                      </div>
                    );
                  })()}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button onClick={() => setSelectedClientId(c.id)} style={{ background: "#6366F1", color: "#fff", border: "none", borderRadius: 7, padding: "7px 14px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Open Client Board</button>
                    <button onClick={() => (c.connected ? disconnectGoogleClient(c) : startGoogleConnection(c))} disabled={googleBusyClientId === c.id} style={{ background: c.connected ? "transparent" : "#10B981", color: c.connected ? "#9CA3AF" : "#fff", border: c.connected ? "1px solid #2D2D3A" : "none", borderRadius: 7, padding: "7px 14px", fontSize: 11, fontWeight: 600, cursor: googleBusyClientId === c.id ? "wait" : "pointer", opacity: googleBusyClientId === c.id ? 0.7 : 1 }}>
                      {googleBusyClientId === c.id ? "⏳ Working..." : (c.connected ? "🔌 Disconnect Google" : "🔗 Connect Google")}
                    </button>
                    <button onClick={() => syncGoogleReviewsForClient(c)} disabled={googleBusyClientId === c.id || !c.connected} style={{ background: "#1E1E2A", color: "#E5E5E5", border: "1px solid #2D2D3A", borderRadius: 7, padding: "7px 14px", fontSize: 11, fontWeight: 600, cursor: googleBusyClientId === c.id || !c.connected ? "not-allowed" : "pointer", opacity: googleBusyClientId === c.id || !c.connected ? 0.5 : 1 }}>
                      🔄 Sync Reviews
                    </button>
                    <button onClick={() => (isTrustpilotConnected(c) ? disconnectTrustpilotClient(c) : startTrustpilotConnection(c))} disabled={trustpilotBusyClientId === c.id} style={{ background: isTrustpilotConnected(c) ? "transparent" : "#10B981", color: isTrustpilotConnected(c) ? "#9CA3AF" : "#fff", border: isTrustpilotConnected(c) ? "1px solid #2D2D3A" : "none", borderRadius: 7, padding: "7px 14px", fontSize: 11, fontWeight: 600, cursor: trustpilotBusyClientId === c.id ? "wait" : "pointer", opacity: trustpilotBusyClientId === c.id ? 0.7 : 1 }}>
                      {trustpilotBusyClientId === c.id ? "⏳ Working..." : (isTrustpilotConnected(c) ? "🔌 Disconnect Trustpilot" : "🔗 Connect Trustpilot")}
                    </button>
                    <button onClick={() => syncTrustpilotReviewsForClient(c)} disabled={trustpilotBusyClientId === c.id || !isTrustpilotConnected(c)} style={{ background: "#1E1E2A", color: "#E5E5E5", border: "1px solid #2D2D3A", borderRadius: 7, padding: "7px 14px", fontSize: 11, fontWeight: 600, cursor: trustpilotBusyClientId === c.id || !isTrustpilotConnected(c) ? "not-allowed" : "pointer", opacity: trustpilotBusyClientId === c.id || !isTrustpilotConnected(c) ? 0.5 : 1 }}>
                      🔄 Sync Trustpilot
                    </button>
                    {can("client.view") && <button onClick={() => generateReport(c)} style={{ background: "#1E1E2A", color: "#E5E5E5", border: "1px solid #2D2D3A", borderRadius: 7, padding: "7px 14px", fontSize: 11, fontWeight: 500, cursor: "pointer" }}>📊 Generate Report</button>}
                    {currentUser?.role === "admin" && <button onClick={() => deleteAgencyClient(c.id)} style={{ background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 7, padding: "7px 14px", fontSize: 11, fontWeight: 600, cursor: "pointer", marginLeft: "auto" }}>🗑 Delete Client</button>}
                  </div>
                </div>
              );
            })}
            {pagedClientDirectory.length === 0 && <div style={{ background: "#111118", border: "1px dashed #2D2D3A", borderRadius: 12, padding: 16, color: "#9CA3AF", fontSize: 12 }}>No clients match your filters.</div>}
          </>}

          {selectedClient && <div style={{ display: "grid", gap: 12 }}>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", marginBottom: 14 }}>
                <div>
                  <button onClick={() => setSelectedClientId(null)} style={{ background: "none", border: "none", color: "#9CA3AF", fontSize: 11, cursor: "pointer", padding: 0, marginBottom: 6 }}>← Back to clients</button>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontSize: 26 }}>{selectedClient.avatar || "🏢"}</div>
                    <div>
                      <div style={{ fontSize: 19, fontWeight: 700, color: "#fff" }}>{selectedClient.name}</div>
                      <div style={{ fontSize: 11, color: "#6B7280" }}>{selectedClient.contact} • {selectedClient.email} • {selectedClient.phone || "No phone"}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>{formatAddress(selectedClient)}</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(110px,1fr))", gap: 8, width: 280 }}>
                  {[{ l: "Plan", v: selectedClient.plan === "enterprise" ? "Pro" : (PLANS.find((p) => p.id === selectedClient.plan)?.name || "N/A"), c: "#6366F1" }, { l: "Price", v: fmtCurrency(PLANS.find((p) => p.id === selectedClient.plan)?.price || 0), c: "#10B981" }, { l: "Status", v: selectedClient.contractStatus, c: "#F59E0B" }, { l: "Next Payment", v: selectedClient.nextPayment || "—", c: "#0EA5E9" }].map((k) => (
                    <div key={k.l} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: "8px 10px" }}>
                      <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{k.l}</div>
                      <div style={{ fontSize: 12, color: k.c, fontWeight: 700 }}>{String(k.v)}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => { setTab("queue"); setSelClient(String(selectedClient.id)); }} style={{ background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Open Client Board</button>
                <button onClick={() => (selectedClient.connected ? disconnectGoogleClient(selectedClient) : startGoogleConnection(selectedClient))} disabled={googleBusyClientId === selectedClient.id} style={{ background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: googleBusyClientId === selectedClient.id ? "wait" : "pointer", opacity: googleBusyClientId === selectedClient.id ? 0.7 : 1 }}>{selectedClient.connected ? "Disconnect Google" : "Connect Google"}</button>
                <button onClick={() => syncGoogleReviewsForClient(selectedClient)} disabled={googleBusyClientId === selectedClient.id} style={{ background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: googleBusyClientId === selectedClient.id ? "wait" : "pointer", opacity: googleBusyClientId === selectedClient.id ? 0.7 : 1 }}>Sync Reviews</button>
                {selectedClient.connected && <button onClick={() => fetchGoogleLocationsForClient(selectedClient)} disabled={googleBusyClientId === selectedClient.id} style={{ background: "#0A0A0F", color: "#93C5FD", border: "1px solid rgba(147,197,253,0.4)", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: googleBusyClientId === selectedClient.id ? "wait" : "pointer", opacity: googleBusyClientId === selectedClient.id ? 0.7 : 1 }}>📍 Fetch Locations</button>}
                <button onClick={() => (isTrustpilotConnected(selectedClient) ? disconnectTrustpilotClient(selectedClient) : startTrustpilotConnection(selectedClient))} disabled={trustpilotBusyClientId === selectedClient.id} style={{ background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: trustpilotBusyClientId === selectedClient.id ? "wait" : "pointer", opacity: trustpilotBusyClientId === selectedClient.id ? 0.7 : 1 }}>{isTrustpilotConnected(selectedClient) ? "Disconnect Trustpilot" : "Connect Trustpilot"}</button>
                <button onClick={() => syncTrustpilotReviewsForClient(selectedClient)} disabled={trustpilotBusyClientId === selectedClient.id || !isTrustpilotConnected(selectedClient)} style={{ background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: trustpilotBusyClientId === selectedClient.id || !isTrustpilotConnected(selectedClient) ? "not-allowed" : "pointer", opacity: trustpilotBusyClientId === selectedClient.id || !isTrustpilotConnected(selectedClient) ? 0.6 : 1 }}>Sync Trustpilot</button>
                <button onClick={() => generateReport(selectedClient)} style={{ background: "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: "pointer" }}>Generate Report</button>
                <Badge type={selectedClient.contractStatus} />
              </div>

              {/* Review Invite Link */}
              <div style={{ marginTop: 12, background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#fff", marginBottom: 8 }}>🔗 Review Invite Link</div>
                <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 8 }}>Share this link with customers to direct them to leave a review. Set it to the client's Google review URL, Trustpilot page, or any platform link.</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    value={selectedClient.inviteLink || ""}
                    onChange={(e) => setClients((prev) => prev.map((c) => c.id === selectedClient.id ? { ...c, inviteLink: e.target.value } : c))}
                    placeholder="https://g.page/r/YOUR-GOOGLE-REVIEW-LINK"
                    style={{ ...inp, flex: 1, marginBottom: 0, fontSize: 12 }}
                  />
                  {selectedClient.inviteLink && (
                    <button
                      onClick={() => { navigator.clipboard.writeText(selectedClient.inviteLink); notify("Invite link copied!"); }}
                      style={{ background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "0 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                    >Copy Link</button>
                  )}
                </div>
              </div>

              {/* Google Locations Manager */}
              {selectedClient.connected && (() => {
                const locs = Array.isArray(selectedClient.googleLocations) ? selectedClient.googleLocations : [];
                const cap = getLocationCap(selectedClient.plan);
                return (
                  <div style={{ marginTop: 12, background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <div style={{ fontSize: 12, color: "#fff", fontWeight: 700 }}>📍 Google Locations
                        <span style={{ marginLeft: 8, background: locs.length > 0 ? "#1E3A2F" : "#1E1E2A", color: locs.length > 0 ? "#10B981" : "#6B7280", fontSize: 10, padding: "2px 7px", borderRadius: 99 }}>
                          {locs.length} / {cap} (plan cap)
                        </span>
                      </div>
                      {locs.length === 0 && <div style={{ fontSize: 11, color: "#9CA3AF" }}>Click "Fetch Locations" above to load</div>}
                    </div>
                    {locs.length > 0 && (
                      <div style={{ display: "grid", gap: 6 }}>
                        {locs.map((loc, i) => (
                          <div key={`loc-${i}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: i < cap ? "#111118" : "#0A0A0F", border: `1px solid ${i < cap ? "#2D2D3A" : "#1E1E2A"}`, borderRadius: 8, padding: "8px 10px", opacity: i < cap ? 1 : 0.4 }}>
                            <div>
                              <div style={{ fontSize: 12, color: i < cap ? "#E5E5E5" : "#6B7280", fontWeight: 600 }}>{loc.label || loc.location}</div>
                              <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>{loc.account}</div>
                            </div>
                            {i >= cap && <span style={{ fontSize: 10, color: "#F59E0B", background: "rgba(245,158,11,0.1)", padding: "2px 7px", borderRadius: 99 }}>Over plan cap — upgrade to sync</span>}
                            {i < cap && <span style={{ fontSize: 10, color: "#10B981" }}>✓ Will sync</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {locs.length > cap && (
                      <div style={{ marginTop: 8, fontSize: 11, color: "#F59E0B" }}>⚠ {locs.length - cap} location(s) exceed the {PLANS.find(p => p.id === selectedClient.plan)?.name || selectedClient.plan} plan cap. Upgrade to Enterprise for up to 10 locations.</div>
                    )}
                  </div>
                );
              })()}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 10 }}>
              {[{ l: "Reviews", v: selectedClientReviewStats.total, c: "#6366F1" }, { l: "New", v: selectedClientReviewStats.pending, c: "#F59E0B" }, { l: "Coverage", v: `${selectedClientCoverage}%`, c: "#10B981" }, { l: "Growth", v: formatSignedDelta(getClientRatingGrowth(selectedClient)), c: getClientRatingGrowth(selectedClient) >= 0 ? "#34D399" : "#F87171" }, { l: "SLA Avg", v: `${selectedClientSlaAvg}h`, c: "#0EA5E9" }, { l: "SLA OK", v: `${Math.round(selectedClientSlaCompliance)}%`, c: "#22C55E" }].map((s) => (
                <div key={s.l} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{s.l}</div>
                  <div style={{ fontSize: 20, color: s.c, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{s.v}</div>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 12 }}>
              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 13, color: "#fff", fontWeight: 700, marginBottom: 8 }}>Client Review Trend</div>
                <TinyTrend data={(selectedClient.monthlyReviews && selectedClient.monthlyReviews.length > 0) ? selectedClient.monthlyReviews : [0, 0, 0]} color="#6366F1" fill="rgba(99,102,241,0.16)" />
                <div style={{ fontSize: 10, color: "#6B7280", marginTop: 6 }}>Last months: {(selectedClient.monthlyReviews || []).join(" • ") || "No trend data"}</div>
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, color: "#fff", fontWeight: 600, marginBottom: 6 }}>Platform-specific performance</div>
                  {selectedClientPlatforms.map((p) => (
                    <div key={`sp-${p.platform}`} style={{ display: "grid", gridTemplateColumns: "1fr 80px 80px 80px", gap: 8, fontSize: 11, color: "#9CA3AF", padding: "6px 0", borderTop: "1px solid #1E1E2A" }}>
                      <div style={{ color: "#fff" }}>{p.platform}</div>
                      <div>{p.reviews} reviews</div>
                      <div style={{ color: "#10B981" }}>{p.coverage}% covered</div>
                      <div style={{ color: "#EC4899" }}>{p.rating || "—"} ★</div>
                    </div>
                  ))}
                  {selectedClientPlatforms.length === 0 && <div style={{ fontSize: 11, color: "#6B7280" }}>No platform data yet.</div>}
                </div>
              </div>
              <div style={{ display: "grid", gap: 12 }}>
                <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 13, color: "#fff", fontWeight: 700, marginBottom: 8 }}>Sentiment Breakdown</div>
                  <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", alignItems: "center", gap: 10 }}>
                    <ProgressDonut value={selectedClientReviewStats.total ? (selectedClientSentiment.positive / selectedClientReviewStats.total) * 100 : 0} color="#10B981" label="Positive" />
                    <div style={{ fontSize: 11, color: "#9CA3AF", lineHeight: 1.8 }}>
                      <div><span style={{ color: "#10B981" }}>●</span> Positive: {selectedClientSentiment.positive}</div>
                      <div><span style={{ color: "#F59E0B" }}>●</span> Neutral: {selectedClientSentiment.neutral}</div>
                      <div><span style={{ color: "#EF4444" }}>●</span> Negative: {selectedClientSentiment.negative}</div>
                    </div>
                  </div>
                </div>
                <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 13, color: "#fff", fontWeight: 700, marginBottom: 8 }}>SLA Performance (Client)</div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <ProgressDonut value={selectedClientSlaCompliance} color="#22C55E" label="Compliant" />
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase" }}>Avg response time</div>
                      <div style={{ fontSize: 24, color: "#0EA5E9", fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{selectedClientSlaAvg}h</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 170px 170px", gap: 8, marginBottom: 12 }}>
                <input placeholder="Search reviews by author/text/response" value={clientReviewSearch} onChange={(e) => setClientReviewSearch(e.target.value)} style={inp} />
                <select value={clientReviewFilterStatus} onChange={(e) => setClientReviewFilterStatus(e.target.value)} style={inp}>
                  <option value="all">All status</option>
                  <option value="pending">New/Pending</option>
                  <option value="approved">Approved</option>
                  <option value="published">Published</option>
                  <option value="rejected">Rejected</option>
                </select>
                <select value={clientReviewFilterPlatform} onChange={(e) => setClientReviewFilterPlatform(e.target.value)} style={inp}>
                  <option value="all">All platforms</option>
                  {[...new Set(selectedClientReviews.map((r) => r.platform))].map((p) => <option key={`platform-${p}`} value={p}>{p}</option>)}
                </select>
              </div>

              {filteredSelectedClientReviews.map((r) => (
                <div key={`client-review-${r.id}`} style={{ background: highlightedReviewId === r.id ? "rgba(99,102,241,0.12)" : "#0A0A0F", border: highlightedReviewId === r.id ? "1px solid #6366F1" : "1px solid #1E1E2A", borderRadius: 10, padding: 12, marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ fontSize: 12, color: "#fff", fontWeight: 600 }}>{r.author} • {r.platform} • {r.date}</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ fontSize: 10, color: "#6B7280" }}>{r.reviewLanguage || detectReviewLanguage(r.text)}</span>
                      <Badge type={r.status} />
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: "#D1D5DB", marginBottom: 6 }}>{r.text}</div>
                  <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 8 }}>Response: {r.aiResponse}</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {canRespond && <button onClick={() => regenerateResponse(r.id)} style={{ background: "transparent", color: "#F59E0B", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 7, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>🔄 Regenerate</button>}
                    {canEditReview && <button onClick={() => { setTab("queue"); setSelClient(String(selectedClient.id)); setFilterSt("all"); setEditRev(r.id); setEditTxt(r.aiResponse); }} style={{ background: "transparent", color: "#6366F1", border: "1px solid #6366F1", borderRadius: 7, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>✎ Open in Queue</button>}
                    {r.status === "rejected" && canRespond && <button onClick={() => { setReviews((p) => p.map((x) => x.id === r.id ? stampReviewAction(x, "reopened", { status: "pending" }) : x)); notify("Review moved back to pending"); }} style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 7, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>↩ Re-open</button>}
                  </div>
                </div>
              ))}
              {filteredSelectedClientReviews.length === 0 && <div style={{ background: "#0A0A0F", border: "1px dashed #2D2D3A", borderRadius: 10, padding: 14, fontSize: 12, color: "#9CA3AF" }}>No reviews match current filters for this client.</div>}
            </div>
          </div>}
        </div>}

        {/* ===== OUTREACH ===== */}
        {tab === "outreach" && <div style={{ padding: "20px 28px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 20 }}>
            {[{ l: "Prospects", v: prospects.length, c: "#6366F1" }, { l: "Contacted", v: prospects.filter(p => p.status === "contacted").length, c: "#F59E0B" }, { l: "Replied", v: prospects.filter(p => p.status === "replied").length, c: "#10B981" }, { l: "Conversion", v: prospects.length > 0 ? `${Math.round(prospects.filter(p => p.status === "replied").length / prospects.length * 100)}%` : "0%", c: "#EC4899" }].map((s, i) => (
              <div key={i} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18 }}>
                <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{s.l}</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: s.c, fontFamily: "'Space Mono', monospace" }}>{s.v}</div>
              </div>
            ))}
          </div>

          {/* Testimonials Banner */}
          <div style={{ background: "linear-gradient(135deg, rgba(99,102,241,0.08), rgba(16,185,129,0.08))", border: "1px solid #1E1E2A", borderRadius: 14, padding: 20, marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#6366F1", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>Testimonials — Use in Outreach</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
              {TESTIMONIALS.map((t, i) => (
                <div key={i} style={{ background: "#111118", borderRadius: 10, padding: 14, border: "1px solid #1E1E2A" }}>
                  <div style={{ display: "flex", gap: 1, marginBottom: 6 }}>{[1,2,3,4,5].map(s => <span key={s} style={{ color: "#F59E0B", fontSize: 11 }}>★</span>)}</div>
                  <p style={{ fontSize: 12, color: "#D1D5DB", lineHeight: 1.6, margin: "0 0 8px" }}>"{t.text.slice(0, 100)}..."</p>
                  <div style={{ fontSize: 11, color: "#6B7280" }}>— {t.contact}, {t.client}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Prospect List */}
          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, overflow: "hidden" }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid #1E1E2A", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>Prospect Pipeline</div>
              <div style={{ fontSize: 11, color: "#9CA3AF" }}>Showing {pagedProspects.length} of {sortedProspects.length} filtered ({prospects.length} total)</div>
            </div>
            <div style={{ padding: "12px 18px", borderBottom: "1px solid #1E1E2A", display: "grid", gridTemplateColumns: "1.6fr 140px 170px 120px", gap: 8 }}>
              <input
                value={prospectSearch}
                onChange={(e) => { setProspectSearch(e.target.value); setProspectPage(1); }}
                placeholder="Search business, contact, email, location"
                style={inp}
              />
              <select value={prospectStatusFilter} onChange={(e) => { setProspectStatusFilter(e.target.value); setProspectPage(1); }} style={inp}>
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="replied">Replied</option>
              </select>
              <select value={prospectSort} onChange={(e) => { setProspectSort(e.target.value); setProspectPage(1); }} style={inp}>
                <option value="last-contact-desc">Sort: Last Contact</option>
                <option value="unanswered-desc">Sort: Most Unanswered</option>
                <option value="rating-desc">Sort: Highest Rating</option>
                <option value="name-asc">Sort: Name A-Z</option>
                <option value="name-desc">Sort: Name Z-A</option>
              </select>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  disabled={currentProspectPage <= 1}
                  onClick={() => setProspectPage((p) => Math.max(1, p - 1))}
                  style={{ flex: 1, background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, fontSize: 12, cursor: currentProspectPage <= 1 ? "not-allowed" : "pointer", opacity: currentProspectPage <= 1 ? 0.5 : 1 }}
                >Prev</button>
                <button
                  disabled={currentProspectPage >= totalProspectPages}
                  onClick={() => setProspectPage((p) => Math.min(totalProspectPages, p + 1))}
                  style={{ flex: 1, background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, fontSize: 12, cursor: currentProspectPage >= totalProspectPages ? "not-allowed" : "pointer", opacity: currentProspectPage >= totalProspectPages ? 0.5 : 1 }}
                >Next</button>
              </div>
            </div>
            {pagedProspects.map(p => (
              <div key={p.id} style={{ padding: "14px 18px", borderBottom: "1px solid #1E1E2A", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center", flex: 1 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: "#1E1E2A", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>🏢</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{p.name} <span style={{ fontSize: 11, color: "#6B7280", fontWeight: 400 }}>• {p.type}</span></div>
                    <div style={{ fontSize: 11, color: "#6B7280" }}>{p.contact} • {p.email} • {p.location}</div>
                    {p.notes && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2, fontStyle: "italic" }}>{p.notes}</div>}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ textAlign: "right", marginRight: 8 }}>
                    <div style={{ fontSize: 12, color: "#F59E0B", fontWeight: 600 }}>{p.unrespondedReviews} unanswered</div>
                    <div style={{ fontSize: 11, color: "#6B7280" }}>{p.rating}★ • {p.lastContact ? `Contacted ${p.lastContact}` : "Not contacted"}</div>
                  </div>
                  <Badge type={p.status} />
                  <button onClick={() => openOutreach(p)} style={{ background: "#6366F1", color: "#fff", border: "none", borderRadius: 7, padding: "7px 14px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                    {p.status === "new" ? "📣 Reach Out" : "📣 Follow Up"}
                  </button>
                </div>
              </div>
            ))}
            {pagedProspects.length === 0 && <div style={{ padding: "14px 18px", fontSize: 12, color: "#9CA3AF" }}>No prospects match your current filters.</div>}
            <div style={{ padding: "10px 18px", borderTop: "1px solid #1E1E2A", fontSize: 11, color: "#6B7280", display: "flex", justifyContent: "space-between" }}>
              <span>Page {currentProspectPage} of {totalProspectPages}</span>
              <span>Use filters to focus large datasets quickly.</span>
            </div>
          </div>
        </div>}

        {/* ===== CONTRACTS ===== */}
        {tab === "contracts" && <div style={{ padding: "20px 28px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, padding: "12px 14px", border: "1px solid #1E1E2A", borderRadius: 10, background: "#111118" }}>
            <div style={{ fontSize: 12, color: "#9CA3AF" }}>
              Prepared date is automatic (today). Select the contract effective date below.
            </div>
            <div style={{ minWidth: 220 }}>
              <label style={{ ...lbl, marginBottom: 6, display: "block" }}>Effective Date</label>
              <input type="date" value={contractEffectiveDate} onChange={e => setContractEffectiveDate(e.target.value)} style={inp} {...datePickerFieldProps} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 20 }}>
            {[{ l: "Signed", v: visibleAgencyClients.filter(c => c.contractStatus === "signed").length, c: "#10B981" }, { l: "Awaiting", v: visibleAgencyClients.filter(c => c.contractStatus === "sent").length, c: "#F59E0B" }, { l: "None", v: visibleAgencyClients.filter(c => c.contractStatus === "none").length, c: "#6B7280" }].map((s, i) => (
              <div key={i} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18 }}>
                <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{s.l}</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: s.c, fontFamily: "'Space Mono', monospace" }}>{s.v}</div>
              </div>
            ))}
          </div>
          {filteredContracts.map(c => (
            <div key={c.id} style={{ padding: "14px 18px", background: "#111118", border: "1px solid #1E1E2A", borderRadius: 10, marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 18 }}>{c.avatar}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: "#6B7280" }}>{c.contact} • {c.email}</div>
                  <div style={{ fontSize: 11, color: "#6B7280" }}>{c.phone || "No phone"} • {formatAddress(c)}</div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ textAlign: "right", marginRight: 6 }}><div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{c.plan ? `€${PLANS.find(p => p.id === c.plan)?.price}/mo` : "—"}</div><div style={{ fontSize: 11, color: "#6B7280" }}>{c.plan ? PLANS.find(p => p.id === c.plan)?.name : "No plan"}</div><div style={{ fontSize: 10, color: "#9CA3AF" }}>{getContractEnvelopeForClient(c.id)?.envelopeId || "No envelope"}</div></div>
                <Badge type={c.contractStatus} />
                {c.plan && c.contractStatus !== "signed" && <button onClick={() => sendContractViaDocuSign(c)} style={{ background: "linear-gradient(135deg,#10B981,#059669)", color: "#fff", border: "none", borderRadius: 7, padding: "6px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{getContractEnvelopeForClient(c.id) ? "↻ Resend Contract Link" : "✍ Send Contract Link"}</button>}
                <button onClick={() => openContractPdf(c, false, contractEffectiveDate)} style={{ background: "#1E40AF", color: "#fff", border: "none", borderRadius: 7, padding: "6px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>👁 PDF</button>
                <button onClick={() => openContractPdf(c, true, contractEffectiveDate)} style={{ background: "#0EA5E9", color: "#fff", border: "none", borderRadius: 7, padding: "6px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>⬇ Download</button>
                {c.contractStatus !== "signed" && <button onClick={() => deleteUnsignedContract(c.id)} style={{ background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 7, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>🗑 Delete Unsigned</button>}
              </div>
            </div>
          ))}
          {filteredContracts.length === 0 && (
            <div style={{ background: "#111118", border: "1px dashed #2D2D3A", borderRadius: 10, padding: 18, textAlign: "center", fontSize: 12, color: "#9CA3AF" }}>
              No contracts found for the selected filter.
            </div>
          )}
        </div>}

        {/* ===== ANALYTICS ===== */}
        {tab === "analytics" && <div style={{ padding: "20px 28px", display: "grid", gap: 14 }}>
          {/* SaaS Revenue KPIs */}
          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 12 }}>
            <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Revenue & SaaS Metrics</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(8,1fr)", gap: 8 }}>
              {[
                { l: "MRR", v: fmtCurrency(mrr), c: "#10B981", tip: "Monthly Recurring Revenue" },
                { l: "ARR", v: fmtCurrency(arr), c: "#34D399", tip: "Annual Recurring Revenue (MRR × 12)" },
                { l: "ARPU", v: fmtCurrency(arpu), c: "#6366F1", tip: "Avg Revenue Per Active Client" },
                { l: "LTV Est.", v: fmtCurrency(ltv), c: "#8B5CF6", tip: "Est. Customer Lifetime Value (ARPU / churn rate)" },
                { l: "New MRR", v: `+${fmtCurrency(newMrr)}`, c: "#22C55E", tip: "MRR from clients joined this month" },
                { l: "Churned MRR", v: `-${fmtCurrency(churnedMrr)}`, c: "#F97316", tip: "MRR from cancelled clients" },
                { l: "Net New MRR", v: (netMrr >= 0 ? "+" : "") + fmtCurrency(netMrr), c: netMrr >= 0 ? "#10B981" : "#EF4444", tip: "New MRR minus Churned MRR" },
                { l: "Pipeline", v: fmtCurrency(pipelineValue), c: "#F59E0B", tip: "Weighted pipeline from prospects" },
              ].map((k) => (
                <div key={k.l} title={k.tip} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: "10px 12px", cursor: "default" }}>
                  <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{k.l}</div>
                  <div style={{ fontSize: 16, color: k.c, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{k.v}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Operational KPIs */}
          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 12 }}>
            <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Operational Metrics</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(8,1fr)", gap: 8 }}>
              {[
                { l: "Active Clients", v: activeClients.length, c: "#10B981" },
                { l: "Churn Rate", v: `${currentChurnRate}%`, c: "#F97316" },
                { l: "Trial Conv.", v: trialConversionRate !== null ? `${trialConversionRate}%` : "—", c: "#A78BFA" },
                { l: "Coverage", v: `${responseCoverageTrend[responseCoverageTrend.length - 1] || 0}%`, c: "#EAB308" },
                { l: "Avg Rating", v: ratingTrend[ratingTrend.length - 1] || "—", c: "#EC4899" },
                { l: "SLA Avg", v: `${slaAverageHours}h`, c: "#0EA5E9" },
                { l: "SLA Compliance", v: `${Math.round(slaCompliance)}%`, c: "#22C55E" },
                { l: "Critical (1-2★)", v: criticalUnresponded.length, c: criticalUnresponded.length > 0 ? "#EF4444" : "#6B7280" },
              ].map((k) => (
                <div key={k.l} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: "10px 12px" }}>
                  <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{k.l}</div>
                  <div style={{ fontSize: 16, color: k.c, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{k.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 12 }}>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Financial Overview</div>
              <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 8 }}>MRR Trend Over Time</div>
              <TinyTrend data={mrrTrend} color="#10B981" fill="rgba(16,185,129,0.16)" />
              <div style={{ fontSize: 10, color: "#6B7280", marginTop: 6 }}>{monthLabels.join(" • ")}</div>
            </div>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Revenue Distribution</div>
              <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 8 }}>Revenue by Plan</div>
              {revenueByPlan.map((r) => (
                <div key={`plan-rev-${r.id}`} style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#D1D5DB", marginBottom: 3 }}><span>{r.label}</span><span>{fmtCurrency(r.value)}</span></div>
                  <div style={{ height: 8, background: "#1E1E2A", borderRadius: 999 }}><div style={{ width: `${(r.value / topPlanRevenue) * 100}%`, height: "100%", borderRadius: 999, background: "linear-gradient(90deg,#6366F1,#8B5CF6)" }} /></div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Client Lifecycle</div>
              <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 8 }}>Churn Trend</div>
              <TinyTrend data={churnTrend} color="#F97316" fill="rgba(249,115,22,0.15)" />
            </div>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Client Lifecycle</div>
              <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 8 }}>Pipeline Value (Prospects)</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 8 }}>
                {[{ l: "New", v: prospects.filter((p) => p.status === "new").length, c: "#6B7280" }, { l: "Contacted", v: prospects.filter((p) => p.status === "contacted").length, c: "#F59E0B" }, { l: "Replied", v: prospects.filter((p) => p.status === "replied").length, c: "#10B981" }].map((s) => (
                  <div key={s.l} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{s.l}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: s.c }}>{s.v}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 12, color: "#9CA3AF" }}>Weighted pipeline value: <strong style={{ color: "#fff" }}>{fmtCurrency(pipelineValue)}</strong></div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Operational Performance</div>
              <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 8 }}>SLA Performance</div>
              <div style={{ display: "grid", gridTemplateColumns: "92px 1fr", alignItems: "center", gap: 12, marginBottom: 10 }}>
                <ProgressDonut value={slaCompliance} color="#22C55E" label="SLA" />
                <div>
                  <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase" }}>Average response time</div>
                  <div style={{ fontSize: 28, color: "#0EA5E9", fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{slaAverageHours}h</div>
                </div>
              </div>
              <TinyBars data={activeClients.map((c) => avgResponseHoursByClient(c))} color="#0EA5E9" />
            </div>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Review Intelligence</div>
              <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 8 }}>Review Volume Trend</div>
              <TinyBars data={reviewVolumeTrend} color="#6366F1" />
              <div style={{ fontSize: 11, color: "#6B7280", marginTop: 8 }}>Response coverage trend</div>
              <TinyTrend data={responseCoverageTrend} color="#EAB308" fill="rgba(234,179,8,0.14)" height={64} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Review Intelligence</div>
              <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 8 }}>Rating Trend</div>
              <TinyTrend data={ratingTrend} color="#EC4899" fill="rgba(236,72,153,0.12)" />
            </div>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Revenue Distribution</div>
              <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 8 }}>Revenue by Geography</div>
              {revenueByGeography.map((g) => (
                <div key={`geo-${g.country}`} style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#D1D5DB", marginBottom: 3 }}><span>{g.country}</span><span>{fmtCurrency(g.value)}</span></div>
                  <div style={{ height: 8, background: "#1E1E2A", borderRadius: 999 }}><div style={{ width: `${(g.value / topGeoRevenue) * 100}%`, height: "100%", borderRadius: 999, background: "linear-gradient(90deg,#0EA5E9,#6366F1)" }} /></div>
                </div>
              ))}
              {revenueByGeography.length === 0 && <div style={{ fontSize: 11, color: "#6B7280" }}>No geography revenue yet.</div>}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Business Health</div>
              <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 10 }}>Health Alerts</div>
              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 11, color: "#9CA3AF" }}>Overdue accounts: <strong style={{ color: "#EF4444" }}>{visibleAgencyClients.filter((c) => c.paymentStatus === "overdue").length}</strong></div>
                <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 11, color: "#9CA3AF" }}>Contracts awaiting signature: <strong style={{ color: "#F59E0B" }}>{visibleAgencyClients.filter((c) => c.contractStatus === "sent").length}</strong></div>
                <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 11, color: "#9CA3AF" }}>Coverage risk clients (&lt;80%): <strong style={{ color: "#EF4444" }}>{activeClients.filter((c) => c.reviewCount > 0 && ((c.responded || 0) / c.reviewCount) < 0.8).length}</strong></div>
              </div>
            </div>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#fff", marginBottom: 12 }}>Generate Analytics Report</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={lbl}>Report Scope</label>
                  <select value={analyticsReportScope} onChange={(e) => setAnalyticsReportScope(e.target.value)} style={inp}>
                    <option value="all">All Active Clients</option>
                    {activeClients.map((c) => <option key={`analytics-client-${c.id}`} value={String(c.id)}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>From</label>
                  <input type="date" value={analyticsReportFrom} onChange={(e) => setAnalyticsReportFrom(e.target.value)} style={inp} {...datePickerFieldProps} />
                </div>
                <div>
                  <label style={lbl}>To</label>
                  <input type="date" value={analyticsReportTo} onChange={(e) => setAnalyticsReportTo(e.target.value)} style={inp} {...datePickerFieldProps} />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={lbl}>Recipient Email (optional for all-clients)</label>
                  <input value={analyticsReportRecipient} onChange={(e) => setAnalyticsReportRecipient(e.target.value)} placeholder="ops@client.com" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Intro Text</label>
                  <textarea value={analyticsReportIntro} onChange={(e) => setAnalyticsReportIntro(e.target.value)} style={{ ...inp, minHeight: 80, resize: "vertical" }} />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
                <button onClick={() => openAnalyticsReportWindow(false)} style={{ background: "#1E40AF", color: "#fff", border: "none", borderRadius: 8, padding: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>👁 Preview</button>
                <button onClick={() => openAnalyticsReportWindow(true)} style={{ background: "#0EA5E9", color: "#fff", border: "none", borderRadius: 8, padding: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>⬇ Download / Print</button>
                <button onClick={sendAnalyticsReport} style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)", color: "#fff", border: "none", borderRadius: 8, padding: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>📨 Send</button>
              </div>
            </div>
          </div>

          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
            <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Sentiment Intelligence</div>
            <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 10 }}>Sentiment by Client</div>
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr 2fr", gap: 8, padding: "4px 10px", fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                <span>Client</span><span>Positive</span><span>Neutral</span><span>Negative</span><span>Distribution</span>
              </div>
              {activeClients.map((c) => {
                const cRevs = reviews.filter((r) => String(r.clientId) === String(c.id));
                const cTotal = cRevs.length;
                const cPos = cRevs.filter((r) => Number(r.rating || 0) >= 4).length;
                const cNeu = cRevs.filter((r) => Number(r.rating || 0) === 3).length;
                const pPos = cTotal ? Math.round((cPos / cTotal) * 100) : 0;
                const pNeu = cTotal ? Math.round((cNeu / cTotal) * 100) : 0;
                const pNeg = cTotal ? 100 - pPos - pNeu : 0;
                return (
                  <div key={`sent-row-${c.id}`} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr 2fr", gap: 8, padding: "8px 10px", background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, alignItems: "center" }}>
                    <div style={{ fontSize: 12, color: "#E5E5E5", fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#10B981", fontFamily: "'Space Mono', monospace" }}>{cTotal ? `${pPos}%` : "—"}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#F59E0B", fontFamily: "'Space Mono', monospace" }}>{cTotal ? `${pNeu}%` : "—"}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#EF4444", fontFamily: "'Space Mono', monospace" }}>{cTotal ? `${pNeg}%` : "—"}</div>
                    <div style={{ display: "flex", height: 8, borderRadius: 99, overflow: "hidden", background: "#1E1E2A" }}>
                      {pPos > 0 && <div style={{ width: `${pPos}%`, background: "#10B981" }} />}
                      {pNeu > 0 && <div style={{ width: `${pNeu}%`, background: "#F59E0B" }} />}
                      {pNeg > 0 && <div style={{ width: `${pNeg}%`, background: "#EF4444" }} />}
                    </div>
                  </div>
                );
              })}
              {activeClients.length === 0 && <div style={{ fontSize: 12, color: "#6B7280", padding: 10 }}>No active clients.</div>}
            </div>
          </div>

          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
            <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Response Performance</div>
            <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 10 }}>Response Rate & Speed by Client</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 14 }}>
              {[
                { l: "Agency Response Rate", v: `${reviews.length ? Math.round((reviews.filter((r) => r.status === "approved" || r.status === "published").length / reviews.length) * 100) : 0}%`, c: "#10B981" },
                { l: "Avg Response Time", v: `${slaAverageHours}h`, c: "#0EA5E9" },
                { l: "SLA Compliance", v: `${Math.round(slaCompliance)}%`, c: "#22C55E" },
              ].map((k) => (
                <div key={`resp-kpi-${k.l}`} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{k.l}</div>
                  <div style={{ fontSize: 22, color: k.c, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{k.v}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr", gap: 8, padding: "4px 10px", fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                <span>Client</span><span>Response Rate</span><span>Avg Time</span><span>SLA</span>
              </div>
              {activeClients.map((c) => {
                const cReviews = reviews.filter((r) => String(r.clientId) === String(c.id));
                const responded = cReviews.filter((r) => r.status === "approved" || r.status === "published").length;
                const responseRate = cReviews.length ? Math.round((responded / cReviews.length) * 100) : null;
                const avgH = avgResponseHoursByClient(c);
                const slaC = slaComplianceByClient(c);
                return (
                  <div key={`resp-row-${c.id}`} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr", gap: 8, padding: "8px 10px", background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, alignItems: "center" }}>
                    <div style={{ fontSize: 12, color: "#E5E5E5", fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: responseRate === null ? "#6B7280" : responseRate >= 80 ? "#10B981" : responseRate >= 50 ? "#F59E0B" : "#EF4444", fontFamily: "'Space Mono', monospace" }}>{responseRate === null ? "—" : `${responseRate}%`}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0EA5E9", fontFamily: "'Space Mono', monospace" }}>{avgH}h</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: slaC >= 80 ? "#22C55E" : slaC >= 60 ? "#F59E0B" : "#EF4444", fontFamily: "'Space Mono', monospace" }}>{slaC}%</div>
                  </div>
                );
              })}
              {activeClients.length === 0 && <div style={{ fontSize: 12, color: "#6B7280", padding: 10 }}>No active clients.</div>}
            </div>
          </div>

          {/* ── MRR Breakdown + Client Count Trend ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>SaaS Growth</div>
              <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 10 }}>MRR Movement (this month)</div>
              {[
                { l: "Opening MRR", v: fmtCurrency(mrrTrend[mrrTrend.length - 2] || mrr), c: "#9CA3AF", bar: 100 },
                { l: "+ New MRR", v: `+${fmtCurrency(newMrr)}`, c: "#10B981", bar: mrr ? Math.round((newMrr / mrr) * 100) : 0 },
                { l: "- Churned MRR", v: `-${fmtCurrency(churnedMrr)}`, c: "#EF4444", bar: mrr ? Math.round((churnedMrr / mrr) * 100) : 0 },
                { l: "= Current MRR", v: fmtCurrency(mrr), c: "#6366F1", bar: 100 },
              ].map((row) => (
                <div key={row.l} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#D1D5DB", marginBottom: 3 }}><span>{row.l}</span><span style={{ color: row.c, fontWeight: 700 }}>{row.v}</span></div>
                  <div style={{ height: 6, background: "#1E1E2A", borderRadius: 99 }}><div style={{ width: `${Math.min(row.bar, 100)}%`, height: "100%", borderRadius: 99, background: row.c }} /></div>
                </div>
              ))}
            </div>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Client Growth</div>
              <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 8 }}>Active Client Count Trend</div>
              <TinyBars data={clientCountTrend} color="#6366F1" />
              <div style={{ fontSize: 10, color: "#6B7280", marginTop: 6 }}>{monthLabels.join(" • ")}</div>
              <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                {[{ l: "Active", v: activeClients.length, c: "#10B981" }, { l: "Prospect", v: prospects.length, c: "#F59E0B" }, { l: "Cancelled", v: visibleAgencyClients.filter(c => c.status === "cancelled").length, c: "#EF4444" }].map((s) => (
                  <div key={s.l} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{s.l}</div>
                    <div style={{ fontSize: 18, color: s.c, fontWeight: 700 }}>{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Platform Review Distribution ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Review Intelligence</div>
              <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 10 }}>Reviews by Platform</div>
              {platformBreakdownArr.length === 0 && <div style={{ fontSize: 12, color: "#6B7280" }}>No reviews yet.</div>}
              {platformBreakdownArr.map(([platform, count]) => (
                <div key={`plat-${platform}`} style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#D1D5DB", marginBottom: 3 }}>
                    <span>{platform}</span><span style={{ color: "#6366F1", fontWeight: 700 }}>{count} <span style={{ color: "#6B7280", fontWeight: 400 }}>({reviews.length ? Math.round((count / reviews.length) * 100) : 0}%)</span></span>
                  </div>
                  <div style={{ height: 7, background: "#1E1E2A", borderRadius: 99 }}><div style={{ width: `${Math.round((count / topPlatformCount) * 100)}%`, height: "100%", borderRadius: 99, background: "linear-gradient(90deg,#6366F1,#8B5CF6)" }} /></div>
                </div>
              ))}
            </div>
            {/* ── Critical Reviews Alert ── */}
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Business Health</div>
              <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 10 }}>Critical Reviews (1-2★ Unresponded)</div>
              {criticalUnresponded.length === 0 && <div style={{ fontSize: 12, color: "#10B981", fontWeight: 600 }}>✓ No critical unresponded reviews. All clear.</div>}
              {criticalUnresponded.slice(0, 6).map((r) => {
                const client = clients.find((c) => String(c.id) === String(r.clientId));
                return (
                  <div key={`crit-${r.id}`} style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 8, padding: "8px 10px", marginBottom: 6 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ fontSize: 12, color: "#FCA5A5", fontWeight: 600 }}>{"★".repeat(Number(r.rating || 1))} {client?.name || "Unknown"}</div>
                      <div style={{ fontSize: 10, color: "#6B7280" }}>{r.platform || "Google"}</div>
                    </div>
                    <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 3 }}>{(r.reviewText || r.text || "").slice(0, 80)}{(r.reviewText || r.text || "").length > 80 ? "…" : ""}</div>
                  </div>
                );
              })}
              {criticalUnresponded.length > 6 && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>+{criticalUnresponded.length - 6} more — go to Reviews to address them.</div>}
            </div>
          </div>

          {/* ── Staff Performance Leaderboard ── */}
          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 14 }}>
            <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>Team Performance</div>
            <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 10 }}>Staff Review Leaderboard</div>
            {staffLeaderboard.length === 0 && <div style={{ fontSize: 12, color: "#6B7280" }}>No review actions recorded yet.</div>}
            <div style={{ display: "grid", gap: 6 }}>
              {staffLeaderboard.map((s, idx) => (
                <div key={`staff-lb-${s.name}`} style={{ display: "grid", gridTemplateColumns: "32px 1fr auto", gap: 10, alignItems: "center", background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: "8px 12px" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: idx === 0 ? "#F59E0B" : idx === 1 ? "#9CA3AF" : idx === 2 ? "#CD7C2F" : "#6B7280", textAlign: "center" }}>#{idx + 1}</div>
                  <div style={{ fontSize: 13, color: "#E5E5E5", fontWeight: 600 }}>{s.name}</div>
                  <div style={{ fontSize: 13, color: "#6366F1", fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{s.count} reviews</div>
                </div>
              ))}
            </div>
          </div>
        </div>}

        {/* ===== SELF-SERVE OPS ===== */}
        {tab === "selfserve" && <div style={{ padding: "20px 28px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 14, marginBottom: 20 }}>
            {[
              { l: "Self-Serve Clients", v: selfServeClients.length, c: "#10B981" },
              { l: "Open Tickets", v: openSupportRequests.length, c: "#F59E0B" },
              { l: "SLA Risk (48h+)", v: selfServeTicketSlaRisk, c: selfServeTicketSlaRisk ? "#EF4444" : "#93C5FD" },
              { l: "Avg Rating", v: selfServeAverageRating ? selfServeAverageRating : "—", c: "#EC4899" },
              { l: "Avg Growth", v: formatSignedDelta(selfServeAverageGrowth), c: selfServeAverageGrowth >= 0 ? "#34D399" : "#F87171" },
              { l: "Self-Serve MRR", v: fmtCurrency(selfServeClients.filter((c) => c.status !== "suspended").reduce((sum, c) => sum + (PLANS.find((p) => p.id === c.plan)?.price || 0), 0)), c: "#6366F1" },
            ].map((s) => (
              <div key={s.l} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 18 }}>
                <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{s.l}</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: s.c, fontFamily: "'Space Mono', monospace" }}>{s.v}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: 16 }}>
            <div>
              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{t("selfServe.accounts", "Self-Serve Accounts")}</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <select value={selfServeStatusFilter} onChange={(e) => { setSelfServeStatusFilter(e.target.value); setSelfServePage(1); }} style={{ ...inp, width: 130 }}>
                      <option value="all">All</option>
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                    </select>
                    <input value={selfServeSearch} onChange={(e) => { setSelfServeSearch(e.target.value); setSelfServePage(1); }} placeholder="Search client, owner, country..." style={{ ...inp, width: 260 }} />
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: "#9CA3AF" }}>Showing {pagedSelfServeClients.length} of {sortedSelfServeClients.length} clients</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => setSelfServePage((p) => Math.max(1, p - 1))} disabled={currentSelfServePage <= 1} style={{ background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: currentSelfServePage <= 1 ? "not-allowed" : "pointer", opacity: currentSelfServePage <= 1 ? 0.5 : 1 }}>Prev</button>
                    <button onClick={() => setSelfServePage((p) => Math.min(totalSelfServePages, p + 1))} disabled={currentSelfServePage >= totalSelfServePages} style={{ background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: currentSelfServePage >= totalSelfServePages ? "not-allowed" : "pointer", opacity: currentSelfServePage >= totalSelfServePages ? 0.5 : 1 }}>Next</button>
                    <div style={{ fontSize: 11, color: "#9CA3AF", display: "flex", alignItems: "center", padding: "0 6px" }}>{t("selfServe.page", "Page")} {currentSelfServePage} {t("selfServe.of", "of")} {totalSelfServePages}</div>
                  </div>
                </div>

                {pagedSelfServeClients.map((client) => {
                  const owner = clientPortalUsers.find((u) => u.clientId === client.id && (u.role || "owner") === "owner");
                  const clientTickets = visibleAgencySupportRequests.filter((ticket) => ticket.clientId === client.id);
                  const plan = PLANS.find((p) => p.id === client.plan);
                  const selected = selectedSelfServeClient?.id === client.id;
                  const ratingGrowth = getClientRatingGrowth(client);
                  return (
                    <div key={`self-serve-client-${client.id}`} style={{ background: selected ? "rgba(99,102,241,0.1)" : "#0A0A0F", border: `1px solid ${selected ? "#6366F1" : "#1E1E2A"}`, borderRadius: 12, padding: 14, marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
                        <div>
                          <button onClick={() => setSelectedSelfServeClientId(client.id)} style={{ background: "transparent", border: "none", color: "#fff", padding: 0, margin: 0, cursor: "pointer", fontSize: 16, fontWeight: 700, textAlign: "left" }}>{client.name}</button>
                          <div style={{ fontSize: 11, color: "#6B7280" }}>{client.contact} • {owner?.username ? `@${owner.username}` : "No owner"}</div>
                          <div style={{ fontSize: 11, color: "#6B7280" }}>{formatAddress(client)}</div>
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <Badge type={client.status === "suspended" ? "overdue" : "active"} label={client.status === "suspended" ? "Suspended" : "Active"} />
                          <Badge type={client.paymentStatus || "paid"} label={client.paymentStatus || "paid"} />
                        </div>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8, marginBottom: 12 }}>
                        {[
                          { l: "Package", v: plan?.name || "Custom", c: "#6366F1" },
                          { l: "Price", v: fmtCurrency(plan?.price || 0), c: "#10B981" },
                          { l: "Payment", v: CLIENT_PAYMENT_METHODS.find((m) => m.id === (client.paymentMethod || "bank-transfer"))?.label || "Bank Transfer", c: "#0EA5E9" },
                          { l: "Tickets", v: clientTickets.length, c: "#F59E0B" },
                          { l: "Growth", v: formatSignedDelta(ratingGrowth), c: ratingGrowth >= 0 ? "#34D399" : "#F87171" },
                        ].map((item) => (
                          <div key={`${client.id}-${item.l}`} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10 }}>
                            <div style={{ fontSize: 9, color: "#6B7280", textTransform: "uppercase" }}>{item.l}</div>
                            <div style={{ fontSize: 12, color: item.c, fontWeight: 700 }}>{item.v}</div>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                        <select disabled={!canSelfServeManage} value={client.paymentMethod || "bank-transfer"} onChange={(e) => updateSelfServeBilling(client.id, { paymentMethod: e.target.value })} style={inp}>
                          {CLIENT_PAYMENT_METHODS.map((method) => <option key={`admin-billing-method-${client.id}-${method.id}`} value={method.id}>{method.label}</option>)}
                        </select>
                        <select disabled={!canSelfServeManage} value={client.paymentStatus || "paid"} onChange={(e) => updateSelfServeBilling(client.id, { paymentStatus: e.target.value })} style={inp}>
                          <option value="trialing">Trialing</option>
                          <option value="paid">Paid</option>
                          <option value="overdue">Overdue</option>
                          <option value="paused">Paused</option>
                        </select>
                      </div>

                      <div style={{ marginBottom: 10, fontSize: 11, color: "#9CA3AF" }}>
                        Detail: <span style={{ color: "#E5E5E5" }}>{client.paymentDetailMasked || "On file"}</span> • Auto-charge: <span style={{ color: client.autoChargeAfterTrial ? "#A7F3D0" : "#FCA5A5" }}>{client.autoChargeAfterTrial ? "Enabled" : "Disabled"}</span>
                      </div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button disabled={!canSelfServeManage} onClick={() => resetSelfServeOwnerPassword(client.id)} style={{ background: "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: canSelfServeManage ? "pointer" : "not-allowed", opacity: canSelfServeManage ? 1 : 0.6 }}>Reset Owner Password</button>
                        <button disabled={!canResetAllSecurityLayers} onClick={() => resetSecurityLayerForClientWorkspace(client.id)} style={{ background: "transparent", color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.45)", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: canResetAllSecurityLayers ? "pointer" : "not-allowed", opacity: canResetAllSecurityLayers ? 1 : 0.6 }}>Reset Client Layer</button>
                        <button disabled={!canSelfServeManage} onClick={() => {
                          const owner = clientPortalUsers.find((u) => u.clientId === client.id && (u.role || "owner") === "owner");
                          const next = window.prompt(`New username for ${client.name} owner:`, owner?.username || "");
                          if (next === null) return;
                          adminChangeClientPortalUsername(client.id, next);
                        }} style={{ background: "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: canSelfServeManage ? "pointer" : "not-allowed", opacity: canSelfServeManage ? 1 : 0.6 }}>Change Owner Username</button>
                        <button disabled={!canSelfServeManage} onClick={() => toggleSelfServeBan(client.id, client.status !== "suspended")} style={{ background: "transparent", color: client.status === "suspended" ? "#10B981" : "#EF4444", border: `1px solid ${client.status === "suspended" ? "rgba(16,185,129,0.35)" : "rgba(239,68,68,0.35)"}`, borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: canSelfServeManage ? "pointer" : "not-allowed", opacity: canSelfServeManage ? 1 : 0.6 }}>{client.status === "suspended" ? "Reactivate" : "Suspend"}</button>
                        <button disabled={!canSelfServeReport} onClick={() => generateReport(client)} style={{ background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: canSelfServeReport ? "pointer" : "not-allowed", opacity: canSelfServeReport ? 1 : 0.6 }}>Generate Report</button>
                        <button onClick={() => (client.connected ? disconnectGoogleClient(client) : startGoogleConnection(client))} disabled={googleBusyClientId === client.id || !canSelfServeManage} style={{ background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: googleBusyClientId === client.id || !canSelfServeManage ? "not-allowed" : "pointer", opacity: googleBusyClientId === client.id || !canSelfServeManage ? 0.6 : 1 }}>{client.connected ? "Disconnect Google" : "Connect Google"}</button>
                        <button onClick={() => syncGoogleReviewsForClient(client)} disabled={googleBusyClientId === client.id || !client.connected || !canSelfServeManage} style={{ background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: googleBusyClientId === client.id || !client.connected || !canSelfServeManage ? "not-allowed" : "pointer", opacity: googleBusyClientId === client.id || !client.connected || !canSelfServeManage ? 0.6 : 1 }}>Sync Reviews</button>
                        <button onClick={() => (isTrustpilotConnected(client) ? disconnectTrustpilotClient(client) : startTrustpilotConnection(client))} disabled={trustpilotBusyClientId === client.id || !canSelfServeManage} style={{ background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: trustpilotBusyClientId === client.id || !canSelfServeManage ? "not-allowed" : "pointer", opacity: trustpilotBusyClientId === client.id || !canSelfServeManage ? 0.6 : 1 }}>{isTrustpilotConnected(client) ? "Disconnect Trustpilot" : "Connect Trustpilot"}</button>
                        <button onClick={() => syncTrustpilotReviewsForClient(client)} disabled={trustpilotBusyClientId === client.id || !isTrustpilotConnected(client) || !canSelfServeManage} style={{ background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: trustpilotBusyClientId === client.id || !isTrustpilotConnected(client) || !canSelfServeManage ? "not-allowed" : "pointer", opacity: trustpilotBusyClientId === client.id || !isTrustpilotConnected(client) || !canSelfServeManage ? 0.6 : 1 }}>Sync Trustpilot</button>
                        <button onClick={() => { setSelfServeClientDashboardId(client.id); setSelfServeClientDashboardTab("overview"); setSelectedSelfServeClientId(client.id); }} style={{ background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: "pointer" }}>{t("selfServe.fullDashboard", "Open Full Dashboard")}</button>
                        <button disabled={!canSelfServeManage} onClick={() => deleteSelfServeAccount(client.id)} style={{ background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: canSelfServeManage ? "pointer" : "not-allowed", opacity: canSelfServeManage ? 1 : 0.6 }}>Delete Account</button>
                      </div>
                    </div>
                  );
                })}
                {pagedSelfServeClients.length === 0 && <div style={{ fontSize: 12, color: "#9CA3AF" }}>No self-serve clients match your filters.</div>}
              </div>

              {selectedSelfServeClient && <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 12 }}>Client 360 • {selectedSelfServeClient.name}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 12 }}>
                  {[
                    { l: "Total Reviews", v: selectedSelfServeReviews.length },
                    { l: "Published", v: selectedSelfServeReviews.filter((r) => r.status === "published").length },
                    { l: "Open Tickets", v: selectedSelfServeTickets.filter((t) => !["resolved", "closed"].includes(t.status)).length },
                  ].map((item) => (
                    <div key={`selfserve-360-${item.l}`} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 10 }}>
                      <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{item.l}</div>
                      <div style={{ fontSize: 20, color: "#fff", fontFamily: "'Space Mono', monospace", fontWeight: 700 }}>{item.v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: "#9CA3AF", lineHeight: 1.8 }}>
                  Contact: <span style={{ color: "#E5E5E5" }}>{selectedSelfServeClient.contact || "—"}</span><br />
                  Email: <span style={{ color: "#E5E5E5" }}>{selectedSelfServeClient.email || "—"}</span><br />
                  Next Payment: <span style={{ color: "#E5E5E5" }}>{selectedSelfServeClient.nextPayment || "—"}</span><br />
                  Trial End: <span style={{ color: "#E5E5E5" }}>{selectedSelfServeClient.trialEndDate || "—"}</span>
                </div>
              </div>}
            </div>

            <div>
              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Managed Registration Requests</div>
                  <button onClick={loadManagedAccountRequests} style={{ background: "transparent", color: "#93C5FD", border: "1px solid rgba(147,197,253,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>Refresh</button>
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                  {[{ id: "pending", label: "Pending", count: managedRequestCounts.pending }, { id: "converted", label: "Converted", count: managedRequestCounts.converted }, { id: "closed", label: "Closed", count: managedRequestCounts.closed }].map((chip) => (
                    <button key={`managed-req-chip-${chip.id}`} onClick={() => setManagedRequestFilter(chip.id)} style={{ background: managedRequestFilter === chip.id ? "rgba(99,102,241,0.2)" : "transparent", color: managedRequestFilter === chip.id ? "#C7D2FE" : "#9CA3AF", border: `1px solid ${managedRequestFilter === chip.id ? "rgba(99,102,241,0.45)" : "#2D2D3A"}`, borderRadius: 999, padding: "5px 11px", fontSize: 11, cursor: "pointer" }}>{chip.label} ({chip.count})</button>
                  ))}
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                  {filteredManagedRequests.map((req) => (
                    <div key={`managed-request-${req.id}`} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
                        <div>
                          <div style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>{req.businessName || "Managed Request"}</div>
                          <div style={{ fontSize: 11, color: "#6B7280" }}>{req.contact || "No contact"} • {req.email || "No email"}</div>
                        </div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <Badge type={req.status === "converted" ? "approved" : req.status === "closed" ? "rejected" : "pending"} label={req.status || "pending"} />
                          <Badge type={req.contractStatus === "sent" ? "approved" : req.contractStatus === "failed" ? "overdue" : "pending"} label={req.contractStatus === "sent" ? "contract sent" : req.contractStatus === "failed" ? "contract failed" : "contract pending"} />
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: "#9CA3AF", lineHeight: 1.7, marginBottom: 8 }}>
                        Address: <span style={{ color: "#E5E5E5" }}>{[req.addressLine1, [req.postcode, req.city].filter(Boolean).join(" "), req.country].filter(Boolean).join(", ") || "—"}</span><br />
                        Plan: <span style={{ color: "#E5E5E5" }}>{PLANS.find((p) => p.id === req.preferredPlan)?.name || req.preferredPlan || "Starter"}</span> • Payment: <span style={{ color: "#E5E5E5" }}>{CLIENT_PAYMENT_METHODS.find((m) => m.id === req.paymentMethod)?.label || req.paymentMethod || "Bank Transfer"}</span><br />
                        Contract sent at: <span style={{ color: "#E5E5E5" }}>{req.contractSentAt ? new Date(req.contractSentAt).toLocaleString() : "—"}</span>{req.contractLastError ? <> • Last error: <span style={{ color: "#FCA5A5" }}>{req.contractLastError}</span></> : null}
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          disabled={!canSelfServeManage || req.status === "converted"}
                          onClick={() => convertManagedRequestToClient(req, false)}
                          style={{ background: "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: !canSelfServeManage || req.status === "converted" ? "not-allowed" : "pointer", opacity: !canSelfServeManage || req.status === "converted" ? 0.6 : 1 }}
                        >Create Managed Client</button>
                        <button
                          disabled={!canSelfServeManage || req.status === "converted"}
                          onClick={() => convertManagedRequestToClient(req, true)}
                          style={{ background: "linear-gradient(135deg,#10B981,#059669)", color: "#fff", border: "none", borderRadius: 8, padding: "7px 11px", fontSize: 12, fontWeight: 700, cursor: !canSelfServeManage || req.status === "converted" ? "not-allowed" : "pointer", opacity: !canSelfServeManage || req.status === "converted" ? 0.6 : 1 }}
                        >Create + Send Contract</button>
                        {req.contractStatus !== "sent" && (
                          <button
                            disabled={!canSelfServeManage}
                            onClick={() => resendManagedRequestContract(req)}
                            style={{ background: "transparent", color: "#F59E0B", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: !canSelfServeManage ? "not-allowed" : "pointer", opacity: !canSelfServeManage ? 0.6 : 1 }}
                          >Resend Contract</button>
                        )}
                      </div>
                    </div>
                  ))}
                  {filteredManagedRequests.length === 0 && <div style={{ fontSize: 12, color: "#9CA3AF" }}>No {managedRequestFilter} managed requests found.</div>}
                </div>
              </div>

              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Support Queue (with ticket IDs)</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {SUPPORT_STATUS_FILTER_OPTIONS.map((option) => (
                      <button key={`admin-support-filter-${option.id}`} onClick={() => setAdminSupportStatusFilter(option.id)} style={{ background: adminSupportStatusFilter === option.id ? "rgba(99,102,241,0.18)" : "#0A0A0F", color: "#E5E5E5", border: `1px solid ${adminSupportStatusFilter === option.id ? "#6366F1" : "#1E1E2A"}`, borderRadius: 999, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>{option.label}</button>
                    ))}
                  </div>
                </div>
                {filteredAdminSupportRequests.map((ticket) => (
                  <div key={`ticket-${ticket.id}`} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 12, padding: 12, marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
                      <div>
                        <div style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>{ticket.subject}</div>
                        <div style={{ fontSize: 11, color: "#6B7280" }}>{ticket.ticketNumber || ticket.id} • {ticket.clientName} • {ticket.requesterName} • {ticket.category}</div>
                      </div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <Badge type={ticket.priority === "urgent" ? "overdue" : ticket.priority === "high" ? "sent" : "pending"} label={ticket.priority} />
                        <Badge type={ticket.status === "resolved" ? "approved" : ticket.status === "closed" ? "rejected" : ticket.status === "in-progress" ? "sent" : "pending"} label={ticket.status} />
                      </div>
                    </div>
                    <div style={{ display: "grid", gap: 7, marginBottom: 8 }}>
                      {getSupportTicketMessages(ticket).map((msg) => {
                        const isClientMsg = msg.senderType === "client";
                        return (
                          <div key={`admin-ticket-msg-${ticket.id}-${msg.id}`} style={{ display: "flex", justifyContent: isClientMsg ? "flex-start" : "flex-end" }}>
                            <div style={{ maxWidth: "84%", background: isClientMsg ? "#111118" : "rgba(16,185,129,0.10)", border: `1px solid ${isClientMsg ? "#2D2D3A" : "rgba(16,185,129,0.25)"}`, borderRadius: isClientMsg ? "8px 8px 8px 2px" : "8px 8px 2px 8px", padding: "8px 10px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                                <div style={{ fontSize: 10, color: "#6B7280" }}>
                                  {msg.senderName || (isClientMsg ? "Client" : "Replai Support")}
                                  {msg.senderUsername ? ` (@${msg.senderUsername})` : ""}
                                  {` • ${new Date(msg.createdAt).toLocaleString()}`}
                                </div>
                                <button onClick={() => deleteSupportTicketMessage(ticket.id, msg.id)} style={{ background: "transparent", border: "none", color: "#FCA5A5", fontSize: 10, cursor: "pointer" }}>Delete</button>
                              </div>
                              <div style={{ fontSize: 12, color: "#D1D5DB", whiteSpace: "pre-wrap", lineHeight: 1.7 }}>{msg.message}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {ticket.billingChangeRequest && <div style={{ marginBottom: 8, fontSize: 11, color: "#A7F3D0", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: 8, padding: 8 }}>
                      Requested billing change: {ticket.billingChangeRequest.oldMethodLabel || ticket.billingChangeRequest.oldMethod} → {ticket.billingChangeRequest.newMethodLabel || ticket.billingChangeRequest.newMethod}
                      {ticket.billingChangeRequest.paymentDetailMasked ? ` • ${ticket.billingChangeRequest.paymentDetailMasked}` : ""}
                      {ticket.billingChangeRequest.appliedAt ? ` • Applied by ${ticket.billingChangeRequest.appliedBy || "Admin"}` : ""}
                    </div>}
                    {ticket.planChangeRequest && <div style={{ marginBottom: 8, fontSize: 11, color: "#BFDBFE", background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 8, padding: 8 }}>
                      Requested plan change: {ticket.planChangeRequest.oldPlanLabel || ticket.planChangeRequest.oldPlan} → {ticket.planChangeRequest.newPlanLabel || ticket.planChangeRequest.newPlan}
                      {ticket.planChangeRequest.effectiveDate ? ` • Effective ${ticket.planChangeRequest.effectiveDate}` : ""}
                      {ticket.planChangeRequest.appliedAt ? ` • Applied by ${ticket.planChangeRequest.appliedBy || "Admin"}` : ""}
                    </div>}
                    {Array.isArray(ticket.attachments) && ticket.attachments.length > 0 && <div style={{ marginBottom: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {ticket.attachments.map((file) => <span key={`${ticket.id}-${file.name}`} style={{ fontSize: 11, color: "#D1D5DB", background: "#111118", border: "1px solid #2D2D3A", borderRadius: 99, padding: "3px 8px" }}>📎 {file.name} ({Math.max(1, Math.round((Number(file.size || 0) / 1024))) } KB)</span>)}
                    </div>}
                    <div style={{ fontSize: 11, color: ticket.acknowledgementEmail?.sent ? "#A7F3D0" : "#FCA5A5", marginBottom: 8 }}>
                      Ack email: {ticket.acknowledgementEmail?.sent ? `Sent to ${ticket.acknowledgementEmail.to}` : "Pending / not configured"}
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 8 }}>
                      <textarea
                        placeholder="Reply to this ticket"
                        value={adminSupportReplyDrafts[ticket.id] || ""}
                        onChange={(e) => setAdminSupportReplyDrafts((prev) => ({ ...prev, [ticket.id]: e.target.value }))}
                        style={{ ...inp, minHeight: 56, resize: "vertical", flex: 1 }}
                      />
                      <button
                        disabled={!canSupportRespond}
                        onClick={() => sendSupportReplyFromAdmin(ticket.id)}
                        style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700, cursor: canSupportRespond ? "pointer" : "not-allowed", opacity: canSupportRespond ? 1 : 0.6 }}>
                        Send
                      </button>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button disabled={!canSupportRespond} onClick={() => respondToSupportRequest(ticket.id)} style={{ background: "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: canSupportRespond ? "pointer" : "not-allowed", opacity: canSupportRespond ? 1 : 0.6 }}>Reply</button>
                      {ticket.billingChangeRequest && <button disabled={!canSupportRespond} onClick={() => applyBillingChangeFromTicket(ticket.id)} style={{ background: "transparent", color: "#10B981", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: canSupportRespond ? "pointer" : "not-allowed", opacity: canSupportRespond ? 1 : 0.6 }}>Apply Billing Change</button>}
                      {ticket.planChangeRequest && <button disabled={!canSupportRespond} onClick={() => applyPlanChangeFromTicket(ticket.id)} style={{ background: "transparent", color: "#60A5FA", border: "1px solid rgba(96,165,250,0.35)", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: canSupportRespond ? "pointer" : "not-allowed", opacity: canSupportRespond ? 1 : 0.6 }}>Apply Plan Change</button>}
                      <button disabled={!canSupportRespond} onClick={() => setSupportRequestStatus(ticket.id, "in-progress")} style={{ background: "transparent", color: "#F59E0B", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: canSupportRespond ? "pointer" : "not-allowed", opacity: canSupportRespond ? 1 : 0.6 }}>Mark In Progress</button>
                      <button disabled={!canSupportRespond} onClick={() => setSupportRequestStatus(ticket.id, "resolved")} style={{ background: "transparent", color: "#10B981", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: canSupportRespond ? "pointer" : "not-allowed", opacity: canSupportRespond ? 1 : 0.6 }}>Resolve</button>
                      <button disabled={!canSupportRespond} onClick={() => setSupportRequestStatus(ticket.id, "closed")} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: canSupportRespond ? "pointer" : "not-allowed", opacity: canSupportRespond ? 1 : 0.6 }}>Close</button>
                      <button onClick={() => deleteSupportTicket(ticket.id)} style={{ background: "transparent", color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "7px 11px", fontSize: 12, cursor: "pointer" }}>Delete Ticket</button>
                    </div>
                  </div>
                ))}
                {filteredAdminSupportRequests.length === 0 && <div style={{ fontSize: 12, color: "#9CA3AF" }}>No support tickets in this view.</div>}
              </div>
            </div>
          </div>
        </div>}

        {/* ===== MAILBOX ===== */}
        {tab === "mailbox" && <div style={{ padding: "20px 24px" }}>
          <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 22, color: "#F3F4F6", fontWeight: 800, letterSpacing: 0.2 }}>Mailbox</div>
              <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>
                {dashboardMailbox.mailbox || "hello@replai.work"} • {dashboardMailbox.configured ? <span style={{ color: "#34D399" }}>Connected</span> : <span style={{ color: "#FCA5A5" }}>Not fully configured</span>}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                onClick={() => setMailboxComposeOpen((prev) => !prev)}
                style={{ background: "linear-gradient(120deg, #E11D48, #7C3AED)", color: "#fff", border: "none", borderRadius: 10, padding: "9px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                {mailboxComposeOpen ? "Close Composer" : "Compose Email"}
              </button>
              <button
                onClick={() => loadDashboardMailbox(true)}
                disabled={dashboardMailbox.loading}
                style={{ background: "#131322", color: "#fff", border: "1px solid #28283E", borderRadius: 10, padding: "9px 14px", fontSize: 12, fontWeight: 700, cursor: dashboardMailbox.loading ? "not-allowed" : "pointer", opacity: dashboardMailbox.loading ? 0.6 : 1 }}>
                {dashboardMailbox.loading ? "Refreshing..." : "Refresh Inbox"}
              </button>
              <button
                onClick={loadSentMailboxMessages}
                style={{ background: "#131322", color: "#fff", border: "1px solid #28283E", borderRadius: 10, padding: "9px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Refresh Sent
              </button>
            </div>
          </div>

          {mailboxComposeOpen && <div style={{ background: "linear-gradient(180deg, #141425, #0E0E18)", border: "1px solid #292941", borderRadius: 14, padding: 14, marginBottom: 14 }}>
            <div style={{ display: "grid", gap: 8 }}>
              <input
                value={mailboxCompose.to}
                onChange={(e) => setMailboxCompose((prev) => ({ ...prev, to: e.target.value }))}
                placeholder="To (email@example.com or comma-separated)"
                style={inp}
              />
              <input
                value={mailboxCompose.subject}
                onChange={(e) => setMailboxCompose((prev) => ({ ...prev, subject: e.target.value }))}
                placeholder="Subject"
                style={inp}
              />
              <textarea
                value={mailboxCompose.body}
                onChange={(e) => setMailboxCompose((prev) => ({ ...prev, body: e.target.value }))}
                placeholder="Write your email"
                style={{ ...inp, minHeight: 120, resize: "vertical" }}
              />
              <div style={{ display: "grid", gap: 8 }}>
                <label style={{ fontSize: 11, color: "#9CA3AF" }}>
                  Attach files
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      addMailboxAttachments(e.target.files);
                      e.target.value = "";
                    }}
                    style={{ display: "block", marginTop: 6, color: "#D1D5DB" }}
                  />
                </label>
                {(mailboxCompose.attachments || []).length > 0 && <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {(mailboxCompose.attachments || []).map((attachment, index) => <button
                    key={`mailbox-attachment-${attachment.name}-${index}`}
                    onClick={() => removeMailboxAttachment(`${attachment.name}-${index}`)}
                    style={{ background: "#161629", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 999, padding: "5px 9px", fontSize: 11, cursor: "pointer" }}>
                    {attachment.name} ({Math.max(1, Math.round(Number(attachment.size || 0) / 1024))} KB) ×
                  </button>)}
                </div>}
                <div style={{ fontSize: 10, color: "#6B7280" }}>
                  Up to 5 attachments, 7.5 MB total. Draft autosave stores message fields only, not attachments.
                  {mailboxDraftStatus === "saving" ? " Saving draft..." : ""}
                  {mailboxDraftStatus === "saved" && mailboxDraftSavedAt ? ` Last saved ${new Date(mailboxDraftSavedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.` : ""}
                  {mailboxDraftStatus === "error" ? " Draft sync failed." : ""}
                </div>
              </div>
              {mailboxSendStatus && (
                <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 12, marginBottom: 8,
                  background: mailboxSendStatus.type === "success" ? "rgba(16,185,129,0.12)" : mailboxSendStatus.type === "queued" ? "rgba(245,158,11,0.12)" : "rgba(239,68,68,0.12)",
                  border: `1px solid ${mailboxSendStatus.type === "success" ? "rgba(16,185,129,0.4)" : mailboxSendStatus.type === "queued" ? "rgba(245,158,11,0.4)" : "rgba(239,68,68,0.4)"}`,
                  color: mailboxSendStatus.type === "success" ? "#A7F3D0" : mailboxSendStatus.type === "queued" ? "#FDE68A" : "#FCA5A5",
                  display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <span>{mailboxSendStatus.type === "success" ? "✓" : mailboxSendStatus.type === "queued" ? "⚠" : "✕"} {mailboxSendStatus.msg}</span>
                  <button onClick={() => setMailboxSendStatus(null)} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                <button
                  onClick={clearMailboxComposer}
                  style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 11px", fontSize: 12, cursor: "pointer" }}>
                  Clear
                </button>
                <button
                  onClick={sendMailboxMessage}
                  disabled={mailboxSending}
                  style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 8, padding: "8px 11px", fontSize: 12, fontWeight: 700, cursor: mailboxSending ? "not-allowed" : "pointer", opacity: mailboxSending ? 0.65 : 1 }}>
                  {mailboxSending ? "Sending..." : "Send"}
                </button>
              </div>
            </div>
          </div>}

          {dashboardMailbox.error && !dashboardMailbox.loading && <div style={{ marginBottom: 12, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 10, padding: "10px 13px", fontSize: 12, color: "#FCA5A5" }}>{dashboardMailbox.error}</div>}
          {!dashboardMailbox.configured && <div style={{ marginBottom: 12, fontSize: 11, color: "#6B7280" }}>Add SUPPORT_EMAIL_PASS=&lt;your-password&gt; on the server and restart backend to unlock mailbox sync.</div>}

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: "0 0 220px", background: "#0E0E17", border: "1px solid #222235", borderRadius: 14, padding: 12, alignSelf: "flex-start" }}>
              <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 10, letterSpacing: 0.6 }}>FOLDERS</div>
              {[
                { id: "inbox", label: "Inbox", count: mailboxInboxMessages.length },
                { id: "sent", label: "Sent", count: mailboxActiveSentMessages.length },
                { id: "archived", label: "Archived", count: mailboxArchivedMessages.length },
                { id: "trash", label: "Trash", count: mailboxDeletedInboxMessages.length + mailboxDeletedSentMessages.length },
              ].map((folder) => (
                <button
                  key={`mailbox-folder-${folder.id}`}
                  onClick={() => {
                    setMailboxFolder(folder.id);
                    setMailboxSelectedKeys([]);
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                    background: mailboxFolder === folder.id ? "linear-gradient(90deg, rgba(225,29,72,0.22), rgba(124,58,237,0.20))" : "transparent",
                    color: mailboxFolder === folder.id ? "#F5F3FF" : "#A1A1AA",
                    border: `1px solid ${mailboxFolder === folder.id ? "rgba(168,85,247,0.65)" : "transparent"}`,
                    borderRadius: 10,
                    padding: "9px 10px",
                    fontSize: 12,
                    marginBottom: 6,
                    cursor: "pointer",
                    textAlign: "left",
                  }}>
                  <span>{folder.label}</span>
                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11 }}>{folder.count}</span>
                </button>
              ))}
            </div>

            <div style={{ flex: "1 1 520px", minWidth: 300, background: "#0F0F1A", border: "1px solid #24243A", borderRadius: 14, overflow: "hidden" }}>
              <div style={{ padding: 12, borderBottom: "1px solid #24243A", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  value={mailboxSearch}
                  onChange={(e) => setMailboxSearch(e.target.value)}
                  placeholder={`Search threads in ${mailboxFolder}`}
                  style={{ ...inp, minWidth: 220, flex: "1 1 220px" }}
                />
                {[
                  { id: "all", label: "All" },
                  { id: "unread", label: "Unread" },
                  { id: "starred", label: "Starred" },
                  { id: "attachments", label: "Attachments" },
                ].map((filter) => (
                  <button
                    key={`mailbox-thread-filter-${filter.id}`}
                    onClick={() => setMailboxThreadFilter(filter.id)}
                    style={{
                      background: mailboxThreadFilter === filter.id ? "rgba(124,58,237,0.2)" : "#17172A",
                      color: mailboxThreadFilter === filter.id ? "#F5F3FF" : "#D1D5DB",
                      border: `1px solid ${mailboxThreadFilter === filter.id ? "rgba(168,85,247,0.5)" : "#343451"}`,
                      borderRadius: 8,
                      padding: "8px 10px",
                      fontSize: 11,
                      cursor: "pointer",
                    }}>
                    {filter.label}
                  </button>
                ))}
                <select
                  value={mailboxSenderFilter}
                  onChange={(e) => setMailboxSenderFilter(e.target.value)}
                  style={{ ...inp, width: 180, minWidth: 160, padding: "8px 10px", fontSize: 11 }}>
                  <option value="all">Any sender</option>
                  {mailboxSenderOptions.map((option) => <option key={`mailbox-sender-filter-${option.value}`} value={option.value}>{option.label}</option>)}
                </select>
                <select
                  value={mailboxDateFilter}
                  onChange={(e) => setMailboxDateFilter(e.target.value)}
                  style={{ ...inp, width: 140, minWidth: 120, padding: "8px 10px", fontSize: 11 }}>
                  <option value="all">Any date</option>
                  <option value="today">Today</option>
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                </select>
                <button
                  onClick={toggleMailboxSelectAllFiltered}
                  disabled={!filteredMailboxThreads.length}
                  style={{ background: "#17172A", color: "#D1D5DB", border: "1px solid #343451", borderRadius: 8, padding: "8px 10px", fontSize: 11, cursor: filteredMailboxThreads.length ? "pointer" : "not-allowed", opacity: filteredMailboxThreads.length ? 1 : 0.6 }}>
                  {allFilteredMailboxSelected ? "Unselect All" : "Select All"}
                </button>
              </div>

              <div style={{ padding: "8px 12px", borderBottom: "1px solid #24243A", display: "flex", gap: 8, flexWrap: "wrap", background: "#121224" }}>
                <button disabled={!mailboxSelectedKeys.length || mailboxActionLoadingId.startsWith("bulk:")} onClick={() => runMailboxBulkAction("read")} style={{ background: "transparent", color: "#A7F3D0", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: mailboxSelectedKeys.length ? "pointer" : "not-allowed", opacity: mailboxSelectedKeys.length ? 1 : 0.55 }}>Mark Read</button>
                <button disabled={!mailboxSelectedKeys.length || mailboxActionLoadingId.startsWith("bulk:")} onClick={() => runMailboxBulkAction("unread")} style={{ background: "transparent", color: "#A7F3D0", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: mailboxSelectedKeys.length ? "pointer" : "not-allowed", opacity: mailboxSelectedKeys.length ? 1 : 0.55 }}>Mark Unread</button>
                <button disabled={!mailboxSelectedKeys.length || mailboxActionLoadingId.startsWith("bulk:")} onClick={() => runMailboxBulkAction("star")} style={{ background: "transparent", color: "#FBBF24", border: "1px solid rgba(251,191,36,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: mailboxSelectedKeys.length ? "pointer" : "not-allowed", opacity: mailboxSelectedKeys.length ? 1 : 0.55 }}>Star</button>
                {mailboxFolder === "trash" && <button disabled={!mailboxSelectedKeys.length || mailboxActionLoadingId.startsWith("bulk:")} onClick={() => runMailboxBulkAction("restore")} style={{ background: "transparent", color: "#60A5FA", border: "1px solid rgba(96,165,250,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: mailboxSelectedKeys.length ? "pointer" : "not-allowed", opacity: mailboxSelectedKeys.length ? 1 : 0.55 }}>Restore</button>}
                <button disabled={!mailboxSelectedKeys.length || mailboxActionLoadingId.startsWith("bulk:")} onClick={() => runMailboxBulkAction(mailboxFolder === "trash" ? "purge" : "delete")} style={{ background: "transparent", color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: mailboxSelectedKeys.length ? "pointer" : "not-allowed", opacity: mailboxSelectedKeys.length ? 1 : 0.55 }}>{mailboxFolder === "trash" ? "Delete Forever" : "Delete"}</button>
                <div style={{ marginLeft: "auto", fontSize: 11, color: "#6B7280", alignSelf: "center" }}>{mailboxSelectedKeys.length} thread{mailboxSelectedKeys.length === 1 ? "" : "s"} selected</div>
              </div>

              <div style={{ maxHeight: 560, overflowY: "auto", padding: 10, display: "grid", gap: 6 }}>
                {dashboardMailbox.loading && <div style={{ fontSize: 12, color: "#9CA3AF", textAlign: "center", padding: 18 }}>Loading inbox...</div>}
                {!dashboardMailbox.loading && <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 2px 8px", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 11, color: "#6B7280" }}>Showing {mailboxVisibleThreads.length} of {filteredMailboxThreads.length} threads</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <button
                      onClick={() => setMailboxThreadPage((p) => Math.max(1, p - 1))}
                      disabled={mailboxThreadPageClamped <= 1}
                      style={{ background: "#17172A", color: "#D1D5DB", border: "1px solid #343451", borderRadius: 8, padding: "4px 8px", fontSize: 10, cursor: mailboxThreadPageClamped <= 1 ? "not-allowed" : "pointer", opacity: mailboxThreadPageClamped <= 1 ? 0.55 : 1 }}>
                      Prev
                    </button>
                    <span style={{ fontSize: 10, color: "#9CA3AF" }}>Page {mailboxThreadPageClamped} of {mailboxThreadTotalPages}</span>
                    <button
                      onClick={() => setMailboxThreadPage((p) => Math.min(mailboxThreadTotalPages, p + 1))}
                      disabled={mailboxThreadPageClamped >= mailboxThreadTotalPages}
                      style={{ background: "#17172A", color: "#D1D5DB", border: "1px solid #343451", borderRadius: 8, padding: "4px 8px", fontSize: 10, cursor: mailboxThreadPageClamped >= mailboxThreadTotalPages ? "not-allowed" : "pointer", opacity: mailboxThreadPageClamped >= mailboxThreadTotalPages ? 0.55 : 1 }}>
                      Next
                    </button>
                  </div>
                </div>}
                {!dashboardMailbox.loading && mailboxVisibleThreads.map((thread, idx) => {
                  const latestMessage = thread.latestMessage;
                  const previewMessage = thread.previewTarget || latestMessage;
                  const isSelected = mailboxSelectedKeys.includes(thread.id);
                  const isPreview = mailboxPreviewMessageKey === thread.id;
                  const isActionLoading = mailboxActionLoadingId === `thread:${thread.id}:archive` || mailboxActionLoadingId === `thread:${thread.id}:delete` || mailboxActionLoadingId === `thread:${thread.id}:restore`;
                  const counterpartText = previewMessage?.source === "sent"
                    ? `To: ${previewMessage?.to || "Unknown recipient"}`
                    : `From: ${previewMessage?.from || "Unknown sender"}`;
                  return (
                    <div
                      key={`mailbox-thread-${thread.id}-${idx}`}
                      onClick={() => setMailboxPreviewMessageKey(thread.id)}
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "flex-start",
                        background: isPreview ? "linear-gradient(90deg, rgba(168,85,247,0.14), rgba(37,99,235,0.08))" : "#141425",
                        border: `1px solid ${isPreview ? "rgba(168,85,247,0.45)" : "#23233A"}`,
                        borderRadius: 10,
                        padding: "10px 11px",
                        cursor: "pointer",
                      }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleMailboxSelection(thread)}
                        style={{ marginTop: 4 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                          <div style={{ fontSize: 12, color: "#E5E7EB", fontWeight: thread.unreadCount ? 700 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {thread.starred ? "★ " : ""}{thread.subject || "(No subject)"}
                            {thread.messages.length > 1 ? ` (${thread.messages.length})` : ""}
                          </div>
                          <div style={{ fontSize: 10, color: "#6B7280", whiteSpace: "nowrap" }}>{latestMessage?.date || ""}</div>
                        </div>
                        <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {counterpartText}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ flex: 1, fontSize: 11, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{thread.snippet || "No preview available."}</div>
                          {thread.attachmentCount > 0 && <div style={{ fontSize: 10, color: "#93C5FD", border: "1px solid rgba(96,165,250,0.3)", borderRadius: 999, padding: "2px 6px", whiteSpace: "nowrap" }}>📎 {thread.attachmentCount}</div>}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        {thread.unreadCount > 0 && <button
                          disabled={isActionLoading}
                          onClick={(e) => {
                            e.stopPropagation();
                            runMailboxThreadAction(thread, "read");
                          }}
                          style={{ background: "transparent", color: "#A7F3D0", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 7, padding: "4px 6px", fontSize: 10, cursor: isActionLoading ? "not-allowed" : "pointer" }}>
                          Read
                        </button>}
                        <button
                          disabled={isActionLoading}
                          onClick={(e) => {
                            e.stopPropagation();
                            runMailboxThreadAction(thread, thread.starred ? "unstar" : "star");
                          }}
                          style={{ background: "transparent", color: "#FBBF24", border: "1px solid rgba(251,191,36,0.35)", borderRadius: 7, padding: "4px 6px", fontSize: 10, cursor: isActionLoading ? "not-allowed" : "pointer" }}>
                          {thread.starred ? "Unstar" : "Star"}
                        </button>
                      </div>
                    </div>
                  );
                })}

                {!dashboardMailbox.loading && filteredMailboxThreads.length === 0 && !dashboardMailbox.error && (
                  <div style={{ background: "#111118", border: "1px dashed #2D2D3A", borderRadius: 10, padding: 20, textAlign: "center", fontSize: 12, color: "#9CA3AF" }}>
                    {dashboardMailbox.configured ? (mailboxSearch.trim() ? "No emails match your search." : "No messages in this folder.") : "Configure the mailbox password on the server to start syncing emails."}
                  </div>
                )}
              </div>
            </div>

            <div style={{ flex: "1 1 320px", minWidth: 280, maxWidth: 420, background: "#0F0F1A", border: "1px solid #24243A", borderRadius: 14, padding: 12, alignSelf: "flex-start" }}>
              <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 10, letterSpacing: 0.5 }}>CONVERSATION PREVIEW</div>
              {!mailboxPreviewThread && <div style={{ fontSize: 12, color: "#9CA3AF" }}>Select a thread to preview.</div>}
              {mailboxPreviewThread && mailboxPreviewMessage && <>
                <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 6 }}>{mailboxPreviewThread.subject || "(No subject)"}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 4 }}>
                  {mailboxPreviewThread.participants.slice(0, 3).join(" • ") || (mailboxPreviewMessage.source === "sent" ? `To: ${mailboxPreviewMessage.to || "Unknown recipient"}` : `From: ${mailboxPreviewMessage.from || "Unknown sender"}`)}
                </div>
                <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 10 }}>
                  Showing {mailboxPreviewVisibleMessages.length} of {mailboxPreviewTotalMessages} message{mailboxPreviewTotalMessages === 1 ? "" : "s"}
                  {mailboxPreviewTotalPages > 1 ? ` • page ${mailboxPreviewPageClamped} of ${mailboxPreviewTotalPages}` : ""}
                </div>
                {mailboxPreviewTotalPages > 1 && <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                  <button
                    onClick={() => setMailboxPreviewPage((prev) => Math.min(mailboxPreviewTotalPages, prev + 1))}
                    disabled={mailboxPreviewPageClamped >= mailboxPreviewTotalPages}
                    style={{ background: "transparent", color: "#93C5FD", border: "1px solid rgba(96,165,250,0.35)", borderRadius: 8, padding: "6px 9px", fontSize: 11, cursor: mailboxPreviewPageClamped >= mailboxPreviewTotalPages ? "not-allowed" : "pointer", opacity: mailboxPreviewPageClamped >= mailboxPreviewTotalPages ? 0.55 : 1 }}>
                    Load Older
                  </button>
                  <button
                    onClick={() => setMailboxPreviewPage(1)}
                    disabled={mailboxPreviewPageClamped === 1}
                    style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: "6px 9px", fontSize: 11, cursor: mailboxPreviewPageClamped === 1 ? "not-allowed" : "pointer", opacity: mailboxPreviewPageClamped === 1 ? 0.55 : 1 }}>
                    Newest
                  </button>
                </div>}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                  {mailboxPreviewMessage.source !== "sent" && mailboxFolder !== "trash" && <button
                    onClick={() => {
                      openMailboxReplyComposer(mailboxPreviewMessage);
                      setMailboxComposeOpen(true);
                    }}
                    style={{ background: "transparent", color: "#60A5FA", border: "1px solid rgba(96,165,250,0.35)", borderRadius: 8, padding: "6px 9px", fontSize: 11, cursor: "pointer" }}>
                    Reply
                  </button>}
                  {mailboxPreviewMessage.source !== "sent" && mailboxFolder !== "trash" && <button
                    onClick={() => runMailboxThreadAction(mailboxPreviewThread, mailboxPreviewThread.archived ? "unarchive" : "archive")}
                    style={{ background: "transparent", color: "#A78BFA", border: "1px solid rgba(167,139,250,0.35)", borderRadius: 8, padding: "6px 9px", fontSize: 11, cursor: "pointer" }}>
                    {mailboxPreviewThread.archived ? "Move to Inbox" : "Archive Thread"}
                  </button>}
                  <button
                    onClick={() => runMailboxThreadAction(mailboxPreviewThread, mailboxPreviewThread.unreadCount ? "read" : "unread")}
                    style={{ background: "transparent", color: "#A7F3D0", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 8, padding: "6px 9px", fontSize: 11, cursor: "pointer" }}>
                    {mailboxPreviewThread.unreadCount ? "Mark Thread Read" : "Mark Thread Unread"}
                  </button>
                  {mailboxFolder === "trash" && <button
                    onClick={() => runMailboxThreadAction(mailboxPreviewThread, "restore")}
                    style={{ background: "transparent", color: "#60A5FA", border: "1px solid rgba(96,165,250,0.35)", borderRadius: 8, padding: "6px 9px", fontSize: 11, cursor: "pointer" }}>
                    Restore Thread
                  </button>}
                  <button
                    onClick={() => runMailboxThreadAction(mailboxPreviewThread, mailboxFolder === "trash" ? "purge" : "delete")}
                    style={{ background: "transparent", color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "6px 9px", fontSize: 11, cursor: "pointer" }}>
                    {mailboxFolder === "trash" ? "Delete Thread Forever" : "Delete Thread"}
                  </button>
                </div>
                <div style={{ marginTop: 12, display: "grid", gap: 10, maxHeight: 420, overflowY: "auto", borderTop: "1px solid #24243A", paddingTop: 12 }}>
                  {mailboxPreviewVisibleMessages.map((message, idx) => {
                    const isExpanded = expandedMailboxMessageId === mailboxMessageKey(message);
                    const isSent = message?.source === "sent";
                    return (
                      <div key={`preview-thread-message-${message.id}-${idx}`} style={{ background: isSent ? "rgba(37,99,235,0.08)" : "#141425", border: `1px solid ${isSent ? "rgba(59,130,246,0.2)" : "#23233A"}`, borderRadius: 10, padding: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                          <div style={{ fontSize: 11, color: "#E5E7EB", fontWeight: 700 }}>{isSent ? (message.to || "Unknown recipient") : (message.from || "Unknown sender")}</div>
                          <div style={{ fontSize: 10, color: "#6B7280" }}>{message.date || ""}</div>
                        </div>
                        <div style={{ fontSize: 10, color: "#9CA3AF", marginBottom: 8 }}>{isSent ? "Sent" : "Received"}</div>
                        {Array.isArray(message?.attachments) && message.attachments.length > 0 && <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                          {message.attachments.map((attachment, attachmentIndex) => {
                            const href = buildMailboxAttachmentDataUrl(attachment);
                            const label = `${attachment.name || `attachment-${attachmentIndex + 1}`} (${Math.max(1, Math.round(Number(attachment.size || 0) / 1024))} KB)`;
                            if (href) {
                              return <a
                                key={`mailbox-message-attachment-${message.id}-${attachmentIndex}`}
                                href={href}
                                download={attachment.name || `attachment-${attachmentIndex + 1}`}
                                style={{ fontSize: 10, color: "#BFDBFE", background: "#111118", border: "1px solid rgba(96,165,250,0.28)", borderRadius: 999, padding: "4px 8px", textDecoration: "none" }}>
                                Download {label}
                              </a>;
                            }
                            return <span key={`mailbox-message-attachment-${message.id}-${attachmentIndex}`} style={{ fontSize: 10, color: "#D1D5DB", background: "#111118", border: "1px solid #2D2D3A", borderRadius: 999, padding: "4px 8px" }}>
                              📎 {label}
                            </span>;
                          })}
                        </div>}
                        {Array.isArray(message?.attachments) && message.attachments.some((attachment) => buildMailboxAttachmentDataUrl(attachment)) && <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
                          {message.attachments.map((attachment, attachmentIndex) => {
                            const href = buildMailboxAttachmentDataUrl(attachment);
                            const type = String(attachment?.type || "").toLowerCase();
                            if (!href) return null;
                            if (type.startsWith("image/")) {
                              return <img
                                key={`mailbox-attachment-inline-${message.id}-${attachmentIndex}`}
                                src={href}
                                alt={attachment.name || `attachment-${attachmentIndex + 1}`}
                                style={{ width: "100%", maxHeight: 220, objectFit: "contain", borderRadius: 8, border: "1px solid #2D2D3A", background: "#0F0F1A" }}
                              />;
                            }
                            if (type === "application/pdf") {
                              return <iframe
                                key={`mailbox-attachment-inline-${message.id}-${attachmentIndex}`}
                                src={href}
                                title={attachment.name || `pdf-${attachmentIndex + 1}`}
                                style={{ width: "100%", height: 240, border: "1px solid #2D2D3A", borderRadius: 8, background: "#0F0F1A" }}
                              />;
                            }
                            return null;
                          })}
                        </div>}
                        <div style={{ fontSize: 12, color: "#D1D5DB", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                          {isExpanded ? (message.body || message.snippet || "No content.") : (message.snippet || message.body || "No content.")}
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                          <button
                            onClick={() => setExpandedMailboxMessageId((prev) => prev === mailboxMessageKey(message) ? "" : mailboxMessageKey(message))}
                            style={{ background: "transparent", color: "#C4B5FD", border: "1px solid rgba(196,181,253,0.35)", borderRadius: 8, padding: "5px 8px", fontSize: 10, cursor: "pointer" }}>
                            {isExpanded ? "Compact" : "Open full"}
                          </button>
                          {!isSent && <button
                            onClick={() => {
                              openMailboxReplyComposer(message);
                              setMailboxComposeOpen(true);
                            }}
                            style={{ background: "transparent", color: "#60A5FA", border: "1px solid rgba(96,165,250,0.35)", borderRadius: 8, padding: "5px 8px", fontSize: 10, cursor: "pointer" }}>
                            Reply to this message
                          </button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>}
            </div>
          </div>
        </div>}

        {/* ===== SETTINGS ===== */}
        {tab === "security-events" && <div style={{ padding: "20px 28px", display: "grid", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
            {[
              { label: "Total Events", value: visibleSecurityEvents.length, color: "#6366F1" },
              { label: "Success", value: visibleSecurityEvents.filter((event) => event.status === "success").length, color: "#10B981" },
              { label: "Failed", value: visibleSecurityEvents.filter((event) => event.status === "failed").length, color: "#EF4444" },
              { label: "Blocked", value: visibleSecurityEvents.filter((event) => event.status === "blocked").length, color: "#F59E0B" },
            ].map((item) => (
              <div key={`security-summary-${item.label}`} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase", marginBottom: 6 }}>{item.label}</div>
                <div style={{ fontSize: 25, color: item.color, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{item.value}</div>
              </div>
            ))}
          </div>

          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "160px 160px 160px 1fr", gap: 8, marginBottom: 12 }}>
              <select value={securityEventStatusFilter} onChange={(e) => setSecurityEventStatusFilter(e.target.value)} style={inp}>
                <option value="all">All statuses</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
                <option value="blocked">Blocked</option>
              </select>
              <select value={securityEventScopeFilter} onChange={(e) => setSecurityEventScopeFilter(e.target.value)} style={inp}>
                <option value="all">All scopes</option>
                <option value="staff">Staff</option>
                <option value="client">Client</option>
              </select>
              <select value={securityEventOwnerFilter} onChange={(e) => setSecurityEventOwnerFilter(e.target.value)} style={inp}>
                <option value="mine">My events</option>
                {canViewAllSecurityEvents && <option value="all">All users</option>}
              </select>
              <input value={securityEventSearch} onChange={(e) => setSecurityEventSearch(e.target.value)} placeholder="Search user, detail, device..." style={inp} />
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ color: "#9CA3AF", textAlign: "left", borderBottom: "1px solid #1E1E2A" }}>
                    <th style={{ padding: "8px 6px" }}>Time</th>
                    <th style={{ padding: "8px 6px" }}>Status</th>
                    <th style={{ padding: "8px 6px" }}>Scope</th>
                    <th style={{ padding: "8px 6px" }}>User</th>
                    <th style={{ padding: "8px 6px" }}>Detail</th>
                    <th style={{ padding: "8px 6px" }}>Device</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAdminSecurityEvents.slice(0, 400).map((event) => (
                    <tr key={event.id} style={{ borderBottom: "1px solid #1E1E2A" }}>
                      <td style={{ padding: "8px 6px", color: "#D1D5DB", whiteSpace: "nowrap" }}>{new Date(event.at).toLocaleString()}</td>
                      <td style={{ padding: "8px 6px" }}>
                        <span style={{
                          display: "inline-block",
                          fontSize: 10,
                          padding: "3px 8px",
                          borderRadius: 99,
                          border: "1px solid #2D2D3A",
                          color: event.status === "success" ? "#34D399" : event.status === "failed" ? "#FCA5A5" : "#FCD34D",
                          background: event.status === "success" ? "rgba(16,185,129,0.12)" : event.status === "failed" ? "rgba(239,68,68,0.12)" : "rgba(245,158,11,0.12)",
                        }}>{event.status}</span>
                      </td>
                      <td style={{ padding: "8px 6px", color: "#D1D5DB", textTransform: "capitalize" }}>{event.scope}</td>
                      <td style={{ padding: "8px 6px", color: "#E5E5E5" }}>
                        <div style={{ fontWeight: 600 }}>{event.userName}</div>
                        <div style={{ color: "#9CA3AF", fontSize: 11 }}>@{event.username || "n/a"} • {event.role}</div>
                      </td>
                      <td style={{ padding: "8px 6px", color: "#D1D5DB", minWidth: 220 }}>{event.detail || "—"}</td>
                      <td style={{ padding: "8px 6px", color: "#9CA3AF", minWidth: 180 }}>
                        <div>{event.deviceLabel || "Unknown Device"}</div>
                        <div style={{ fontSize: 10 }}>{event.deviceId || ""}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredAdminSecurityEvents.length === 0 && <div style={{ marginTop: 10, fontSize: 12, color: "#9CA3AF" }}>No security events match these filters.</div>}
          </div>

          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 14, color: "#fff", fontWeight: 700 }}>Backend Activity Trail</div>
                <div style={{ fontSize: 11, color: "#9CA3AF" }}>Tracks dashboard actions with actor, role, and timestamp. Showing {filteredBackendActivity.length} of {backendActivityAudit.length} entries.</div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {(activityActorFilter || activityActionFilter !== "all" || activityDateFrom || activityDateTo || backendActivitySearch) && (
                  <button
                    onClick={() => { setActivityActorFilter(""); setActivityActionFilter("all"); setActivityDateFrom(""); setActivityDateTo(""); setBackendActivitySearch(""); }}
                    style={{ background: "transparent", color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>
                    Clear Filters
                  </button>
                )}
                <button
                  onClick={() => {
                    const rows = filteredBackendActivity;
                    const header = "id,at,action,detail,actor_name,actor_username,actor_role,ip";
                    const csv = [header, ...rows.map((e) => [e.id, e.at, e.action, `"${(e.detail || "").replace(/"/g, '""')}"`, e.actor?.name, e.actor?.username, e.actor?.role, e.ip].join(","))].join("\n");
                    const blob = new Blob([csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a"); a.href = url; a.download = `activity-audit-${new Date().toISOString().slice(0,10)}.csv`; a.click();
                    URL.revokeObjectURL(url);
                  }}
                  style={{ background: "transparent", color: "#34D399", border: "1px solid rgba(52,211,153,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>
                  Export CSV
                </button>
                <button onClick={loadBackendActivityAudit} style={{ background: "transparent", color: "#93C5FD", border: "1px solid rgba(147,197,253,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>{backendActivityLoading ? "Refreshing..." : "Refresh"}</button>
              </div>
            </div>
            {/* ── Filter Row ── */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 140px 140px", gap: 8, marginBottom: 8 }}>
              <input value={backendActivitySearch} onChange={(e) => setBackendActivitySearch(e.target.value)} placeholder="Search action, detail, role..." style={inp} />
              <select value={activityActionFilter} onChange={(e) => setActivityActionFilter(e.target.value)} style={inp}>
                <option value="all">All categories</option>
                <option value="mailbox">Mailbox</option>
                <option value="staff">Staff</option>
                <option value="dashboard">Dashboard</option>
                <option value="client">Client</option>
                <option value="stripe">Stripe</option>
                <option value="system">Other / System</option>
              </select>
              <input value={activityDateFrom} onChange={(e) => setActivityDateFrom(e.target.value)} type="date" title="From date" style={inp} />
              <input value={activityDateTo} onChange={(e) => setActivityDateTo(e.target.value)} type="date" title="To date" style={inp} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <input value={activityActorFilter} onChange={(e) => setActivityActorFilter(e.target.value)} placeholder="Filter by actor name or username..." style={inp} />
            </div>
            <div style={{ maxHeight: 380, overflowY: "auto", display: "grid", gap: 8 }}>
              {pagedBackendActivity.map((entry) => (
                <div key={entry.id} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 9, padding: "9px 10px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <div style={{ fontSize: 12, color: "#E5E7EB", fontWeight: 700 }}>{entry.action || "system.event"}</div>
                      {entry.action?.startsWith("mailbox.") && <span style={{ fontSize: 10, background: "rgba(99,102,241,0.18)", color: "#A5B4FC", borderRadius: 5, padding: "1px 6px" }}>mailbox</span>}
                      {entry.action?.startsWith("staff.") && <span style={{ fontSize: 10, background: "rgba(16,185,129,0.18)", color: "#6EE7B7", borderRadius: 5, padding: "1px 6px" }}>staff</span>}
                      {entry.action?.startsWith("dashboard.") && <span style={{ fontSize: 10, background: "rgba(245,158,11,0.18)", color: "#FCD34D", borderRadius: 5, padding: "1px 6px" }}>dashboard</span>}
                      {entry.action?.startsWith("client.") && <span style={{ fontSize: 10, background: "rgba(59,130,246,0.18)", color: "#93C5FD", borderRadius: 5, padding: "1px 6px" }}>client</span>}
                      {entry.action?.startsWith("stripe.") && <span style={{ fontSize: 10, background: "rgba(139,92,246,0.18)", color: "#C4B5FD", borderRadius: 5, padding: "1px 6px" }}>stripe</span>}
                    </div>
                    <div style={{ fontSize: 11, color: "#6B7280" }}>{entry.at ? new Date(entry.at).toLocaleString() : ""}</div>
                  </div>
                  <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 4 }}>{entry.detail || "No detail"}</div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <div style={{ fontSize: 11, color: "#D1D5DB" }}>
                      {entry.actor?.name || "System"} (@{entry.actor?.username || "system"}) • {entry.actor?.role || "system"}
                    </div>
                    {entry.ip && <div style={{ fontSize: 10, color: "#6B7280", fontFamily: "'Space Mono', monospace" }}>{entry.ip}</div>}
                  </div>
                </div>
              ))}
              {filteredBackendActivity.length === 0 && <div style={{ fontSize: 12, color: "#9CA3AF" }}>No backend activity trail entries match the current filters.</div>}
            </div>
            {activityTotalPages > 1 && (
              <div style={{ display: "flex", gap: 8, justifyContent: "center", alignItems: "center", marginTop: 12 }}>
                <button onClick={() => setActivityPage((p) => Math.max(1, p - 1))} disabled={activityPageClamped <= 1} style={{ background: "transparent", color: activityPageClamped <= 1 ? "#374151" : "#9CA3AF", border: "1px solid #1E1E2A", borderRadius: 7, padding: "5px 12px", fontSize: 11, cursor: activityPageClamped <= 1 ? "default" : "pointer" }}>← Prev</button>
                <span style={{ fontSize: 11, color: "#9CA3AF" }}>Page {activityPageClamped} of {activityTotalPages} ({filteredBackendActivity.length} entries)</span>
                <button onClick={() => setActivityPage((p) => Math.min(activityTotalPages, p + 1))} disabled={activityPageClamped >= activityTotalPages} style={{ background: "transparent", color: activityPageClamped >= activityTotalPages ? "#374151" : "#9CA3AF", border: "1px solid #1E1E2A", borderRadius: 7, padding: "5px 12px", fontSize: 11, cursor: activityPageClamped >= activityTotalPages ? "default" : "pointer" }}>Next →</button>
              </div>
            )}
          </div>
        </div>}

        {/* ===== REGISTRATIONS ===== */}
        {tab === "registrations" && <div style={{ padding: "20px 28px", display: "grid", gap: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#fff" }}>New Registrations</div>
              <div style={{ fontSize: 12, color: "#9CA3AF" }}>{registrationNotifications.length} total · {registrationNotifications.filter((n) => !registrationsSeenIds.includes(n.id)).length} unread</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {registrationNotifications.some((n) => !registrationsSeenIds.includes(n.id)) && (
                <button onClick={() => setRegistrationsSeenIds(registrationNotifications.map((n) => n.id))} style={{ background: "transparent", color: "#10B981", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 8, padding: "7px 12px", fontSize: 12, cursor: "pointer" }}>Mark all read</button>
              )}
              {registrationNotifications.length > 0 && (
                <button onClick={() => { if (window.confirm("Clear all registration notifications?")) { setRegistrationNotifications([]); setRegistrationsSeenIds([]); }}} style={{ background: "transparent", color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "7px 12px", fontSize: 12, cursor: "pointer" }}>Clear All</button>
              )}
            </div>
          </div>
          {registrationNotifications.length === 0 ? (
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>No new registrations yet. New self-serve signups will appear here.</div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {registrationNotifications.map((n) => {
                const isNew = !registrationsSeenIds.includes(n.id);
                return (
                  <div key={n.id} style={{ background: "#111118", border: `1px solid ${isNew ? "rgba(99,102,241,0.45)" : "#1E1E2A"}`, borderRadius: 12, padding: 16, display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "start" }}
                    onClick={() => { if (isNew) setRegistrationsSeenIds((prev) => [...prev, n.id]); }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        {isNew && <span style={{ background: "#6366F1", color: "#fff", fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 99, textTransform: "uppercase" }}>New</span>}
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{n.businessName}</div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "4px 16px", fontSize: 12, color: "#9CA3AF" }}>
                        <div><span style={{ color: "#6B7280" }}>Contact:</span> {n.contactName}</div>
                        <div><span style={{ color: "#6B7280" }}>Email:</span> {n.email}</div>
                        <div><span style={{ color: "#6B7280" }}>Phone:</span> {n.phone || "—"}</div>
                        <div><span style={{ color: "#6B7280" }}>Username:</span> @{n.username}</div>
                        <div><span style={{ color: "#6B7280" }}>Plan:</span> {n.plan}</div>
                        <div><span style={{ color: "#6B7280" }}>Payment:</span> {n.paymentMethod}</div>
                        <div style={{ gridColumn: "1/-1" }}><span style={{ color: "#6B7280" }}>Registered:</span> {new Date(n.registeredAt).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>}

        {/* ===== LANDING PAGE CMS ===== */}
        {tab === "cms" && (() => {
          const d = landingContentDraft;
          const set = (key, val) => setLandingContentDraft((p) => ({ ...p, [key]: val }));
          // Dynamic array helpers
          const getArr = (key, defVal) => Array.isArray(d?.[key]) ? d[key] : defVal;
          const setArr = (key, arr) => setLandingContentDraft((p) => ({ ...p, [key]: arr }));
          const addItem = (key, blank) => setArr(key, [...getArr(key, []), blank]);
          const removeItem = (key, idx) => setArr(key, getArr(key, []).filter((_, i) => i !== idx));
          const updateItem = (key, idx, field, val) => setArr(key, getArr(key, []).map((item, i) => i === idx ? { ...item, [field]: val } : item));
          const saveLandingContent = async () => {
            if (!d || !agencySecret) return;
            setLandingContentSaving(true);
            try {
              await apiFetch("/api/admin/landing-content", {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-Agency-Secret": agencySecret },
                body: JSON.stringify(d),
              });
              setLandingContent({ ...d });
              notify("Landing page saved — changes are live on www.replai.work");
            } catch { notify("Failed to save landing page content", "error"); }
            setLandingContentSaving(false);
          };
          const cmsSection = (title, children) => (
            <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#A5B4FC", marginBottom: 12 }}>{title}</div>
              {children}
            </div>
          );
          const cmsRow = (fields) => (
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${fields.length},1fr)`, gap: 8, marginBottom: 8 }}>
              {fields.map(([k, ph, multiline]) => (
                <div key={k}>
                  <label style={lbl}>{k.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase())}</label>
                  {multiline
                    ? <textarea value={d[k] || ""} rows={2} onChange={(e) => set(k, e.target.value)} placeholder={ph || ""} style={{ ...inp, resize: "vertical" }} />
                    : <input value={d[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph || ""} style={inp} />}
                </div>
              ))}
            </div>
          );
          const testimonials = getArr("testimonials", [
            { name: "Marco B.", business: "Bella's Pizzeria — Brooklyn, NY", quote: "Since Replai started handling our reviews, our Google rating went from 4.1 to 4.4 in just 3 weeks. Every review gets a professional response within hours. Worth every cent." },
            { name: "Jenny L.", business: "Glow Skin Studio — Austin, TX", quote: "I used to spend 2 hours a week writing review responses. Now I spend zero. Replai handles everything perfectly — our clients love the thoughtful replies." },
            { name: "Dave P.", business: "QuickFix Auto — Denver, CO", quote: "We had a tough 1-star review that could've hurt us badly. Replai crafted a response that turned the reviewer into a repeat customer. Game-changing service." },
          ]);
          const faqs = getArr("faqs", [
            { q: "🔒 Who sees my reviews?", a: "Only you and us. Your review data is private and never shared with third parties. We don't train AI models on your business data." },
            { q: "🔐 Do you store my credentials?", a: "We store a secure access token from Google — not your password. Full GDPR compliant." },
            { q: "⏹️ Can I revoke access anytime?", a: "Yes. Cancel anytime with 30 days notice. Access is revoked immediately in your Google Business Profile settings." },
            { q: "Do I need to install anything?", a: "No. 100% done-for-you. We handle everything — monitoring, writing, publishing. No tool to learn." },
            { q: "Which platforms do you support?", a: "Google Business Profile is our primary platform with full integration. We also handle Facebook and Trustpilot reviews on request." },
            { q: "Can I review responses first?", a: "Yes! On request, we send each draft for approval. Most clients prefer fully hands-off." },
            { q: "Can I try before committing?", a: "Absolutely. We'll write a free sample response to one of your recent reviews — no payment, no commitment." },
            { q: "What about multiple locations?", a: "Our Enterprise plan covers up to 10 Google Business locations. Need more? Contact us for custom pricing." },
          ]);
          return (
          <div style={{ padding: "20px 28px", display: "grid", gap: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "#fff" }}>Landing Page CMS</div>
                <div style={{ fontSize: 12, color: "#9CA3AF" }}>Edit all text on www.replai.work. Changes go live instantly on save.</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {d && <button onClick={() => setLandingContentDraft({ ...landingContent })} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}>Reset changes</button>}
                <button onClick={saveLandingContent} disabled={!d || landingContentSaving} style={{ background: !d || landingContentSaving ? "#374151" : "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 700, cursor: !d || landingContentSaving ? "not-allowed" : "pointer" }}>{landingContentSaving ? "Saving…" : "Save & Publish"}</button>
              </div>
            </div>

            {landingContentLoading && <div style={{ color: "#9CA3AF", fontSize: 13, padding: 16 }}>Loading current content…</div>}

            {!landingContentLoading && !d && <div style={{ padding: 16, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, display: "flex", gap: 12, alignItems: "center" }}>
              <div style={{ flex: 1, fontSize: 13, color: "#9CA3AF" }}>Click <strong style={{ color: "#fff" }}>Load Content</strong> to fetch the current landing page text from the server for editing.</div>
              <button onClick={async () => {
                setLandingContentLoading(true);
                try {
                  const r = await apiFetch("/api/landing-content");
                  const json = await r.json();
                  setLandingContent(json);
                  setLandingContentDraft({ ...json });
                } catch { notify("Could not load landing content", "error"); }
                setLandingContentLoading(false);
              }} style={{ background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Load Content</button>
            </div>}

            {d && <>
              {/* ── Hero ── */}
              {cmsSection("Hero Section", <>
                {cmsRow([["heroBadge","Now accepting new clients"]])}
                {cmsRow([["heroTitle","Choose your review workflow...", true]])}
                {cmsRow([["heroSubtitle","Run reviews your way...", true]])}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
                  {[["ctaPrimary","Start with Replai"],["ctaPortal","Client Portal Login"],["ctaSelfServe","Register for Self-Serve"],["ctaManaged","Register as Managed"]].map(([k,ph]) => (
                    <div key={k}><label style={lbl}>{k.replace(/([A-Z])/g," $1").trim()}</label><input value={d[k]||""} onChange={(e)=>set(k,e.target.value)} placeholder={ph} style={inp}/></div>
                  ))}
                </div>
              </>)}

              {/* ── Proof Bar ── */}
              {cmsSection("Proof Bar (4 stats)", <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
                  {[["proof1Value","24hrs"],["proof1Label","Max Response Time"],["proof2Value","100%"],["proof2Label","Reviews Answered"],["proof3Value","0 hrs"],["proof3Label","Your Time Spent"],["proof4Value","€0"],["proof4Label","To Try It Out"]].map(([k,ph]) => (
                    <div key={k}><label style={lbl}>{k.replace(/([A-Z])/g," $1").trim()}</label><input value={d[k]||""} onChange={(e)=>set(k,e.target.value)} placeholder={ph} style={inp}/></div>
                  ))}
                </div>
              </>)}

              {/* ── Dual Options ── */}
              {cmsSection("Two Product Modes Section", <>
                {cmsRow([["dualSectionLabel","Two Product Modes"],["dualHeading","Pick the setup that fits your team."]])}
                {cmsRow([["dualSubtitle","You can start hands-off and switch to self-serve later...", true]])}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 6 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 6 }}>Agency-Managed Card</div>
                    {cmsRow([["dualAgencyHeading","Agency-Managed Dashboard"]])}
                    {cmsRow([["dualAgencyPara","Best for owners who want results...", true]])}
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 6 }}>Self-Serve Card</div>
                    {cmsRow([["dualSelfHeading","Client Self-Serve Dashboard"]])}
                    {cmsRow([["dualSelfPara","Best for teams who want direct control...", true]])}
                  </div>
                </div>
              </>)}

              {/* ── Problem Section ── */}
              {cmsSection("Problem Section (3 cards)", <>
                {cmsRow([["problemSectionLabel","The Problem"],["problemTitle","Unanswered reviews are costing you customers right now."]])}
                {[0,1,2].map((i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
                    <div><label style={lbl}>Icon {i+1}</label><input value={d[`prob${i}Icon`]||""} onChange={(e)=>set(`prob${i}Icon`,e.target.value)} placeholder="📉" style={inp}/></div>
                    <div><label style={lbl}>Title</label><input value={d[`prob${i}Title`]||""} onChange={(e)=>set(`prob${i}Title`,e.target.value)} style={inp}/></div>
                    <div><label style={lbl}>Body</label><input value={d[`prob${i}Body`]||""} onChange={(e)=>set(`prob${i}Body`,e.target.value)} style={inp}/></div>
                    <div><label style={lbl}>Stat</label><input value={d[`prob${i}Stat`]||""} onChange={(e)=>set(`prob${i}Stat`,e.target.value)} style={inp}/></div>
                  </div>
                ))}
              </>)}

              {/* ── How It Works ── */}
              {cmsSection("How It Works (3 steps)", <>
                {cmsRow([["howSectionLabel","How It Works"],["howTitle","We do the work. You get the results."]])}
                {cmsRow([["howSubtitle","No software to learn. No dashboard to check.", true]])}
                {[0,1,2].map((i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr 120px", gap: 8, marginBottom: 8 }}>
                    <div><label style={lbl}>Icon {i+1}</label><input value={d[`step${i}Icon`]||""} onChange={(e)=>set(`step${i}Icon`,e.target.value)} placeholder="🤝" style={inp}/></div>
                    <div><label style={lbl}>Title</label><input value={d[`step${i}Title`]||""} onChange={(e)=>set(`step${i}Title`,e.target.value)} style={inp}/></div>
                    <div><label style={lbl}>Body</label><input value={d[`step${i}Body`]||""} onChange={(e)=>set(`step${i}Body`,e.target.value)} style={inp}/></div>
                    <div><label style={lbl}>Tag</label><input value={d[`step${i}Tag`]||""} onChange={(e)=>set(`step${i}Tag`,e.target.value)} placeholder="5-min setup" style={inp}/></div>
                  </div>
                ))}
              </>)}

              {/* ── Social Proof section headings ── */}
              {cmsSection("Social Proof Section", <>
                {cmsRow([["socialSectionLabel","Trusted By"],["socialHeading","Real businesses, real results."]])}
                {cmsRow([["socialSubtitle","See what our clients are saying...", true]])}
              </>)}

              {/* ── Testimonials (dynamic) ── */}
              {cmsSection("Testimonials", <>
                {testimonials.map((t, i) => (
                  <div key={i} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid #1E1E2A" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <div style={{ fontSize: 11, color: "#6B7280" }}>Testimonial {i+1}</div>
                      <button onClick={() => removeItem("testimonials", i)} style={{ background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 6, padding: "3px 8px", fontSize: 11, cursor: "pointer" }}>Remove</button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 6 }}>
                      <div><label style={lbl}>Name</label><input value={t.name||""} onChange={(e)=>updateItem("testimonials",i,"name",e.target.value)} style={inp}/></div>
                      <div><label style={lbl}>Business / Location</label><input value={t.business||""} onChange={(e)=>updateItem("testimonials",i,"business",e.target.value)} style={inp}/></div>
                    </div>
                    <div><label style={lbl}>Quote (without quotation marks)</label><textarea value={t.quote||""} rows={2} onChange={(e)=>updateItem("testimonials",i,"quote",e.target.value)} style={{ ...inp, resize: "vertical" }}/></div>
                  </div>
                ))}
                <button onClick={() => addItem("testimonials", { name: "", business: "", quote: "" })} style={{ background: "#1E1E2A", color: "#A5B4FC", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}>+ Add Testimonial</button>
              </>)}

              {/* ── Platforms section ── */}
              {cmsSection("Platforms Section", <>
                {cmsRow([["platformsSectionLabel","Supported Platforms"],["platformsHeading","Google-first. Others on request."]])}
                {cmsRow([["platformsSubtitle","We specialize in Google Business Profile...", true]])}
                {cmsRow([["platformsCallout","Need Yelp, TripAdvisor, or another platform?"]])}
              </>)}

              {/* ── Pricing section ── */}
              {cmsSection("Pricing Section", <>
                {cmsRow([["pricingSectionLabel","Pricing"],["pricingTitle","Simple plans for done-for-you or self-serve."]])}
                {cmsRow([["pricingSubtitle","Choose Replai-managed service or use the self-serve client portal.", true]])}
              </>)}

              {/* ── CTA section ── */}
              {cmsSection("Bottom CTA Section", <>
                {cmsRow([["ctaSectionTitle","See it before you pay for it."],["ctaSectionButton","Request Your Free Sample"]])}
                {cmsRow([["ctaSectionSubtitle","Send us your Google Business listing. Free sample response, no strings attached.", true]])}
              </>)}

              {/* ── FAQs (dynamic) ── */}
              {cmsSection("FAQ", <>
                {faqs.map((f, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 40px", gap: 8, marginBottom: 8, alignItems: "start" }}>
                    <div><label style={lbl}>Question {i+1}</label><input value={f.q||""} onChange={(e)=>updateItem("faqs",i,"q",e.target.value)} style={inp}/></div>
                    <div><label style={lbl}>Answer</label><textarea value={f.a||""} rows={2} onChange={(e)=>updateItem("faqs",i,"a",e.target.value)} style={{ ...inp, resize: "vertical" }}/></div>
                    <div style={{ paddingTop: 20 }}><button onClick={() => removeItem("faqs", i)} style={{ background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 6, padding: "7px 8px", fontSize: 11, cursor: "pointer" }}>✕</button></div>
                  </div>
                ))}
                <button onClick={() => addItem("faqs", { q: "", a: "" })} style={{ background: "#1E1E2A", color: "#A5B4FC", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}>+ Add FAQ</button>
              </>)}

              {/* ── Contact Section ── */}
              {cmsSection("Contact Section", <>
                {[
                  { k: "contactSectionLabel", l: "Section Label", p: "Get in Touch" },
                  { k: "contactHeading", l: "Heading", p: "Contact Us" },
                  { k: "contactSubtitle", l: "Subtitle / Description", p: "We'd love to hear from you." },
                ].map(({ k, l, p }) => (
                  <div key={k}><label style={lbl}>{l}</label><input value={d[k]||""} onChange={(e)=>set(k,e.target.value)} placeholder={p} style={inp}/></div>
                ))}
              </>)}

              {/* ── Footer ── */}
              {cmsSection("Footer", <>
                {[
                  { k: "footerCopyright", l: "Copyright Text", p: "© 2026 Replai." },
                  { k: "footerLinkImprint", l: "Impressum Link Label", p: "Impressum" },
                  { k: "footerLinkPrivacy", l: "Privacy Link Label", p: "Privacy Policy" },
                  { k: "footerLinkTerms", l: "Terms Link Label", p: "Terms of Service" },
                  { k: "footerLinkContact", l: "Contact Link Label", p: "Contact" },
                ].map(({ k, l, p }) => (
                  <div key={k}><label style={lbl}>{l}</label><input value={d[k]||""} onChange={(e)=>set(k,e.target.value)} placeholder={p} style={inp}/></div>
                ))}
              </>)}

              {/* ── Imprint ── */}
              {cmsSection("Imprint (Legal)", <>
                {[
                  { k: "imprintTitle", l: "Page Title", p: "Impressum" },
                  { k: "imprintBusinessName", l: "Business Name", p: "Replai" },
                  { k: "imprintProprietor", l: "Proprietor / Owner", p: "Festus Okunlola" },
                  { k: "imprintAddress", l: "Business Address", p: "Fritz-Litzlfelderstraße 21, 85614 Kirchseeon, Germany" },
                  { k: "imprintEmail", l: "Email", p: "hello@replai.work" },
                  { k: "imprintPhone", l: "Phone", p: "+49 176 65714223" },
                  { k: "imprintWebsite", l: "Website URL", p: "https://replai.work" },
                  { k: "imprintJurisdiction", l: "Jurisdiction / Registration", p: "Registered and operates under German law." },
                  { k: "imprintVat", l: "VAT / Tax ID", p: "VAT ID will be provided upon business registration." },
                  { k: "imprintDispute", l: "Dispute Resolution (HTML allowed)", p: "We are not obligated to participate in online dispute resolution." },
                  { k: "imprintContentResp", l: "Content Responsibility", p: "We are responsible for content we provide and services we deliver." },
                ].map(({ k, l, p }) => (
                  <div key={k}>
                    <label style={lbl}>{l}</label>
                    {k==="imprintDispute"
                      ? <textarea value={d[k]||""} rows={3} onChange={(e)=>set(k,e.target.value)} placeholder={p} style={{ ...inp, resize: "vertical" }}/>
                      : <input value={d[k]||""} onChange={(e)=>set(k,e.target.value)} placeholder={p} style={inp}/>
                    }
                  </div>
                ))}
              </>)}

              {/* ── Privacy Policy ── */}
              {cmsSection("Privacy Policy", <>
                <div><label style={lbl}>Page Title</label><input value={d.privacyTitle||""} onChange={(e)=>set("privacyTitle",e.target.value)} placeholder="Privacy Policy" style={inp}/></div>
                <div>
                  <label style={lbl}>Full Body (HTML allowed)</label>
                  <textarea
                    value={d.privacyBody||""}
                    rows={14}
                    onChange={(e)=>set("privacyBody",e.target.value)}
                    placeholder="<h3>1. Data Controller</h3><p>...</p>"
                    style={{ ...inp, resize: "vertical", fontFamily: "monospace", fontSize: 11 }}
                  />
                  <div style={{ fontSize: 11, color: "#6B7280", marginTop: 4 }}>HTML tags are supported. This replaces the entire privacy policy body on the landing page.</div>
                </div>
              </>)}

              {/* ── Terms of Service ── */}
              {cmsSection("Terms of Service", <>
                <div><label style={lbl}>Page Title</label><input value={d.termsTitle||""} onChange={(e)=>set("termsTitle",e.target.value)} placeholder="Terms of Service" style={inp}/></div>
                <div>
                  <label style={lbl}>Full Body (HTML allowed)</label>
                  <textarea
                    value={d.termsBody||""}
                    rows={14}
                    onChange={(e)=>set("termsBody",e.target.value)}
                    placeholder="<h3>1. Acceptance of Terms</h3><p>...</p>"
                    style={{ ...inp, resize: "vertical", fontFamily: "monospace", fontSize: 11 }}
                  />
                  <div style={{ fontSize: 11, color: "#6B7280", marginTop: 4 }}>HTML tags are supported. This replaces the entire terms of service body on the landing page.</div>
                </div>
              </>)}

              <div style={{ fontSize: 12, color: "#6B7280", background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: "10px 14px" }}>
                All saved content overrides default text on www.replai.work. Testimonials and FAQs can be freely added or removed — changes rebuild those sections dynamically.
              </div>
            </>}
          </div>
        );})()}

        {/* ===== SETTINGS ===== */}
        {tab === "settings" && <div style={{ padding: "20px 28px", maxWidth: 760 }}>
          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 22 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 600, color: "#fff" }}>Business Info</h3>
            {[
              { l: "Name", key: "name" },
              { l: "Trading As", key: "tradingAs" },
              { l: "Address", key: "address" },
              { l: "Phone", key: "phone" },
              { l: "Email", key: "email" },
            ].map((field) => (
              <div key={field.l} style={{ marginBottom: 10 }}><label style={lbl}>{field.l}</label><input type="text" value={businessInfoDraft[field.key]} onChange={(e) => setBusinessInfoDraft((prev) => ({ ...prev, [field.key]: e.target.value }))} style={inp} /></div>
            ))}
            <button onClick={saveBusinessInfoDraft} style={{ background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontWeight: 700, cursor: "pointer" }}>Save Business Info</button>
          </div>

          <div style={{ marginTop: 16, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 22 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 600, color: "#fff" }}>My Profile</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label style={lbl}>First Name</label><input value={staffProfileDraft.firstName} onChange={(e) => setStaffProfileDraft((prev) => ({ ...prev, firstName: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Last Name</label><input value={staffProfileDraft.lastName} onChange={(e) => setStaffProfileDraft((prev) => ({ ...prev, lastName: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Gender</label><select value={staffProfileDraft.gender} onChange={(e) => setStaffProfileDraft((prev) => ({ ...prev, gender: e.target.value }))} style={inp}><option value="">Select gender</option><option value="female">Female</option><option value="male">Male</option><option value="other">Other</option></select></div>
              <div><label style={lbl}>Email</label><input value={staffProfileDraft.email} onChange={(e) => setStaffProfileDraft((prev) => ({ ...prev, email: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Phone</label><input value={staffProfileDraft.phone} onChange={(e) => setStaffProfileDraft((prev) => ({ ...prev, phone: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>City</label><input value={staffProfileDraft.city} onChange={(e) => setStaffProfileDraft((prev) => ({ ...prev, city: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Country</label><input value={staffProfileDraft.country} onChange={(e) => setStaffProfileDraft((prev) => ({ ...prev, country: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Preferred Work Language</label><select value={staffProfileDraft.preferredLanguage} onChange={(e) => setStaffProfileDraft((prev) => ({ ...prev, preferredLanguage: e.target.value }))} style={inp}>{LANGUAGE_OPTIONS.map((lang) => <option key={`settings-profile-lang-${lang}`} value={lang}>{lang}</option>)}</select></div>
              <div><label style={lbl}>Timezone</label><select value={staffProfileDraft.timezone} onChange={(e) => setStaffProfileDraft((prev) => ({ ...prev, timezone: e.target.value }))} style={inp}>{TIMEZONE_OPTIONS.map((tz) => <option key={`settings-profile-timezone-${tz}`} value={tz}>{tz}</option>)}</select></div>
              <div><label style={lbl}>Notification Preference</label><select value={staffProfileDraft.notificationPreference} onChange={(e) => setStaffProfileDraft((prev) => ({ ...prev, notificationPreference: e.target.value }))} style={inp}>{NOTIFICATION_PREFERENCES.map((pref) => <option key={`settings-profile-pref-${pref.id}`} value={pref.id}>{pref.label}</option>)}</select></div>
              <div style={{ gridColumn: "1 / span 2" }}>
                <label style={{ ...lbl, display: "block", marginBottom: 10 }}>Sound Notifications</label>
                <div style={{ display: "grid", gap: 10 }}>
                  {[
                    { key: "review",  label: "New Review",       icon: "⭐" },
                    { key: "chat",    label: "Staff Chat Message", icon: "💬" },
                    { key: "mailbox", label: "New Mailbox Email",  icon: "📬" },
                  ].map(({ key, label, icon }) => {
                    const s = normalizeSoundSettings(staffProfileDraft.soundSettings)[key];
                    return (
                      <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, background: "#0A0A0F", border: "1px solid #2D2D3A", borderRadius: 8, padding: "10px 14px" }}>
                        <span style={{ fontSize: 16 }}>{icon}</span>
                        <span style={{ flex: 1, fontSize: 12, color: "#E5E5E5", fontWeight: 600 }}>{label}</span>
                        <select
                          value={s.tone}
                          onChange={(e) => setStaffProfileDraft((prev) => ({ ...prev, soundSettings: { ...normalizeSoundSettings(prev.soundSettings), [key]: { ...normalizeSoundSettings(prev.soundSettings)[key], tone: e.target.value } } }))}
                          style={{ ...inp, width: 110, marginBottom: 0, padding: "5px 8px", fontSize: 11 }}
                          disabled={!s.enabled}
                        >
                          {SOUND_TONES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                        </select>
                        <button
                          type="button"
                          onClick={() => playNotificationSound(s.tone)}
                          disabled={!s.enabled}
                          title="Preview this sound"
                          style={{ background: "#1E1E2A", border: "1px solid #2D2D3A", borderRadius: 6, color: "#9CA3AF", cursor: s.enabled ? "pointer" : "not-allowed", fontSize: 14, padding: "4px 8px", opacity: s.enabled ? 1 : 0.4 }}
                        >▶</button>
                        <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", userSelect: "none", fontSize: 12, color: s.enabled ? "#34D399" : "#6B7280" }}>
                          <input
                            type="checkbox"
                            checked={s.enabled}
                            onChange={(e) => setStaffProfileDraft((prev) => ({ ...prev, soundSettings: { ...normalizeSoundSettings(prev.soundSettings), [key]: { ...normalizeSoundSettings(prev.soundSettings)[key], enabled: e.target.checked } } }))}
                            style={{ accentColor: "#6366F1" }}
                          />
                          {s.enabled ? "On" : "Off"}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div><label style={lbl}>Username</label><input value={staffProfileDraft.username} onChange={(e) => setStaffProfileDraft((prev) => ({ ...prev, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") }))} style={inp} placeholder="letters, numbers, underscores only" /></div>
              <div style={{ gridColumn: "1 / span 2" }}><label style={lbl}>Short Bio</label><textarea value={staffProfileDraft.bio} onChange={(e) => setStaffProfileDraft((prev) => ({ ...prev, bio: e.target.value }))} style={{ ...inp, minHeight: 88, resize: "vertical" }} /></div>
            </div>
            <button onClick={saveCurrentUserProfileDraft} style={{ marginTop: 12, background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontWeight: 700, cursor: "pointer" }}>Save Profile</button>
          </div>

          <div style={{ marginTop: 16, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 22 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 600, color: "#fff" }}>Onboarding Experience</h3>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 12 }}>Open the full onboarding flow again for this account, including admin users.</div>
            <button onClick={relaunchOnboarding} style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontWeight: 700, cursor: "pointer" }}>Launch Onboarding</button>
          </div>

          <div style={{ marginTop: 16, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 22 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 600, color: "#fff" }}>Password</h3>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 12 }}>Change your account password anytime.</div>
            <div style={{ marginBottom: 10 }}><label style={lbl}>Current password</label><input type="password" value={settingsPasswordForm.currentPassword} onChange={(e) => setSettingsPasswordForm((p) => ({ ...p, currentPassword: e.target.value }))} style={inp} /></div>
            <div style={{ marginBottom: 10 }}><label style={lbl}>New password</label><input type="password" value={settingsPasswordForm.nextPassword} onChange={(e) => setSettingsPasswordForm((p) => ({ ...p, nextPassword: e.target.value }))} style={inp} /></div>
            <div style={{ marginBottom: 12 }}><label style={lbl}>Confirm new password</label><input type="password" value={settingsPasswordForm.confirmPassword} onChange={(e) => setSettingsPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))} style={inp} /></div>
            <button onClick={changeOwnPasswordFromSettings} style={{ background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontWeight: 700, cursor: "pointer" }}>Update My Password</button>
          </div>

          <div style={{ marginTop: 16, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 22 }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 600, color: "#fff" }}>Security Dashboard</h3>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 12 }}>All staff must keep every recommended security layer active.</div>
            <div style={{ marginBottom: 10, fontSize: 12, color: currentSecurityProgress.enabled === currentSecurityProgress.total ? "#34D399" : "#F59E0B" }}>
              Security coverage: {currentSecurityProgress.enabled}/{currentSecurityProgress.total} layers active
            </div>
            <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
              {SECURITY_LAYER_DEFINITIONS.map((layer) => {
                const active = !!currentSecurityLayers[layer.key];
                return (
                  <div key={`staff-security-layer-${layer.key}`} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 12, color: "#fff", fontWeight: 700 }}>{layer.label}</div>
                      <div style={{ fontSize: 11, color: "#9CA3AF" }}>{layer.detail}</div>
                    </div>
                    <button disabled={active} onClick={() => updateOwnSecurityLayer(layer.key, true)} style={{ background: active ? "rgba(16,185,129,0.15)" : "#1E1E2A", color: active ? "#34D399" : "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "6px 10px", fontSize: 11, fontWeight: 700, cursor: active ? "default" : "pointer" }}>{active ? "Active" : "Activate"}</button>
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={activateAllRecommendedSecurityLayers} style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontWeight: 700, cursor: "pointer" }}>Activate All Recommended Layers</button>
            </div>
            {canResetAllSecurityLayers && <div style={{ marginTop: 8, fontSize: 11, color: "#FCA5A5" }}>Targeted per-layer reset is available per staff in Staff Management and per client in Self-Serve Accounts.</div>}
            <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
              {currentSecurityLayers.mfaApp && <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 11, color: "#D1D5DB", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#34D399", fontWeight: 700 }}>✓</span> Authenticator MFA configured</span>
                <button onClick={() => updateOwnSecurityLayer("mfaApp", true)} style={{ background: "transparent", color: "#A7F3D0", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>Re-enroll</button>
              </div>}
              {currentSecurityLayers.backupCodes && <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 11, color: "#D1D5DB", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <span>Backup codes remaining: <strong style={{ color: "#fff" }}>{Array.isArray(currentSecurityState.backupCodeHashes) ? currentSecurityState.backupCodeHashes.length : 0}</strong></span>
                <button onClick={regenerateBackupCodesForCurrentUser} style={{ background: "transparent", color: "#A7F3D0", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>Regenerate</button>
              </div>}
              {currentSecurityLayers.suspiciousLoginLock && <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 11, color: "#D1D5DB" }}>
                Trusted devices: <strong style={{ color: "#fff" }}>{(currentSecurityState.trustedDevices || []).length}</strong>
              </div>}
              {currentSecurityLayers.loginAlerts && <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 11, color: "#D1D5DB" }}>
                Recent login alerts: <strong style={{ color: "#fff" }}>{(currentSecurityState.loginAlerts || []).length}</strong>
              </div>}
            </div>
            {revealedBackupCodes && <div style={{ marginTop: 10, padding: 12, background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#A7F3D0", marginBottom: 6 }}>Recovery Backup Codes</div>
              <div style={{ fontSize: 11, color: "#D1FAE5", marginBottom: 8 }}>Generated for {revealedBackupCodes.ownerName}. Save these now, they are shown only once.</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 }}>
                {revealedBackupCodes.codes.map((code) => (
                  <div key={`client-shown-backup-code-${code}`} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: "7px 8px", fontSize: 12, color: "#E5E5E5", fontFamily: "'Space Mono', monospace" }}>{code}</div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: "#D1FAE5", marginBottom: 8 }}>You can enter these on sign-in in the <strong>Backup code</strong> field inside Two-Step Verification.</div>
              <button onClick={() => setRevealedBackupCodes(null)} style={{ background: "transparent", color: "#A7F3D0", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 8, padding: "8px 10px", fontSize: 11, cursor: "pointer" }}>I have saved these codes</button>
            </div>}
            {revealedBackupCodes && <div style={{ marginTop: 10, padding: 12, background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#A7F3D0", marginBottom: 6 }}>Recovery Backup Codes</div>
              <div style={{ fontSize: 11, color: "#D1FAE5", marginBottom: 8 }}>Generated for {revealedBackupCodes.ownerName}. Save these now, they are shown only once.</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 }}>
                {revealedBackupCodes.codes.map((code) => (
                  <div key={`staff-shown-backup-code-${code}`} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: "7px 8px", fontSize: 12, color: "#E5E5E5", fontFamily: "'Space Mono', monospace" }}>{code}</div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: "#D1FAE5", marginBottom: 8 }}>You can enter these on sign-in in the <strong>Backup code</strong> field inside Two-Step Verification.</div>
              <button onClick={() => setRevealedBackupCodes(null)} style={{ background: "transparent", color: "#A7F3D0", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 8, padding: "8px 10px", fontSize: 11, cursor: "pointer" }}>I have saved these codes</button>
            </div>}
          </div>

          <div style={{ marginTop: 16, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 22 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 600, color: "#fff" }}>Electronic Signatures</h3>
            <div style={{ fontSize: 12, color: "#9CA3AF", lineHeight: 1.8 }}>
              Contract PDFs no longer embed your real signature image. Replai now routes contracts through a DocuSign-style electronic signature workflow and stores envelope metadata instead.
            </div>
            <div style={{ marginTop: 12, background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12, fontSize: 12, color: "#D1D5DB", lineHeight: 1.8 }}>
              • Admin sends contract from the Contracts tab<br />
              • Client signs inside portal account view<br />
              • Contract status updates to <strong>signed</strong> after completion<br />
              • Provider signature image is not inserted into the PDF
            </div>
          </div>

          <div style={{ marginTop: 16, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 22 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 600, color: "#fff" }}>AI & Platform Integration</h3>
            <div style={{ marginBottom: 12, background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12, fontSize: 12, color: "#9CA3AF", lineHeight: 1.7 }}>
              Claude key is now server-side only for security. Configure <strong style={{ color: "#fff" }}>ANTHROPIC_API_KEY</strong> in your backend environment.
              The dashboard sends regenerate requests to <strong style={{ color: "#fff" }}>/api/ai-response</strong>.
            </div>
            <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12, fontSize: 12, color: "#9CA3AF", lineHeight: 1.7 }}>
              Google integration is now wired to backend endpoints. Configure <strong style={{ color: "#fff" }}>GOOGLE_AUTH_URL</strong>, <strong style={{ color: "#fff" }}>GOOGLE_BRIDGE_URL</strong> and <strong style={{ color: "#fff" }}>GOOGLE_BRIDGE_API_KEY</strong> to use the real OAuth + review sync flow.
              <div style={{ marginTop: 8, color: googleBridgeConfig.enabled ? "#10B981" : "#F59E0B" }}>Status: {googleBridgeConfig.enabled ? "Google bridge configured" : "Google bridge not configured yet"}</div>
            </div>
            <div style={{ marginTop: 10, background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12, fontSize: 12, color: "#9CA3AF", lineHeight: 1.7 }}>
              {tp("Trustpilot integration is now wired to backend endpoints. Configure")} <strong style={{ color: "#fff" }}>TRUSTPILOT_AUTH_URL</strong>, <strong style={{ color: "#fff" }}>TRUSTPILOT_BRIDGE_URL</strong> and <strong style={{ color: "#fff" }}>TRUSTPILOT_BRIDGE_API_KEY</strong> {tp("to use the real OAuth + review sync flow.")}
              <div style={{ marginTop: 8, color: trustpilotBridgeConfig.enabled ? "#10B981" : "#F59E0B" }}>Status: {trustpilotBridgeConfig.enabled ? tp("Trustpilot bridge configured") : tp("Trustpilot bridge not configured yet")}</div>
            </div>
          </div>

          {/* White-label Branding (admin only) */}
          {can("settings.view") && <div style={{ marginTop: 16, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 22 }}>
            <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 600, color: "#fff" }}>White-label Branding</h3>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 14, lineHeight: 1.7 }}>
              Agencies can replace the Replai logo with their own brand in the sidebar and client portal header. Changes are saved locally and apply immediately.
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>Enable white-label mode</label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#D1D5DB", cursor: "pointer" }}>
                <input type="checkbox" checked={agencyBranding.enabled} onChange={(e) => saveAgencyBranding({ enabled: e.target.checked })} />
                {agencyBranding.enabled ? "White-label active — sidebar uses your brand" : "Disabled — showing default Replai branding"}
              </label>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label style={lbl}>Agency / Brand name</label>
                <input type="text" value={agencyBranding.brandName} onChange={(e) => saveAgencyBranding({ brandName: e.target.value })} placeholder="e.g. Spark Agency" style={inp} />
              </div>
              <div>
                <label style={lbl}>Logo emoji</label>
                <input type="text" value={agencyBranding.logoEmoji} onChange={(e) => saveAgencyBranding({ logoEmoji: e.target.value })} placeholder="e.g. 🚀" style={inp} maxLength={4} />
              </div>
              <div>
                <label style={lbl}>Primary color (hex)</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="color" value={agencyBranding.primaryColor || "#10B981"} onChange={(e) => saveAgencyBranding({ primaryColor: e.target.value })} style={{ width: 36, height: 36, border: "none", background: "none", cursor: "pointer", padding: 0 }} />
                  <input type="text" value={agencyBranding.primaryColor} onChange={(e) => saveAgencyBranding({ primaryColor: e.target.value })} placeholder="#10B981" style={{ ...inp, flex: 1 }} />
                </div>
              </div>
              <div>
                <label style={lbl}>Support email (client portal)</label>
                <input type="email" value={agencyBranding.supportEmail} onChange={(e) => saveAgencyBranding({ supportEmail: e.target.value })} placeholder="support@youragency.com" style={inp} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={lbl}>Footer text (reports & portal)</label>
                <input type="text" value={agencyBranding.footerText} onChange={(e) => saveAgencyBranding({ footerText: e.target.value })} placeholder="Powered by Your Agency | yourwebsite.com" style={inp} />
              </div>
            </div>
            {agencyBranding.enabled && (
              <div style={{ marginTop: 12, background: "#0A0A0F", border: `1px solid ${brandColor}40`, borderRadius: 10, padding: 12, fontSize: 12, color: "#D1D5DB" }}>
                Preview: <span style={{ color: brandColor, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{brandLogo ? `${brandLogo} ` : ""}{brandName}</span>
              </div>
            )}
          </div>}

          <div style={{ marginTop: 16, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 22 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 600, color: "#fff" }}>Bank Transfer Details By Country</h3>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 12 }}>
              Configure destination bank details per country. Payment reminders sent from the client message modal will automatically include these details.
            </div>

            <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12, marginBottom: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <input placeholder="Country code (e.g. DE)" value={bankTransferConfigForm.countryCode} onChange={(e) => setBankTransferConfigForm((p) => ({ ...p, countryCode: e.target.value }))} style={inp} />
                <input placeholder="Country name (e.g. Germany)" value={bankTransferConfigForm.countryName} onChange={(e) => setBankTransferConfigForm((p) => ({ ...p, countryName: e.target.value }))} style={inp} />
                <input placeholder="Account holder" value={bankTransferConfigForm.accountHolder} onChange={(e) => setBankTransferConfigForm((p) => ({ ...p, accountHolder: e.target.value }))} style={inp} />
                <input placeholder="Bank name" value={bankTransferConfigForm.bankName} onChange={(e) => setBankTransferConfigForm((p) => ({ ...p, bankName: e.target.value }))} style={inp} />
                <input placeholder="IBAN / account number" value={bankTransferConfigForm.ibanOrAccount} onChange={(e) => setBankTransferConfigForm((p) => ({ ...p, ibanOrAccount: e.target.value }))} style={inp} />
                <input placeholder="SWIFT/BIC" value={bankTransferConfigForm.swiftBic} onChange={(e) => setBankTransferConfigForm((p) => ({ ...p, swiftBic: e.target.value }))} style={inp} />
                <input placeholder="Routing code (optional)" value={bankTransferConfigForm.routingCode} onChange={(e) => setBankTransferConfigForm((p) => ({ ...p, routingCode: e.target.value }))} style={inp} />
                <input placeholder="Currency (e.g. EUR)" value={bankTransferConfigForm.currency} onChange={(e) => setBankTransferConfigForm((p) => ({ ...p, currency: e.target.value }))} style={inp} />
                <input placeholder="Reference prefix (e.g. RPL)" value={bankTransferConfigForm.referencePrefix} onChange={(e) => setBankTransferConfigForm((p) => ({ ...p, referencePrefix: e.target.value }))} style={inp} />
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#D1D5DB" }}>
                  <input type="checkbox" checked={bankTransferConfigForm.enabled} onChange={(e) => setBankTransferConfigForm((p) => ({ ...p, enabled: e.target.checked }))} /> Enabled
                </label>
              </div>
              <button onClick={addBankTransferConfig} style={{ marginTop: 10, background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "8px 12px", fontWeight: 700, cursor: "pointer" }}>Add Country Config</button>
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              {bankTransferConfigs.map((cfg) => (
                <div key={cfg.id} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ fontSize: 12, color: "#fff", fontWeight: 700 }}>{cfg.countryCode} — {cfg.countryName} {cfg.enabled === false ? <span style={{ color: "#F59E0B" }}>(disabled)</span> : null}</div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => startEditBankTransferConfig(cfg)} style={{ background: "transparent", color: "#93C5FD", border: "1px solid rgba(147,197,253,0.35)", borderRadius: 7, padding: "4px 8px", fontSize: 10, cursor: "pointer" }}>Edit</button>
                      <button onClick={() => removeBankTransferConfig(cfg.id)} style={{ background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 7, padding: "4px 8px", fontSize: 10, cursor: "pointer" }}>Delete</button>
                    </div>
                  </div>
                  {editingBankTransferConfigId === cfg.id ? (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                        <input placeholder="Country code" value={bankTransferEditForm.countryCode} onChange={(e) => setBankTransferEditForm((p) => ({ ...p, countryCode: e.target.value }))} style={inp} />
                        <input placeholder="Country name" value={bankTransferEditForm.countryName} onChange={(e) => setBankTransferEditForm((p) => ({ ...p, countryName: e.target.value }))} style={inp} />
                        <input placeholder="Account holder" value={bankTransferEditForm.accountHolder} onChange={(e) => setBankTransferEditForm((p) => ({ ...p, accountHolder: e.target.value }))} style={inp} />
                        <input placeholder="Bank name" value={bankTransferEditForm.bankName} onChange={(e) => setBankTransferEditForm((p) => ({ ...p, bankName: e.target.value }))} style={inp} />
                        <input placeholder="IBAN / account number" value={bankTransferEditForm.ibanOrAccount} onChange={(e) => setBankTransferEditForm((p) => ({ ...p, ibanOrAccount: e.target.value }))} style={inp} />
                        <input placeholder="SWIFT/BIC" value={bankTransferEditForm.swiftBic} onChange={(e) => setBankTransferEditForm((p) => ({ ...p, swiftBic: e.target.value }))} style={inp} />
                        <input placeholder="Routing code" value={bankTransferEditForm.routingCode} onChange={(e) => setBankTransferEditForm((p) => ({ ...p, routingCode: e.target.value }))} style={inp} />
                        <input placeholder="Currency" value={bankTransferEditForm.currency} onChange={(e) => setBankTransferEditForm((p) => ({ ...p, currency: e.target.value }))} style={inp} />
                        <input placeholder="Reference prefix" value={bankTransferEditForm.referencePrefix} onChange={(e) => setBankTransferEditForm((p) => ({ ...p, referencePrefix: e.target.value }))} style={inp} />
                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#D1D5DB" }}>
                          <input type="checkbox" checked={bankTransferEditForm.enabled} onChange={(e) => setBankTransferEditForm((p) => ({ ...p, enabled: e.target.checked }))} /> Enabled
                        </label>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={saveBankTransferConfigEdit} style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 7, padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Save</button>
                        <button onClick={cancelEditBankTransferConfig} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 7, padding: "5px 10px", fontSize: 11, cursor: "pointer" }}>Cancel</button>
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 11, color: "#9CA3AF", whiteSpace: "pre-wrap" }}>{buildBankTransferInstructionText(cfg, `${cfg.referencePrefix || "RPL"}-1234`)}</div>
                  )}
                </div>
              ))}
              {bankTransferConfigs.length === 0 && <div style={{ fontSize: 12, color: "#9CA3AF" }}>No country configs yet.</div>}
            </div>
          </div>

          {can("staff.manage") && <div style={{ marginTop: 16, background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, padding: 22 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 600, color: "#fff" }}>Staff Management</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div><label style={lbl}>Name</label><input value={staffForm.name} onChange={(e) => setStaffForm((p) => ({ ...p, name: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Username</label><input value={staffForm.username} onChange={(e) => setStaffForm((p) => ({ ...p, username: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Password</label><input type="password" value={staffForm.password} onChange={(e) => setStaffForm((p) => ({ ...p, password: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Email</label><input value={staffForm.email} onChange={(e) => setStaffForm((p) => ({ ...p, email: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Phone</label><input value={staffForm.phone} onChange={(e) => setStaffForm((p) => ({ ...p, phone: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>City</label><input value={staffForm.city} onChange={(e) => setStaffForm((p) => ({ ...p, city: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Country</label><input value={staffForm.country} onChange={(e) => setStaffForm((p) => ({ ...p, country: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Preferred Language</label><select value={staffForm.preferredLanguage} onChange={(e) => setStaffForm((p) => ({ ...p, preferredLanguage: e.target.value }))} style={inp}>{LANGUAGE_OPTIONS.map((lang) => <option key={`settings-create-lang-${lang}`} value={lang}>{lang}</option>)}</select></div>
              <div>
                <label style={lbl}>Profile Picture</label>
                <input type="file" accept="image/*" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => setStaffForm((p) => ({ ...p, avatar: String(reader.result || "") }));
                  reader.readAsDataURL(file);
                }} style={{ ...inp, padding: 8 }} />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={lbl}>{t("staff.privileges", "Privileges")}</label>
              <PermissionMatrix
                selected={staffForm.permissions}
                onChange={(permissions) => setStaffForm((p) => ({ ...p, permissions }))}
                t={t}
                idPrefix="settings-staff-create"
              />
            </div>
            <button onClick={addStaff} style={{ marginTop: 12, background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontWeight: 700, cursor: "pointer" }}>Create Staff Account</button>

            <div style={{ marginTop: 16, background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 8 }}>Filter Staff</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                <input placeholder="Search name/username/email/phone" value={staffFilter.query} onChange={(e) => setStaffFilter((p) => ({ ...p, query: e.target.value }))} style={inp} />
                <input placeholder="City or all" value={staffFilter.city === "all" ? "" : staffFilter.city} onChange={(e) => setStaffFilter((p) => ({ ...p, city: e.target.value || "all" }))} style={inp} />
                <input placeholder="Country or all" value={staffFilter.country === "all" ? "" : staffFilter.country} onChange={(e) => setStaffFilter((p) => ({ ...p, country: e.target.value || "all" }))} style={inp} />
              </div>
              <div style={{ marginTop: 10 }}>
                {filteredStaff.map((s) => (
                  <div key={s.id} style={{ padding: "8px 0", borderBottom: "1px solid #1E1E2A" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <div style={{ width: 26, height: 26, borderRadius: 99, background: "#1E1E2A", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {s.avatar ? <img src={s.avatar} alt={s.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 11, color: "#fff" }}>{(s.name || "?")[0]}</span>}
                        </div>
                        <div style={{ fontSize: 12 }}>
                          <div style={{ color: "#fff" }}>{s.name} <span style={{ color: "#6B7280" }}>@{s.username}</span> {s.mustChangePassword && <span style={{ color: "#F59E0B" }}>• password reset pending</span>}</div>
                          <div style={{ color: "#9CA3AF" }}>Language: {s.preferredLanguage || "English"}</div>
                          <div style={{ color: "#9CA3AF" }}>{s.email || "—"} • {s.phone || "—"} • {s.city || "—"}, {s.country || "—"}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ fontSize: 10, color: "#9CA3AF" }}>{s.permissions?.length || 0} perms</div>
                        {s.role !== "admin" && <button onClick={() => startEditStaff(s)} style={{ background: "transparent", color: "#6366F1", border: "1px solid #6366F1", borderRadius: 7, padding: "4px 8px", fontSize: 10, cursor: "pointer" }}>Edit</button>}
                        {s.role !== "admin" && canResetAllSecurityLayers && <button onClick={() => resetSecurityLayerForStaffMember(s.id)} style={{ background: "transparent", color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.45)", borderRadius: 7, padding: "4px 8px", fontSize: 10, cursor: "pointer" }}>Reset Layer</button>}
                        {s.role !== "admin" && <button onClick={() => deleteStaff(s.id)} style={{ background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 7, padding: "4px 8px", fontSize: 10, cursor: "pointer" }}>Delete</button>}
                      </div>
                    </div>

                    {editingStaffId === s.id && (
                      <div style={{ marginTop: 10, padding: 10, background: "#111118", border: "1px solid #2D2D3A", borderRadius: 8 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                          <input placeholder="First Name" value={staffEditForm.firstName} onChange={(e) => setStaffEditForm((p) => ({ ...p, firstName: e.target.value }))} style={inp} />
                          <input placeholder="Last Name" value={staffEditForm.lastName} onChange={(e) => setStaffEditForm((p) => ({ ...p, lastName: e.target.value }))} style={inp} />
                          <select value={staffEditForm.gender} onChange={(e) => setStaffEditForm((p) => ({ ...p, gender: e.target.value }))} style={inp}><option value="">Select gender</option><option value="female">Female</option><option value="male">Male</option><option value="other">Other</option></select>
                          <input placeholder="Email" value={staffEditForm.email} onChange={(e) => setStaffEditForm((p) => ({ ...p, email: e.target.value }))} style={inp} />
                          <input placeholder="Phone" value={staffEditForm.phone} onChange={(e) => setStaffEditForm((p) => ({ ...p, phone: e.target.value }))} style={inp} />
                          <input placeholder="City" value={staffEditForm.city} onChange={(e) => setStaffEditForm((p) => ({ ...p, city: e.target.value }))} style={inp} />
                          <input placeholder="Country" value={staffEditForm.country} onChange={(e) => setStaffEditForm((p) => ({ ...p, country: e.target.value }))} style={inp} />
                          <select value={staffEditForm.preferredLanguage} onChange={(e) => setStaffEditForm((p) => ({ ...p, preferredLanguage: e.target.value }))} style={inp}>{LANGUAGE_OPTIONS.map((lang) => <option key={`settings-edit-lang-${lang}`} value={lang}>{lang}</option>)}</select>
                          <input type="password" placeholder="Optional password reset" value={staffEditForm.resetPassword} onChange={(e) => setStaffEditForm((p) => ({ ...p, resetPassword: e.target.value }))} style={inp} />
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 6 }}>{t("staff.privileges", "Privileges")}</div>
                          <PermissionMatrix
                            selected={staffEditForm.permissions}
                            onChange={(permissions) => setStaffEditForm((p) => ({ ...p, permissions }))}
                            t={t}
                            idPrefix={`settings-staff-edit-${s.id}`}
                          />
                        </div>
                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                          <button onClick={saveStaffEdit} style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 7, padding: "6px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Save</button>
                          <button onClick={() => setEditingStaffId(null)} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 7, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>}
        </div>}

        {tab === "credentials" && (() => {
          const canConsole = currentUser?.role === "admin" || can("credentials.console");
          if (!canConsole) return null;

          /* build unified rows from staff + portal users */
          const staffRows = staffMembers.map((s) => ({
            entityType: "staff",
            id: s.id,
            name: s.name,
            username: s.username || "",
            role: s.role === "admin" ? "admin" : "staff",
            clientName: null,
            clientId: null,
            mustChangePassword: !!s.mustChangePassword,
          }));
          const portalRows = clientPortalUsers.map((u) => {
            const clientObj = clients.find((c) => c.id === u.clientId);
            return {
              entityType: "portal",
              id: u.id,
              name: u.name,
              username: u.username || "",
              role: u.role || "owner",
              clientName: clientObj ? clientObj.name : `Client #${u.clientId}`,
              clientId: u.clientId,
              mustChangePassword: !!u.mustChangePassword,
            };
          });
          const allRows = [...staffRows, ...portalRows];
          const q = credentialsFilter.query.trim().toLowerCase();
          const filteredRows = allRows.filter((r) => {
            if (credentialsFilter.type === "staff" && r.entityType !== "staff") return false;
            if (credentialsFilter.type === "portal" && r.entityType !== "portal") return false;
            if (q && !r.name.toLowerCase().includes(q) && !r.username.toLowerCase().includes(q) && !(r.clientName || "").toLowerCase().includes(q)) return false;
            return true;
          });

          const isEditing = (row) => credInlineEdit && credInlineEdit.id === row.id;

          return (
            <div style={{ padding: "20px 28px" }}>
              <div style={{ marginBottom: 18 }}>
                <h2 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: "#fff" }}>🔐 Credentials Console</h2>
                <div style={{ fontSize: 12, color: "#9CA3AF" }}>
                  All staff and client portal accounts in one place. Reset passwords, change usernames, and grant console access to trusted staff.
                </div>
              </div>

              {/* Filter bar */}
              <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                <input
                  placeholder="Search name, username or client…"
                  value={credentialsFilter.query}
                  onChange={(e) => setCredentialsFilter((p) => ({ ...p, query: e.target.value }))}
                  style={{ ...inp, flex: 1, minWidth: 220 }}
                />
                {[{ v: "all", l: "All" }, { v: "staff", l: "Staff" }, { v: "portal", l: "Portal Users" }].map(({ v, l }) => (
                  <button key={`cred-type-${v}`} onClick={() => setCredentialsFilter((p) => ({ ...p, type: v }))}
                    style={{ background: credentialsFilter.type === v ? "#6366F1" : "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 13px", fontSize: 12, cursor: "pointer" }}>
                    {l}
                  </button>
                ))}
              </div>

              {/* Table */}
              <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 14, overflow: "hidden" }}>
                {/* Header */}
                <div style={{ display: "grid", gridTemplateColumns: "90px 1fr 140px 130px 90px 1fr", gap: 0, padding: "8px 14px", background: "#0A0A0F", borderBottom: "1px solid #1E1E2A", fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  <div>Type</div>
                  <div>Name / Username</div>
                  <div>Client</div>
                  <div>Role</div>
                  <div>Status</div>
                  <div>Actions</div>
                </div>

                {filteredRows.length === 0 && (
                  <div style={{ padding: 20, fontSize: 12, color: "#9CA3AF", textAlign: "center" }}>No accounts match your filter.</div>
                )}

                {filteredRows.map((row, idx) => {
                  const editing = isEditing(row);
                  return (
                    <div key={`cred-row-${row.id}`} style={{ display: "grid", gridTemplateColumns: "90px 1fr 140px 130px 90px 1fr", gap: 0, padding: "10px 14px", borderBottom: idx < filteredRows.length - 1 ? "1px solid #1E1E2A" : "none", alignItems: "center", background: editing ? "#13131E" : "transparent" }}>
                      {/* Type badge */}
                      <div>
                        <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, background: row.entityType === "staff" ? "rgba(99,102,241,0.18)" : "rgba(16,185,129,0.15)", color: row.entityType === "staff" ? "#818CF8" : "#34D399" }}>
                          {row.entityType === "staff" ? "Staff" : "Portal"}
                        </span>
                      </div>

                      {/* Name + username */}
                      <div>
                        <div style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>{row.name}</div>
                        {editing ? (
                          <input
                            autoFocus
                            value={credInlineEdit.newUsername}
                            onChange={(e) => setCredInlineEdit((p) => ({ ...p, newUsername: e.target.value }))}
                            style={{ ...inp, marginTop: 4, fontSize: 11, padding: "4px 7px" }}
                            placeholder="New username"
                          />
                        ) : (
                          <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "'Space Mono', monospace" }}>@{row.username}</div>
                        )}
                      </div>

                      {/* Client */}
                      <div style={{ fontSize: 11, color: "#D1D5DB" }}>
                        {row.clientName || <span style={{ color: "#4B5563" }}>—</span>}
                      </div>

                      {/* Role */}
                      <div>
                        <span style={{ padding: "2px 7px", borderRadius: 5, fontSize: 10, fontWeight: 600, background: row.role === "admin" ? "rgba(245,158,11,0.15)" : "#1E1E2A", color: row.role === "admin" ? "#FCD34D" : "#9CA3AF", textTransform: "capitalize" }}>
                          {row.role}
                        </span>
                      </div>

                      {/* Status */}
                      <div style={{ fontSize: 10, color: row.mustChangePassword ? "#F59E0B" : "#34D399" }}>
                        {row.mustChangePassword ? "⚠ reset pending" : "✓ active"}
                      </div>

                      {/* Actions */}
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                        {editing ? (
                          <>
                            <button onClick={() => {
                              if (row.entityType === "staff") adminChangeStaffMemberUsername(row.id, credInlineEdit.newUsername);
                              else adminChangeAnyPortalUserUsername(row.id, credInlineEdit.newUsername);
                              setCredInlineEdit(null);
                            }} style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 6, padding: "4px 9px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Save</button>
                            <button onClick={() => setCredInlineEdit(null)} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 6, padding: "4px 9px", fontSize: 11, cursor: "pointer" }}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button
                              disabled={row.entityType === "staff" && row.role === "admin" && currentUser?.role !== "admin"}
                              onClick={() => setCredInlineEdit({ id: row.id, entityType: row.entityType, newUsername: row.username })}
                              style={{ background: "transparent", color: "#93C5FD", border: "1px solid rgba(147,197,253,0.35)", borderRadius: 6, padding: "4px 9px", fontSize: 11, cursor: "pointer" }}>
                              Rename
                            </button>
                            <button
                              disabled={row.entityType === "staff" && row.role === "admin" && currentUser?.role !== "admin"}
                              onClick={() => {
                                if (row.entityType === "staff") adminResetStaffMemberPassword(row.id);
                                else adminResetAnyPortalUserPassword(row.id);
                              }}
                              style={{ background: "transparent", color: "#F87171", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 6, padding: "4px 9px", fontSize: 11, cursor: "pointer" }}>
                              Reset Pwd
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ marginTop: 10, fontSize: 11, color: "#4B5563" }}>
                {filteredRows.length} account{filteredRows.length !== 1 ? "s" : ""} shown • Click <strong style={{ color: "#9CA3AF" }}>Rename</strong> to edit a username inline • <strong style={{ color: "#9CA3AF" }}>Reset Pwd</strong> generates a temp password displayed once
              </div>
            </div>
          );
        })()}
      </div>

      {selfServeClientDashboardId && focusedSelfServeClient && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", backdropFilter: "blur(4px)", zIndex: 120, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={(e) => { if (e.target === e.currentTarget) setSelfServeClientDashboardId(null); }}>
          <div style={{ width: "min(1180px, 96vw)", maxHeight: "92vh", overflowY: "auto", background: "#0A0A0F", border: "1px solid #2D2D3A", borderRadius: 14 }}>
            <div style={{ position: "sticky", top: 0, zIndex: 2, background: "rgba(17,17,24,0.95)", borderBottom: "1px solid #1E1E2A", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div>
                <button onClick={() => setSelfServeClientDashboardId(null)} style={{ background: "transparent", border: "none", color: "#9CA3AF", fontSize: 11, cursor: "pointer", padding: 0, marginBottom: 4 }}>{t("selfServe.backToOps", "Back to Self-Serve Ops")}</button>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#fff" }}>{focusedSelfServeClient.name}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF" }}>{focusedSelfServeClient.contact || "—"} • {focusedSelfServeClient.email || "—"}</div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {[{ id: "overview", label: "Overview" }, { id: "billing", label: "Billing" }, { id: "reviews", label: "Reviews" }, { id: "support", label: "Support" }, { id: "actions", label: "Actions" }].map((item) => (
                  <button key={`ss-client-tab-${item.id}`} onClick={() => setSelfServeClientDashboardTab(item.id)} style={{ background: selfServeClientDashboardTab === item.id ? "#1E1E2A" : "transparent", color: selfServeClientDashboardTab === item.id ? "#fff" : "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: "7px 10px", fontSize: 12, cursor: "pointer" }}>{item.label}</button>
                ))}
                <button onClick={() => setSelfServeClientDashboardId(null)} style={{ background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "7px 10px", fontSize: 12, cursor: "pointer" }}>{t("selfServe.closeDashboard", "Close Client Dashboard")}</button>
              </div>
            </div>

            <div style={{ padding: 16 }}>
              {selfServeClientDashboardTab === "overview" && (() => {
                const ssRevs = focusedSelfServeReviews;
                const ssTotal = ssRevs.length;
                const ssResponded = ssRevs.filter((r) => r.status === "approved" || r.status === "published").length;
                const ssResponseRate = ssTotal ? Math.round((ssResponded / ssTotal) * 100) : null;
                const ssPos = ssRevs.filter((r) => Number(r.rating || 0) >= 4).length;
                const ssNeu = ssRevs.filter((r) => Number(r.rating || 0) === 3).length;
                const ssNeg = ssRevs.filter((r) => Number(r.rating || 0) <= 2).length;
                const ssPctPos = ssTotal ? Math.round((ssPos / ssTotal) * 100) : 0;
                const ssPctNeu = ssTotal ? Math.round((ssNeu / ssTotal) * 100) : 0;
                const ssPctNeg = ssTotal ? 100 - ssPctPos - ssPctNeu : 0;
                const ssAvgH = avgResponseHoursByClient(focusedSelfServeClient);
                const ssSlaC = slaComplianceByClient(focusedSelfServeClient);
                const ssPlanTarget = planTargetHours[focusedSelfServeClient.plan] || 12;
                const ssPlatforms = focusedSelfServeClient.platformsManaged || getPlatformsForPlan(focusedSelfServeClient.plan);
                const ssPlatformMeta = {
                  "Google Business Profile": { icon: "🔵", short: "Google",     color: "#4285F4", connected: focusedSelfServeClient.connected },
                  "Facebook":                { icon: "🟦", short: "Facebook",   color: "#1877F2", connected: !!focusedSelfServeClient.facebookConnected },
                  "Trustpilot":              { icon: "🟢", short: "Trustpilot", color: "#00B67A", connected: focusedSelfServeClient.trustpilotConnected },
                  "Yelp":                    { icon: "🔴", short: "Yelp",       color: "#D32323", connected: !!focusedSelfServeClient.yelpConnected },
                  "TripAdvisor":             { icon: "🟤", short: "TripAdvisor",color: "#34E0A1", connected: !!focusedSelfServeClient.tripadvisorConnected },
                };
                const ssConnected = ssPlatforms.filter((p) => (ssPlatformMeta[p] || {}).connected).length;
                const ssCoverPct = ssPlatforms.length ? Math.round((ssConnected / ssPlatforms.length) * 100) : 0;
                const ssAvgRating = Number(focusedSelfServeClient.avgRating || 0);
                const ssRatingGoal = ssAvgRating >= 4.8 ? 5.0 : ssAvgRating >= 4.5 ? 4.8 : ssAvgRating >= 4.0 ? 4.5 : ssAvgRating >= 3.5 ? 4.0 : 3.8;
                const ssRatingPct = ssRatingGoal > 0 ? Math.min(100, Math.round((ssAvgRating / ssRatingGoal) * 100)) : 0;
                const ssMilestones = [10, 25, 50, 100, 250, 500, 1000];
                const ssNextMilestone = ssMilestones.find((m) => m > ssTotal) || ssTotal + 50;
                const ssPrevMilestone = [...ssMilestones].reverse().find((m) => m <= ssTotal) || 0;
                const ssMilestonePct = ssNextMilestone > ssPrevMilestone ? Math.round(((ssTotal - ssPrevMilestone) / (ssNextMilestone - ssPrevMilestone)) * 100) : 100;
                const ssCovGoal = 90;
                const ssPortalCoverage = ssTotal ? Math.round((ssResponded / ssTotal) * 100) : 0;
                const ssCovGoalPct = Math.min(100, Math.round((ssPortalCoverage / ssCovGoal) * 100));
                const ratingGrowth = getClientRatingGrowth(focusedSelfServeClient);
                return (
                  <div style={{ display: "grid", gap: 12 }}>
                    {/* KPI strip */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10 }}>
                      {[
                        { l: "Status", v: focusedSelfServeClient.status || "active", c: "#10B981" },
                        { l: "Plan", v: PLANS.find((p) => p.id === focusedSelfServeClient.plan)?.name || "Custom", c: "#6366F1" },
                        { l: "Avg Rating", v: ssAvgRating > 0 ? `⭐ ${ssAvgRating.toFixed(2)}` : "—", c: "#EC4899" },
                        { l: "Growth", v: formatSignedDelta(ratingGrowth), c: ratingGrowth >= 0 ? "#34D399" : "#F87171" },
                        { l: "Reviews", v: ssTotal, c: "#F59E0B" },
                      ].map((item) => (
                        <div key={`ss-client-kpi-${item.l}`} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12 }}>
                          <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{item.l}</div>
                          <div style={{ fontSize: 18, color: item.c, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{item.v}</div>
                        </div>
                      ))}
                    </div>

                    {/* Sentiment + Response Performance */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                        <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 8 }}>Sentiment</div>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
                          {[{ l: "Positive", v: ssPctPos, n: ssPos, c: "#10B981" }, { l: "Neutral", v: ssPctNeu, n: ssNeu, c: "#F59E0B" }, { l: "Negative", v: ssPctNeg, n: ssNeg, c: "#EF4444" }].map((s) => (
                            <div key={`ss-sent-${s.l}`} style={{ flex: 1, background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, textAlign: "center" }}>
                              <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{s.l}</div>
                              <div style={{ fontSize: 18, color: s.c, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{ssTotal ? `${s.v}%` : "—"}</div>
                              <div style={{ fontSize: 10, color: "#6B7280" }}>{s.n} reviews</div>
                            </div>
                          ))}
                        </div>
                        {ssTotal > 0 && <div style={{ display: "flex", height: 7, borderRadius: 99, overflow: "hidden", background: "#0A0A0F" }}>
                          {ssPctPos > 0 && <div style={{ width: `${ssPctPos}%`, background: "#10B981" }} />}
                          {ssPctNeu > 0 && <div style={{ width: `${ssPctNeu}%`, background: "#F59E0B" }} />}
                          {ssPctNeg > 0 && <div style={{ width: `${ssPctNeg}%`, background: "#EF4444" }} />}
                        </div>}
                      </div>
                      <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                        <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 8 }}>Response Performance</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                          {[
                            { l: "Response Rate", v: ssResponseRate === null ? "—" : `${ssResponseRate}%`, c: ssResponseRate === null ? "#6B7280" : ssResponseRate >= 80 ? "#10B981" : ssResponseRate >= 50 ? "#F59E0B" : "#EF4444" },
                            { l: "Avg Response", v: `${ssAvgH}h`, c: "#0EA5E9", sub: `Target: ${ssPlanTarget}h` },
                            { l: "SLA Compliance", v: `${ssSlaC}%`, c: ssSlaC >= 80 ? "#22C55E" : ssSlaC >= 60 ? "#F59E0B" : "#EF4444" },
                          ].map((k) => (
                            <div key={`ss-resp-${k.l}`} style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10 }}>
                              <div style={{ fontSize: 9, color: "#6B7280", textTransform: "uppercase", marginBottom: 2 }}>{k.l}</div>
                              <div style={{ fontSize: 18, color: k.c, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{k.v}</div>
                              {k.sub && <div style={{ fontSize: 9, color: "#6B7280", marginTop: 2 }}>{k.sub}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Per-platform coverage */}
                    <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase" }}>Platform Coverage</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ fontSize: 11, color: "#9CA3AF" }}>{ssConnected} of {ssPlatforms.length} connected</div>
                          <div style={{ width: 100, height: 6, background: "#1E1E2A", borderRadius: 99, overflow: "hidden" }}>
                            <div style={{ width: `${ssCoverPct}%`, height: "100%", background: ssCoverPct === 100 ? "#10B981" : ssCoverPct >= 50 ? "#F59E0B" : "#EF4444", borderRadius: 99 }} />
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: ssCoverPct === 100 ? "#10B981" : ssCoverPct >= 50 ? "#F59E0B" : "#EF4444", fontFamily: "'Space Mono', monospace" }}>{ssCoverPct}%</div>
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(ssPlatforms.length, 4)}, 1fr)`, gap: 8 }}>
                        {ssPlatforms.map((p) => {
                          const meta = ssPlatformMeta[p] || { icon: "⚪", short: p, color: "#6B7280", connected: false };
                          const pRevs = ssRevs.filter((r) => r.platform === meta.short || r.platform === p);
                          const pRating = pRevs.length ? Number((pRevs.reduce((s, r) => s + Number(r.rating || 0), 0) / pRevs.length).toFixed(1)) : null;
                          return (
                            <div key={`ss-plat-${p}`} style={{ background: "#0A0A0F", border: `1px solid ${meta.connected ? "rgba(99,102,241,0.25)" : "#1E1E2A"}`, borderRadius: 10, padding: 10 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                                <span style={{ fontSize: 14 }}>{meta.icon}</span>
                                <span style={{ fontSize: 11, fontWeight: 700, color: "#fff" }}>{meta.short}</span>
                                <span style={{ marginLeft: "auto", fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 99, background: meta.connected ? "rgba(52,211,153,0.12)" : "rgba(107,114,128,0.15)", color: meta.connected ? "#34D399" : "#6B7280" }}>{meta.connected ? "Active" : "Not connected"}</span>
                              </div>
                              <div style={{ fontSize: 16, fontWeight: 700, color: meta.color, fontFamily: "'Space Mono', monospace" }}>{pRating !== null ? `⭐ ${pRating}` : "—"}</div>
                              <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}>{pRevs.length} review{pRevs.length !== 1 ? "s" : ""}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Review Goals */}
                    <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                      <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", marginBottom: 10 }}>Review Goals & Milestones</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                        <div>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <div style={{ fontSize: 11, color: "#E5E5E5", fontWeight: 600 }}>Star Rating Goal</div>
                            <div style={{ fontSize: 11, color: ssRatingPct >= 100 ? "#10B981" : "#F59E0B", fontFamily: "'Space Mono', monospace" }}>{ssRatingPct >= 100 ? "Achieved!" : `${ssRatingPct}%`}</div>
                          </div>
                          <div style={{ height: 7, background: "#1E1E2A", borderRadius: 99, overflow: "hidden", marginBottom: 5 }}>
                            <div style={{ width: `${ssRatingPct}%`, height: "100%", borderRadius: 99, background: ssRatingPct >= 100 ? "#10B981" : "linear-gradient(90deg,#F59E0B,#FBBF24)" }} />
                          </div>
                          <div style={{ fontSize: 10, color: "#9CA3AF" }}>Current: <strong style={{ color: "#fff" }}>⭐ {ssAvgRating > 0 ? ssAvgRating.toFixed(1) : "—"}</strong> · Goal: <strong style={{ color: "#FCD34D" }}>⭐ {ssRatingGoal.toFixed(1)}</strong></div>
                        </div>
                        <div>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <div style={{ fontSize: 11, color: "#E5E5E5", fontWeight: 600 }}>Review Milestone</div>
                            <div style={{ fontSize: 11, color: "#6366F1", fontFamily: "'Space Mono', monospace" }}>{ssMilestonePct}%</div>
                          </div>
                          <div style={{ height: 7, background: "#1E1E2A", borderRadius: 99, overflow: "hidden", marginBottom: 5 }}>
                            <div style={{ width: `${ssMilestonePct}%`, height: "100%", borderRadius: 99, background: "linear-gradient(90deg,#6366F1,#8B5CF6)" }} />
                          </div>
                          <div style={{ fontSize: 10, color: "#9CA3AF" }}>Have: <strong style={{ color: "#fff" }}>{ssTotal}</strong> · Next: <strong style={{ color: "#A5B4FC" }}>{ssNextMilestone}</strong></div>
                        </div>
                        <div>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <div style={{ fontSize: 11, color: "#E5E5E5", fontWeight: 600 }}>Response Coverage</div>
                            <div style={{ fontSize: 11, color: ssCovGoalPct >= 100 ? "#10B981" : "#0EA5E9", fontFamily: "'Space Mono', monospace" }}>{ssCovGoalPct >= 100 ? "Goal met!" : `${ssCovGoalPct}%`}</div>
                          </div>
                          <div style={{ height: 7, background: "#1E1E2A", borderRadius: 99, overflow: "hidden", marginBottom: 5 }}>
                            <div style={{ width: `${ssCovGoalPct}%`, height: "100%", borderRadius: 99, background: ssCovGoalPct >= 100 ? "#10B981" : "linear-gradient(90deg,#0EA5E9,#38BDF8)" }} />
                          </div>
                          <div style={{ fontSize: 10, color: "#9CA3AF" }}>Current: <strong style={{ color: "#fff" }}>{ssPortalCoverage}%</strong> · Goal: <strong style={{ color: "#7DD3FC" }}>{ssCovGoal}%</strong></div>
                        </div>
                      </div>
                    </div>

                    {/* Business info */}
                    <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14, fontSize: 12, color: "#D1D5DB", lineHeight: 1.9 }}>
                      Address: {formatAddress(focusedSelfServeClient)} · Phone: {focusedSelfServeClient.phone || "—"} · Next payment: {focusedSelfServeClient.nextPayment || "—"} · Trial end: {focusedSelfServeClient.trialEndDate || "—"}
                    </div>
                  </div>
                );
              })()}

              {selfServeClientDashboardTab === "billing" && <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                  <select disabled={!canSelfServeManage} value={focusedSelfServeClient.paymentMethod || "bank-transfer"} onChange={(e) => updateSelfServeBilling(focusedSelfServeClient.id, { paymentMethod: e.target.value })} style={inp}>
                    {CLIENT_PAYMENT_METHODS.map((method) => <option key={`focus-billing-method-${method.id}`} value={method.id}>{method.label}</option>)}
                  </select>
                  <select disabled={!canSelfServeManage} value={focusedSelfServeClient.paymentStatus || "paid"} onChange={(e) => updateSelfServeBilling(focusedSelfServeClient.id, { paymentStatus: e.target.value })} style={inp}>
                    <option value="trialing">Trialing</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                    <option value="paused">Paused</option>
                  </select>
                  <input disabled placeholder="Billing detail (masked)" value={focusedSelfServeClient.paymentDetailMasked || "On file"} style={inp} />
                </div>
              </div>}

              {selfServeClientDashboardTab === "reviews" && <div style={{ display: "grid", gap: 8 }}>
                {focusedSelfServeReviews.slice(0, 40).map((review) => (
                  <div key={`focus-review-${review.id}`} style={{ background: "#111118", border: `1px solid ${Number(review.rating || 0) <= 2 ? "rgba(239,68,68,0.35)" : "#1E1E2A"}`, borderRadius: 10, overflow: "hidden" }}>
                    <div style={{ padding: "10px 14px", borderBottom: "1px solid #1E1E2A", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>{review.author || "Customer"}</div>
                        <div style={{ fontSize: 11, color: "#6B7280" }}>{review.platform || "Google"} · {review.date || "—"}</div>
                      </div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <Stars r={review.rating} />
                        <Badge type={review.status} label={review.status} />
                      </div>
                    </div>
                    <div style={{ padding: 12 }}>
                      <div style={{ fontSize: 13, color: "#D1D5DB", lineHeight: 1.7, marginBottom: review.aiResponse ? 10 : 0 }}>{review.text || "—"}</div>
                      {review.aiResponse && (
                        <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, fontSize: 12, color: "#A7F3D0", lineHeight: 1.7 }}>
                          <span style={{ color: "#10B981", fontWeight: 700 }}>Response: </span>{review.aiResponse}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {focusedSelfServeReviews.length === 0 && <div style={{ fontSize: 12, color: "#9CA3AF" }}>No reviews for this client yet.</div>}
              </div>}

              {selfServeClientDashboardTab === "support" && <div style={{ display: "grid", gap: 8 }}>
                {focusedSelfServeTickets.map((ticket) => (
                  <div key={`focus-ticket-${ticket.id}`} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 10, padding: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <div style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>{ticket.subject}</div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <Badge type={ticket.priority === "urgent" ? "overdue" : "pending"} label={ticket.priority} />
                        <Badge type={ticket.status === "resolved" ? "approved" : ticket.status === "closed" ? "rejected" : "pending"} label={ticket.status} />
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 6 }}>{ticket.ticketNumber || ticket.id}</div>
                    <div style={{ fontSize: 12, color: "#D1D5DB", lineHeight: 1.7 }}>{ticket.message}</div>
                  </div>
                ))}
                {focusedSelfServeTickets.length === 0 && <div style={{ fontSize: 12, color: "#9CA3AF" }}>No support tickets for this client.</div>}
              </div>}

              {selfServeClientDashboardTab === "actions" && <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button disabled={!canSelfServeManage} onClick={() => resetSelfServeOwnerPassword(focusedSelfServeClient.id)} style={{ background: "#1E1E2A", color: "#fff", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 11px", fontSize: 12, cursor: canSelfServeManage ? "pointer" : "not-allowed", opacity: canSelfServeManage ? 1 : 0.6 }}>Reset Owner Password</button>
                <button disabled={!canSelfServeManage} onClick={() => toggleSelfServeBan(focusedSelfServeClient.id, focusedSelfServeClient.status !== "suspended")} style={{ background: "transparent", color: focusedSelfServeClient.status === "suspended" ? "#10B981" : "#EF4444", border: `1px solid ${focusedSelfServeClient.status === "suspended" ? "rgba(16,185,129,0.35)" : "rgba(239,68,68,0.35)"}`, borderRadius: 8, padding: "8px 11px", fontSize: 12, cursor: canSelfServeManage ? "pointer" : "not-allowed", opacity: canSelfServeManage ? 1 : 0.6 }}>{focusedSelfServeClient.status === "suspended" ? "Reactivate" : "Suspend"}</button>
                <button disabled={!canSelfServeReport} onClick={() => generateReport(focusedSelfServeClient)} style={{ background: "#0A0A0F", color: "#D1D5DB", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 11px", fontSize: 12, cursor: canSelfServeReport ? "pointer" : "not-allowed", opacity: canSelfServeReport ? 1 : 0.6 }}>Generate Report</button>
                <button disabled={!canSelfServeManage} onClick={() => deleteSelfServeAccount(focusedSelfServeClient.id)} style={{ background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "8px 11px", fontSize: 12, cursor: canSelfServeManage ? "pointer" : "not-allowed", opacity: canSelfServeManage ? 1 : 0.6 }}>Delete Account</button>
              </div>}
            </div>
          </div>
        </div>
      )}

      {/* ===== OUTREACH MODAL ===== */}
      {modal === "outreach" && outreachRecipient && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 16, width: 680, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #1E1E2A", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <div><div style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>📣 Outreach to {outreachRecipient.name}</div><div style={{ fontSize: 12, color: "#6B7280" }}>{outreachRecipient.contact} • {outreachRecipient.email}</div></div>
              <button onClick={() => setModal(null)} style={{ background: "none", border: "none", color: "#6B7280", fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: 22, overflowY: "auto", flex: 1 }}>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Template</label>
                <select value={outreachTemplate.id} onChange={e => changeOutreachTemplate(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
                  {OUTREACH_TEMPLATES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 14 }}><label style={lbl}>Subject</label><input type="text" value={outreachSubject} onChange={e => setOutreachSubject(e.target.value)} style={inp} /></div>
              <div style={{ marginBottom: 14 }}><label style={lbl}>Message</label><textarea value={outreachBody} onChange={e => setOutreachBody(e.target.value)} style={{ ...inp, minHeight: 280, resize: "vertical", lineHeight: 1.6 }} /></div>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Attachment (optional)</label>
                <input type="file" onChange={(e) => setOutreachAttachment(e.target.files?.[0] || null)} style={{ ...inp, padding: 8 }} />
                {outreachAttachment && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>Selected: {outreachAttachment.name}</div>}
              </div>
              <button onClick={sendOutreach} style={{ width: "100%", background: "linear-gradient(135deg, #6366F1, #8B5CF6)", color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>📨 Send to {outreachRecipient.email}</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== CLIENT MESSAGE MODAL ===== */}
      {modal === "clientMsg" && clientMsgRecipient && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 16, width: 680, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #1E1E2A", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <div><div style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>✉️ Message to {clientMsgRecipient.name}</div><div style={{ fontSize: 12, color: "#6B7280" }}>{clientMsgRecipient.contact} • {clientMsgRecipient.email}</div></div>
              <button onClick={() => setModal(null)} style={{ background: "none", border: "none", color: "#6B7280", fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: 22, overflowY: "auto", flex: 1 }}>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Message Type</label>
                <select value={clientMsgTemplate.id} onChange={e => changeClientMsgTemplate(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
                  {CLIENT_MSG_TEMPLATES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 14 }}><label style={lbl}>Subject</label><input type="text" value={clientMsgSubject} onChange={e => setClientMsgSubject(e.target.value)} style={inp} /></div>
              <div style={{ marginBottom: 14 }}><label style={lbl}>Message</label><textarea value={clientMsgBody} onChange={e => setClientMsgBody(e.target.value)} style={{ ...inp, minHeight: 280, resize: "vertical", lineHeight: 1.6 }} /></div>
              {clientMsgTemplate.id === "payment-reminder" && (
                <div style={{ marginBottom: 14, background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 10, padding: 12 }}>
                  <button onClick={previewPaymentReminderMsg} style={{ width: "100%", background: "transparent", color: "#93C5FD", border: "1px solid rgba(147,197,253,0.35)", borderRadius: 8, padding: "9px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Preview Reminder Before Send</button>
                  {clientMsgPreview && (
                    <div style={{ marginTop: 10, fontSize: 12, color: "#D1D5DB", lineHeight: 1.7 }}>
                      <div style={{ marginBottom: 6, color: "#9CA3AF" }}>Preview ({clientMsgPreview.country || "country not set"})</div>
                      <div style={{ marginBottom: 6 }}><strong>Subject:</strong> {clientMsgPreview.subject}</div>
                      <div style={{ whiteSpace: "pre-wrap", background: "#111118", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10 }}>{clientMsgPreview.message}</div>
                    </div>
                  )}
                </div>
              )}
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Attachment (optional)</label>
                <input type="file" onChange={(e) => setClientMsgAttachment(e.target.files?.[0] || null)} style={{ ...inp, padding: 8 }} />
                {clientMsgAttachment && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>Selected: {clientMsgAttachment.name}</div>}
              </div>
              <button onClick={sendClientMsg} style={{ width: "100%", background: "linear-gradient(135deg, #10B981, #059669)", color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>📨 Send to {clientMsgRecipient.email}</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== REPORT MODAL ===== */}
      {modal === "report" && modalData.client && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 16, width: 980, maxHeight: "90vh", overflowY: "auto", padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
              <div>
                <div style={{ fontFamily: "'Space Mono', monospace", color: "#10B981", fontWeight: 700, fontSize: 20 }}>Replai<span style={{ color: "#6366F1" }}>.</span>work</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginTop: 4 }}>Detailed Client Report — {modalData.client.name}</div>
                <div style={{ fontSize: 12, color: "#6B7280" }}>Prepared on {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })} • Client-facing PDF ready</div>
              </div>
              <button onClick={() => setModal(null)} style={{ background: "none", color: "#9CA3AF", border: "none", fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "0.95fr 1.05fr", gap: 12, marginBottom: 14 }}>
              <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 12, padding: 12 }}>
                <div style={{ fontSize: 12, color: "#fff", fontWeight: 700, marginBottom: 10 }}>Report Builder</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                  <div>
                    <label style={lbl}>Report Period — From</label>
                    <input type="date" value={reportFrom} onChange={e => setReportFrom(e.target.value)} style={inp} {...datePickerFieldProps} />
                  </div>
                  <div>
                    <label style={lbl}>Report Period — To</label>
                    <input type="date" value={reportTo} onChange={e => setReportTo(e.target.value)} style={inp} {...datePickerFieldProps} />
                  </div>
                </div>
                <label style={lbl}>Pre-Text Email Intro</label>
                <textarea value={reportIntro} onChange={e => setReportIntro(e.target.value)} style={{ ...inp, minHeight: 96, resize: "vertical", lineHeight: 1.6, marginBottom: 8 }} />
                <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 8 }}>Use placeholders like {"{contact}"} for personalization.</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <button onClick={() => setReportIntro("Hi {contact},\n\nPlease find your monthly Replai report attached. We included KPI highlights, trend insights, and operational recommendations for the selected period.")} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 10px", fontSize: 11, cursor: "pointer" }}>Use Executive Intro</button>
                  <button onClick={() => setReportIntro("Hi {contact},\n\nHere is your detailed performance report with review volume, response coverage, rating movement, and SLA compliance for this period.")} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: "8px 10px", fontSize: 11, cursor: "pointer" }}>Use Operational Intro</button>
                </div>
              </div>

              <div style={{ background: "#0A0A0F", borderRadius: 12, border: "1px solid #1E1E2A", padding: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 10 }}>Professional PDF Preview</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 10 }}>
                  {[{ l: "Reviews", v: buildReportMetrics(modalData.client, reportFrom, reportTo).totalReviews, c: "#6366F1" }, { l: "Responses", v: buildReportMetrics(modalData.client, reportFrom, reportTo).responded, c: "#10B981" }, { l: "Response Rate", v: `${buildReportMetrics(modalData.client, reportFrom, reportTo).responseRate}%`, c: "#F59E0B" }, { l: "Rating", v: buildScopedMetrics([modalData.client.id], reportFrom, reportTo).avgRating || "—", c: "#EC4899" }].map((s, i) => (
                    <div key={i} style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 8, padding: 10, textAlign: "center" }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: s.c, fontFamily: "'Space Mono', monospace" }}>{s.v}</div>
                      <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{s.l}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4 }}>Review vs Response Trend</div>
                    <TinyBars data={(modalData.client.monthlyReviews || []).map((v) => Number(v || 0))} color="#6366F1" />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4 }}>Rating Trend</div>
                    <TinyTrend data={[Math.max(0, (modalData.client.avgRating || 4) - 0.3), Math.max(0, (modalData.client.avgRating || 4) - 0.1), modalData.client.avgRating || 4]} color="#EC4899" fill="rgba(236,72,153,0.12)" />
                  </div>
                </div>
                <div style={{ fontSize: 12, color: "#9CA3AF", lineHeight: 1.8 }}>
                  <strong style={{ color: "#fff" }}>Client:</strong> {modalData.client.name}<br/>
                  <strong style={{ color: "#fff" }}>Contact:</strong> {modalData.client.contact} ({modalData.client.email})<br/>
                  <strong style={{ color: "#fff" }}>Phone:</strong> {modalData.client.phone || "—"}<br/>
                  <strong style={{ color: "#fff" }}>Address:</strong> {formatAddress(modalData.client)}<br/>
                  <strong style={{ color: "#fff" }}>Platforms:</strong> {(modalData.client.platformsManaged || getPlatformsForPlan(modalData.client.plan)).join(", ")}<br/>
                  <strong style={{ color: "#fff" }}>Range:</strong> {reportFrom} to {reportTo}
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
              <button onClick={() => setModal(null)} style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: 10, fontSize: 13, cursor: "pointer" }}>Close</button>
              <button onClick={() => openReportPdfWindow(false)} style={{ background: "#1E40AF", color: "#fff", border: "none", borderRadius: 8, padding: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>👁 Preview PDF</button>
              <button onClick={() => openReportPdfWindow(true)} style={{ background: "#0EA5E9", color: "#fff", border: "none", borderRadius: 8, padding: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>⬇ Download PDF</button>
              <button onClick={sendReport} style={{ background: "linear-gradient(135deg, #6366F1, #8B5CF6)", color: "#fff", border: "none", borderRadius: 8, padding: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>📨 Send Report</button>
            </div>
          </div>
        </div>
      )}

      {modal === "contract-sign" && modalData.client && modalData.envelope && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 16, width: 760, maxHeight: "90vh", overflowY: "auto", padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
              <div>
                <div style={{ fontFamily: "'Space Mono', monospace", color: "#10B981", fontWeight: 700, fontSize: 20 }}>DocuSign</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginTop: 4 }}>Electronic contract signature</div>
                <div style={{ fontSize: 12, color: "#6B7280" }}>Envelope {modalData.envelope.envelopeId} • {modalData.client.name}</div>
              </div>
              <button onClick={() => setModal(null)} style={{ background: "none", color: "#9CA3AF", border: "none", fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>

            <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14, marginBottom: 14, fontSize: 12, color: "#D1D5DB", lineHeight: 1.8 }}>
              You are signing electronically for <strong>{modalData.client.name}</strong>. The provider signature image is not embedded in the contract PDF. Execution metadata is stored against this DocuSign envelope instead.
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <label style={lbl}>Full legal name</label>
                <input value={contractESignForm.fullName} onChange={(e) => setContractESignForm((p) => ({ ...p, fullName: e.target.value }))} style={inp} />
              </div>
              <div>
                <label style={lbl}>Title / role</label>
                <input value={contractESignForm.title} onChange={(e) => setContractESignForm((p) => ({ ...p, title: e.target.value }))} placeholder="Owner" style={inp} />
              </div>
            </div>

            <div style={{ background: "#0A0A0F", border: "1px solid #1E1E2A", borderRadius: 12, padding: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 8 }}>Contract preview</div>
              <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 8 }}>Effective date: {modalData.envelope.effectiveDate || contractEffectiveDate}</div>
              <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #1E1E2A", borderRadius: 10, background: "#111118" }}>
                <iframe title="contract-preview" srcDoc={buildContractHtml(modalData.client, modalData.envelope.effectiveDate || contractEffectiveDate)} style={{ width: "100%", height: 320, border: "none", background: "#fff" }} />
              </div>
            </div>

            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: "#E5E5E5", marginBottom: 16 }}>
              <input type="checkbox" checked={contractESignForm.accept} onChange={(e) => setContractESignForm((p) => ({ ...p, accept: e.target.checked }))} style={{ marginTop: 3 }} />
              <span>I confirm I am authorized to sign this agreement electronically on behalf of the client.</span>
            </label>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setModal(null)} style={{ flex: 1, background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: 10, cursor: "pointer" }}>Cancel</button>
              <button onClick={completeClientContractSigning} style={{ flex: 1, background: "linear-gradient(135deg,#10B981,#059669)", color: "#fff", border: "none", borderRadius: 8, padding: 10, fontWeight: 700, cursor: "pointer" }}>Complete DocuSign Signature</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== NEW PROSPECT MODAL ===== */}
      {modal === "newProspect" && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 16, width: 480, padding: 28 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginBottom: 18 }}>Add New Prospect</div>
            {[{ k: "name", l: "Business Name", p: "Tony's Deli" }, { k: "contact", l: "Contact Person", p: "Tony Rossi" }, { k: "email", l: "Email", p: "tony@example.com" }, { k: "location", l: "Location", p: "Austin, TX" }, { k: "type", l: "Business Type", p: "Restaurant" }].map(f => (
              <div key={f.k} style={{ marginBottom: 12 }}><label style={lbl}>{f.l}</label><input placeholder={f.p} style={inp} id={`np_${f.k}`} /></div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button onClick={() => setModal(null)} style={{ flex: 1, background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: 10, fontSize: 13, cursor: "pointer" }}>Cancel</button>
              <button onClick={() => {
                const g = k => document.getElementById(`np_${k}`).value;
                if (!g("name") || !g("email")) return;
                setProspects(p => [...p, { id: Date.now(), name: g("name"), contact: g("contact"), email: g("email"), location: g("location"), type: g("type") || "Business", unrespondedReviews: 0, rating: 0, lastContact: null, status: "new", notes: "" }]);
                setModal(null); notify("Prospect added");
              }} style={{ flex: 1, background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Add Prospect</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== NEW CLIENT MODAL ===== */}
      {modal === "newClient" && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ background: "#111118", border: "1px solid #1E1E2A", borderRadius: 16, width: 580, maxHeight: "90vh", overflowY: "auto", padding: 28 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginBottom: 18 }}>Add New Client</div>
            {[{ k: "name", l: "Business Name" }, { k: "contact", l: "Contact Person" }, { k: "email", l: "Email" }, { k: "phone", l: "Phone Number" }].map(f => (
              <div key={f.k} style={{ marginBottom: 12 }}><label style={lbl}>{f.l}</label><input style={inp} id={`nc_${f.k}`} /></div>
            ))}
            <div style={{ marginBottom: 12 }}><label style={lbl}>Address Line</label><input style={inp} id="nc_addressLine1" placeholder="Street + number" /></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div><label style={lbl}>Postcode</label><input style={inp} id="nc_postcode" /></div>
              <div><label style={lbl}>City</label><input style={inp} id="nc_city" /></div>
            </div>
            <div style={{ marginBottom: 12 }}><label style={lbl}>Country</label><input style={inp} id="nc_country" placeholder="Germany" /></div>
            <div style={{ marginBottom: 12 }}><label style={lbl}>Plan</label>
              <select style={inp} id="nc_plan" defaultValue="starter" onChange={(e) => {
                const elm = document.getElementById("nc_platforms");
                if (elm) elm.value = getPlatformsForPlan(e.target.value).join(", ");
              }}>{PLANS.map(p => <option key={p.id} value={p.id}>{p.name} — €{p.price}/mo</option>)}</select>
            </div>
            <div style={{ marginBottom: 12 }}><label style={lbl}>Payment Method</label>
              <select style={inp} id="nc_paymentMethod" defaultValue="bank-transfer">
                {CLIENT_PAYMENT_METHODS.map((method) => <option key={`new-client-payment-${method.id}`} value={method.id}>{method.label}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}><label style={lbl}>Payment Detail (optional)</label><input style={inp} id="nc_paymentDetail" placeholder="billing@business.com or card/account hint" /></div>
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Platforms Managed (auto from plan)</label>
              <textarea id="nc_platforms" defaultValue={getPlatformsForPlan("starter").join(", ")} style={{ ...inp, minHeight: 60, resize: "vertical" }} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button onClick={() => setModal(null)} style={{ flex: 1, background: "transparent", color: "#9CA3AF", border: "1px solid #2D2D3A", borderRadius: 8, padding: 10, fontSize: 13, cursor: "pointer" }}>Cancel</button>
              <button onClick={async () => {
                const g = k => document.getElementById(`nc_${k}`).value;
                if (!g("name") || !g("email")) return;
                const planId = document.getElementById("nc_plan").value;
                const addressLine1 = document.getElementById("nc_addressLine1").value;
                const postcode = document.getElementById("nc_postcode").value;
                const city = document.getElementById("nc_city").value;
                const country = document.getElementById("nc_country").value;
                const platformsManaged = document.getElementById("nc_platforms").value.split(",").map(s => s.trim()).filter(Boolean);
                const paymentMethod = String(document.getElementById("nc_paymentMethod")?.value || "bank-transfer").trim().toLowerCase();
                const paymentDetail = String(document.getElementById("nc_paymentDetail")?.value || "").trim();
                if (paymentMethod === "bank-transfer") {
                  const bankCountry = String(country || "").trim();
                  if (!bankCountry) {
                    notify("Country is required for local bank transfer setup", "error");
                    return;
                  }
                  const bankConfig = getBankTransferConfigForCountry(bankCountry);
                  if (!bankConfig) {
                    notify(`Local bank transfer not possible for ${bankCountry}. Please choose Stripe, Credit Card, or PayPal.`, "error");
                    return;
                  }
                }
                const joinDate = new Date().toISOString().split("T")[0];
                const autoChargeAfterTrial = AUTO_BILLING_METHODS.includes(paymentMethod);
                const trialEndDate = autoChargeAfterTrial ? addDaysISO(joinDate, CLIENT_FREE_TRIAL_DAYS) : null;
                const newClient = {
                  id: Date.now(),
                  name: g("name"),
                  contact: g("contact"),
                  email: g("email"),
                  phone: g("phone"),
                  addressLine1,
                  postcode,
                  city,
                  country,
                  location: [city, country].filter(Boolean).join(", "),
                  platformsManaged: platformsManaged.length ? platformsManaged : getPlatformsForPlan(planId),
                  avatar: "🏢",
                  plan: planId,
                  status: "active",
                  contractStatus: "none",
                  connected: false,
                  trustpilotConnected: false,
                  googleLocations: [],
                  reviewCount: 0,
                  avgRating: 0,
                  ratingAtOnboarding: 0,
                  lastPayment: null,
                  nextPayment: autoChargeAfterTrial ? trialEndDate : null,
                  paymentStatus: autoChargeAfterTrial ? "trialing" : "pending",
                  paymentMethod,
                  paymentDetailMasked: paymentDetail ? maskPaymentDetail(paymentDetail) : "",
                  joinDate,
                  monthlyReviews: [],
                  responded: 0,
                  serviceMode: "agency",
                  trialStartDate: autoChargeAfterTrial ? joinDate : null,
                  trialEndDate,
                  autoChargeAfterTrial,
                  stripeCustomerId: null,
                  stripeSubscriptionId: null,
                };
                setClients(p => [...p, newClient]);
                setModal(null);
                await sendContractViaDocuSign(newClient);
              }} style={{ flex: 1, background: "#6366F1", color: "#fff", border: "none", borderRadius: 8, padding: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Add Client</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes slideIn{from{transform:translateX(20px);opacity:0}to{transform:translateX(0);opacity:1}}*{box-sizing:border-box}::-webkit-scrollbar{width:5px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:#2D2D3A;border-radius:99px}select option{background:#111118;color:#E5E5E5}`}</style>
    </div>
  );
}
